/**
 * Versions-/Update-Log. Manuell gepflegt: bei jedem nennenswerten Feature einen
 * Eintrag oben ergänzen (neueste Version zuerst). `APP_VERSION` = oberster Eintrag.
 */
export interface ChangelogEntry {
  version: string;
  /**
   * ISO-Datum (JJJJ-MM-TT) — ältere Einträge ohne Uhrzeit. Neue Einträge nutzen
   * ISO-Datum+Zeit (JJJJ-MM-TTTHH:mm, lokale Zeit) für mehr Präzision, siehe
   * `hasTime()`/`ChangelogPage.tsx`.
   */
  date: string;
  title: string;
  /** Art der Änderungen — steuert nur das Badge. */
  tag: 'feature' | 'fix' | 'improvement';
  /** Beschreibungen zur Umsetzung (was wurde gebaut). */
  changes: string[];
}

/** true, wenn `entry.date` eine Uhrzeit trägt (JJJJ-MM-TTTHH:mm) statt nur ein Datum. */
export function hasTime(date: string): boolean {
  return date.includes('T');
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '3.530.0',
    date: '2026-08-20T17:15',
    title: 'Urlaubs- und Reiseanträge: Admins dürfen auch nach der Genehmigung löschen',
    tag: 'feature',
    changes: [
      'Im „Verwaltung"-Tab von Fehlzeiten und Reisekosten gibt es jetzt einen Löschen-Button je Zeile — unabhängig vom Status. Vorher konnte nur der Mitarbeiter selbst und nur einen noch ausstehenden Antrag löschen.',
    ],
  },
  {
    version: '3.529.0',
    date: '2026-08-20T16:45',
    title: 'Jahresübersicht zeigt Tage ohne Erfassung',
    tag: 'feature',
    changes: [
      'Die persönliche Jahresübersicht zeigt jetzt, an welchen Arbeitstagen bis heute weder eine Zeitbuchung noch ein genehmigter Urlaub, eine Krankmeldung oder ein Arzttermin erfasst wurde — als Kennzahl und als Liste.',
    ],
  },
  {
    version: '3.528.0',
    date: '2026-08-20T16:30',
    title: 'Jahresübersicht: Farbe an die übrigen Charts angeglichen',
    tag: 'improvement',
    changes: [
      'Kennzahlen, Monatsverlauf und Rang-Listen der persönlichen Jahresübersicht nutzen jetzt dieselbe blaue Akzentfarbe wie die Admin-„Zeit-Analyse" — vorher ein neutraler Ton.',
    ],
  },
  {
    version: '3.527.0',
    date: '2026-08-20T16:15',
    title: 'Reisekosten lassen sich in Zeiteinträge wandeln',
    tag: 'feature',
    changes: [
      'Eine genehmigte, eigene Reise zeigt jetzt „In Zeiterfassung übernehmen" — spart das doppelte Erfassen von Reisetagen. Firma, Kategorie, Projekt und Tätigkeit werden einmal für die ganze Reise gewählt; die Stunden je Tag sind aus der Reise vorgeschlagen und vor dem Anlegen editierbar. Ein Tag ohne Stunden wird übersprungen statt als leerer Eintrag angelegt.',
      'Eine Reise lässt sich nur einmal wandeln — ist es schon passiert, zeigt die Reise stattdessen, wann.',
    ],
  },
  {
    version: '3.526.0',
    date: '2026-08-20T15:30',
    title: 'Zeiterfassung: Zeitprotokoll und meistgenutzte Projekte überarbeitet',
    tag: 'improvement',
    changes: [
      'Das Zeitprotokoll auf dem Zeiterfassungs-Dashboard läuft jetzt über dieselbe Tabellen-Komponente wie die übrigen Listen im Haus: Spalten ein-/ausblenden, Dichte umstellen, CSV-Export der eigenen Buchungen — und auf iPad/Handy automatisch Karten statt einer separaten, seltener gepflegten Ansicht.',
      '„Meistgenutzte Projekte" zeigt jetzt eine Rang-Liste mit Platzierung (1., 2., 3. …) statt einer schlichten Balkenliste — dieselbe Optik wie das Berichtscenter der Analyse.',
      'Mobile-Audit für iPhone und iPad mini über alle Zeiterfassungs-Seiten lief ohne Überlauf oder zu kleine Berührungsziele.',
    ],
  },
  {
    version: '3.525.0',
    date: '2026-08-20T15:00',
    title: 'Stücklisten-Wizard mit Baukasten-Blockauswahl',
    tag: 'feature',
    changes: [
      'Eine Stückliste anlegen ist jetzt ein Wizard statt eines leeren Formulars: Vorlage wählen, Blöcke (Baukasten) an- oder abwählen — z. B. „1. Hauptwelle" —, Vorschau ansehen, anlegen. Nicht jedes Projekt braucht jeden Block; abgewählte Blöcke fallen aus der neuen Stückliste heraus.',
      'Die Vorschau zeigt sofort die Mengen und Artikel, die die Projektauslegung schon hergibt — beim Anlegen landet man in einer bereits gefüllten Stückliste, nicht in einer leeren.',
      'Neuer Reiter „Stückliste" unter „Technische Auslegung" auf der Projekt-Detailseite — bisher gab es dafür nur die globale Übersicht unter Technik.',
      'Das alte, einfache Anlege-Formular ist entfallen; der Wizard deckt seinen Umfang ab und mehr.',
    ],
  },
  {
    version: '3.524.0',
    date: '2026-08-20T14:45',
    title: 'Zeiterfassung: persönliche Jahresübersicht',
    tag: 'feature',
    changes: [
      'Neue Seite „Jahresübersicht" zeigt die eigenen Zeitbuchungen eines Kalenderjahres grafisch: Gesamtstunden, Monatsverlauf, Top-Projekte und Top-Kategorien — mit Jahres-Umschalter, erreichbar über die Navigation und einen neuen Knopf im Zeiterfassungs-Dashboard.',
      'Bewusst ohne Kosten (wie schon die Kosten-Spalte der Admin-Auswertung) — die eigene Übersicht zeigt ausschließlich Stunden, nicht den daraus ablesbaren Stundensatz.',
    ],
  },
  {
    version: '3.523.0',
    date: '2026-08-20T14:30',
    title: 'Zusatzmaterial am Artikel: neue Tabelle und Artikelauswahl wie im Beleg (VB-26026)',
    tag: 'improvement',
    changes: [
      'Der Reiter „Zusatzmaterial" am Artikel trägt jetzt die gleiche Tabelle wie die übrigen Listen im Haus: Spalten sortieren, Spalten ein- und ausblenden, Dichte umstellen, CSV-Export — und auf dem iPad Karten statt Seitwärts-Scrollen.',
      'Die Artikelauswahl im Dialog ist dasselbe Vollbild-Pop-up wie in den Verkaufsbelegen (Warengruppen links, Treffer mittig, Vorschau mit Variantenvergleich rechts). Das alte, schmale „Artikel wählen"-Fenster ist damit weg.',
      'Schon hinterlegte Zeilen und der Artikel selbst stehen im Pop-up nicht mehr zur Auswahl. Bisher konnte man sie wählen und lief erst beim Speichern in eine Fehlermeldung.',
      'Eine mehrstufige Zeile lässt sich aufklappen und zeigt die vollständige Auflösung — was über sie zusätzlich mitkommt, skaliert auf eine Einheit des Hauptartikels. Bisher stand dort nur das Abzeichen „mehrstufig".',
      'Ab sechs Zeilen gibt es ein Suchfeld über Nummer, Bezeichnung und Notiz. Die Artikelnummer sortiert natürlich (9 vor 10), Zeilen ohne Notiz stehen beim Sortieren nach Notiz hinten.',
      'Behoben: Der Knopf „+ Zusatzmaterial" zog sich als schwarzer Balken über die ganze Kartenbreite (im Screenshot der Meldung gut zu sehen) — er sitzt jetzt oben rechts in der Karte.',
    ],
  },
  {
    version: '3.522.0',
    date: '2026-08-20T12:30',
    title: 'Zeit-Analyse: Fehler beim Blättern behoben (250/500 Buchungen pro Seite)',
    tag: 'fix',
    changes: [
      'Bei „Alle Buchungen" in der Zeit-Analyse zeigte ein Wechsel der Seitengröße von 100 auf 250 oder 500 fälschlich „Keine Buchungen im Filter" — der Server lehnte diese Werte ab, ohne dass die Seite den Fehler anzeigte. Die Grenze ist jetzt auf 500 angehoben, und ein Fehlerbanner macht künftige Ladefehler sichtbar statt sie wie leere Ergebnisse aussehen zu lassen.',
    ],
  },
  {
    version: '3.521.0',
    date: '2026-08-20T12:00',
    title: 'Stücklisten-Blöcke: Artikel aus der Projektauslegung',
    tag: 'feature',
    changes: [
      'Manche Bauteile — als erstes die Hauptwelle — haben in der Stücklisten-Vorlage einen festen Katalog-Artikel, obwohl der wirklich passende Artikel sich pro Projekt ändert (bei der Hauptwelle: das Rohr passend zur Beckenbreite). Solche Zeilen lassen sich jetzt an eine bestimmte Zeile der Projektauslegung binden.',
      'Beim Füllen der Stückliste zeigt eine so verknüpfte Zeile ein Blockzeichen (z. B. „1. Hauptwelle") und übernimmt den Artikel, den die Auslegung tatsächlich gewählt hat — statt des Katalog-Artikels der Vorlage.',
      'Ist die Auslegung an der Stelle noch offen, meldet die Zeile das ausdrücklich statt stillschweigend den Katalog-Artikel unterzuschieben.',
      'In der Vorlagenpflege gibt es dafür zwei neue Spalten: „Block" (welche Baugruppe der Auslegung) und „Auslegungszeile" (welche Zeile genau). Bestehende Vorlagenzeilen sind davon unberührt, solange niemand etwas einträgt.',
      'Nebenbei behoben: Zeigt eine Menge im Füll-Dialog schon einen Wert aus der Auslegung, steht er jetzt als Platzhalter im Feld — bisher sah eine bereits gefüllte Zeile aus wie eine leere.',
    ],
  },
  {
    version: '3.520.0',
    date: '2026-08-18T15:00',
    title: 'Projektauslegung: sortieren und Zahlen prüfen',
    tag: 'improvement',
    changes: [
      'Zeilen der Projektauslegung lassen sich mit zwei Pfeilen innerhalb ihres Abschnitts sortieren. Bisher hingen neue Zeilen hinten an und blieben dort.',
      'Über die Abschnittsgrenze verschiebt der Pfeil nicht — das wäre ein Umhängen und kein Sortieren.',
      'Angaben, die eine Zahl tragen sollen (z. B. „Anzahl Räumer"), nehmen jetzt nur noch Zahlen an. Bisher ließ sich „2 pro Becken" eintragen; die Kettenberechnung meldete danach „fehlt", und man suchte den Fehler an der falschen Stelle. Zusätze gehören in die Bemerkung.',
      'Die deutsche Schreibweise mit Komma bleibt erlaubt. Bereits gespeicherte Freitexte bleiben unangetastet — geprüft wird nur beim Speichern.',
    ],
  },
  {
    version: '3.519.0',
    date: '2026-08-18T14:00',
    title: 'Motorberechnung und Kontrolle Hauptwelle',
    tag: 'feature',
    changes: [
      'Zwei neue Reiter unter „Technische Auslegung": Motorberechnung und Hauptwelle — die letzten beiden Rechenblätter der alten Excel-Vorlage, die noch fehlten.',
      'Die Motorberechnung rechnet Drehmoment, Abtriebsdrehzahl und Beckenoberfläche und schlägt zu jeder Getriebebauart (Stirnrad, Schnecke, Kegelrad) Getriebe und Motor vor — mit dem maximalen Getriebemoment der Stufe.',
      'Reicht das größte Getriebe der Tabelle nicht für das gerechnete Moment, steht das dort. Ein zu schwaches Getriebe wird nicht als Auswahl ausgegeben.',
      'Beckenmaße, Balkenanzahl, Balkenlänge und Kettenlänge werden nicht erneut erfasst: sie kommen aus Projektdaten, Projektauslegung und Kettenberechnung. Fehlt ein Wert, nennt die Seite ihn samt Fundort.',
      'Der Reiter „Hauptwelle" prüft den Flanschabstand links und rechts gegen das Sollmaß von 300 mm und nennt die Abweichung in Millimetern. Der Wellendurchmesser folgt aus der Beckenbreite (sechs Stufen) — ohne Eingabe.',
      'Gespeichert werden nur die Eingaben; alles Gerechnete entsteht bei jedem Aufruf neu — ein abgelegtes Drehmoment wäre nach der ersten Änderung der Balkenanzahl falsch.',
      'Geprüft gegen die Zahlen des echten Blattes: 2,139 U/min bei 2,4 cm/s, Flanschabstand links 338,5 mm und rechts 300,2 mm, Wellentabelle je Beckenbreite.',
      'Noch offen: Die vier SEW-Motoren haben in der Vorlage keine Artikelnummer — der Vorschlag lässt sich deshalb noch nicht in die Stückliste schreiben. Das Blatt „Gewichtsermittlung (BW)" ist in der Vorlage leer, dazu fehlt eine Vorgabe.',
    ],
  },
  {
    version: '3.518.0',
    date: '2026-08-18T13:00',
    title: 'Stückliste als Excel exportieren — mit exaktem Rückweg',
    tag: 'feature',
    changes: [
      'Neuer Knopf „Exportieren" im Stücklisten-Editor: Die Liste wird als Excel-Datei ausgegeben, mit denselben Spalten wie das gewohnte Blatt (Typ, POS-NR., Menge, Beschreibung, Artikelnr., Material, Dimensionen, Gruppe, Einheit, Stück, Lieferant, Bemerkung).',
      'Zwei ausgeblendete Spalten merken sich Ebene und Zeilennummer. Kommt die Datei bearbeitet zurück, erkennt der Abgleich jede Zeile eindeutig wieder — eine in der Datei umbenannte Zeile bleibt dieselbe Zeile statt „weggefallen und neu".',
      'Wer die versteckten Spalten löscht, verliert nur die Genauigkeit: dann greift der bisherige Abgleich über Artikelnummer, alten Code, Zeichnungsnummer und Bezeichnung — wie bei einer SolidWorks-Datei.',
      'Mengen stehen als echte Zahlen in der Datei, nicht als Text — man kann darin also rechnen.',
      'In der kumulierten Ansicht exportiert derselbe Knopf die Auswertung je Artikel (im Excel das Blatt „Bef. kommuliert").',
      'Geprüft wird der Rundlauf selbst: exportieren, wieder einlesen, vergleichen — kein Zugang, keine Änderung, kein Wegfall, keine Verschiebung.',
    ],
  },
  {
    version: '3.517.0',
    date: '2026-08-18T12:00',
    title: 'Befestigungsmaterial hängt jetzt an seinem Bauteil',
    tag: 'improvement',
    changes: [
      'Abgeleitetes Befestigungsmaterial steht nicht mehr flach am Ende der Stückliste, sondern eingerückt unter dem Bauteil, aus dem es folgt — so wie im Excel das Blatt „Befestigungsmaterial" 39 Blöcke führte, einen je Bauteil.',
      'Die eingerückte Zeile trägt die Menge JE STÜCK („10 Scheiben je Balken"); die Gesamtmenge rechnet ProERP daraus. Ändert die Konstruktion 12 Balken auf 15, stimmt das Befestigungsmaterial sofort — ohne erneutes Ableiten.',
      'Meterware zählt dabei in Stück: Unter einem Balken mit 71,04 m / 12 Stück hängen die Schrauben für zwölf Balken, nicht für 71 Meter.',
      'Ein zweiter Ableitungslauf ersetzt die Zeilen des vorigen, statt alles zu verdoppeln. Von Hand ergänztes Material bleibt stehen — es trägt die Herkunfts-Kennzeichnung nicht.',
      'Der Dialog zeigt die Vorschau jetzt als Blöcke: Bauteil als Kopfzeile, darunter die Teile mit „je Stück" und „Ges. Menge". Die Vorschau sieht damit aus wie das Ergebnis.',
      'Liegt ein Bauteil auf der letzten erlaubten Baumebene, landet sein Material am Ende der Liste und ProERP sagt es — still auslassen wäre der teuerste Fehler.',
      'Die kumulierte Ansicht liefert unverändert die Gesamtmenge je Artikel (168 Scheiben), jetzt aus den eingerückten Zeilen.',
    ],
  },
  {
    version: '3.516.0',
    date: '2026-08-18T11:00',
    title: 'Bedarfsliste aus der Stückliste erzeugen',
    tag: 'feature',
    changes: [
      'Neuer Knopf „Bedarfsliste erzeugen" im Stücklisten-Editor: Aus der kumulierten Sicht entstehen Bedarfspositionen — damit ist die Kette Projektauslegung → Stückliste → Bedarfsliste → Bestellung geschlossen. Die Pivot-Tabelle „Bef. kommuliert" im Excel entfällt.',
      'Vorher zeigt eine Vorschau genau, was entsteht: Position, Menge, Baugruppe und Herkunft — und darunter, welche Zeilen wegfallen und warum.',
      'Artikel werden nicht erfunden: Eine Nummer, die es im Stamm nicht gibt, wird eine Position ohne Artikelbezug — wie ein Platzhalter aus der Auslegung. Eine Zeile ganz ohne Nummer wird gemeldet statt übertragen.',
      'Die Baugruppe einer Position ist die oberste Ebene, unter der sie hängt. Hängt derselbe Artikel unter zwei Baugruppen, bleibt das Feld leer und der Fall wird genannt — eine der beiden zu behaupten wäre geraten.',
      'Die Zielliste wird ersetzt, nicht ergänzt: Beim zweiten Lauf stünde sonst jeder Artikel doppelt, und niemand könnte sagen, welche Zahl gilt. Läuft schon eine Bestellung auf die Liste, greift dieselbe Sperre wie überall.',
      'Gerechnet wird über den gespeicherten Stand — steht im Editor Ungespeichertes offen, sagt der Dialog das.',
      'Nach dem Übertrag führt ein Verweis direkt in die entstandene Liste.',
      'Aufgeräumte Werkzeugleiste im Editor: Die drei Wege, die viele Zeilen auf einmal erzeugen — Vorlage, Befestigungsmaterial, SolidWorks-Import — stecken jetzt im Menü „Füllen". Einzeln nebeneinander brach die Leiste auf zwei Zeilen um.',
    ],
  },
  {
    version: '3.515.0',
    date: '2026-08-18T10:00',
    title: 'SolidWorks-Stückliste importieren — mit Abgleich',
    tag: 'feature',
    changes: [
      'Neuer Knopf „Aus SolidWorks importieren" im Stücklisten-Editor. Die Exportdatei (.xls/.xlsx/.xlsm) wird eingelesen und mit der bestehenden Liste verglichen, bevor irgendetwas übernommen wird.',
      'Die Gegenüberstellung zeigt getrennt: was neu ist, was sich ändert (je Feld alt → neu — Menge, Bezeichnung, Werkstoff, Maße, Gruppe, Artikelnummer), was wegfällt und was gleich bleibt.',
      'Zwei Arten zu übernehmen: „Abgleichen" übernimmt nur Zugänge und Änderungen — Reihenfolge, Einrückung und von Hand ergänzte Zeilen bleiben. „Ersetzen" macht die Liste zur Datei; vorher wird angezeigt, welche Zeilen dabei verschwinden.',
      'Was der CAD-Export nicht kennt — Einheit, Lieferant, Stückzahl, Bemerkung — bleibt beim Abgleichen stehen. Ein leeres Feld in der Datei löscht nie einen Wert in ProERP.',
      'Alte Artikel-Codes aus dem Blatt (z. B. bef.m16.001) lassen sich im Dialog direkt einem Artikel zuordnen; die Gegenüberstellung rechnet sofort damit neu. Ohne Zuordnung behält die Zeile den Code, damit sie beim nächsten Import wiedererkannt wird.',
      'Jede Auffälligkeit der Datei wird mit der echten Blattzeile genannt: fehlende Bezeichnung, unlesbare Menge, kaputte Bezüge, doppelte Positionsnummern, zu tiefe Ebenen.',
      'Hat die Arbeitsmappe mehrere Blätter, wird das mit der Stücklisten-Kopfzeile selbst gefunden und lässt sich umstellen. Ein Blatt ohne Kopfzeile wird als solches gemeldet.',
      'Zu jeder importierten Stückliste bleibt festgehalten, aus welcher Datei der Stand kam.',
    ],
  },
  {
    version: '3.514.0',
    date: '2026-08-18T09:00',
    title: 'Die Projektauslegung füllt jetzt die Stückliste',
    tag: 'feature',
    changes: [
      'Was in der Projektauslegung als Artikel gewählt ist, steht beim Füllen der Stückliste schon mit Menge da — ohne dass dafür etwas eingerichtet werden muss.',
      'Eine Zeile der Stücklisten-Vorlage kann jetzt sagen: „meine Menge kommt aus der Projektauslegung", mal einem Faktor. Damit entstehen zum Beispiel zwei Sensoren je Räumer von selbst — bisher stand diese Regel nur als Hinweis in der Vorlage.',
      'Balken sind speziell: Die Auslegungszeile „Balken" führt jetzt Stück und daneben mm je Stück, mit der Meterzahl als Vorschau. Beides zusammen ergibt die Bestellmenge in Metern.',
      'Das Feld „Balkenanzahl" in der Auslegung ist deshalb weg — es war dieselbe Zahl zweimal. Die Kettenberechnung nimmt die Stückzahl der Balken-Zeile. Laufende Projekte, in denen noch das alte Feld gefüllt ist, rechnen unverändert weiter.',
      'Ist ein Wert in der Auslegung noch leer, bleibt die Zeile leer und die Maske sagt, WELCHER Wert fehlt — geraten wird nichts.',
      'Eine eingetragene Menge schlägt immer die Auslegung: Wer im Füll-Dialog etwas tippt, hat einen Grund.',
    ],
  },
  {
    version: '3.513.0',
    date: '2026-08-17T23:59',
    title: 'Projekt-Register neu gegliedert',
    tag: 'improvement',
    changes: [
      'Neues Register „Technische Auslegung": Es bündelt, was die Technik am Projekt tut — Projektauslegung, Kettenberechnung und Protokolle. Die Kettenberechnung war vorher ein eigenes Register, die Protokolle lagen unter „Checkliste".',
      'Der Materialbedarf ist kein eigenes Register mehr, sondern der erste Unterpunkt der Warenwirtschaft. Die Reihenfolge dort folgt jetzt dem Ablauf: Bedarf → Bestellung → Lager → Kommissionierung.',
      'Damit hat die Projektseite elf statt vierzehn Register — bei gleichem Inhalt.',
      'Alte Links funktionieren weiter: Wer ein Lesezeichen auf die Kettenberechnung, den Materialbedarf oder die Protokolle hat, landet direkt am neuen Platz statt auf der Übersicht.',
      'Das gewählte Register steht jetzt in der Adresse. Vorher ging es beim Neuladen verloren, und ein weitergegebener Link öffnete immer die Übersicht.',
      'Nebenbei behoben: „?subtab=" galt bisher für alle Register gemeinsam — ein Link auf die Planung konnte nebenbei die Warenwirtschaft umstellen.',
    ],
  },
  {
    version: '3.512.0',
    date: '2026-08-17T20:30',
    title: 'Cockpit: Fehlgeschlagene Jobs aufräumen — und ehrlich zählen',
    tag: 'fix',
    changes: [
      'Neu: Bei fehlgeschlagenen Hintergrund-Jobs stehen zwei Knöpfe — „erneut versuchen" (für Arbeit, die noch aussteht) und „verwerfen" (für Läufe, die ohnehin alle fünf Minuten wiederholt werden). Bisher blieben die Jobs in der Warteschlange liegen, bis jemand auf den Server ging; die Zahl im Cockpit stand deshalb auch nach der Reparatur weiter auf Gelb.',
      'Verwerfen fragt vorher nach und rührt nur Fehlgeschlagenes an — wartende, laufende und verzögerte Jobs bleiben unangetastet. Der Vorgang steht im Änderungsprotokoll.',
      'Zahlen berichtigt: Bei zwei verschiedenen Fehlergründen stand hinter jedem die Gesamtzahl der Warteschlange („19×" und „19×", was sich wie 38 liest). Gezählt wird jetzt je Grund.',
      'Neu dabei: wann der Fehler zuletzt auftrat. „zuletzt vor 2 Tagen" beantwortet die Frage, die sonst offen bleibt — ist gerade etwas kaputt, oder sieht man alte Leichen?',
    ],
  },
  {
    version: '3.511.0',
    date: '2026-08-17T19:05',
    title: 'Cockpit prüft den Dateispeicher wirklich',
    tag: 'fix',
    changes: [
      'Der Baustein „Dateispeicher" war bisher grün, sobald ein Speicher eingetragen war — angesprochen wurde er nie. Deshalb stand ein grüner Dateispeicher neben 19 fehlgeschlagenen Abgleich-Jobs, die genau an diesem Speicher scheiterten: Das Symptom war gelb, die Ursache grün.',
      'Jetzt klopft das Cockpit bei jedem Aufruf kurz an (ein einzelner Zugriff auf den Wurzelordner, kein Durchlauf der Ablage). Antwortet der Speicher nicht, steht dort der Grund — 401 heißt Zugangsdaten, ENOTFOUND heißt Netz oder Adresse — und daneben, welche Einstellung zu prüfen ist.',
      'Das ist der übliche Grund für fehlgeschlagene DMS-Abgleiche: Der Abgleich liest alle fünf Minuten die Ablage; ist sie nicht erreichbar, scheitert der ganze Lauf. 19 Fehlschläge sind dann nicht 19 Probleme, sondern eines, das 19-mal auftrat.',
    ],
  },
  {
    version: '3.510.0',
    date: '2026-08-17T17:00',
    title: 'Befestigungsmaterial ableiten (VB-26025)',
    tag: 'feature',
    changes: [
      'Neuer Knopf „Befestigung ableiten" im Stücklisten-Editor: Er rechnet das Befestigungsmaterial aus dem Zusatzmaterial der Artikel — im Excel war das das Blatt „Befestigungsmaterial" mit 39 Blöcken und 206 Zeilen.',
      'Die Mengen sind im Dialog bewusst nicht eingebbar. Wer eine Menge ändern will, ändert den Satz am Artikel; dann stimmt er auch im Angebot, im Beleg und im Materialbedarf — statt an vier Stellen gepflegt zu werden.',
      'Je Teil steht dabei, woher die Menge kommt: „12 × 10 (Balken PRO 9) + 8 × 6 (Wandkonsole RLS)". Damit ist im Beleg nachvollziehbar, warum 168 Scheiben drinstehen.',
      'Gerechnet wird über den ganzen Baum: Zwei Räumerbrücken mit je zwölf Balken brauchen 48 Schrauben, nicht 24. Eine Baugruppe selbst bekommt keinen Satz — sonst käme er doppelt.',
      'Neu an der Stücklistenzeile: die Stückzahl. Bei Meterware sind „12 Stück á 5920 mm" und „71,04 m" zwei verschiedene Angaben — Schrauben zählen je Bauteil. Beim Füllen aus der Vorlage wird sie mitgeschrieben.',
      'Ehrlich zum Stand: 14 der 39 Blöcke lassen sich heute laden (248 Sätze auf 45 Bauteile). Die anderen 25 zeigen auf Bauteile, die in der Vorlage gar keine Artikelnummer haben — vor allem Stahlteile und die Motoren. Sobald diese Bauteile eine Nummer haben, kommen ihre Sätze nach; bis dahin bleibt das Befestigungsmaterial dort Handarbeit.',
      'Drei Zeilen des Blattes brauchen eine Entscheidung der Technik und werden benannt statt geraten: zwei Zeilen der Hauptwelle, deren Stückzahl von einem Distanzring abhängt, und eine Zeile mit Stückzahl 0.',
    ],
  },
  {
    version: '3.509.0',
    date: '2026-08-17T16:40',
    title: 'App-Cockpit: welche Konten, welcher Job — und ein Fehlalarm weniger',
    tag: 'fix',
    changes: [
      'Zu jedem Befund stehen jetzt die konkret betroffenen Einträge namentlich da: welche Konten ihr 2FA-Geheimnis noch im Klartext haben, welcher Job woran gescheitert ist, welche Migration hängt. „3 von 12 Konten" war eine Zahl, mit der sich nicht arbeiten ließ.',
      'Bei fehlgeschlagenen Hintergrund-Jobs steht der Fehlergrund direkt auf der Seite statt nur im Server-Log — gruppiert, weil bei 19 gleich gescheiterten Jobs fast immer eine Ursache dahintersteckt.',
      'Fehlalarm behoben: Eine Migration, die als „zurückgerollt" markiert ist, wurde als laufender Ausfall gemeldet — dabei ist genau diese Markierung der Haken, mit dem ein Fehlschlag abgeräumt wird. Der aufgeräumte Vorfall vom 15. Juli stand deshalb dauerhaft als roter Ausfall im Cockpit, obwohl seither jede Version normal live ging. Erledigte Fehlschläge erscheinen jetzt nur noch als Fußnote.',
    ],
  },
  {
    version: '3.508.0',
    date: '2026-08-17T14:00',
    title: 'Stückliste aus Vorlage füllen (VB-26025)',
    tag: 'feature',
    changes: [
      'Neue Seite Technik → „Stücklisten-Vorlagen": der Katalog, der bisher das Excel-Blatt „Haupteingabe" war. Bezeichnung, Artikelnummer, Einheit, Werkstoff und Lieferant stehen dort fertig — im Projekt wird nur noch die Menge eingetragen.',
      'Im Stücklisten-Editor gibt es dafür den Knopf „Aus Vorlage füllen". Die Maske zeigt alle Kandidatenzeilen nach Abschnitten, mit Suche und einem Filter „nur Zeilen mit Menge".',
      'Eine Menge kann aus vier Quellen kommen — genau wie im Blatt: von Hand, als Stück × Länge (Meterware), aus der Kettenberechnung oder abgeleitet aus anderen Zeilen („zwei Füllblöcke je Balken"). Gerechnete Mengen sind sichtbar, aber nicht überschreibbar.',
      'Zeilen ohne Menge fallen heraus, eine Abschnitts-Überschrift verschwindet mit ihrem Inhalt, und ein Zusatztext fällt mit der Position darüber weg — dieselben Regeln wie im Blatt.',
      'Bei Meterware steht die Zuschnittlänge in der Bemerkung („12 Stück á 5920 mm"). Wer 71,04 m bestellt, braucht sie.',
      'Drei Zusatztexte sind im Blatt gerechnet („8 Stränge zu je 32 Kettenglieder …"). Sie werden jetzt beim Füllen aus der Kettenberechnung gebildet, statt als fester Text im Katalog zu stehen — sonst stünden diese Zahlen ab dem nächsten Projekt falsch in der Liste.',
      'Die Vorschau im Dialog ist dasselbe wie das Ergebnis: es ist dieselbe Rechnung. Übernommen wird in den Entwurf — gespeichert wird wie immer erst mit „Speichern", und vorhandene Zeilen bleiben erhalten.',
      'Ist für das Projekt noch keine Kettenberechnung gerechnet, sagt der Dialog das und lässt die betroffenen Zeilen leer, statt eine Menge zu raten.',
      'Der erste Stand der Vorlage (252 Zeilen, 4 Abschnitte, 205 mit Artikelnummer) wird einmalig aus der Excel-Vorlage geladen; gepflegt wird danach im ERP.',
    ],
  },
  {
    version: '3.507.0',
    date: '2026-08-17T11:00',
    title: 'Kettenberechnung am Projekt (VB-26025)',
    tag: 'feature',
    changes: [
      'Neues Register „Kettenberechnung" am Projekt, direkt neben der Projektauslegung — das, was bisher das Excel-Blatt „KETTEN - Berechnungen" war.',
      'Vier Angaben genügen: Gesamtlänge des Kettenwegs, Länge der Antriebskette, Glieder der Ersatzkette und die Geometrie des Kettentyps. Anzahl Räumer und Balkenanzahl kommen aus der Projektauslegung und werden nicht erneut abgefragt.',
      'Gerechnet werden Kettenglieder, Kettenlänge, Balkenabstände, die Aufteilung der Balken auf zwei Kettenreihen samt Strängen — und die Bestellmenge der Förderkette in Metern.',
      'Die Aufteilung auf zwei Kettenreihen brauchte im Excel den Solver. Sie ist in einer Rechnung lösbar und wird jetzt direkt bestimmt: bei 506 Gliedern und 15 Balken ergibt das 4 Balken mit 33 Gliedern und 11 mit 34 — dieselben Werte wie bisher.',
      'Fehlt eine Angabe, wird nicht gerechnet und auch nichts geraten: die Seite nennt den fehlenden Wert und sagt, in welchem Abschnitt der Projektauslegung er steht.',
      'Ergebnisse werden nicht gespeichert, sondern bei jedem Aufruf neu gerechnet — eine abgelegte Kettenlänge wäre nach der ersten Änderung der Beckenlänge falsch.',
    ],
  },
  {
    version: '3.506.0',
    date: '2026-08-14T11:30',
    title: 'App-Cockpit sagt jetzt, was nicht passt',
    tag: 'improvement',
    changes: [
      'Ganz oben steht in einem Satz, wie die Lage ist — „Alle 8 geprüften Bausteine in Ordnung." oder „1 Ausfall, 3 beeinträchtigt — bitte ansehen." Daneben ein Balken, der zeigt, wie sich die Bausteine verteilen.',
      'Neu: Abschnitt „Was nicht passt". Zu jedem Problem steht dort, was gemessen wurde, welche Folge es hat und was konkret zu tun ist (mit Datei, Befehl oder Runbook). Bisher stand da nur „Ausfall" — und man musste selbst herausfinden, was das bedeutet.',
      'Läuft alles, fehlt der Abschnitt ganz. Bewusst fehlende Infrastruktur (z. B. Redis) zählt nicht als Problem — sonst stünde dauerhaft etwas in der Liste, das niemand abarbeiten wird.',
      'Die Bausteine sind jetzt nach Dringlichkeit sortiert: Ausfall zuerst, dann beeinträchtigt, dann der Rest. Vorher stand ein Ausfall irgendwo zwischen acht grünen Kacheln.',
      'Neue Prüfung „Datenbank-Migrationen": Eine hängengebliebene Migration blockiert jeden weiteren Deploy — das war bisher nur in den Server-Logs zu sehen und hat schon zweimal zu einem Ausfall geführt. Eine gerade laufende Migration löst keinen Alarm aus.',
      'Neue Prüfung „Hintergrund-Jobs": Fehlgeschlagene Jobs haben jetzt eine eigene Ampel. Bisher stand die Zahl nur in der Tabelle — ein gestorbener Job fiel niemandem auf, außer dass ein Dokument plötzlich nicht durchsuchbar war.',
      'Bei Backup und Restore-Test zeigt ein Balken, wie frisch die letzte Meldung ist — mit Marke beim vorgesehenen Takt. „vor 40 Stunden" allein sagt nichts; erst „von täglich" macht daraus eine Aussage.',
      'Diagramme: Änderungen je Tag über zwei Wochen (Wochenenden fallen als Täler auf), die größten Tabellen der Datenbank mit Größe und Zeilenzahl, die Auslastung der Warteschlangen als Streifen und der Speicherverbrauch als Balken.',
    ],
  },
  {
    version: '3.505.0',
    date: '2026-08-14T10:00',
    title: 'Stücklisten in der Technik (VB-26025)',
    tag: 'feature',
    changes: [
      'Neue Seite Technik → Stücklisten: die Stückliste eines Projekts, die bisher ausschließlich in Excel entstand. Mehrere je Projekt sind möglich (z. B. je Räumer).',
      'Der Baum hat bis zu vier Unterebenen. Zeilen lassen sich ein- und ausrücken, verschieben und löschen — Unterzeilen gehen dabei immer mit.',
      'Die Spalte „Ges. Menge" wird gerechnet: Menge × Mengen ALLER Elternteile. Sie lässt sich nicht eintippen. Ändert sich oben im Baum eine Menge, ziehen alle Zeilen darunter sofort nach — im Excel ist genau das Handarbeit.',
      'Ansicht „Kumuliert": Summe je Artikel über den ganzen Baum, mit den Positionsnummern, aus denen sie stammt. Das ersetzt die Pivot-Tabelle „Bef. kommuliert" und ist die Grundlage der Bedarfsliste.',
      'Zeilentypen wie im Blatt: Position, Abschnitt (Überschrift) und Zusatztext. Abschnitt und Zusatztext tragen bewusst keine Menge und keinen Artikel.',
      'Eine Zeile kann auf einen Artikel zeigen oder Freitext bleiben. Es wird hier nie ein Artikel angelegt — neue Artikelnummern laufen weiter über den Antrag mit Vier-Augen-Freigabe.',
      'Gespeichert wird die ganze Liste auf einmal (ein Vorgang, ein Eintrag im Protokoll). Der Speicherstand steht immer sichtbar im Kopf.',
      'Noch offen: Excel-Export und -Rücklauf sowie „Bedarfsliste erzeugen" — beides ist geplant und baut auf dieser Stufe auf.',
    ],
  },
  {
    version: '3.504.0',
    date: '2026-08-13T09:20',
    title: 'Auswertungen: Ansprechpartner- und Referenzlisten, Excel und PDF',
    tag: 'feature',
    changes: [
      'Die Analyse kann jetzt Ansprechpartner-Listen ausgeben: „Alle Ansprechpartner in Mexiko" oder „alle Einkäufer in Kroatien" — gefiltert nach Land, Ort, PLZ, Funktion, Abteilung, Sprache und wahlweise nur Hauptansprechpartnern.',
      'Ansprechpartner haben im System keine eigene Adresse — Land, Ort und PLZ hängen am Kunden. Genau danach wird gefiltert: Wer „Mexiko" eingibt, bekommt alle Personen, deren Adresse dort liegt, samt Anschrift, E-Mail und Telefon in einer Zeile. Der Hauptansprechpartner ist dabei erkennbar und steht je Firma oben.',
      'Im Kunden-Modus liefert der Schalter „Ansprechpartner mitliefern" zu jedem Kunden seine Kontaktpersonen mit, aufklappbar in der Zeile. Kunden ohne Ansprechpartner bleiben dabei in der Liste — im Ansprechpartner-Modus fallen sie dagegen heraus. Eine Zeile über der Tabelle sagt deshalb immer, was man gerade vor sich hat.',
      'Neu ist außerdem der Modus „Projekte": die Referenzliste als Auswertung, filterbar nach Land, Anlagentyp, Produkt, Branche und Baujahr-Spanne.',
      'Beim Land ist es egal, ob man „HR" oder „Kroatien" tippt — beides findet dieselben Datensätze. Bei zweistelligen Eingaben wird bewusst nur der Ländercode verglichen, damit „HR" nicht plötzlich Bahrain mitbringt.',
      'Jede Auswertung lässt sich zusätzlich zu CSV als Excel-Mappe und als PDF ausgeben. „Kunden inkl. Ansprechpartner" wird dabei zu zwei Blättern — Kunden und Ansprechpartner, verknüpft über die Kunden-ID —, damit Umsätze nicht doppelt gezählt werden.',
      'Das PDF wird wie überall im System direkt im Fenster angezeigt; Herunterladen steckt als Knopf in der Vorschau. Kunden-, Ansprechpartner- und Projektzeilen sind echte Links: Rechtsklick „In neuem Tab öffnen" und Cmd-/Strg-Klick funktionieren.',
      'Excel und PDF setzen — wie schon der CSV-Export — die Berechtigung „Datei-Export" voraus. Ohne sie bleiben die Knöpfe ausgeblendet.',
    ],
  },
  {
    version: '3.503.0',
    date: '2026-08-12T08:30',
    title: 'Die Suche findet jetzt wirklich alles — auch bei Tippfehlern',
    tag: 'feature',
    changes: [
      'Die globale Suche (⌘K / Strg+K) durchsucht jetzt 30 statt 14 Bereiche. Neu dabei sind unter anderem Lieferadressen, Transporte, Sperrlager, QM-Prozesse, Bedarfslisten, Bedarfsvorschauen, Wareneingänge, Kommissionieraufträge, Zeichnungs- und Materialnummern, Verbesserungen, Vertriebspipeline, Inventuren, Baustellenberichte, Gesellschaften und Benutzerkonten.',
      'Straßennamen sind endlich durchsuchbar: Kunden und Lieferadressen werden jetzt auch über Straße, Postleitzahl, Ort und Land gefunden.',
      'Auch sonst wird deutlich mehr gelesen als früher — Matchcodes, Alt-Artikelnummern, Hersteller, EAN, Zolltarifnummern, Anlagentyp, Kennzeichen und Spedition eines Transports, Referenzen und Notizen auf Belegen.',
      'Die Suche schaut jetzt in die Positionen von Angeboten, Bestellungen und Bedarfslisten hinein: Wer eine Werkstoffangabe aus einem Langtext eintippt, bekommt den passenden Beleg — nicht eine Liste einzelner Zeilen.',
      'Neu ist die Tippfehler-Toleranz: Wer sich vertippt, bekommt ähnliche Treffer vorgeschlagen — „Begen" findet „Beggen", „Müler" findet „Müller".',
      'Diese Vorschläge stehen in einem eigenen Abschnitt „Ähnliche Treffer" unter den genauen Treffern, sind nach Ähnlichkeit sortiert (beste Vermutung oben) und einzeln als „ähnlich · 78 %" gekennzeichnet — man sieht also immer, was ein echter und was ein geratener Treffer ist.',
      'Vorschläge erscheinen bewusst nur dann, wenn die normale Suche fast nichts gefunden hat. Wer genau trifft, bekommt keine Vermutungen dazwischengeschoben.',
      'Damit die Schnellsuche bei 30 Bereichen übersichtlich bleibt, zeigt sie die ersten acht Kategorien und blendet den Rest hinter „… weitere Kategorien anzeigen" ein; die Filter-Pillen darüber haben dafür ein „+N weitere". Kategorien mit einem Nummerntreffer (z. B. P-1042) stehen ganz oben.',
      'Auf der Ergebnisseite ist jede Trefferzeile jetzt ein echter Link — „In neuem Tab öffnen" und Cmd-/Strg-Klick funktionieren dort ab sofort.',
      'Die Suche zeigt weiterhin ausschließlich Daten aus Bereichen, für die man das Leserecht hat. Bereiche ohne Recht werden jetzt gar nicht mehr abgefragt, was die Suche für viele Rollen zusätzlich spürbar schneller macht.',
    ],
  },
  {
    version: '3.502.0',
    date: '2026-08-12T08:05',
    title: '„Maximieren zum Bearbeiten" führt nicht mehr in eine Sackgasse',
    tag: 'fix',
    changes: [
      'Der Knopf „Maximieren zum Bearbeiten" im Materialbedarf konnte in einer Sackgasse enden: Statt des Vollbild-Editors stand dort nur „Projekt nicht gefunden." — ohne Grund, ohne „Schließen" und, weil die Seite bewusst ohne Rahmen läuft, ohne jede Navigation. Von außen sah das aus, als täte der Knopf schlicht nichts (VB-26019).',
      'Ursache war, dass der Editor das Projekt als Voraussetzung behandelte, obwohl er es nur für Nummer und Name in der Kopfzeile und für den Dateinamen des Exports braucht. Ließ es sich nicht laden, räumte das die ganze Seite ab — samt der Positionen, an denen gearbeitet werden sollte.',
      'Jetzt öffnet der Editor in jedem Fall: Die Positionen sind da, oben steht ein Hinweis mit dem konkreten Grund — fehlt eine Berechtigung, nennt er sie beim Namen —, und „Schließen" führt wie gewohnt in die Bedarfsliste zurück.',
      'Lassen sich die Positionen selbst nicht laden, sagt die Tabelle das jetzt. Vorher stand dort „Keine Positionen für den aktuellen Filter.", was wie eine leere Liste aussah und den Fehler verdeckte.',
      'Außerdem haben alle Vollbild-Seiten (Beleg-Editor, PDF-Vorschau, Wareneingang, Kommissionierung, Baustellenbericht, Protokoll, Materialbedarf, Referenzenkarte) dieselbe Fehlergrenze wie der Rest der App: Nach einem Update lädt sich eine solche Seite bei veraltetem Stand selbst neu, statt in einer nackten Fehlerseite zu enden.',
      'Die gemeldete Vermutung, es liege an der Rolle, hat sich nicht bestätigt: Der Weg funktioniert mit genau den Rechten der Rolle „Technik" — das hält jetzt ein Test fest, damit es so bleibt.',
    ],
  },
  {
    version: '3.501.0',
    date: '2026-08-11T20:10',
    title: 'Zusatzmaterial kommt erst, wenn die Menge steht',
    tag: 'fix',
    changes: [
      'Das Zusatzmaterial wird im Belegblatt jetzt erst aufgelöst, wenn die Menge der Hauptposition eingetippt ist — nicht mehr im selben Moment, in dem der Artikel gewählt wird.',
      'Vorher war die Mengenzelle zu diesem Zeitpunkt noch leer (der Cursor springt erst danach hinein). Gerechnet wurde deshalb mit der Ersatzmenge 1: Wer „3" eintippte, bekam Zusatzmaterial für ein Stück.',
      'Ausgelöst wird nach einer kurzen Tipp-Pause. Damit zählt „12" als zwölf und nicht unterwegs schon als eins — und wer direkt nach der Menge speichert, bekommt sein Zusatzmaterial trotzdem.',
      'Bleibt die Menge leer, passiert nichts. „Zusatzmaterial einfügen" im Zeilenmenü gibt es unverändert für den Fall, dass man es doch von Hand braucht.',
    ],
  },
  {
    version: '3.500.0',
    date: '2026-08-11T19:20',
    title: 'Belegblatt: leerer Preis blockiert das Speichern nicht mehr (VB-26016)',
    tag: 'fix',
    changes: [
      'Eine Position ohne Preis lässt sich wieder speichern. Bisher meldete das Belegblatt „Unvollständige Positionen: Beschreibung, Menge (≠ 0) und Preis prüfen." und verweigerte den ganzen Beleg, obwohl nur das Preisfeld leer war.',
      'Leer bedeutet in der Anwendung ohnehin schon 0,00 — bei der Summenrechnung und beim Speichern. Nur die Prüfung selbst wertete es als Fehler; sie war damit strenger als alles andere.',
      'Betroffen war unter anderem jeder Artikel ohne gepflegten Verkaufspreis: Der kommt aus der Artikelsuche mit leerem Preisfeld in die Zeile.',
      'Eine unlesbare Preiseingabe („abc") wird weiterhin abgewiesen — die würde sonst still zu 0 und stünde falsch auf dem Beleg.',
      'Gilt für Verkauf und Beschaffung gleichermaßen (beide nutzen dasselbe Belegblatt).',
    ],
  },
  {
    version: '3.499.0',
    date: '2026-08-11T18:55',
    title: 'Materialbedarf im Projekt sichtbar (VB-26017)',
    tag: 'feature',
    changes: [
      'Das Projekt hat ein neues Register „Materialbedarf" — zwischen „Planung" und „Warenwirtschaft", also dort, wo der Bedarf im Ablauf steht: Bedarf, dann Bestellung, dann Lager.',
      'Es zeigt eine Zeile je Bedarfsliste des Projekts: Bezeichnung, Anzahl der Positionen, den Fortschrittsbalken und wie viele Positionen noch offen sind. Damit ist ohne Wechsel in ein anderes Modul zu sehen, ob und wie weit der Bedarf erfasst ist.',
      'Eine Zeile lässt sich aufklappen — dann stehen die Positionen mit Menge, Status und offener Menge darunter (die ersten 50; der Rest ist einen Klick entfernt).',
      'Ein Klick auf die Zeile öffnet die Bedarfsliste selbst, direkt die richtige, wenn das Projekt mehrere führt.',
      'Das Register ist eine reine Anzeige: Gepflegt wird der Bedarf weiterhin unter Material → Materialbedarf — anlegen, importieren, reservieren, bestellen passiert unverändert dort. Hat ein Projekt noch keine Bedarfsliste, sagt das Register das und verlinkt dorthin.',
      'Anlass war die Meldung VB-26017: Seit dem Umzug des Materialbedarfs ins Material-Modul war am Projekt selbst nicht mehr erkennbar, ob dafür Material erfasst ist.',
    ],
  },
  {
    version: '3.498.0',
    date: '2026-08-11T18:30',
    title: 'Zusatzmaterial am Artikel — und Artikeländerungen der Technik brauchen Freigabe (VB-26016)',
    tag: 'feature',
    changes: [
      'Am Artikel lässt sich hinterlegen, was üblicherweise mitgeht: echte Artikel mit einer Stückzahl je Einheit — Schrauben, Muttern, Dichtband. Der Reiter heißt „Zusatzmaterial" und steht ab sofort bei jedem Artikel (früher musste man erst das Häkchen „Stückliste" setzen, damit er überhaupt erschien).',
      'Beim Einfügen eines Artikels — im Materialbedarf, in der Projektauslegung, im Angebot und im Auftrag — kommt das Zusatzmaterial mengenskaliert als eigene Positionen mit: 3 Kettenräder ziehen 12 Schrauben nach. Das gilt mehrstufig, ein Zusatzmaterial kann selbst wieder welches haben.',
      'Ob es mitkommt, entscheidet man je Erfassung; die Vorbelegung steht am Artikel („kommt normalerweise mit" / „auf Wunsch"). Das Häkchen erscheint nur bei Artikeln, die überhaupt Zusatzmaterial haben, und sagt vorher, was passieren wird.',
      'Die erzeugten Positionen sind danach ganz normale, unabhängige Positionen — änderbar, löschbar, mit eigenem Preis. Erkennbar bleiben sie durch die Einrückung und den Vermerk „Zusatz zu <Artikelnummer>".',
      'Dreht sich eine Kette im Kreis (A braucht B, B braucht A), wird sie abgebrochen UND gemeldet — nicht stillschweigend übersprungen. Beim Anlegen einer solchen Zeile weist der Server sie gleich mit Begründung ab.',
      'Neue Übersicht „Technik → Zusatzmaterial": welche Artikel überhaupt etwas hinterlegt haben, wie viele Zeilen direkt und aufgelöst dazugehören und ob es mehrstufig ist. Ein Klick führt zum Artikel. Damit entfällt die Stücklisten-Übergabe von der Technik ans Büro.',
      'Die Technik darf den Artikelstamm jetzt bearbeiten — ihre Änderungen werden aber erst nach Freigabe durch das Sekretariat wirksam. Statt „Speichern" steht dort „Änderung beantragen"; beantragt wird nur der Unterschied.',
      'Die Anträge laufen in derselben Warteschlange wie die Artikelnummern-Anträge (Technik → Anträge), als zweites Register. Die Freigabe sieht Vorher und Nachher fertig aufbereitet und entscheidet mit Ja oder Nein; Ablehnen verlangt einen Grund.',
      'Solange ein Antrag zur Freigabe liegt, ist der Artikel für direkte Änderungen gesperrt — für alle, auch fürs Sekretariat. Sonst gälte am Ende ein Stand, den niemand beantragt hat. Der eigene Antrag lässt sich jederzeit zurückziehen, dann ist der Artikel wieder frei.',
      'Preise, Bestand und Sperrkennzeichen sind nicht beantragbar; Melde- und Höchstbestand ebenso wenig — das ist Disposition und bleibt beim Einkauf.',
      'Der Reiter „Änderungen" am Artikel zeigt die Freigaben mit: beantragt, freigegeben, abgelehnt oder zurückgezogen, jeweils mit Vorher/Nachher, Antragsteller und Freigeber.',
    ],
  },
  {
    version: '3.497.0',
    date: '2026-08-11T18:05',
    title: 'Änderungsprotokoll: Zeitachse statt Tabelle, Beträge als Beträge',
    tag: 'improvement',
    changes: [
      'Der Reiter „Änderungen" am Artikel ist keine Tabelle mehr, sondern eine Zeitachse mit Tagesgruppen („Heute", „Gestern", sonst das Datum). Vorher bekamen Zeitpunkt, Person und Aktion zwei Drittel der Fläche für je zwei Wörter, während die eigentliche Änderung im Rest umbrach — und in jeder Zeile stand erneut das volle Datum.',
      'Je Eintrag stehen jetzt Uhrzeit, ein farbiger Punkt für die Art der Änderung und rechts die geänderten Felder über die volle Breite.',
      'Geldbeträge stehen als Beträge da: Aus „12000000 → 13500000" ist „12,00 € → 13,50 €" geworden. Intern rechnet die App in Millionstel-Einheiten (damit sich beim Rechnen kein Cent verliert) — im Protokoll landete bisher genau diese Rohzahl.',
      'Auch der Staffelpreis zeigte seinen Betrag roh. Ursache war ein Fehler in der Währungs-Ermittlung: Bei Feldern ohne die Endung „Amount" schlug sich das Feld selbst als Währung nach, die Formatierung scheiterte still und fiel auf die Rohzahl zurück.',
      'Zahlen erscheinen deutsch (2,4 statt 2.4). Kennungen wie die Warengruppe bleiben ohne Tausenderpunkt — aus 1100 darf keine „1.100" werden.',
      'Feldnamen, die vorher englisch durchrutschten (Preis, Länge, Anfangssaldo, Kategorie, Hersteller …), heißen jetzt auf Deutsch; der interne Marker der Ablängungen steht als „Ablängung" statt als Code.',
      'Feldname und Wert stehen in zwei Spalten untereinander, statt als Fließtext in einer Zeile zu kleben — bei mehreren Feldern war das eine Textwand.',
      'Ein langer Wert (z. B. eine Beschreibung) steht jetzt unter dem alten Wert statt dahinter; vorher rutschte der Pfeil ans Zeilenende und der neue Wert begann weit darunter. Sehr lange Werte werden gekürzt, der volle Text steht im Tooltip.',
      'Löschen und Archivieren tragen keine Feldangaben — dort steht jetzt nichts statt eines einsamen Gedankenstrichs. Im Artikel-Verlauf entfällt außerdem die Zeile „Artikel: dieser Artikel".',
      'Der globale Audit-Log (Administratoren) bleibt eine Tabelle — dort trägt jede Zeile ein anderes Objekt aus einem anderen Modul —, bekommt aber dieselbe aufgeräumte Darstellung der Änderungen und dieselbe Betrags-/Zahlen-Formatierung.',
    ],
  },
  {
    version: '3.496.0',
    date: '2026-08-11T16:40',
    title: 'Artikel: Änderungsverlauf und achtzehn Filter',
    tag: 'feature',
    changes: [
      'Neuer Reiter „Änderungen" am Artikel: wer wann was geändert hat — mit altem UND neuem Wert. Protokolliert wurde das schon immer, sichtbar war es nur im globalen Protokoll, und das ist Administratoren vorbehalten.',
      'Das Protokoll zeigt jetzt echte Vorher/Nachher-Zeilen statt des gesendeten Formularinhalts. Bisher landete der komplette Datensatz im Log — also der neue Stand, auch für die zwanzig Felder, die das Formular nur mitgeschickt hat; die eine echte Änderung ging darin unter.',
      'Ein Speichern ohne Änderung erzeugt keinen Eintrag mehr. Folgenlose Klicks hätten sonst die echten Änderungen zugedeckt.',
      'Zeichnungen und Staffelpreise stehen im Verlauf des Artikels — auch gelöschte. Sie liegen im Protokoll unter ihrer eigenen Kennung; eine Suche nach dem Artikel allein hätte genau die Vorgänge übersehen, nach denen man sucht.',
      'Lagerbewegungen bleiben bewusst draußen: Sie sind das Bestandsbuch selbst und haben ihren eigenen Reiter.',
      'Den Verlauf sieht jeder, der den Artikel sehen darf — nicht nur Administratoren.',
      'Artikelstamm-Übersicht: statt drei jetzt achtzehn Filter. Sichtbar bleiben Suche, Aktiv-Status, Bestandslage und Artikeltyp; hinter „Weitere Filter" (mit Zähler) stecken Kategorie und Standardlieferant als Mehrfachauswahl, Hauptwarengruppe, Hersteller, Ursprungsland, Einheit, Beschaffung, die Merkmale Gesperrt/Stückliste/Ablängbar sowie VK-Preis- und Bestandsbereich.',
      'Die Bestandslage ist eine Auswahlliste geworden: „unter Mindestbestand", „ohne Bestand", „auf Lager", „über Höchstbestand". Als Häkchen ließen sich einander ausschließende Fälle gleichzeitig ankreuzen. Alte Lesezeichen mit „unter Mindestbestand" funktionieren weiter.',
      'Merkmale kennen drei Zustände (ja / nein / egal). Mit einem Häkchen wäre „zeig mir alles, was NICHT gesperrt ist" nicht formulierbar gewesen.',
      'In den Auswahllisten stehen nur Werte, die es wirklich gibt, samt Trefferanzahl — statt Freitextfeldern, in denen sich ein Tippfehler wie „keine Treffer" anfühlt.',
      'Gefiltert wird über den gesamten Stamm, nicht nur über die angezeigte Seite. Jeder gesetzte Filter erscheint als entfernbarer Chip über der Tabelle, steht in der Adresszeile (Link weitergeben, „Zurück" landet in derselben Auswahl) und gilt auch für den CSV-Export.',
    ],
  },
  {
    version: '3.495.0',
    date: '2026-08-11T14:55',
    title: 'Bereich heißt „Artikel & Lager"',
    tag: 'improvement',
    changes: [
      'Die Bereichs-Überschrift „Artikel & Lagerverwaltung" in der Seitenleiste heißt jetzt „Artikel & Lager" — kürzer, und „…verwaltung" sagte nichts dazu.',
      'Wer seine Bereiche selbst sortiert oder ausgeblendet hatte, behält das auch über diese zweite Umbenennung hinweg.',
    ],
  },
  {
    version: '3.494.0',
    date: '2026-08-11T12:10',
    title: 'Navigation nach dem Arbeitsablauf gegliedert (VB-26015)',
    tag: 'improvement',
    changes: [
      'Die Seitenleiste folgt jetzt dem Ablauf einer Abwicklung: „Verkauf & Kontakte" (Verkauf, Vertriebspipeline, CRM, Referenzen) → „Projekte" (Projekte, Zeitplan) → „Artikel & Lagerverwaltung" → „Logistik" → „Technik".',
      'Referenzen sind kein eigener Bereich mehr — sie sind ein Verkaufsargument und stehen jetzt bei den Kontakten.',
      'Das Sperrlager zählt zur Bestandsführung und steht deshalb bei „Artikel & Lagerverwaltung" statt bei der Logistik. Der Materialbedarf bleibt dort, wo er war — die Frage aus der Meldung hat der Melder selbst so beantwortet.',
      'In der Logistik steht jetzt die Kommissionierung vorn, dann Wareneingang, dann Transporte — die Reihenfolge des Alltags statt des Warenwegs.',
      'Technik: Zeichnungsnummern, Anträge, Materialcodes und Warengruppen liegen zusammengefasst unter „Artikelverwaltung" (aufklappbar).',
      'Wer seine Bereiche selbst sortiert oder ausgeblendet hatte, behält das: Die gespeicherten Einstellungen werden auf die neuen Namen umgeschlüsselt. Eine geteilte Gruppe („Vertrieb & Projekte") wirkt auf beide Nachfolger.',
      'Die Bereichsseiten (Klick auf eine Bereichs-Überschrift) heißen entsprechend um; das Dashboard selbst bleibt unverändert.',
    ],
  },
  {
    version: '3.493.0',
    date: '2026-08-10T20:25',
    title: 'Partnerportal: beide unterschreiben, Personal namentlich',
    tag: 'fix',
    changes: [
      'Bau-Tagesbericht: Bauherr UND Monteur können jetzt unterschreiben. Bisher verschwand der Knopf mit der ersten Unterschrift — er hing an der Bearbeitbarkeit, und die erste Unterschrift sperrt den Tag. Die zweite Rolle kam damit nie dran, obwohl der Server sie längst annahm.',
      'Der Knopf sagt jetzt, wer noch dran ist („Bauleiter / Monteur unterschreibt"), und im Dialog steht nur noch die offene Rolle zur Auswahl — dieselbe Rolle ein zweites Mal hätte die vorhandene Unterschrift überschrieben.',
      'Die Warnung im Dialog stimmt jetzt auch beim zweiten Mal: Statt „wird gesperrt" steht dort, dass der Tag bereits gesperrt ist und von wem — die zweite Unterschrift kommt hinzu, am Inhalt ändert sich nichts.',
      'Neu: Wer an diesem Tag montiert hat, lässt sich im Portal eintragen — Name, Rolle (Leitmonteur, Schweißer, Elektriker …) und Stunden, mit den Tagesstunden als Vorschlag. Das Feld gab es in der Antwort, aber nirgends in der Maske; erfassbar war es nur intern.',
      'Die Namen stehen im Tagesbericht und werden mit unterschrieben. Aus den Zeilen rechnet der Bericht außerdem die Personenstunden — namentliche Zeilen gewinnen gegen die grobe Angabe „Stunden × Anzahl".',
      'Personal eines Partners bleibt Fremdpersonal: An unsere HR-Datensätze lässt es sich nicht hängen (das Portal-Schema kennt das Feld nicht, der Dienst setzt es zusätzlich hart auf null).',
      'Auf einem gesperrten Tag steht die Aufstellung nur noch da — Eingabefelder gibt es dort keine mehr; Stunden stehen dort jetzt mit Komma, und die Zahlen-/Zeitfelder sind auf Fingergröße gewachsen.',
    ],
  },
  {
    version: '3.492.0',
    date: '2026-08-10T20:10',
    title: 'Passkeys im Partnerportal möglich',
    tag: 'fix',
    changes: [
      'Im Partnerportal ließ sich kein Passkey einrichten: Der Browser meldete „The RP ID \'erp.probig.com\' is invalid for this domain". Ein Passkey gilt immer nur für genau die Domain, unter der er angelegt wurde — und das Portal läuft unter einer eigenen (partner.probig.com). Für Partner blieb damit nur der Authenticator.',
      'Die Domain für Passkeys richtet sich jetzt nach dem Konto: Partner-Zugänge gehören zur Portal-Domain, interne Zugänge unverändert zur ERP-Domain. Bestehende Passkeys von Mitarbeitern sind nicht betroffen.',
      'Passt die eingestellte Domain nicht zur aufgerufenen, schreibt der Server das beim Start ins Log — statt dass es erst im Browser eines Partners auffällt.',
      'Achtung Betrieb: In Coolify muss WEBAUTHN_PORTAL_ORIGIN=https://partner.probig.com gesetzt und neu deployt werden. Ohne die Variable bleibt es beim alten Verhalten (Runbook in docs/deployment-coolify.md).',
    ],
  },
  {
    version: '3.491.0',
    date: '2026-08-10T19:15',
    title: 'Partnerportal: eigenen Zugang selbst verwalten',
    tag: 'feature',
    changes: [
      'Neuer Menüpunkt „Zugang" im Partnerportal: Passkeys hinzufügen und entfernen, Authenticator einrichten oder entfernen, Passwort ändern.',
      'Der Anlass: Ein Partner mit neuem Telefon musste bisher bei uns anrufen — und wer sich beim ersten Login für den Authenticator entschieden hatte, kam nie mehr an einen Passkey. Die Endpunkte dafür gab es serverseitig von Anfang an, es fehlte nur die Oberfläche.',
      'Der letzte verbleibende zweite Faktor lässt sich nicht entfernen: Der Knopf ist gesperrt und sagt warum. Wer sich hier aussperrt, kommt ohne uns nicht wieder hinein.',
      'Beim Passwortwechsel steht jetzt da, dass alle Sitzungen enden, und ein Knopf führt zurück zur Anmeldung — vorher wäre die Seite anschließend in lauter Fehlermeldungen gelaufen.',
      'Der Punkt „Zugang" ist immer sichtbar, auch für Partner ohne freigeschaltete Sicht — sonst käme jemand ohne Bedarfsvorschau und Montagen nicht an seinen zweiten Faktor.',
    ],
  },
  {
    version: '3.490.0',
    date: '2026-08-10T18:05',
    title: 'Dateien ansehen statt herunterladen',
    tag: 'improvement',
    changes: [
      'Belege, Berichte, Protokolle, Zeichnungen, Anhänge und Fotos öffnen sich jetzt überall in einem Vorschau-Fenster, statt im Download-Ordner zu landen. „Herunterladen" steht als Knopf darin — wer die Datei wirklich behalten will, bekommt sie weiterhin.',
      'Die Vorschau erkennt den Dateityp: PDFs werden gezeichnet, Bilder angezeigt, und für alles andere (z. B. Excel) steht ehrlich da, dass es sich hier nicht anzeigen lässt — statt eines leeren Fensters.',
      'Auch das Partnerportal hat jetzt eine Vorschau: Einsatz- und Bedarfsberichte, der Bau-Tagesbericht und hochgeladene Unterlagen lassen sich direkt ansehen. Auf dem Handy auf der Baustelle war der Download bisher der Weg in einen unauffindbaren Ordner.',
      'Im Dateimanager sind „Öffnen" und „Vorschau" zu einer Aktion „Ansehen" zusammengefallen — sie taten dasselbe.',
      'Technisch: Der frühere Weg „neuer Tab mit blob:-URL" ist ersatzlos entfernt. In Safari zeigte er PDFs gar nicht an (ADR 0075).',
    ],
  },
  {
    version: '3.489.0',
    date: '2026-08-10T17:45',
    title: 'Partnerportal fürs Telefon',
    tag: 'improvement',
    changes: [
      'Das Portal wird auf dem Telefon benutzt — vom Lieferanten in der Disposition, vom Monteur auf der Baustelle. Gemessen mit vier Gerätegrößen (ab iPhone SE, 320 Pixel) und danach überarbeitet.',
      'Bedarfsvorschau: Die Positionen stehen auf dem Telefon als Karten statt in einer Tabelle. Vorher hatte jede Termingruppe einen eigenen seitlichen Scrollbalken — und die Menge, um die es dem Lieferanten geht, lag außerhalb des Bildes. Die Karte nennt sie jetzt zuerst. Ab Tablet bleibt es die Tabelle; beide zeigen dieselben Angaben.',
      'Termine werden richtig zusammengefasst: Zwei Positionen im selben Monat standen bisher als zwei getrennte Blöcke mit identischer Überschrift „August 2026" da, weil nach dem genauen Datum gruppiert, aber nach Monat überschrieben wurde.',
      'Die Navigation im Kopf war auf dem Telefon breiter als der Platz — der dritte Punkt war abgeschnitten und nur durch seitliches Wischen erreichbar. Jetzt stehen Symbol und Text übereinander und die drei Punkte teilen sich die Zeile.',
      'Antippen eines Eingabefelds zoomte auf iPhone und iPad in die Seite hinein und nicht zurück (Safari macht das bei Schrift unter 16 Pixel). Im Portal sind Felder auf Touch-Geräten jetzt groß genug.',
      'Noch offen: eine Navigationsleiste am unteren Rand im Daumenbereich. Sie würde ein Umbauen der Kopfzeile erfordern und ist deshalb eine eigene Entscheidung.',
    ],
  },
  {
    version: '3.488.0',
    date: '2026-08-10T17:10',
    title: 'Restore-Test täglich statt monatlich',
    tag: 'improvement',
    changes: [
      'Der automatische Wiederherstellungs-Test läuft ab sofort täglich statt einmal im Monat (Anweisung Tech-Lead). Er spielt das jüngste Backup in eine Wegwerf-Datenbank, zählt die Tabellen und wirft sie wieder weg — die Produktionsdatenbank wird dabei nie angefasst.',
      'Das Cockpit erwartet entsprechend einen Lauf pro Tag: Bis 36 Stunden ist die Kachel grün, danach beeinträchtigt (ein Lauf ausgefallen), ab 72 Stunden Ausfall. Vorher galten 45 Tage als frisch — ein Backup, dessen Wiederherstellung seit Wochen niemand geprüft hat, ist aber eine Vermutung und keine Sicherung. Ein fehlgeschlagener Lauf ist unabhängig vom Alter sofort ein Ausfall.',
      'Achtung, ein Merge allein stellt nichts um: Der Zeitplan liegt als Cron auf dem Server. Die Zeile „30 3 1 * *" muss dort auf „30 3 * * *" geändert werden — Anleitung in tools/backup/README.md.',
    ],
  },
  {
    version: '3.487.0',
    date: '2026-08-10T16:20',
    title: 'Bau-Tagesbericht: Der Bauherr sieht, was er unterschreibt',
    tag: 'improvement',
    changes: [
      'Bisher bekam der Bauherr ein leeres Unterschriftsfeld gereicht und zeichnete gegen, ohne gesehen zu haben, was er gegenzeichnet. Das entwertet genau das, wofür der Tagesbericht da ist: Er ist ein Beweisdokument, und eine blind geleistete Unterschrift ist im Streitfall angreifbar. Über dem Feld steht jetzt im Klartext, was bestätigt wird — Witterung, Arbeitszeit, Stunden, Personal, ausgeführte Arbeiten, Behinderungen, besondere Vorkommnisse, Regieleistungen.',
      'Leere Blöcke bleiben stehen und heißen ausdrücklich „keine". Das ist kein Schönheitsfehler, sondern der Punkt: „Keine Behinderungen" ist rechtlich eine Aussage und nicht eine fehlende Angabe. Wer sie ausblendet, lässt jemanden etwas bestätigen, das er nie gelesen hat. Kursiv gesetzt, damit man sie von einer echten Eintragung unterscheidet.',
      'Daneben führt ein Link zum vollständigen Tagesbericht als PDF — die Zusammenfassung ist die Kurzfassung, das PDF ist das Dokument.',
      'Der Knopf zum Vergrößern heißt jetzt „Gerät übergeben", weil das der Moment ist. In dieser Ansicht stehen im Portal links die Angaben zum Lesen und rechts das Unterschriftsfeld — beides gleichzeitig, damit niemand hin- und herschalten muss, während er das iPad in der Hand hält. Die Formularfelder verschwinden dabei, die Zusammenfassung nicht.',
      'Gilt intern wie im Partnerportal: Es ist derselbe Bauherr und dasselbe Dokument. Was er sieht, darf sich nicht danach richten, wer ihm das Gerät hinhält — die Zusammenfassung wird deshalb aus einer gemeinsamen Quelle gebaut.',
    ],
  },
  {
    version: '3.486.0',
    date: '2026-08-10T15:40',
    title: 'Partnerportal: Der Monteur kann vor Ort arbeiten',
    tag: 'feature',
    changes: [
      'Ein Montage-Partner sah bisher nur Titel, Ort, Zeitraum und Stand seines Einsatzes. Damit fährt niemand auf eine Baustelle und arbeitet dort — er weiß nicht, wofür er da ist, hat keine Zeichnung und keine Einweisung. Jetzt sieht er den ganzen Einsatz: die Anweisungen aus dem Notizfeld, Projektnummer und -name, die Art des Einsatzes und wer sonst vor Ort ist. Die Anweisungen stehen als eigener Block, nicht klein am Rand — sie sind der Grund, warum jemand die Seite aufmacht.',
      'Achtung, das ist die Kehrseite: Das Notizfeld am Einsatz geht mit hinaus. Steht dort eine kaufmännische Bemerkung, liest der Fremdbetrieb sie mit. Der Einsatz-Dialog warnt jetzt sichtbar am Feld, sobald ein Partner zugeordnet ist. Ein zweites „Hinweise für den Partner"-Feld wäre die falsche Antwort — das hieße alles doppelt pflegen, und die wichtige Anweisung stünde am Ende in dem Feld, das der Monteur nicht sieht.',
      'Neu sind Unterlagen am Einsatz, in beide Richtungen: Wir legen Zeichnungen, Einweisung und Sicherheitsunterlagen ab, der Partner sieht und lädt sie herunter — und er kann Fotos und Nachweise von der Baustelle zurückladen. Ob eine Datei nach außen geht, steht an jeder Zeile und ist dort umschaltbar; beim Hochladen ist es vorbelegt, aber offen sichtbar. Ohne zugeordneten Partner geht nie etwas als freigegeben hoch. Seinen eigenen Nachweis sieht der Partner immer.',
      'Der Fremdmonteur führt jetzt denselben Bau-Tagesbericht wie unsere Monteure und lässt den Bauherrn auf seinem Gerät unterschreiben — Witterung, Zeiten, Leistung, Behinderungen, Regieleistungen, alles mit Auto-Save je Feld. Kein zweites Formular: Ein eigener Portal-Tagesbericht hätte zwei Wahrheiten über denselben Bautag erzeugt, und spätestens bei der Abrechnung stünden unterschiedliche Stunden da.',
      'Mit der Unterschrift ist der Tag gesperrt und lässt sich nicht mehr ändern — der Dialog sagt das vorher, weil man danach das Gerät weiterreicht. Zurückziehen kann der Partner eine Unterschrift nicht; das ist eine Korrektur, und die wird bei uns getroffen. Den fertigen Tagesbericht bekommt er als PDF, dieselbe Fassung, die auch wir drucken.',
      'Das ist der erste Weg, über den fachliche Daten von außen ins ERP kommen — entsprechend eng abgesichert: Freischaltung am Partnerkonto, Eigentumsprüfung je Einsatz direkt in der Datenbankabfrage (ein fremder Einsatz wird gar nicht erst gefunden und beantwortet sich wie ein nicht vorhandener), eine Positivliste erlaubter Dateiarten und eine Größengrenze. 17 Tests fahren genau diese Abwehr ab.',
    ],
  },
  {
    version: '3.485.0',
    date: '2026-08-10T13:45',
    title: 'Bedarfsvorschau gehört einem Lieferanten',
    tag: 'feature',
    changes: [
      'Eine Bedarfsvorschau kann jetzt einem Lieferanten zugeordnet werden — beim Anlegen oder später. Der Lieferant steht in der Übersicht und im Kopf der Liste. Bisher war „je Lieferant" nur Arbeitsweise und nirgends festgehalten.',
      'Die Artikel-Auswahl in der Liste bietet nur noch Artikel dieses Lieferanten an. Das war der eigentliche Anlass: Bisher standen alle Artikel zur Wahl, obwohl der Lieferant im Portal ohnehin nur die sieht, für die er hinterlegt ist — ein fremder Artikel wäre für ihn unsichtbar geblieben. Ein Schalter über der Tabelle zeigt bei Bedarf wieder alle und nennt beide Anzahlen.',
      'Auch „Aus Projektbedarf" hält sich daran: Übernommen wird nur Bedarf dieses Lieferanten. Eine Bedarfsposition gehört ihm, wenn sie ihn selbst nennt; sonst zählt der Standardlieferant des Artikels — genau wie im Projektbedarf, wo „kein Lieferant an der Position" schon immer „nimm den vom Artikel" bedeutet hat.',
      'Die Projektauswahl im Ableiten-Dialog zählt entsprechend nur die Positionen dieses Lieferanten. Sonst verspräche sie „12 offene Positionen" und die Ableitung nähme zwei. Was einem anderen Lieferanten gehört, wird nach dem Lauf gezählt gemeldet statt still weggelassen.',
      'Listen ohne Lieferant verhalten sich unverändert: Sie bieten alle Artikel an und leiten allen Bedarf ab.',
      'Der Artikel einer Zeile bleibt in der Tabelle immer sichtbar — auch wenn er nicht in der gefilterten Auswahl steht. Das trifft genau die Positionen, für die die Ableitung gebaut ist: eine Bedarfsposition, die den Lieferanten ausdrücklich nennt, während der Artikel laut Stamm einem anderen gehört. Auswählen kann man weiterhin nur aus der Auswahl.',
    ],
  },
  {
    version: '3.484.0',
    date: '2026-08-10T12:15',
    title: 'Bedarfsvorschau aus dem Projektbedarf befüllen',
    tag: 'feature',
    changes: [
      'Eine Bedarfsliste lässt sich jetzt aus dem Materialbedarf der Projekte befüllen, statt sie abzutippen. Der Knopf „Aus Projektbedarf" öffnet eine Auswahl der Projekte mit offenem Bedarf; je Projekt und Artikel entsteht eine Zeile.',
      'Übernommen wird die noch offene Menge — Bedarf minus reserviert, bestellt und eingegangen. Was bestellt ist, hat der Lieferant schwarz auf weiß; es gehört nicht mehr in eine Vorschau. Der angenehme Nebeneffekt: Sobald bestellt wurde, verschwindet die Zeile beim nächsten Ableiten von selbst.',
      'Den Termin gibt es im Materialbedarf nicht, deshalb wird er gerechnet: Liefertermin des Projekts minus Vorlauf, standardmäßig vier Wochen und im Dialog änderbar. Der errechnete Termin steht schon vor dem Klick an jedem Projekt. Hat ein Projekt keinen Liefertermin, bleiben seine Zeilen ohne Termin — ein erfundener Termin wäre schlimmer als keiner, der Lieferant plant danach.',
      'Der Knopf lässt sich beliebig oft drücken. Ersetzt werden nur frühere Ableitungen derselben Projekte; von Hand gepflegte Zeilen und Ableitungen anderer Projekte bleiben unangetastet. Abgeleitete Zeilen sind im Raster markiert, damit niemand Handarbeit an einer Zeile investiert, die beim nächsten Lauf ersetzt wird.',
      'Positionen ohne hinterlegten Artikel werden übersprungen — über den Artikel läuft die Zuordnung zum Lieferanten, eine Freitext-Position hat keinen Empfänger. Sie werden aber gezählt und nach dem Lauf ausdrücklich genannt, zusammen mit den Zeilen ohne Termin.',
      'Freigeschaltet wird durch das Befüllen nichts: Ob eine Liste im Partnerportal erscheint, entscheidet weiterhin allein der Freigabeschalter. Ist die Liste bereits freigegeben, warnt der Dialog vorher — abgeleitete Zeilen tragen Projektnummer und Projektname als Referenz.',
    ],
  },
  {
    version: '3.483.0',
    date: '2026-08-10T11:15',
    title: 'Beleg schließen: richtiger Dialog, sichtbarer Speicherstand',
    tag: 'improvement',
    changes: [
      'Wer einen Beleg mit ungespeicherten Änderungen schloss, bekam den grauen Browser-Kasten „Ungespeicherte Änderungen verwerfen?" mit genau zwei Knöpfen. Verwerfen war damit die einzige Art zu gehen — obwohl fast immer Speichern gemeint war.',
      'Jetzt fragt ein Dialog im gewohnten Aussehen und bietet den dritten Weg an: „Speichern und schließen", „Verwerfen und schließen", „Abbrechen". Schlägt das Speichern fehl (unvollständige Positionen, Server nicht erreichbar), bleibt der Dialog stehen und der Beleg offen — sonst wäre die Meldung mit der Seite weg und die Arbeit verloren.',
      'Der Speicherstand oben in der Leiste war auf schmalen Bildschirmen ausgeblendet — ausgerechnet auf dem Handy, wo man am ehesten mitten in der Erfassung unterbrochen wird. Er steht jetzt immer da und benennt auch den ruhigen Fall: „Nicht gespeichert", „Speichert …" oder „Gespeichert". Genau dann, wenn nichts blinkt, ist „ist das jetzt drin?" die Frage.',
      'Gilt für Verkauf und Beschaffung, in beiden Ansichten (Belegblatt und Werkbank) — die Kopfleiste ist dieselbe.',
    ],
  },
  {
    version: '3.482.0',
    date: '2026-08-10T10:30',
    title: 'Partnerportal: Bericht als PDF im PROBIG-Design',
    tag: 'feature',
    changes: [
      'Lieferanten und Montage-Partner können ihre Ansicht jetzt als PDF-Bericht herunterladen — je ein Bericht für die Bedarfsvorschau und für die Montage-Einsätze. Der Knopf steht oben auf der jeweiligen Seite und erscheint nur, wenn es auch etwas zu berichten gibt.',
      'Der Bericht trägt die PROBIG-Corporate-Identity: Dunkelblau als Leitfarbe für Titel, Tabellenköpfe und Kennzahlen, Türkis nur als schmaler Akzent, Fließtext in Dunkelgrau. Logo und Firmenanschrift kommen aus den Firmenstammdaten — sind sie nicht gepflegt, entfällt der Block, der Bericht wird trotzdem erzeugt.',
      'Bedarfsvorschau: Kennzahlen (nächster Termin mit Menge, Artikel, Projekte, Positionen ohne Termin), darunter die Mengen je Monat als Balken und die Positionen nach Termin gruppiert. Wie am Bildschirm werden Mengen verschiedener Einheiten nie zusammengezählt — jede Einheit wird einzeln genannt. Auf jeder Seite steht der Hinweis, dass die Vorschau eine Planung und keine Bestellung ist.',
      'Montage: Kennzahlen (nächster Einsatz, steht an, läuft, erledigt) und die Einsätze getrennt nach „Steht an" und „Erledigt" mit tagesgenauem Zeitraum, Ort und Stand.',
      'Der Bericht ist kein zweiter Zugang und kein zweiter Datenweg: Er hängt an derselben Freischaltung wie die Bildschirmsicht, und er bekommt dieselben schmalen Portal-Daten. Interne Notizen und Listennamen kann er deshalb gar nicht drucken — ein Test hält das fest, indem er dem Bericht eine „reichere" Zeile unterschiebt.',
    ],
  },
  {
    version: '3.481.0',
    date: '2026-08-07T12:40',
    title: 'Partnerportal: Kennzahlen, Diagramme, Projektname und Passwortwechsel',
    tag: 'feature',
    changes: [
      'Die Übersicht im Partnerportal ist jetzt ein Dashboard: nächster Termin samt Menge, Zahl der Artikel und Projekte, offene Montage-Einsätze — und darunter der Bedarfsverlauf als Diagramm. Vorher stand dort eine Kachelwand mit Links, also ein Inhaltsverzeichnis statt einer Übersicht.',
      'Der Bedarf je Monat wird als gestapeltes Balkendiagramm gezeigt, getrennt nach Einheit. Das ist keine Optik, sondern Notwendigkeit: „500 Stk + 40 kg" ist keine Zahl. Über Artikel hinweg wird nie summiert; für einen einzelnen Artikel gibt es zusätzlich einen Mengenverlauf als Linie, weil dort die Einheit gleich bleibt.',
      'Lieferanten sehen in der Bedarfsvorschau jetzt die Projekt-Referenz. Damit lassen sich Teillieferungen zuordnen und Rückfragen beziehen sich auf etwas. Achtung: Die Referenz ist Freitext und nennt im Alltag Kunden- und Anlagennamen — ein Lieferant erfährt damit, für wen wir bauen. Der Dialog beim Freigeben einer Bedarfsliste sagt das jetzt ausdrücklich; wer es für eine Liste nicht will, gibt sie nicht frei. Interne Notizen und der Listenname bleiben unverändert intern.',
      'Beim ersten Login muss ein Partner ein eigenes Passwort setzen. Das Anfangspasswort wird von einer Administratorin vergeben und durchgegeben — es ist damit mindestens einer weiteren Person bekannt und darf nicht das Dauerpasswort bleiben. Dasselbe gilt nach einem Zurücksetzen. Vor dem Wechsel gibt das Portal keine Seite frei.',
      'Die Montage-Ansicht ist ein Zeitstrahl mit Kennzahlen (steht an / läuft / erledigt). Offene Einsätze ohne Termin werden eigens hervorgehoben — das ist eine Rückfrage, kein Normalzustand.',
      'Das Portal hat jetzt eine dunkle Ansicht (Voreinstellung: Systemeinstellung des Partners) und respektiert „Bewegung reduzieren" auch in den Diagrammen — recharts animiert per JavaScript, wo die CSS-Regel nicht greift.',
    ],
  },
  {
    version: '3.480.0',
    date: '2026-08-07T10:05',
    title: 'Partnerportal: Lieferant lässt sich jetzt verknüpfen (fehlte)',
    tag: 'fix',
    changes: [
      'Die Verknüpfung eines Partners mit seinem Lieferantenkonto ließ sich bisher nur anzeigen, nicht setzen — die Spalte „Kontakt" blieb dauerhaft leer. Damit lief jede Bedarfsvorschau ins Leere: Ein Lieferant sah im Portal „Dein Zugang ist noch keinem Lieferantenkonto zugeordnet", ohne dass es einen Weg gegeben hätte, das zu ändern. Neu ist die Aktion „Lieferant" in der Partnerliste.',
      'Der Dialog sucht serverseitig über alle Geschäftspartner (nicht nur die ersten 50) und zeigt ausschließlich Kontakte vom Typ „Lieferant". Findet die Suche nichts, nennt die Meldung den häufigsten Grund: Der Kontakt ist im CRM als „Kunde" geführt. Eine bestehende Verknüpfung ist sichtbar und lässt sich lösen.',
      'Ist die Bedarfsvorschau freigeschaltet, aber kein Lieferant verknüpft, steht in der Liste jetzt „Lieferant fehlt" statt eines Strichs — der Zustand, in dem der Partner nichts sieht, obwohl er soll, fällt damit sofort auf.',
      'Montage-Partner sind davon nicht betroffen: Dort läuft die Zuordnung direkt über den Einsatz, ein CRM-Kontakt ist nicht nötig.',
    ],
  },
  {
    version: '3.479.0',
    date: '2026-08-07T09:20',
    title: 'Partnerportal ist ausrollbar: eigene Domain, ohne Umweg über das Edge-Passwort',
    tag: 'improvement',
    changes: [
      'Das Portal läuft unter einer eigenen Domain (partner.probig.com), wird aber vom selben Container ausgeliefert wie das ERP — getrennt wird über den Hostnamen, nicht über ein zweites Deployment. Dadurch bleibt alles same-origin: Es braucht kein CORS und damit auch keine Einstellung, die man falsch setzen kann, ohne dass es auffällt.',
      'Die Weiche greift VOR der Auslieferung der ERP-Oberfläche. Ein Partner-Browser bekommt das ERP-Bündel damit nie zu sehen — auch nicht versehentlich. Fehlt der Portal-Build im Image, antwortet die Partner-Domain mit einer klaren Meldung statt mit der internen Oberfläche.',
      'Das Edge-Passwort (Basic Auth), das die ganze Instanz schützt, ist auf der Partner-Domain ausgenommen. Das ist keine Bequemlichkeit: Es ist für alle dasselbe und ließe sich einem Partner nicht einzeln entziehen — und sein Browser schickt ohnehin ein Anmelde-Token, das die Passwortabfrage abgewiesen hätte. An seine Stelle treten die eigene Anmeldung mit Pflicht-2FA, die Deny-by-default-Sperre und ein engeres Anfragelimit. Die ERP-Domain bleibt unverändert hinter dem Passwort.',
      'Die Partner-Domain bekommt zusätzlich eine engere Sicherheitsrichtlinie als das ERP (keine Kartenkacheln, keine eingebetteten Dokumente) und wird für Suchmaschinen auf „nicht indexieren" gesetzt. Ohne gesetzte Portal-Domain existiert kein Portal — der Ausgangszustand ist der sichere.',
      'Anleitung für Coolify (DNS, zweite Domain, eine Umgebungsvariable, Redeploy) samt Smoke-Test und Fehlerbildern steht in docs/deployment-coolify.md.',
    ],
  },
  {
    version: '3.478.0',
    date: '2026-08-07T07:15',
    title: 'Partnerportal: Fremdmonteure sehen ihre Einsätze',
    tag: 'feature',
    changes: [
      'Ein Montage-Einsatz lässt sich jetzt einer Partnerfirma zuordnen — im Einsatz-Dialog unter „Montage-Partner (Portal)". Der Partner sieht den Einsatz danach in seinem Portal: Titel, Ort, Zeitraum und Stand. Ein Einsatz ohne Zuordnung erscheint in keinem Portal.',
      'Anders als bei der Bedarfsvorschau braucht es keine zweite Freigabe: Die Zuordnung IST die Freigabe. Wer einen Einsatz einem Fremdbetrieb zuweist, hat sich damit schon entschieden, dass der ihn sieht. Zur Auswahl stehen nur Partner, für die die Montage-Sicht freigeschaltet ist — sonst gäbe es Zuordnungen, die aussehen, als wären sie wirksam, beim Partner aber nie ankommen. Jede Zuordnung steht benannt im Protokoll.',
      'Was der Partner NICHT sieht: die Notizen zum Einsatz, unsere eigenen Monteure und den Projektbezug. Alle drei sind intern — die Notiz enthält im Alltag Absprachen und kaufmännische Bemerkungen, die Monteure sind Personaldaten. Titel und Ort gehen mit, sonst wäre die Sicht wertlos: Der Partner fährt ohnehin dorthin.',
      'Einsätze ohne Termin stehen im Portal oben, nicht unten — anders als beim Bedarf. Ein Bedarf ohne Termin ist unbestimmt und darf warten, ein Einsatz ohne Termin ist eine offene Absprache. Gruppiert wird nach Stand, nicht nach Datum: Ein Einsatz, dessen Termin vorbei ist, der aber noch auf „geplant" steht, bleibt unter „Steht an" — genau dort muss jemand nachhaken. Termine erscheinen tagesgenau; ein Monteur reist an.',
    ],
  },
  {
    version: '3.477.0',
    date: '2026-08-06T20:45',
    title: 'Partnerportal: Lieferanten sehen ihre Bedarfsvorschau',
    tag: 'feature',
    changes: [
      'Ein Lieferant sieht im Portal jetzt, welche Mengen wir von ihm erwarten und bis wann — nach Termin gruppiert, über den vollen Zeitraum. Die Zuordnung läuft über den Standardlieferanten am Artikel: Ein Partner sieht die Zeilen der Artikel, bei denen er hinterlegt ist, und sonst nichts.',
      'Sichtbar wird eine Bedarfsliste erst durch eine ausdrückliche Freigabe. Der Schalter dafür steht oben in der Liste, damit auf einen Blick erkennbar ist, ob Lieferanten mitlesen. Vor dem Freigeben fragt ein Dialog nach und sagt, was der Partner tatsächlich sieht — Zurücknehmen geht sofort. Beides steht benannt im Protokoll („freigegeben" / „Freigabe zurückgenommen").',
      'Was NICHT hinausgeht: die Projekt-Referenz, die Notiz zur Position und der Name der Bedarfsliste. Alle drei sind Freitext und nennen im Alltag Kunden- und Anlagennamen. Aus demselben Grund bleiben Zeilen ohne Artikel (Freitext) und Artikel ohne hinterlegten Lieferanten immer intern — sie gehören keinem Lieferanten, wären sonst aber für jeden sichtbar.',
      'Die Zeilen werden je Artikel UND Termin zusammengefasst: „800 Stück irgendwann" hilft einem Lieferanten nicht, „300 im März, 500 im Juni" schon. Zugleich sieht er dieselbe Position nicht mehrfach, nur weil sie intern auf mehrere Listen verteilt ist. Angezeigt wird der Monat, nicht der Tag — eine Vorschau ist keine Zusage auf den Tag genau, und das soll die Anzeige nicht suggerieren.',
      'Ist ein Zugang noch keinem Lieferanten zugeordnet, sagt die Seite das ausdrücklich und bittet um Rückmeldung — statt eine leere Liste zu zeigen, die wie „gerade nichts geplant" aussieht. Das sind zwei verschiedene Sachverhalte, und nur einer davon ist ein Fall für uns.',
    ],
  },
  {
    version: '3.476.0',
    date: '2026-08-06T18:30',
    title: 'Partnerportal: Fundament, eigene Anmeldung und Verwaltung der Zugänge',
    tag: 'feature',
    changes: [
      'Lieferanten und Montage-Partner bekommen einen eigenen Zugang unter partner.probig.com, um selbst nachzusehen, was sie betrifft — Bedarfsvorschau für Lieferanten, Einsätze für Montage-Partner. Diese Fassung bringt das Fundament: Datenmodell, Anmeldung und die Verwaltung der Zugänge. Die beiden Sichten selbst folgen als Nächstes. Partner sehen ausschließlich, sie ändern nichts.',
      'Das Portal ist eine eigene Anwendung, kein versteckter Zweig im ERP. Das ist der Kern der Sache: Interner Code wird an den Browser eines Partners gar nicht erst ausgeliefert. Ein ausgeblendeter Menüpunkt wäre nur ausgeblendet — nicht abwesend.',
      'Ein Partner-Zugang erreicht ausschließlich Portal-Adressen; alles andere wird abgewiesen, unabhängig davon, ob der jeweilige Bereich intern eine Berechtigungsprüfung trägt. Umgekehrt greift ein Mitarbeiter-Zugang im Portal nicht. Innerhalb des Portals gilt: Was nicht ausdrücklich freigegeben ist, gibt es nicht — genau umgekehrt zur internen Regel, wo ein nicht annotierter Bereich offen ist. Für eine Fläche, die aus dem Internet erreichbar ist, wäre „vergessen = offen" die falsche Voreinstellung.',
      'Angemeldet wird wie intern: Passwort plus zweiter Faktor, Authenticator-App oder Passkey, freie Wahl beim ersten Login. Wer sich an der falschen Maske anmeldet — Mitarbeiter im Portal oder Partner im ERP —, bekommt eine klare Ansage statt eines Zugangs, der überall auf „keine Berechtigung" läuft.',
      'Neu unter Administration → Partnerzugänge: Partner anlegen, die Sichten einzeln freischalten und Zugänge für einzelne Personen vergeben. Ein frisch angelegter Partner sieht nichts, bis jemand bewusst freischaltet. Das Anfangspasswort erzeugt das System und zeigt es genau einmal an — es ist nirgends gespeichert und steht auch nicht im Protokoll; Zeichen, die sich am Telefon verwechseln lassen (0/O, 1/l/I), kommen nicht vor.',
      'Einen Zugang zu entziehen wirkt sofort: Wird ein Partner oder eine Person deaktiviert, endet auch eine bereits laufende Sitzung mit der nächsten Anfrage — nicht erst nach Ablauf des Tokens. Jede Vergabe, Freischaltung und Sperre landet benannt im Audit-Log („Bedarfsvorschau freigeschaltet"), damit später nachvollziehbar bleibt, wer wem wann Einsicht gegeben hat.',
    ],
  },
  {
    version: '3.475.0',
    date: '2026-08-06T17:45',
    title: 'Analyse: Zeitraum „Gesamt" jetzt ab 2000 statt ab 1900',
    tag: 'fix',
    changes: [
      'Die untere Grenze des neuen Zeitraum-Presets „Gesamter Zeitraum (inkl. Altdaten)" ist auf Wunsch des Tech-Leads von 1900 auf das Jahr 2000 angehoben — Belege ohne verwertbares Altsystem-Datum (Fallback exakt auf 1900-01-01) fallen dadurch wieder heraus, das betrifft aber nur Datensätze ohne echtes Belegdatum.',
    ],
  },
  {
    version: '3.474.0',
    date: '2026-08-06T17:15',
    title: 'Projektprotokolle: Unterschrift auf Papier statt am Bildschirm, und Protokolle sind löschbar',
    tag: 'improvement',
    changes: [
      'Die Unterschrift am Bildschirm ist entfallen. Sie hat nichts belegt, was der Papierweg nicht besser belegt: Die Protokolle gehen ohnehin per Mail hinaus, der Kunde ist beim Ausfüllen gar nicht mit dem iPad in der Hand dabei. Der Weg ist jetzt geradlinig: ausfüllen → „Zur Unterschrift senden" (PDF + Mail-Entwurf) → Kunde unterschreibt auf dem Ausdruck → „Unterschrieben erhalten".',
      'Neu ist dafür der Rücklauf: Beim Abschließen lässt sich der unterschriebene Scan hinterlegen (PDF oder Foto, bis 25 MB) — zusammen mit Datum, Name der unterschreibenden Person und einer Bemerkung. Der Scan liegt am Protokoll und lässt sich jederzeit wieder öffnen. Das Datum ist das der Unterschrift laut Scan, nicht das des Hochladens: Der Rücklauf trudelt oft Tage nach der Abnahme ein.',
      'Der Scan ist bewusst optional. Manchmal gibt es nur eine Bestätigung per Mail — dann lässt sich das Protokoll trotzdem abschließen, statt ewig auf „Zur Unterschrift versendet" stehen zu bleiben. Die Liste sagt, was vorliegt: „Rücklauf-Scan" oder der Name der unterschreibenden Person.',
      'Protokolle lassen sich jetzt löschen — in jedem Status, auch unterschriebene. Vorher gab es nur „Verwerfen", und abgeschlossene Protokolle ließen sich gar nicht mehr entfernen; wer eines versehentlich angelegt oder abgeschlossen hatte, wurde es nicht mehr los. Vor dem Löschen wird gefragt, bei einem unterschriebenen Protokoll mit dem ausdrücklichen Hinweis, dass es Teil der Projektakte ist und ein hinterlegter Scan mitgeht. Jede Löschung steht im Audit-Log, samt der Information, ob das Protokoll bereits unterschrieben war.',
      'Im PDF stehen die Unterschriftszeilen jetzt konsequent leer — darauf wird ja unterschrieben. Liegt der Rücklauf vor, steht darunter ein Vermerk „Unterschrieben zurückerhalten am … · Name" statt eines Schriftzugs.',
    ],
  },
  {
    version: '3.473.0',
    date: '2026-08-06T16:15',
    title: 'Analyse: neuer Zeitraum „Gesamt" bezieht Büroware-Altdaten mit ein',
    tag: 'fix',
    changes: [
      'Importierte Altdaten aus dem Vorsystem Büroware fehlten in der Analyse komplett — nicht weil sie gefiltert wurden, sondern weil kein Zeitraum-Preset weit genug zurückreichte (maximal „Letzte 24 Monate"). Ein neues Preset „Gesamter Zeitraum (inkl. Altdaten)" reicht bis 1900 zurück — das deckt auch den Datums-Fallback der Migrations-ETL für Belege ohne lesbares Altsystem-Datum ab. Gilt einheitlich für Artikel-, Belege- und Projekt-Abfrage sowie das Berichtscenter.',
      'Der Lagerbewegungs-Chart der Artikel-Abfrage bleibt für Altdaten weiterhin leer — die Büroware-Migration hat nie eine Bewegungshistorie importiert, nur den aktuellen Bestand je Artikel. Das ist ein fehlender Daten-Import, kein Abfrage-Fehler, und offen in der Moduldoku vermerkt.',
    ],
  },
  {
    version: '3.472.0',
    date: '2026-08-06T15:30',
    title: 'Projektprotokolle: Freigabe, Baustellenvorbereitung, Probelauf und Abnahme aus dem System',
    tag: 'feature',
    changes: [
      'Die vier gelenkten Protokolle des Projektablaufs — Freigabe (9.2.4), Baustellenvorbereitung (2.3.2), Checkliste Probelauf (2.3.4) und Übergabe-/Abnahmeprotokoll (2.3.6) — werden nicht mehr als Word-Datei ausgefüllt, sondern im neuen Projekt-Register „Protokolle" angelegt. Jedes entsteht aus einer Vorlage; Kunde, Projekt, Ort, Ansprechpartner, Montagetermin und Supervisor sind beim Anlegen bereits eingetragen. Was nicht sicher bekannt ist, bleibt leer statt falsch: die Telefonnummer des Supervisors steht am Mitarbeiter, nicht am Einsatz.',
      'Eine Quelle je Text, zweisprachig. Deutsche und englische Fassung stehen in EINER Vorlagendefinition nebeneinander; Formular, Prüfung und PDF entstehen daraus. Damit ist der Grund weg, warum die Word-Dateien auseinandergelaufen sind — ein Text, der nur in einer Sprache existiert, bricht jetzt den Build. Wo die englische Papiervorlage inhaltlich abwich, gilt die deutsche: Beim Prüfpunkt „Wandlager der Hauptwelle" verlangte die englische Fassung eine Schweißstelle, die deutsche einen Sicherungsanker — zwei verschiedene Befestigungsarten. Es gilt der Sicherungsanker.',
      'Die Protokolle hängen zusammen, statt jedes für sich zu stehen: Das Datum der unterschriebenen Planfreigabe steht anschließend in der Baustellenvorbereitung. Ein Prüfpunkt im Probelauf, der auf „Mangel" gesetzt wird, erscheint als Störung im Baustellenbericht des Einsatzes — mit Wiedervorlage und Verantwortlichem. Und das Abnahmeprotokoll übernimmt die offenen Mängel des Einsatzes, statt sie ein zweites Mal abtippen zu lassen. Wird ein Mangel zurückgenommen, verschwindet auch die Spiegelung wieder.',
      'Unterschrieben wird vor Ort auf dem iPad: großer Unterschriftsbereich, „Vergrößern" für die Übergabe an den Kunden, große Tippziele für die Prüfpunkte (das wird mit Handschuhen im Becken bedient). Das Abnahmeprotokoll kennt bis zu drei Kundenvertreter — der Dialog zeigt, wie viele Plätze je Rolle noch frei sind, statt eine bereits geleistete Unterschrift stumm zu überschreiben. Eine Unterschrift lässt sich zurückziehen, solange das Protokoll offen ist.',
      'Alternativ der Rücklauf-Weg: PDF laden, Mail-Entwurf öffnet sich mit Text (kein Server-Mailversand, wie beim Bau-Tagesbericht), Protokoll steht auf „Zur Unterschrift versendet" — und der unterschriebene Scan lässt sich später verknüpfen. Der belegt die Unterschrift genauso wie die digitale Variante; die Liste zeigt, welcher Weg es war.',
      'Sobald alle Rollen unterschrieben haben, ist das Protokoll unveränderlich und der Projektstand dieses Tages eingefroren. Zwei Termine, die bisher niemand gepflegt hat, entstehen dabei von selbst am Projekt: das Planfreigabe-Datum und das Ende der Gewährleistung (aus Übergabedatum und vereinbarter Dauer, mit dem 29.02. korrekt auf den 28.02. gezogen). Verwerfen geht nur, solange nicht unterschrieben ist — ein unterschriebenes Protokoll ist Teil der Projektakte.',
      'Das PDF druckt alle vier Protokolle im ProERP-Briefpapier, in der Sprache des Protokolls, mit dem QM-Kopf und dem Hinweis der Papiervorlagen, dass ein Ausdruck nur am Tag des Ausdrucks gilt. Leere Felder werden zu Schreiblinien statt zu „—": Das Protokoll geht auch als Ausdruck auf die Baustelle. Und für jede erlaubte Unterschrift steht eine Zeile im Dokument — auch für die noch offene, damit auf Papier unterschrieben werden kann.',
      'Die Zusatzblöcke, die bisher nur die englische Vorlage kannte (Kran, bauseits gestelltes Personal mit Schweißzertifikat, Werkzeugliste, Entsorgung), sind erhalten und je Protokoll an- oder abwählbar — nichts von den Papiervorlagen ist unterwegs verloren gegangen.',
    ],
  },
  {
    version: '3.471.0',
    date: '2026-08-06T12:00',
    title: 'Rechnung wieder öffnen — korrigieren bleibt möglich, aber nachvollziehbar',
    tag: 'feature',
    changes: [
      'Seit dem Prozess-Audit lässt sich eine versendete Rechnung nicht mehr über das Status-Menü in den Entwurf zurückholen — das war der stille Weg, auf dem eine festgeschriebene Rechnung unbemerkt wieder änderbar wurde. Korrigieren muss im Alltag aber möglich bleiben, deshalb gibt es dafür jetzt eine eigene Aktion.',
      '„Beleg-Aktionen → Rechnung wieder öffnen": Die Rechnung geht zurück in den Entwurf, Positionen und Steuersatz sind wieder änderbar. Dafür ist ein Grund Pflicht — er wird im Änderungsprotokoll festgehalten, zusammen mit dem Status, aus dem die Rechnung geholt wurde.',
      'Die Belegnummer bleibt bestehen. Sie ist bereits vergeben und beim Kunden; eine neue Nummer würde eine Lücke im Nummernkreis reißen.',
      'Zwei Fälle bleiben gesperrt: eine BEZAHLTE Rechnung (der Zahlungseingang der Buchhaltung hängt am Betrag) und eine Rechnung, zu der bereits eine Gutschrift existiert (dann ist die Korrektur schon auf dem offiziellen Weg gelaufen). In beiden Fällen nennt die Meldung den Grund.',
      'Lieferscheine sind bewusst nicht dabei — dort ist der Warenausgang gebucht, Korrektur läuft über den Storno.',
      'Die Aktion hat ein eigenes Recht: „Verkauf → Rechnung öffnen". Es ist standardmäßig NICHT vergeben und muss in der Administration bewusst zugeteilt werden.',
    ],
  },
  {
    version: '3.470.0',
    date: '2026-08-06T11:00',
    title: 'Berichtscenter: Kennzahlen-Kacheln und Rang-Listen statt Tabellen',
    tag: 'improvement',
    changes: [
      'Der Tab „Berichte" in der Analyse zeigt jetzt vier Kopf-Kennzahlen (Top-Verkaufsschlager, Top-Kunde, stärkste Warengruppe, Anzahl unbewegter Ladenhüter) sowie alle vier Berichte als Rang-Listen mit Gold/Silber/Bronze-Platzierung, proportionalem Balken und Anteil am Gesamt-Zeitraum je Zeile — vorher reine Balkendiagramme mit Tabellen darunter.',
      'Neue Bausteine `HeroStat` und `RankedList` in `AnalyticsCharts.tsx` — beide reines HTML/CSS ohne Diagramm-Bibliothek, nutzen die bestehenden App-Farbtöne für Hell-/Dunkelmodus.',
      'Die Berichte-Antwort liefert jetzt zusätzlich `totals` (gelieferte Gesamtmenge und Gesamt-Umsatz im Zeitraum) als Grundlage für den Anteil je Zeile.',
    ],
  },
  {
    version: '3.469.0',
    date: '2026-08-05T17:45',
    title: 'Absender-Wechsel im Einklang mit den Nummernkreisen der Einstellungen',
    tag: 'fix',
    changes: [
      'Der Absender-Wechsel hat den zurückzudrehenden Nummernkreis bisher über das Belegdatum gesucht. Die Nummernkreise unter Einstellungen → Nummernkreis sind aber je Belegart, Gesellschaft und VERGABEJAHR geführt — und das Vergabejahr steht in der Belegnummer selbst (AN-**26**10001). Ein rückdatierter Entwurf (Belegdatum im Vorjahr) traf dadurch den falschen Kreis und ließ sich fälschlich nur noch per Storno umstellen. Jetzt bestimmen laufende Nummer UND Jahr aus der Nummer, welcher Zähler zurückgeht — exakt der Kreis, der in den Einstellungen steht.',
      'Die Ablehnung sagt jetzt, was wirklich los ist: Eine Alt- oder Import-Nummer (Format von vor der Nummernkreis-Umstellung) „stammt nicht aus dem aktuellen Nummernkreis-Schema" — statt fälschlich zu behaupten, es sei inzwischen ein weiterer Beleg angelegt worden. Und wenn der Zähler tatsächlich weitergedreht wurde, nennt die Meldung beide möglichen Ursachen: ein weiterer Beleg oder eine Zählerkorrektur in den Einstellungen.',
      'Einstellungen → Nummernkreis zeigt Alt-Zähler jetzt als solche: die „geteilt"-Zeilen von vor der Umstellung auf Gesellschafts-Nummernkreise und die Zähler für Anzahlungs-/Schlussrechnung (die zählen bewusst über den Rechnungskreis) tragen ein Badge „Alt-Zähler", zeigen keine „nächste Nummer" mehr an und lassen sich nicht mehr bearbeiten — eine Korrektur dort wäre wirkungslos gewesen, sah aber aus wie eine echte. Sie stehen jetzt am Ende der Liste. Vorjahres-Zeilen lebender Kreise versprechen ebenfalls keine „nächste Nummer" mehr (gezogen wird nur im aktuellen Jahr), bleiben aber korrigierbar.',
      'Der Wechsel der betreuenden Gesellschaft im Kundenstamm folgt jetzt denselben Regeln: Er stellte bisher ALLE Belege des Kunden still um — ohne neue Nummern, auch festgeschriebene Rechnungen. Jetzt werden nur Entwürfe umgestellt (mit neuer Belegnummer aus dem richtigen Kreis); was sich nicht sauber umnummerieren lässt, bleibt unverändert stehen und wird in der Meldung gezählt. Festgeschriebene Belege bleiben grundsätzlich bei ihrer Gesellschaft — das ist Historie.',
      'Dublettenschutz im Rechnungs-Verbund: Rechnung, Anzahlungs- und Schlussrechnung teilen sich einen Nummernkreis und denselben RE-Präfix. Nach einer Zählerkorrektur in den Einstellungen konnte die Vergabe eine RE-Nummer erzeugen, die eine der Schwester-Belegarten schon trug. Die Vergabe prüft jetzt über alle drei Arten hinweg und dreht den Zähler bei Bedarf weiter.',
      'Pool-Bestellungen (Bestellpool) können den Absender jetzt direkt im Editor wechseln — der Pool zählt wie ein Entwurf (nie beim Lieferanten). Vorher bot der Editor das Feld an, der Server lehnte aber jede Änderung ab. Umgekehrt verschwindet „Absender wechseln" im Menü überall dort, wo ohnehin beide Wege gesperrt sind (storniert, geliefert, Ware eingegangen, festgeschriebene Rechnung) — statt in eine Sackgasse aus Fehlermeldungen zu führen.',
      'Zwei Vorschau-Korrekturen im Nummernkreis-Tab: Die Zeile „Meldung (Verbesserungen)" zeigt jetzt das echte Format VB-26001 (vorher „FEEDBACK_ITEM-2026-0001"), und die Kommissionierliste (Alt-Feature) vergibt wieder ihr Alt-Format KL-2026-0001 — die Vergabe war stillschweigend auf das neue Nummernschema gerutscht, die Einstellungen zeigten das alte.',
    ],
  },
  {
    version: '3.468.0',
    date: '2026-08-05T17:30',
    title: 'Prozess-Audit: 12 Fehler in Belegfluss, Zahlungsplan und Wareneingang behoben',
    tag: 'fix',
    changes: [
      'Belegstatus folgen jetzt einem Übergangsgraphen: Eine versendete Rechnung lässt sich nicht mehr per Statusmenü in den Entwurf zurückholen (sie war damit wieder änder- und löschbar), und ein Lieferschein wechselt auf „Versendet" nur noch über den Warenausgang — ein reiner Statuswechsel buchte keine Lagerabgänge und sperrte den Warenausgang dauerhaft. Das Statusmenü zeigt nur noch erreichbare Ziele.',
      'Der Storno eines gebuchten Lieferscheins bucht die Lagerabgänge jetzt zurück — kompensiert wird genau das, was tatsächlich gebucht wurde. Vorher blieb der Bestand nach einem Storno dauerhaft zu niedrig.',
      'Beim Buchen eines Wareneingangs laufen Statuswechsel und Lagerbuchung jetzt in einem Schritt. Vorher konnte der Beleg auf „Gebucht" stehen, obwohl nichts gebucht wurde (z. B. wenn die Bestellung parallel storniert war) — ohne Weg zurück.',
      'Überlieferungen gelten nur noch für die Position, die tatsächlich überliefert wurde. Vorher hob eine einzige überlieferte Position den Mengendeckel für alle Positionen des Wareneingangs auf — eine parallel schon abgehakte Position wurde dadurch doppelt in den Bestand gebucht.',
      'Doppelte Rechnungsnummern sind unterbunden: Der Nummernkreis prüfte eine nachhinkende Nummer nur gegen die eigene Belegart — Rechnung, Anzahlungs- und Schlussrechnung drucken aber identisch formatierte RE-Nummern aus demselben Zähler.',
      'Zahlungsplan: Das Löschen einer Entwurfs-Rechnung gibt die Plan-Position wieder frei (vorher blieb sie dauerhaft „fakturiert" — der Betrag war nie mehr stellbar). Ein Plan, dessen Prozente nicht aufgehen, wird nicht mehr still zurechtgerechnet, sondern sichtbar inkonsistent — erst dann greift die Fakturasperre wie vorgesehen. Beträge in fremder Währung werden abgelehnt statt still umetikettiert. Und die Rechnung aus einer Plan-Position entsteht komplett in einer Transaktion — keine halben 0-€-Rechnungen mehr bei einem Abbruch.',
      'Beschaffung: Eine stornierte Bestellung lässt sich nicht mehr in eine Eingangsrechnung wandeln (fällige Verbindlichkeit für nie gelieferte Ware), eine Pool-Bestellung auch nicht. Die Menge einer Position kann nicht mehr unter den bereits gebuchten Wareneingang gedrückt werden, und der Wareneingang meldet unbekannte Positionen als Fehler, statt gezählte Ware still zu verschlucken.',
      'Die Umsatz-Auswertung je Artikel (ABC-Analyse) zählt stornierte und Entwurfs-Rechnungen nicht mehr als Umsatz.',
      'Das vollständige Protokoll mit allen Befunden, Schweregraden und Commits steht in docs/prozess-audit-2026-08-05.md. Zwei Punkte brauchen eine Entscheidung des Tech-Leads (Wandel-Kette vs. Zahlungsplan; Daten-Korrektur für Alt-Anzahlungen) und sind dort beschrieben.',
    ],
  },
  {
    version: '3.467.0',
    date: '2026-08-05T15:30',
    title: 'Analyse: neues Berichtscenter (Verkaufsschlager, Ladenhüter, Warengruppen, Kunden)',
    tag: 'feature',
    changes: [
      'Neuer Tab „Berichte" in der Analyse: Verkaufsschlager (Top-Artikel nach Menge), Ladenhüter (Artikel mit der geringsten Bewegung), Warengruppen-Ranking und Kunden-Rangliste — vier kuratierte Berichte statt einer freien Abfrage, mit gemeinsamem Zeitraum-Filter und einem Export mit vier Arbeitsblättern.',
      'Wichtig für die Auslegung der Zahlen: „gelieferte Menge" kommt jetzt aus den Lieferschein-Positionen, nicht aus den Rechnungspositionen. Eine Anzahlungs- oder Schlussrechnung im Anlagenbau ist oft pauschal und nicht je Artikel aufgeschlüsselt — der Lieferschein bildet dagegen immer ab, was tatsächlich ausgeliefert wurde. Das gilt jetzt einheitlich im ganzen Modul: auch die bestehende Artikel-Abfrage zeigt „Geliefert" statt „Verkauft" (Umsatz bleibt weiterhin aus den Rechnungen).',
      'Ladenhüter sind aktive, gelagerte Artikel mit der geringsten Lieferbewegung im gewählten Zeitraum, absteigend nach Lagerbestand sortiert, wenn mehrere gar nicht bewegt wurden — die mit der größten gebundenen Kapitalmenge zuerst.',
    ],
  },
  {
    version: '3.466.0',
    date: '2026-08-05T15:20',
    title: 'Absender (Verkaufs-/Einkaufsfirma) am Beleg wählen und wechseln',
    tag: 'feature',
    changes: [
      'Beim Anlegen eines Verkaufsbelegs stand nirgends, aus welcher Gesellschaft er ausgestellt wird — obwohl genau das die Belegnummer bestimmt: PROBIG AT und PROBIG DE haben getrennte Nummernkreise, und die Gesellschaft steckt als Ziffer in der Nummer (AN-26**1**0001 gegenüber AN-26**2**0001). Beim Angebot fehlte das Feld ganz, weil im Frontend eine zweite, veraltete Liste der betroffenen Belegarten lag. Diese Liste gibt es jetzt nur noch einmal.',
      'Neu steht im Anlege-Dialog von Verkauf UND Einkauf ein Pflichtfeld „Absender". Es ist vorbelegt — aus dem Projekt, sonst aus dem Kundenstamm, sonst aus dem Land des Kunden — und darunter steht, woher die Vorbelegung kommt. Wer bewusst aus der anderen Gesellschaft anbieten will, stellt es einfach um.',
      'Wurde ein Beleg trotzdem mit der falschen Gesellschaft angelegt, lässt er sich jetzt umstellen: „Beleg-Aktionen → Absender wechseln". Solange der Beleg ein Entwurf ist und seine Nummer die zuletzt vergebene ihres Kreises, wird nur umnummeriert — der alte Zähler geht um eins zurück, es entsteht keine Lücke in der Nummernfolge.',
      'Hat inzwischen ein anderer Beleg nachgezogen, ginge das nicht mehr ohne Lücke. Dann sagt der Dialog genau das und bietet den zweiten Weg an: Beleg stornieren und unter der richtigen Gesellschaft neu ausstellen, mit allen Positionen und einer Nummer aus deren Kreis. Beide Belege verweisen in der Notiz aufeinander. Denselben Weg nimmt ein Beleg, der nicht mehr im Entwurf steht.',
      'Zwei Grenzen sind bewusst gezogen: Eine bereits gestellte Rechnung wird NICHT über diesen Weg storniert — dafür gibt es die Gutschrift, alles andere liefe an der Buchhaltung vorbei. Und eine Bestellung mit Wareneingang bleibt, wie sie ist: der Wareneingang hängt an diesem Beleg.',
      'Im Beleg-Editor heißt das Feld jetzt „Absender" (vorher „Versender (Gesellschaft)") und der Hinweis darunter sagt, dass ein Wechsel eine neue Belegnummer vergibt. Die Bestellung hat dieses Feld überhaupt zum ersten Mal.',
    ],
  },
  {
    version: '3.465.0',
    date: '2026-08-05T13:50',
    title: 'Artikel, der noch verwendet wird, kann nicht mehr gelöscht werden',
    tag: 'fix',
    changes: [
      'Bisher ließ sich ein Artikel löschen, während er auf einem Angebot lag — danach scheiterte jedes weitere Speichern dieses Belegs. Der Beleg war ohne Zutun kaputt, und ohne den Artikel war nicht mehr erkennbar, was dort stehen sollte.',
      'Löschen wird jetzt abgelehnt, solange der Artikel irgendwo verwendet wird: Verkaufsbelege, Bestellungen, Bedarfslisten, Projektauslegungen, Fertigungs- und Kommissionieraufträge, Forecasts. Die Meldung nennt, wie viele es sind, bis zu drei Nummern zum Nachschauen („und N weitere") und den Ausweg.',
      'Der Ausweg ist „Deaktivieren": der Artikel taucht dann in keiner Auswahl mehr auf, Bestand, Historie und bestehende Belege bleiben erhalten. Löschen bleibt für Fehlanlagen.',
      'Weil das Löschen ein „Rückgängig"-Löschen ist (die Zeile verschwindet sofort, der Server wird erst nach dem Undo-Fenster gefragt), erscheint die Ablehnung als Fehlermeldung ein paar Sekunden später — die Zeile kommt dann zurück. Die Meldung zeigt jetzt den Grund vom Server statt eines pauschalen „Löschen fehlgeschlagen.".',
    ],
  },
  {
    version: '3.464.0',
    date: '2026-08-05T13:40',
    title: 'Vier-Augen: Ein Statuswechsel kostet die Freigabe nicht mehr',
    tag: 'fix',
    changes: [
      'Bei einem freigegebenen Beleg konnte ein reiner Statuswechsel — etwa auf „Versendet" — die Freigabe aufheben. Der Beleg stand danach auf „Keine gültige Freigabe" und musste erneut durch die Vier-Augen-Runde, obwohl inhaltlich nichts geändert wurde. Genau für das Versenden war ja freigegeben worden.',
      'Betroffen waren Belege, die vor dem 28. Juli freigegeben wurden: Für die gibt es keinen gespeicherten Vergleichsstand, und ohne den galt vorsichtshalber „alles geändert" — auch wenn der Aufruf nur den Status enthielt. Dasselbe passierte dort beim Eintragen einer internen Notiz oder beim Wechsel des Sachbearbeiters.',
      'Jetzt wird zuerst gefragt, ob der Vorgang überhaupt etwas Inhaltliches anfasst. Status, interne Notiz, Projektzuordnung und Sachbearbeiter zählen nicht dazu — bei ihnen passiert nichts, unabhängig davon, wie alt die Freigabe ist.',
      'Die Kontrolle bleibt scharf: Wird neben dem Status auch etwas Inhaltliches geändert (Preise, Positionen, Kunde, Termine, Belegtexte …), erlischt die Freigabe wie bisher. Ein mitgeschickter Statuswechsel hebelt nichts aus.',
      'Ebenfalls unverändert: Ein freigegebener Beleg bleibt im Editor schreibgeschützt, und ein Beleg, der auf Freigabe wartet, kann weiterhin nicht versendet werden.',
    ],
  },
  {
    version: '3.463.0',
    date: '2026-08-05T13:10',
    title: 'Beleg-Editor: „Preise aus Artikelstamm laden"',
    tag: 'feature',
    changes: [
      'Neuer Knopf unter den Positionen: „Preise aus Artikelstamm laden". Er setzt die Einzelpreise aller Artikel-Positionen auf den Preis, der heute im Artikelstamm steht — im Verkauf der Verkaufspreis, im Einkauf der Einkaufspreis. Gedacht für zwei Fälle: nach einer Preispflege im Artikelstamm, und für Positionen, die ohne Preis in den Beleg gekommen sind (Import oder Einfügen aus Excel).',
      'Der Knopf überschreibt auch Preise, die von Hand eingetragen wurden — das ist Absicht, denn gewollt ist der aktuelle Stammpreis. Damit das keine Einbahnstraße ist, erscheint die Meldung mit „Rückgängig": ein Klick stellt die vorherigen Preise wieder her. Gespeichert wird ohnehin nichts, bevor du auf „Speichern" gehst.',
      'Positionen, deren Artikel im Stamm keinen Preis hat, bleiben unangetastet und werden in der Meldung mit ihrer Artikelnummer genannt. Ein leerer oder auf 0 stehender Stammpreis darf einen erfassten Preis nicht überschreiben — sonst hätte der Knopf still Beträge gelöscht.',
      'Ebenfalls in Ruhe gelassen: Überschriften, Textzeilen, Seitenumbrüche und freie Positionen ohne Artikelbezug (im Einkauf z. B. Fracht).',
      'Bei abgelängten Artikeln wird der Preis der tatsächlich verknüpften Variante genommen, nicht der des Basisartikels — im Beleg steht ja die Basis-Nummer, gemeint ist aber die abgelängte Ausführung.',
      'Der Knopf steht in beiden Ansichten (Belegblatt und Werkbank) und in Verkauf wie Einkauf. In einem Beleg, der nicht mehr änderbar ist, erscheint er nicht.',
    ],
  },
  {
    version: '3.462.0',
    date: '2026-08-05T12:30',
    title: 'Verständliche Meldung statt „Artikel <UUID> nicht gefunden"',
    tag: 'fix',
    changes: [
      'Beim Speichern eines Angebots kam die Meldung „Artikel 84becdd1-178a-4899-ae33-85f910ed6f24 nicht gefunden." — eine Kennung, mit der niemand etwas anfangen kann. Sie sagte weder, WELCHE der Positionen gemeint war, noch was zu tun ist.',
      'Jetzt nennt die Meldung die laufende Nummer der Position, ihre Beschreibung und die Artikelnummer — und dazu den Weg heraus: in dieser Position den Artikel neu auswählen oder die Verknüpfung entfernen, dann erneut speichern. Die Positionsnummer ist die, die im Beleg-Raster links steht.',
      'Ursache ist ein gelöschter Artikel: Ein Artikel lässt sich löschen, während er auf einem Angebot liegt — danach scheitert jedes weitere Speichern dieses Belegs. Das Löschen selbst ist noch nicht gesperrt (offener Punkt), die Meldung führt aber jetzt zur Lösung.',
      'Auch außerhalb von Belegen (Bestellung, Inventur, Projektauslegung, Staffelpreise, Zeichnungen) beginnt die Meldung nicht mehr mit einer nackten Kennung, sondern mit dem Sachverhalt; die Kennung steht nur noch am Ende für den Support.',
    ],
  },
  {
    version: '3.461.0',
    date: '2026-08-05T10:40',
    title: 'Materialbedarf: Artikel einer Position tauschen',
    tag: 'fix',
    changes: [
      'Eine Bedarfsposition ließ sich in Menge, Baugruppe und Notiz ändern — der ARTIKEL aber nicht. Falsch erfasst, Nachfolgeartikel oder eine Freitext-Zeile, für die es inzwischen einen echten Artikel gibt: Es half nur löschen und neu anlegen (und damit Position, Notiz und Lieferant nochmal eintippen).',
      'Im Vollbild-Editor öffnet ein Klick auf die Artikel-Nr. jetzt die Artikel-Auswahl und tauscht den Artikel der Zeile. In der Bedarfsliste geht es über das Detail-Panel („Artikel → Tauschen").',
      'Bezeichnung und Einheit zieht der Server aus dem Artikelstamm nach; eine unbekannte Nummer macht die Position zur Freitext-Zeile — genau wie beim Anlegen. Position, Menge, Baugruppe, Notiz und Lieferant bleiben stehen.',
      'War für die Position schon Lagerbestand reserviert, wird die Reservierung beim Tausch freigegeben — sie lag auf dem Bestand des ALTEN Artikels. Danach kann für den neuen Artikel neu reserviert werden.',
      'Ist die Position bereits bestellt oder geliefert, bleibt der Tausch gesperrt (Knopf inaktiv, Server lehnt ab): Die Zähler „Bestellt" und „Erhalten" gehören zu Bestellpositionen des alten Artikels und würden sonst etwas Falsches behaupten. Dort weiterhin: Position löschen und neu anlegen.',
    ],
  },
  {
    version: '3.460.1',
    date: '2026-08-05T10:15',
    title: 'Befehle in der Suchpalette jetzt mit # statt >',
    tag: 'improvement',
    changes: [
      'In der Suche & Befehle-Palette (⌘K) ruft ein führendes # die Befehlsliste auf — vorher war es >. Platzhalter, Fußzeile und die Kürzel-Übersicht (?) nennen das neue Zeichen.',
      '> ist damit ein normaler Suchbegriff und wird gesucht statt abgefangen.',
    ],
  },
  {
    version: '3.460.0',
    date: '2026-08-05T09:50',
    title: 'Projektauslegung: eigenes Register, Standardteile wählen, Stückliste erzeugen (VB-26014)',
    tag: 'feature',
    changes: [
      'Neues Projekt-Register „Projektauslegung" neben den Projektdaten: das Datenblatt der Projektübergabe (zwölf Abschnitte, 46 Zeilen aus dem Haus-Excel) jetzt in der Anwendung.',
      'Jede Positionszeile ist zuerst eine Artikelzeile mit Artikelsuche. Im Zeilen-Modal umschaltbar auf „Warengruppe" (Platzhalter, wenn es den Artikel noch nicht gibt — Hauptwelle, Skimrinne) oder „Angabe" (Werkstoff, ITP … erzeugt keine Position).',
      'Warengruppen-Auswahl über den ganzen Stamm, nach Hauptwarengruppen gebündelt; die Vorlage schlägt nur die Startgruppe und den Excel-Text („üblich: NH78") vor.',
      '„Vorlage laden" ergänzt fehlende Zeilen und überschreibt nie eine Eingabe — zweimal laden ist harmlos.',
      'Zeilen je Abschnitt mit freier Bezeichnung ergänzbar; die neue Zeile öffnet direkt zur Artikelwahl.',
      'Maße, Sprache und Zertifikate stehen weiter am Projekt und werden nicht gespiegelt: die Kopf-Felder schreiben direkt in die Projektdaten und sind so gekennzeichnet.',
      '„Stückliste erzeugen" schreibt aus den gefüllten Positionszeilen eine Bedarfsliste (neu oder bestehende ersetzen) — Abschnitt wird Baugruppe. Platzhalter werden echte Positionen ohne Artikel (Nummer „XXXX.?????"), damit die Langläufer VOR der Planfreigabe bestellt werden können. Übersprungene Zeilen werden gemeldet.',
    ],
  },
  {
    version: '3.459.0',
    date: '2026-08-04T13:40',
    title: 'Kommissionierung: nach Liefertermin sortiert, Termin auf dem iPad deutlich sichtbar (VB-26013)',
    tag: 'improvement',
    changes: [
      'Gemeldet als VB-26013: Auf dem iPad war in der Übersicht der Kommissionierungen nicht zu erkennen, wann ein Projekt ausgeliefert werden soll — und die Liste war nach Anlagedatum sortiert, nicht nach Dringlichkeit.',
      'Die Liste öffnet jetzt nach Liefertermin aufsteigend: Was zuerst raus muss, steht oben. Aufträge ohne Termin stehen am Ende — kein Termin heißt keine Priorität. Bei gleichem Termin steht der ältere Auftrag zuerst.',
      'Auf dem iPad mini (Hochformat) steht der Liefertermin jetzt als eigener Block rechts in jeder Karte: beschriftet, groß und mit der Lage im Klartext darunter — „heute", „morgen", „in 16 Tagen" oder „seit 2 Tagen überfällig".',
      'Überfällige Termine sind rot, die nächsten drei Tage amber. Die Farbe steht nie allein: Derselbe Sachverhalt steht als Wort daneben — im Lager ist das Licht schlecht und Farbe allein zu unsicher.',
      'Ist kein Termin gesetzt, steht das jetzt ausdrücklich da („kein Liefertermin") statt einer fehlenden Zeile, bei der man rätselt, ob die Anzeige klemmt.',
      'Auch in der Tabellenansicht am Rechner steht unter dem Datum die Lage des Termins, und der Spaltenkopf „Liefertermin" zeigt mit einem Pfeil, dass danach sortiert wird.',
      'Im Projekt-Register „Kommissionierung" bleibt die Reihenfolge „neueste zuerst" — dort haben alle Aufträge denselben Liefertermin.',
    ],
  },
  {
    version: '3.458.0',
    date: '2026-08-04T12:30',
    title: 'Audit-Log: Klarnamen statt IDs, deutlich mehr Filter',
    tag: 'improvement',
    changes: [
      'Das Update-/Audit-Log zeigte bisher technische Rohdaten: „SalesDocument", eine 36-stellige UUID und den kompletten JSON-Block der Änderung. Damit war nicht erkennbar, WAS eigentlich geändert wurde.',
      'Die Objekt-Spalte nennt jetzt den Datensatz beim Namen — „AN-2026-0042 · Müller AG", „P-2026-001 · Kläranlage", „BE-2600014 · Zulieferer GmbH" — darunter Modul und Objektart („Verkauf · Verkaufsbeleg"). Der Name ist ein echter Link auf den Datensatz (Rechtsklick → in neuem Tab öffnen funktioniert). Die technische ID steht nur noch im Tooltip, falls sie im Support-Fall gebraucht wird.',
      'Auch gelöschte Datensätze werden noch aufgelöst — gerade beim Eintrag „Gelöscht" ist der Name die entscheidende Information.',
      'Die Änderungen erscheinen als lesbare Zeilen („Netto: 12.400", „Freigabe: RELEASED") statt als JSON. Feldnamen sind auf Deutsch, Ja/Nein statt true/false, Datumsangaben im gewohnten Format — und IDs INNERHALB der Änderung werden ebenfalls zu Klarnamen samt Link. Lange Änderungen sind auf vier Zeilen gekürzt und lassen sich aufklappen.',
      'Neue Filter: Modul (Verkauf, Logistik, Personal …), Objektart als Auswahlliste statt Freitextfeld (nach Modul gruppiert und beim Modulwechsel passend eingeschränkt) sowie Benutzer. Aktion und Zeitraum bleiben.',
      'Die Freitextsuche versteht jetzt die deutschen Begriffe: „Bestellung" findet Bestellungen, „Kommissionier" die Kommissionieraufträge. Eine eingefügte ID sucht weiterhin punktgenau den betroffenen Datensatz.',
      'Auf Tablet und Handy erscheint die Liste als Karten statt als gequetschte Tabelle — die Umschaltgrenze liegt jetzt so, dass auch das iPad mini im Hochformat die Kartenansicht bekommt.',
    ],
  },
  {
    version: '3.457.0',
    date: '2026-08-04T11:00',
    title: 'Zeiterfassung → Analyse: Tabelle mit allen Buchungen',
    tag: 'feature',
    changes: [
      'Die Zeit-Analyse zeigte nur Summen — Diagramme und eine Tabelle je Mitarbeiter. Darunter steht jetzt jede einzelne Buchung: Datum, Mitarbeiter, Stunden, Projekt, Kategorie, Unterkategorie und Notiz. Damit lässt sich nachsehen, welche Buchungen hinter einer auffälligen Zahl im Diagramm stecken.',
      'Filterbar: Zeitraum, Gesellschaft und Mitarbeiter gelten wie bisher für die ganze Seite; direkt über der Tabelle kommen Projekt, Kategorie und ein Suchfeld dazu (sucht in Mitarbeiter, Projekt, Kategorie und Notiz). Sortieren per Klick auf Datum, Mitarbeiter oder Stunden.',
      'Über der Tabelle stehen Trefferzahl und Stundensumme — beide für ALLE Treffer des Filters, nicht nur für die angezeigte Seite. Geblättert wird serverseitig, damit auch ein Jahr mit vielen Tausend Buchungen flüssig bleibt.',
      'Der CSV-Export liefert ebenfalls alle Treffer des Filters, nicht bloß die sichtbare Seite (bei mehr als 20.000 Zeilen mit Hinweis, den Zeitraum einzugrenzen).',
      'Die Spalte „Kosten" ist vorhanden, aber standardmäßig ausgeblendet: aus Kosten und Stunden ließe sich der persönliche Stundensatz ablesen. Über „Spalten" ist sie jederzeit zuschaltbar.',
      'Die Tabelle ist wie die ganze Seite auf Administratoren der Zeiterfassung beschränkt (Recht „timetracking.manage") und rein lesend — korrigiert wird weiter dort, wo Buchungen entstehen.',
    ],
  },
  {
    version: '3.456.0',
    date: '2026-08-04T10:00',
    title: 'Verbesserungen: Excel, PDF und Word als Anhang',
    tag: 'improvement',
    changes: [
      'Im Melde-Dialog („Verbesserungen → Meldung abgeben") ließen sich bisher nur Bilder anhängen. Jetzt gehen auch PDF, Excel (xlsx/xls/csv), Word (docx/doc), PowerPoint und einfache Textdateien — per Einfügen, Ablegen oder Dateiauswahl wie bisher.',
      'Office-Dateien werden auch dann erkannt, wenn der Browser beim Ablegen keinen Dateityp mitschickt (das passiert je nach Quelle) — geprüft wird Typ ODER Dateiendung.',
      'Anhänge ohne Bildvorschau erscheinen als Karte mit Symbol und Dateinamen; ein Klick öffnet die Datei. Ihre Inhalte werden dafür nicht mehr im Hintergrund geladen — das Öffnen einer Meldung mit großen Anhängen bleibt schnell.',
      'Die Obergrenze steigt von 10 auf 25 MB je Datei: für Bildschirmfotos war 10 reichlich, eine Excel-Mappe mit Bildern oder ein gescanntes PDF sprengt das schnell.',
      'Weiterhin abgelehnt werden Programme, Archive und SVG — letzteres sieht wie ein Bild aus, ist aber ausführbarer Code.',
    ],
  },
  {
    version: '3.455.0',
    date: '2026-08-04T09:15',
    title: 'Sperrlager: Einträge lassen sich löschen (VB-26012)',
    tag: 'feature',
    changes: [
      'Gemeldet als VB-26012: Im Sperrlager konnte man Einträge nur hinzufügen. Verbautes oder entsorgtes Restmaterial blieb für immer in der Liste — damit war das Werkzeug auf Dauer nicht brauchbar.',
      'In der Liste hat jede Zeile jetzt rechts einen Papierkorb-Knopf. Auf dem Tablet steht er oben rechts in der Karte, damit man ihn im Lager auch dort erreicht.',
      'In der Detailansicht steht „Löschen" neben „Bearbeiten". Nach dem Löschen landet man wieder in der Liste — die Detailseite des Eintrags gibt es dann ja nicht mehr.',
      'Vor dem Löschen kommt eine Rückfrage, die genau sagt, was verschwindet: Menge, Bezeichnung, Stellplatz und die Anzahl der Fotos. Im Regal liegen mehrere ähnliche Reste, und ein Sperrlager-Eintrag hat keine Belegnummer, an der man das später prüfen könnte.',
      'Der Eintrag wird aus dem Sperrlager entfernt, nicht aus der Datenbank gelöscht: Im Protokoll bleibt nachvollziehbar, wer wann was entfernt hat.',
      'Löschen hängt am eigenen Recht „Sperrlager löschen" (Administrator und Lagerist haben es). Wer das Recht nicht hat, sieht die Knöpfe nicht.',
    ],
  },
  {
    version: '3.454.0',
    date: '2026-08-04T08:30',
    title: 'Materialbedarf: Kommissionierauftrag direkt aus der Bedarfsliste (VB-26011)',
    tag: 'fix',
    changes: [
      'Gemeldet als VB-26011: Aus dem Materialbedarf ließ sich keine Kommissionierliste mehr anlegen. Der Knopf dafür saß im Projekt unter „Warenwirtschaft → Kommissionierung" — also genau dort, wo die Bedarfsliste bis zum 30.07. auch hing. Seit die Bedarfsliste im Modul Material lebt, lag er nicht mehr auf dem Arbeitsweg.',
      'Jetzt steht „Kommissionierauftrag erzeugen" oben rechts im Kopf der Bedarfsliste (Materialbedarf → Projekt). Im Projekt-Register gibt es den Knopf unverändert weiter.',
      'Vor dem Anlegen kommt eine Rückfrage, die sagt, was passiert: wie viele Positionen übernommen werden („12 von 14 Positionen") und dass Positionen ohne Bedarf wegfallen.',
      'Wichtig dabei: Der Auftrag umfasst den gesamten Materialbedarf des Projekts — alle Bedarfslisten, nicht nur die gerade geöffnete — und den unabhängig von Filter und Sortierung der Ansicht. Genau das steht jetzt in der Rückfrage, weil man auf der Seite immer nur eine Liste vor sich hat.',
      'Läuft für das Projekt schon ein Auftrag, nennt die Rückfrage dessen Nummer. Verboten wird ein zweiter nicht — Teillieferungen sind der Normalfall —, aber ein Auftrag aus Versehen kostet das Lager Zeit.',
      'Ist der Bedarf leer oder hat keine Position Bedarf, bleibt „Auftrag erzeugen" gesperrt und sagt warum, statt einen leeren Auftrag mit KO-Nummer zu erzeugen.',
      'Wie gehabt: Der Auftrag ist eine Momentaufnahme (späteres Ändern des Bedarfs ändert ihn nicht) und startet als Entwurf, der vor dem Kommissionieren freigegeben werden muss.',
    ],
  },
  {
    version: '3.453.0',
    date: '2026-08-03T19:10',
    title: 'Kommissionieren: grüner Abschluss, „Später"-Knopf, Collis zum Antippen',
    tag: 'improvement',
    changes: [
      'Der Knopf, mit dem du eine Position abschließt, ist jetzt grün statt schwarz. Auf dem Tablet erkennt man damit auf einen Blick, welcher der beiden Knöpfe unten der abschließende ist — auch aus einem Meter Abstand und mit Handschuhen.',
      'Links daneben steht neu „Später" (schwarz). Damit springst du zur nächsten offenen Position, ohne die aktuelle zu buchen — die bleibt unverändert offen und lässt sich jederzeit über die Liste wieder aufrufen. Der bisherige „Liste"-Knopf an dieser Stelle ist entfallen; zur Liste kommst du weiterhin oben links.',
      'Das Packstück wählst du nicht mehr über ein Aufklappmenü, sondern durch Antippen einer Kachel. Alle Collis stehen gleichzeitig da, und jede Kachel zeigt, wie viele Positionen schon drin sind und ob noch ein Foto fehlt — genau das, wonach man beim Packen entscheidet. „Ohne Packstück" ist als eigene Kachel wählbar.',
      'Auch beim Aufteilen auf mehrere Collis (Tranchen) sind die Collis jetzt Knöpfe: Menge oben, Colli-Auswahl darunter. Auf dem iPad mini passt damit alles nebeneinander, ohne dass die Zeile umbricht.',
      'Das Grün des Abschluss-Knopfes ist bewusst etwas dunkler als das Grün der Status-Hinweise — sonst wäre weiße Schrift darauf im Lager bei schlechtem Licht schwer zu lesen. Im dunklen Design wird der Knopf hell mit dunkelgrüner Schrift, wie alle anderen Hauptknöpfe dort auch.',
    ],
  },
  {
    version: '3.452.0',
    date: '2026-08-03T18:00',
    title: 'Mehrere Bedarfslisten je Projekt, Import direkt in der Liste',
    tag: 'feature',
    changes: [
      'Ein Projekt kann jetzt mehrere Bedarfslisten führen — z. B. „Hauptbedarf" und „Ersatzteile" — statt wie bisher genau eine. Jede Liste hat eine eigene Bezeichnung (Pflichtfeld, je Projekt eindeutig), eigene Positionen und ihre eigenen „Infos zur Materialliste". Auf der Detailseite wechselt man über Reiter; die gewählte Liste steht in der Adresse und ist damit verlinkbar.',
      'Bestehende Bedarfe wurden automatisch in eine Liste namens „Materialliste" überführt, samt der bisherigen projektweiten Notiz — es geht nichts verloren und niemand muss etwas nacharbeiten.',
      'Die Übersicht „Materialbedarf" zeigt weiterhin eine Zeile je Projekt mit der Gesamtsumme. Hat ein Projekt mehr als eine Liste, klappt die Zeile auf und man springt direkt in die gewünschte Liste.',
      'Positionen importieren geht jetzt direkt in der Bedarfsliste (vorher nur über den Umweg „Maximieren zum Bearbeiten"). Import, Umsortieren und „Bedarf aus Beleg übernehmen" wirken immer nur in der geöffneten Liste — „bestehende Positionen ersetzen" leert also nicht mehr den kompletten Projektbedarf. Positionsnummern zählen je Liste ab 1.',
      'Eine Liste löschen entfernt ihre Positionen mit und gibt Lager-Reservierungen frei; solange eine Position eine laufende Bestellung hat, ist das Löschen gesperrt (sonst fände der Wareneingang seine Position nicht mehr).',
      'Der Filter „Listen-Status" (In Konstruktion / Bereit zum Bestellen / Bestellt) ist entfallen. Er ließ sich seit längerem gar nicht mehr setzen und zeigte damit nichts Verlässliches — der Fortschrittsbalken aus den Positions-Status sagt genauer, wie weit eine Liste ist.',
      '„Infos zur Materialliste" heißt jetzt so ohne den Zusatz „(Techniker)": das Feld ist für jeden gedacht, der an der Liste arbeitet.',
    ],
  },
  {
    version: '3.451.1',
    date: '2026-08-01T09:00',
    title: 'Belege: Gesamtgewicht wird wieder aus den Artikeldaten berechnet (VB-26010)',
    tag: 'fix',
    changes: [
      'Gemeldet als VB-26010: Im Beleg blieb das Gewicht leer, obwohl die Artikelgewichte hinterlegt sind. Dahinter steckten zwei Dinge — beide behoben.',
      'Erstens war der berechnete Wert nirgends zu sehen: Das Feld „Gewicht (kg)" im Beleg ist die Stelle für eine eigene Angabe und deshalb normalerweise leer. Die Berechnung landete in einer Info-Zeile, die keine Editor-Ansicht anzeigt. Jetzt steht der berechnete Wert direkt im Feld (als Platzhalter) und ausgeschrieben darunter: „Automatisch berechnet: 236,22 kg (Summe der Artikelgewichte)".',
      'Zweitens war der gespeicherte Wert veraltet: Er entstand nur, wenn am Beleg etwas geändert wurde. Wer — wie üblich — erst das Angebot schreibt und die Artikelgewichte danach pflegt, bekam nie ein Gewicht. Jetzt rechnet ProERP beim Öffnen des Belegs neu, ohne dass man den Beleg anfassen muss.',
      'Fehlt an einem Artikel das Gewicht, sagt der Hinweis das jetzt auch: „Kein Artikelgewicht hinterlegt — Gewicht hier von Hand eintragen oder am Artikel pflegen." Vorher war nicht zu unterscheiden, ob die Berechnung fehlschlug oder die Stammdaten fehlen.',
      'Eine eigene Eingabe im Feld übersteuert die Berechnung wie bisher; leert man das Feld, gilt wieder der berechnete Wert. Das PDF druckt denselben Wert wie der Editor.',
      'Belege aus der Datenübernahme behalten ihr Gewicht aus dem Vorsystem, auch wenn ihre Artikel keine Gewichte tragen.',
    ],
  },
  {
    version: '3.451.0',
    date: '2026-07-31T23:30',
    title: 'Listen: Rechtsklick „In neuem Tab öffnen" — und ein Fehler beim Projekt-Link behoben',
    tag: 'feature',
    changes: [
      'In allen großen Listen (Projekte, Kunden, Ansprechpartner, Lieferadressen, Artikel, Bestellungen, Belege, Kommissionierungen, Materialbedarf, Bedarfsvorschau, Sperrlager) ist die erste Spalte — also die Nummer bzw. der Code — jetzt ein echter Link. Damit geht dort, was vorher nirgends ging: Rechtsklick → „In neuem Tab öffnen", Cmd-/Strg-Klick, Mittelklick, „Link kopieren", und die Ziel-Adresse steht beim Überfahren unten im Browser.',
      'Sichtbar verändert sich nichts: Die Nummern sehen aus wie bisher, kein Unterstrich, keine andere Farbe. Ein Klick irgendwo in der Zeile öffnet den Datensatz weiterhin im selben Tab — es kommt nur die Möglichkeit dazu, das anders zu tun.',
      'Warum die erste Spalte und nicht die ganze Zeile: Ein Link darf eine Tabellenzeile technisch nicht umschließen. Die Zeile selbst kann deshalb kein Link werden, die Spalte schon.',
      'Behoben: In der Bestellliste führte ein Klick auf das Projekt nicht zum Projekt, sondern in die Bestellung. Der Projekt-Link und die Zeile haben beide navigiert, und die Zeile war schneller. In der Verkaufsliste war der Fehler schon einzeln geflickt, in der Bestellliste nicht — jetzt ist die Prüfung zentral und gilt für alle Listen und alle künftigen Links in Zeilen.',
      'Ebenfalls behoben: Ein Cmd-/Strg-Klick auf eine Zeile öffnete bisher kein zweites Fenster, sondern navigierte einfach im aktuellen — die Tastenkombination wurde verschluckt.',
      'Der Beleg-Editor merkt sich die Herkunft jetzt in der Adresse statt unsichtbar im Verlauf. Dadurch bringt „Schließen" Filter, Seite und Scrollstand der Belegliste auch dann zurück, wenn du den Beleg in einem neuen Tab geöffnet oder die Seite neu geladen hast.',
      'Erster Schritt: In Kacheln, Karten und Dashboard-Widgets folgt dasselbe.',
    ],
  },
  {
    version: '3.450.0',
    date: '2026-07-31T22:40',
    title: 'Beleg-Editor: Speichern ist jetzt eine einzige Anfrage',
    tag: 'improvement',
    changes: [
      'Der Editor schickt die Positionen nicht mehr Zeile für Zeile ans System, sondern die komplette Liste auf einmal. Beim Speichern eines Belegs mit 30 Positionen waren das bisher bis zu 31 Anfragen nacheinander — jetzt ist es eine, unabhängig davon, wie lang der Beleg ist.',
      'Damit ist auch das Umsortieren schnell. Eine Position nach oben zu ziehen hat vorher jede darunterliegende Zeile einzeln neu geschrieben; das war der letzte Fall, in dem Speichern spürbar gedauert hat.',
      'Die Reihenfolge im Beleg ist jetzt schlicht die Reihenfolge, in der die Zeilen im Editor stehen. Nebeneffekt: Belege, deren Positionsnummern durch früheres Löschen Lücken hatten, werden beim nächsten Speichern automatisch geradegezogen.',
      'Alles zusammen läuft in einem Vorgang: Anlegen, Ändern, Löschen und Umsortieren gelten entweder komplett oder gar nicht. Bricht etwas ab, steht kein halb umsortierter Beleg da.',
      'Im Protokoll (Audit) steht jetzt ein Eintrag pro Speichervorgang statt einem pro Position — das entspricht auch eher dem, was tatsächlich passiert ist.',
      'Gilt für Verkaufsbelege und Bestellungen. Bei Bestellungen bleiben Verknüpfung zur Projekt-Materialliste und bereits eingegangene Mengen unangetastet; die veranlassten Mengen am Bedarf werden weiterhin korrekt nachgezogen.',
    ],
  },
  {
    version: '3.449.0',
    date: '2026-07-31T22:15',
    title: 'Materialbedarf: Reservieren und Bestellen direkt aus der Bedarfsliste',
    tag: 'feature',
    changes: [
      'Das Detail-Panel der Bedarfsliste (Materialbedarf → Projekt → Zeilenklick) ist jetzt ein Cockpit statt einer reinen Anzeige: Lagerbestand reservieren, in den Bestellkorb legen und die Quelle (Lager/Bestellen) umstellen — ohne in den Vollbild-Editor zu wechseln.',
      'Reservieren kann jetzt Teilmengen: Das Mengenfeld ist mit dem sinnvollen Maximum vorbelegt und lässt sich überschreiben (z. B. 100 von 236,22 m). Das System prüft gegen freien Bestand und offenen Bedarf und sagt klar, warum etwas nicht geht.',
      'Bestellen sammelt wie im Vollbild-Editor im Bestellkorb; die Leiste unten legt je Lieferant eine Sammelbestellung an. Anders als dort bleibt man danach auf der Seite — die Dispo geht einfach weiter, die neue Bestellnummer erscheint in der Beleg-Spalte.',
      'Tastatur, solange das Panel offen ist: R reserviert, B bestellt, L schaltet die Quelle um, die Pfeiltasten blättern von Position zu Position, Enter im Mengenfeld bestätigt, Esc schließt. Bewusst Einzeltasten statt Cmd+R/Cmd+B — die gehören dem Browser (Neuladen bzw. Lesezeichen). Die Tasten stehen als Hinweis direkt auf den Knöpfen.',
      'Das Panel blockiert die Seite nicht mehr: kein abgedunkelter Hintergrund, Tabelle und Korb-Leiste bleiben sichtbar und bedienbar, ein Klick auf eine andere Zeile wechselt einfach die Position.',
      'Die Tabelle selbst bleibt bewusst eine ruhige Leseansicht; Strukturarbeiten (Positionen anlegen, Import, Massen-Änderungen) laufen weiter im Vollbild-Editor.',
    ],
  },
  {
    version: '3.448.1',
    date: '2026-07-31T21:30',
    title: 'Beleg-Editor: Positionen verschwinden beim Speichern nicht mehr',
    tag: 'fix',
    changes: [
      'Nach dem Speichern verschwanden alle Positionen für ein paar Sekunden und kamen dann einzeln zurück. Ursache: Der Editor schrieb jede Position einzeln und lud den Beleg nach JEDEM dieser Schritte neu — dabei baute er sich jedes Mal komplett neu auf, mit dem jeweiligen Zwischenstand. Jetzt wird einmal am Ende neu geladen, wenn alles geschrieben ist.',
      'Speichern ist außerdem deutlich schneller: Bisher wurde beim Anhängen einer einzigen Position jede bestehende Position mitgeschrieben — bei 30 Positionen also 31 Anfragen nacheinander. Jetzt wird nur noch angefasst, was sich tatsächlich geändert hat oder wirklich seinen Platz gewechselt hat.',
      'Beim Umsortieren wird weiterhin die ganze Liste neu durchnummeriert — das ist selten und stellt sicher, dass die Reihenfolge auch bei alten Belegen stimmt.',
      'Gilt für Verkaufsbelege und Bestellungen gleichermaßen.',
    ],
  },
  {
    version: '3.448.0',
    date: '2026-07-31T21:15',
    title: 'Materialbedarf: „Bedarf anlegen" für Projekte ohne Liste',
    tag: 'fix',
    changes: [
      'Die Materialbedarf-Übersicht zeigt nur Projekte, die schon Bedarfspositionen haben. Seit der Projekt-Tab „Bedarf / Material" entfallen ist, gab es damit für ein Projekt ohne Liste keinen Einstieg mehr: es stand nicht in der Tabelle, war also nicht anklickbar — und der Vollbild-Editor, in dem Positionen entstehen, war nicht erreichbar.',
      'Neuer Knopf „Bedarf anlegen" oben rechts: Projekt suchen und wählen, danach öffnet sich dessen Bedarfsliste. Dort legst du die Positionen wie gewohnt an, importierst sie oder übernimmst sie aus einem Beleg.',
      'Wählst du ein Projekt, das schon eine Bedarfsliste hat, sagt der Dialog das und öffnet die bestehende Liste — es wird nichts überschrieben.',
      'Der Hinweis auf der leeren Übersicht führt jetzt auch weiter, statt nur zu erklären, dass hier irgendwann etwas erscheint.',
    ],
  },
  {
    version: '3.447.0',
    date: '2026-07-31T20:30',
    title: 'Zahlungsplan-Vorlagen: selbst anlegen statt drei feste Knöpfe',
    tag: 'feature',
    changes: [
      'Die Vorlagen-Knöpfe über dem Zahlungsplan (10 / 30 / 50 / 10, 30 / 70, 100 %) waren fest im Programm verdrahtet und kannten nur Prozentsätze. Bezeichnung, Typ und Auslöser jeder Zeile hat ProERP aus der Reihenfolge geraten — die erste hieß immer „Anzahlung", die letzte „Schlussrechnung", dazwischen „Teilzahlung 1".',
      'Das genügte nicht mehr, seit die Bezeichnung als Überschrift auf der erzeugten Rechnung steht: „1. Teilrechnung / 2. Teilrechnung" ließ sich mit den alten Vorlagen gar nicht erzeugen.',
      'Jetzt legst du Vorlagen selbst an — und zwar aus einem echten Plan: Zahlungsplan im Auftrag fertig bauen, „Vorlagen verwalten" öffnen, Namen vergeben, speichern. Die Vorlage trägt je Position Bezeichnung, Art, Auslöser (Meilenstein oder „Datum beim Anwenden ausfüllen") und Anteil. Beim nächsten Auftrag steht sie als Knopf da und setzt genau diese Positionen ein.',
      'Die drei alten Knöpfe sind ersatzlos entfallen. Bis die erste eigene Vorlage existiert, steht dort ein Hinweis statt Knöpfen.',
      'Eine Vorlage überschreibt alle geplanten Positionen. Ist der Plan noch leer, passiert das sofort; stehen schon Positionen drin, wird vorher gefragt und die Vorlage mit ihren Anteilen angezeigt — bereits fakturierte Positionen bleiben in jedem Fall unberührt.',
      'Vorlagen speichern bewusst nur Prozent-Anteile, keine festen Euro-Beträge: ein fester Betrag passt auf keinen zweiten Auftrag. Enthält der Plan so eine Position, sagt ProERP das beim Speichern statt sie stillschweigend weglassen. Ebenso muss die Summe 100 % ergeben — sonst wäre die Vorlage auf jedem Auftrag sofort unstimmig.',
      'Ein konkretes Datum wandert nicht in die Vorlage, weil es zum einzelnen Auftrag gehört. Die eingesetzte Position ist dann als unvollständig markiert, bis du das Datum setzt.',
      'Vorlagen gelten firmenweit. Anwenden darf sie jeder; anlegen und löschen, wer Verkaufsbelege bearbeiten darf.',
    ],
  },
  {
    version: '3.446.0',
    date: '2026-07-31T19:30',
    title: 'Fix: Admin-Reset des 2. Faktors sperrte Passkey-only-Nutzer komplett aus',
    tag: 'fix',
    changes: [
      'Wenn ein Admin bei einem Benutzer den 2. Faktor zurückgesetzt hat (z. B. Gerät verloren), wurde bisher NUR der Authenticator (TOTP) gelöscht — vorhandene Passkeys blieben unangetastet. Für Nutzer, die ausschließlich einen Passkey eingerichtet hatten, war der „Reset" dadurch wirkungslos: der Login verlangte weiterhin genau den (verlorenen) Passkey, und der Nutzer kam nicht mehr rein.',
      'Der Reset löscht jetzt beides — TOTP UND alle Passkeys —, sodass sich der Nutzer beim nächsten Login komplett neu einrichten kann (Auswahl TOTP oder Passkey wie beim Erstlogin). Checkbox im Dialog entsprechend umbenannt: „2. Faktor zurücksetzen (TOTP UND Passkeys, z. B. Gerät verloren)".',
    ],
  },
  {
    version: '3.445.0',
    date: '2026-07-31T19:00',
    title: 'Zahlungsplan: Rechnungs-PDF trägt die Bezeichnung der Zahlungsplan-Zeile',
    tag: 'improvement',
    changes: [
      'Wird eine Rechnung aus einer Zahlungsplan-Zeile erzeugt, steht im PDF-Kopf jetzt die Bezeichnung dieser Zeile — also „1. Teilrechnung" statt nur „Rechnung". Auch in der Fußzeile mehrseitiger Rechnungen.',
      'Die Bezeichnung wird beim Fakturieren festgeschrieben: Wenn du später eine Zahlungsplan-Zeile umbenennst, ändert sich eine bereits erzeugte Rechnung nicht mehr.',
      'Bei einem englischen Beleg wird die Bezeichnung unverändert übernommen — der Zahlungsplan gehört zum deutschen Vertrag, und die Nummerierung soll wiedererkennbar bleiben.',
      'Gilt für neu erzeugte Rechnungen. Bestehende behalten ihre bisherige Überschrift.',
    ],
  },
  {
    version: '3.444.0',
    date: '2026-07-31T18:30',
    title: 'Projektphasen: neue Standardfolge und weniger Klicks (VB-26009)',
    tag: 'improvement',
    changes: [
      'Umgesetzt aus VB-26009: Die bisherigen Standardphasen (Engineering, Beschaffung, Fertigung, Montage, Inbetriebnahme, Abnahme) passten nicht zum tatsächlichen Ablauf — Beschaffung läuft im Einkauf, eine Inbetriebnahme gibt es bei uns nicht, dafür fehlten die Schritte zwischen Fertigung und Montage.',
      'Neue Standardfolge: Projektübergabe → Engineering → Planfreigabe → Fertigung → Kommissionierung → Transport → Lieferung → Montage → Abnahme. Ein Klick auf „Standardphasen anlegen" erzeugt alle neun auf einmal.',
      'Hat ein Projekt schon Phasen, gibt es „Standardphasen ergänzen" mit der Zahl der fehlenden Phasen. Der Dialog zeigt vorher, was dazukommt, und man kann einzelne abwählen — vorhandene Phasen bleiben unberührt, mehrfaches Auslösen legt nichts doppelt an.',
      'Der geplante Zeitraum lässt sich jetzt direkt in der Zeile setzen. Bisher musste man je Phase den Bearbeiten-Dialog öffnen — das war der Hauptgrund für das viele Klicken.',
      'Über der Liste steht der Fortschritt („3 von 9 Phasen fertig · aktiv: Fertigung") samt Balken. Die Spalte „Art" ist weg: der Typ steht als Unterzeile am Namen und nur dann, wenn er sich vom Namen unterscheidet. Fertige Phasen sind an einem Häkchen erkennbar.',
      'Im Anlegen-Dialog füllt die gewählte Art den Namen vor (überschreibbar); die alten Typen Beschaffung und Inbetriebnahme stehen weiter zur Verfügung, aber getrennt unter „Weitere (Altbestand)" — bestehende Projekte behalten ihre Phasen unverändert.',
      'Zeitplan und Zeitplan-PDF kennen die neuen Phasen mit eigener Farbe und Bezeichnung (deutsch und englisch).',
    ],
  },
  {
    version: '3.443.0',
    date: '2026-07-31T16:30',
    title: 'Zahlungsplan: aufgeräumte Übersicht, Detail-Dialog und moderner Datums-Picker (VB-26007)',
    tag: 'improvement',
    changes: [
      'Umgesetzt aus VB-26007: Der Zahlungsplan funktionierte, war aber kaum zu lesen — jede Zeile bestand aus fünf bis sieben Eingabefeldern nebeneinander, und das Datumsfeld öffnete den Kalender des Betriebssystems mitten in der Tabelle.',
      'Der Zahlungsplan ist jetzt eine einklappbare Karte. Zugeklappt steht im Kopf, was zählt: ob die Summe zum Auftragswert passt, wie viele Positionen offen sind, wie viele bereits fakturiert wurden und ob es ungespeicherte Änderungen gibt. Der Zustand wird gemerkt — einmal zugeklappt bleibt zugeklappt, über Belege hinweg.',
      'Die Tabelle zeigt die Positionen jetzt lesbar statt als Formular: Bezeichnung mit Typ, Auslöser (Meilenstein oder Datum), Anteil, der daraus errechnete Nettobetrag und der Status. Bereits fakturierte und stornierte Positionen stehen in derselben Tabelle statt in einem zweiten Block, mit einem Link direkt zur Rechnung.',
      'Bearbeitet wird eine Position über einen Klick auf die Zeile oder das Stift-Symbol — im Detail-Dialog mit ordentlich beschrifteten Feldern. Das Datum kommt dort aus dem ProERP-Datums-Picker (deutscher Monatskalender, „Heute"-Kurzweg, Tastaturbedienung), jetzt mit Platz statt gequetscht in der Tabellenzeile.',
      'Fehlende Angaben fallen sofort auf: Der Dialog meldet sie am Feld, unvollständige Zeilen sind in der Tabelle als solche markiert, und beim Speichern öffnet ProERP die beanstandete Position direkt zur Korrektur. Bisher kam nur eine Sammelmeldung als Hinweisblase.',
      'Der Betrag je Position steht jetzt in der Tabelle — inklusive der Rundungskorrektur auf der letzten Prozent-Position, exakt wie sie das Backend beim Speichern rechnet. Damit stimmt die Vorschau mit dem gespeicherten Plan überein.',
    ],
  },
  {
    version: '3.442.0',
    date: '2026-07-31T15:45',
    title: 'Vier-Augen-Freigabe am Beleg einklappbar (VB-26006)',
    tag: 'improvement',
    changes: [
      'Umgesetzt aus VB-26006: Die Freigabe-Chronik stand bei jedem Beleg dauerhaft offen und nahm Platz weg, obwohl sie nach erteilter Freigabe nur noch selten gebraucht wird.',
      'Die Karte „Vier-Augen-Freigabe" lässt sich jetzt ein- und ausklappen und startet zugeklappt. Der Zustand wird gemerkt, wie beim Belegfluss — wer die Chronik offen haben will, klappt sie einmal auf.',
      'Zugeklappt bleibt der aktuelle Stand sichtbar: Ein Badge im Kopf zeigt „Freigegeben", „Wartet auf Freigabe", „Abgelehnt" oder „Keine gültige Freigabe", daneben die Anzahl der Ereignisse. Die vollständige Abfolge steht unverändert nach dem Aufklappen.',
    ],
  },
  {
    version: '3.441.0',
    date: '2026-07-31T15:30',
    title: 'Kundenliste startet nach Kundennummer sortiert (VB-26008)',
    tag: 'improvement',
    changes: [
      'CRM → Kunden & Lieferanten öffnet jetzt nach Kundennummer aufsteigend statt nach Name A–Z. Im Altsystem war die Nummer die Leitordnung, und danach wird auch hier zuerst gesucht.',
      'Geändert hat sich nur der Startwert: Jede andere Sortierung ist einen Klick auf den Spaltenkopf entfernt und bleibt — wie bisher — in der Adresszeile gespeichert. Wer einen Link mit eigener Sortierung teilt oder gespeichert hat, bekommt weiterhin genau diese.',
      'Nebenbei: Der aktive Spaltenkopf sagt die Sortierrichtung jetzt auch Screenreadern, nicht nur über das Pfeil-Symbol. Das gilt für alle Tabellen mit sortierbaren Spalten.',
    ],
  },
  {
    version: '3.440.0',
    date: '2026-07-31T15:00',
    title: 'Projekt-Zeitplan als PDF für den Kunden (VB-26003)',
    tag: 'feature',
    changes: [
      'Im Reiter „Planung → Zeitplan" eines Projekts gibt es jetzt „PDF (DE)" und „PDF (EN)". Damit lässt sich die Zeitschiene direkt aus dem ERP an den Kunden schicken — die parallele Pflege einer Excel-Liste entfällt. Exportiert wird immer das im Reiter gewählte Jahr.',
      'Das PDF ist als Kundendokument gebaut, nicht als interner Ausdruck: Querformat mit Firmenanschrift im Kopf, darunter Kunde, Liefertermin und Projektzeitraum, dann das Kalenderwochen-Raster mit einem farbigen Balken je Phase samt „Heute"-Linie und gestricheltem Liefertermin. Unter der Grafik stehen alle Phasen noch einmal als Tabelle mit Start, Ende und Status („Abgeschlossen", „In Arbeit", „Geplant") — Balken allein sind zum Ablesen konkreter Termine zu ungenau.',
      'Bewusst getrennt vom bestehenden Zeitplan-Export unter „Zeitplan" in der Navigation: der zeigt alle aktiven Projekte auf einer Seite und trägt den Vermerk „Vertraulich" — der gehört nicht zum Kunden. Das neue Dokument enthält ausschließlich das eine Projekt und keine internen Kennzahlen.',
      'Die Legende zeigt nur die Phasentypen, die es im Projekt wirklich gibt. Liegen im gewählten Jahr keine Termine, erscheint ein Hinweis statt eines leeren Rasters — die Tabelle mit den echten Terminen bleibt trotzdem.',
    ],
  },
  {
    version: '3.439.0',
    date: '2026-07-31T14:45',
    title: 'Zahlungsplan: Datumsfeld für den Auslöser modernisiert',
    tag: 'improvement',
    changes: [
      'Das Datumsfeld für den Auslöser einer Zahlungsplan-Position nutzt jetzt den vorhandenen Kalender-Picker (Popover, deutsche Lokalisierung) statt des nativen Browser-Datumsfelds — konsistent mit den übrigen Datumsfeldern der Anwendung.',
    ],
  },
  {
    version: '3.438.0',
    date: '2026-07-31T14:00',
    title: 'Dashboard: anstehende Transporte auf einen Blick, Lager wird benachrichtigt (VB-26004)',
    tag: 'feature',
    changes: [
      'Neues Dashboard-Widget „Transporte heute": zeigt, was heute ankommt und abgeholt wird — mit Uhrzeit, Bezug (Bestellung bzw. Projekt) und einem Klick direkt in den Transport. Im Kopf steht, wie viele Anlieferungen und Abholungen es sind.',
      'Überfällige Transporte stehen ganz oben und sind als solche markiert. Ein Transport, der gestern hätte abgeholt werden sollen und noch offen ist, ist dringender als alles von heute — deshalb zeigt das Widget bewusst nicht nur den heutigen Tag.',
      'Wird ein Transport erfasst, bekommt die Rolle „Lager" eine Benachrichtigung mit Richtung, Termin und Ziel, samt Link zum Transport. Wer den Transport selbst anlegt, bekommt keine.',
      'Das Widget erscheint nur bei Personen mit Transport-Leserecht und lässt sich wie jedes andere über „Dashboard anpassen" verschieben oder ausblenden.',
    ],
  },
  {
    version: '3.437.0',
    date: '2026-07-31T13:15',
    title: 'Verbesserungen: Meldungen als fertiges Paket herunterladen (VB-26005)',
    tag: 'feature',
    changes: [
      'Umgesetzt aus VB-26005: Bisher musste die Systemverwaltung jede Meldung einzeln öffnen, den Prompt kopieren und die Screenshots von Hand herunterladen.',
      'Neuer Knopf „Als Paket (ZIP)". Das Archiv enthält je Meldung den Prompt als Markdown-Datei (z. B. „VB-26005.md") und die Screenshots dazu, benannt nach der Fallnummer und fortlaufend nummeriert („VB-26005-1.png", „VB-26005-2.png"). Entpacken, alles auswählen, hochladen — fertig.',
      'Mehrere Meldungen gehen in ein Paket: Im Listenkopf bündelt der Knopf die gerade gefilterte Auswahl. Dann liegt zusätzlich „00-alle-meldungen.md" im Archiv — die Datei mit der Übersicht und allen Fällen, auf die man den Assistenten ansetzt.',
      'Der Prompt nennt die Screenshots mit dem Namen, den sie im Paket haben. Vorher stand dort der Originalname vom Melder („Bildschirmfoto 2026-07-31 um 20.18.32.png"), was im entpackten Ordner nicht auffindbar war.',
      'Das Archiv wird auf dem Server gebaut, weil die Bilder dort schon liegen. Ohne zusätzliche Fremd-Bibliothek: PNG und JPEG sind bereits komprimiert, deshalb werden sie unkomprimiert verpackt — das spart eine Abhängigkeit und kostet praktisch keine Dateigröße. Geprüft wird das Ergebnis nicht nur gegen die eigenen Annahmen, sondern mit dem echten Entpacker.',
      'Fehlt ein Screenshot im Dateispeicher, entsteht das Paket trotzdem — und der Prompt behauptet dann auch nicht, dass ein Bild beiliegt.',
    ],
  },
  {
    version: '3.436.0',
    date: '2026-07-31T12:30',
    title: 'Materialbedarf: Positionsnummer in der Bedarfsliste (VB-26002)',
    tag: 'improvement',
    changes: [
      'Die Bedarfsliste eines Projekts hat jetzt ganz links eine schmale Spalte „Pos." mit der Positionsnummer. Damit ist auf einen Blick zu sehen, in welcher Reihenfolge die Liste steht — Wunsch VB-26002.',
      'Zur Einordnung: Die Reihenfolge war nie durcheinander. Der Import vergibt die Positionen in der Reihenfolge der Datei, und die Liste war schon immer danach sortiert. Ohne sichtbare Nummer wirkte sie nur beliebig, weil die Artikelnummern nicht der Reihe nach laufen. An den Daten wurde nichts geändert.',
      'Ein Klick auf den Spaltenkopf dreht die Reihenfolge um; über „Spalten" lässt sich die Nummer bei Bedarf auch ausblenden.',
    ],
  },
  {
    version: '3.435.0',
    date: '2026-07-31T12:00',
    title: 'Materialbedarf: Bedarfsliste eines Projekts aufgeräumt',
    tag: 'improvement',
    changes: [
      'Unter Material → Materialbedarf zeigt die Liste eines Projekts jetzt sechs statt vierzehn Spalten. Artikelnummer und Bezeichnung stehen zweizeilig zusammen, ebenso Lieferant und Beleg. Die sieben Mengenspalten (Lager, Frei, Reserviert, Bestellt, Erhalten, Offen) sind zu einer Zelle „Bedarf & Deckung" verdichtet: Bedarf mit Einheit, Deckungsbalken, offene Restmenge.',
      'Neu über der Tabelle: der Stand der Liste auf einen Blick — wie viele Positionen offen, überfällig oder ohne Lieferant sind, plus ein Balken mit der Statusverteilung aller Positionen.',
      'Ein Klick auf eine Zeile öffnet ein Panel mit allen Einzelmengen, Lieferant, verknüpften Belegen und Notiz. Wer die Mengen dauerhaft als Spalten will, holt sie über „Spalten" zurück — die Auswahl wird pro Nutzer gemerkt.',
      'Der Status ist zusätzlich als Farbstreifen am Zeilenanfang sichtbar, nicht nur als Wort im Chip. Ein Filter „Nur offene" blendet fertige Positionen aus, und eine Summenzeile schließt die Tabelle ab.',
      'Die Techniker-Infos stehen als Hinweis über der Tabelle statt als dauerhaft geöffnetes Textfeld — bearbeitet wird erst auf Klick. Die Seite ist eine Leseansicht; geändert wird weiterhin im Vollbild-Editor.',
      'Fachlich korrigiert: Summen über Positionen mit unterschiedlichen Einheiten (Stk + m + m²) werden nicht mehr addiert — dort steht jetzt „gemischte Einheiten", und Aggregate zählen Positionen. Der Excel-/PDF-Export enthält unabhängig von den sichtbaren Spalten immer alle Mengen.',
    ],
  },
  {
    version: '3.434.0',
    date: '2026-07-31T11:30',
    title: 'Alte Artikelnummer: jetzt suchbar, sichtbar und pflegbar',
    tag: 'improvement',
    changes: [
      'Die alte Artikelnummer („Referenz") kam beim Import schon immer mit — man hat sie nur nirgends gesehen. Sie steht jetzt auf der Artikel-Detailseite unter den Stammdaten, zusammen mit dem Matchcode aus dem Altsystem.',
      'Wichtiger: Die Artikelsuche findet beide. Wer die alte Nummer eintippt, bekam bisher null Treffer, obwohl der Artikel längst da war — gesucht wurde nur über neue Nummer und Bezeichnung. Das gilt überall, wo Artikel gesucht werden, auch bei der Positionserfassung im Beleg.',
      'Beide Felder lassen sich jetzt auch von Hand pflegen (Artikel bearbeiten → „Zoll & Logistik"). Bisher ging das nur über einen erneuten Import.',
      'Vorbereitet für den kommenden Büroware-Export: Der Import erkennt die Spalte automatisch, egal ob sie „Referenz", „Artikelnummer alt", „alte_nummer" oder „Fremdartikelnummer" heißt.',
    ],
  },
  {
    version: '3.433.0',
    date: '2026-07-31T11:00',
    title: 'Verbesserungen: zwei Fehler behoben — Screenshot-Anzeige und verdeckte Formularfelder',
    tag: 'fix',
    changes: [
      'Ein angehängter Screenshot ließ sich nicht anschauen: Statt des Bildes kam „Dir fehlt die Berechtigung feedback.read", obwohl die Berechtigung da war. Grund war ein technischer: Wenn der Browser ein Bild lädt, schickt er die Anmeldung nicht mit — der Server sah also einen anonymen Zugriff. Screenshots werden jetzt wie die Fotos in Kommissionierung und Wareneingang geladen, also angemeldet. Klick auf das Vorschaubild öffnet das Original weiterhin in einem neuen Tab.',
      'Die Detailgrad-Anzeige lag über den Eingabefeldern und hat den Text abgeschnitten. Sie sitzt jetzt in der Fußzeile des Dialogs, unterhalb des scrollbaren Bereichs: dauerhaft sichtbar, aber ohne etwas zu verdecken. Ein- und Ausklappen funktioniert wie gehabt.',
    ],
  },
  {
    version: '3.432.0',
    date: '2026-07-31T09:30',
    title: 'Verbesserungen: Screenshots, Prompt-Export und vollständige Bereichsliste',
    tag: 'improvement',
    changes: [
      'Umgesetzt aus VB-26001 — der ersten Meldung, die über das Modul selbst hereinkam.',
      'Screenshots lassen sich jetzt anhängen. Der schnellste Weg: Bildschirmfoto machen und im Formular mit ⌘V bzw. Strg+V einfügen — kein Zwischenspeichern als Datei nötig. Ziehen-und-Ablegen und die normale Dateiauswahl gehen auch. Bilder können am Vorgang jederzeit nachgereicht werden, was besonders nach einer Rückfrage hilft.',
      'Neu ist ein Knopf „Als Prompt kopieren": Er legt die komplette Meldung als fertigen Arbeitsauftrag in die Zwischenablage — Einordnung, alle Felder, technischer Kontext und der Rückfrage-Verlauf. Daneben „Als Datei" für den Download als .md, falls die Meldung hochgeladen statt eingefügt werden soll. Im Listenkopf gibt es dasselbe für die gerade gefilterte Auswahl, also mehrere Meldungen auf einmal.',
      'Screenshots lassen sich nicht in Text einbetten — der Prompt nennt deshalb die angehängten Dateien beim Namen und weist darauf hin, sie separat mitzugeben.',
      'Die Detailgrad-Anzeige unten im Formular ist jetzt ein- und ausklappbar. Zugeklappt bleibt nur eine schmale Fortschrittszeile stehen, die Rückmeldung geht also nicht verloren. Die Entscheidung wird gemerkt und gilt auch für die nächste Meldung.',
      'Die Bereichsliste war unvollständig — ausgerechnet „Verbesserungen" selbst fehlte darin. Sie deckt jetzt alle Bereiche der Navigation ab (unter anderem Zeitplan, Vertriebspipeline, Referenzen, Materialbedarf, Bedarfsvorschau, Sperrlager, Selektionspool, Einstellungen, Personen & Rollen). Damit das nicht wieder passiert, prüft ein Test bei jedem Lauf, ob zu jedem Navigationspunkt ein Bereich existiert.',
      'Erlaubt sind PNG, JPEG, WebP und GIF bis 10 MB. SVG ist bewusst ausgeschlossen: Solche Dateien können Code enthalten, auch wenn der Browser sie wie ein Bild anzeigt.',
    ],
  },
  {
    version: '3.431.0',
    date: '2026-07-31T01:00',
    title: 'Transporte: Filter und Sortierung als Chips/Knöpfe statt Dropdowns',
    tag: 'improvement',
    changes: [
      'Status und Richtung filtert man jetzt über Chips mit Zähler („Offen 3", „Abgefahren 1" …) statt über zwei Dropdowns — die Zahl ist ohne Klick sichtbar, genau das ist vor jeder Liste die erste Frage.',
      'Neu: Sortier-Knöpfe „Termin"/„Nummer" mit Pfeil, per Klick umschaltbar zwischen auf- und absteigend. Ein fehlender Termin springt beim Umdrehen nicht nach oben, sondern bleibt am Ende.',
      'Die Liste holt jetzt alle Transporte auf einmal und filtert/sortiert selbst — so zeigt jeder Chip seine Anzahl, nicht nur der gerade aktive.',
    ],
  },
  {
    version: '3.430.0',
    date: '2026-07-31T00:45',
    title: 'Materialbedarf: ruhigere Liste, Brotkrumen in der Bedarfsliste',
    tag: 'improvement',
    changes: [
      'Die Übersicht hatte fünf Zahlenspalten (offen, teilweise, bestellt, gedeckt, geliefert), in denen meist nur Striche standen — und wie weit eine Liste ist, musste man sich aus den Zahlen zusammenrechnen. Statt dessen steht dort jetzt eine Spalte „Fortschritt": ein schmaler Balken in Prozessreihenfolge und daneben im Klartext „7 offen von 24", „nichts offen" oder „vollständig geliefert".',
      'Die Spalte „Listen-Status" ist entfallen. Der Status lässt sich weiterhin filtern und wird wie bisher in der Bedarfsliste selbst gesetzt.',
      'Wer die Einzelzahlen braucht, holt sie sich über „Spalten" wieder dazu — sie sind nur standardmäßig ausgeblendet, nicht weg.',
      'In der Bedarfsliste eines Projekts fehlten die Brotkrumen. Jetzt steht dort „Materialbedarf › Projektnummer", der Weg zurück in die Übersicht ist also ein Klick.',
    ],
  },
  {
    version: '3.429.0',
    date: '2026-07-31T00:30',
    title: 'Transporte: Liste und Detailseite modernisiert',
    tag: 'improvement',
    changes: [
      'Die Übersicht bekommt eine eigene Kartenliste fürs Handy statt einer gequetschten Tabelle (Muster Kommissionierung/Wareneingang).',
      'Ein Bug behoben: Der Fortschritts-Knopf in der Zeile hat zusätzlich zum Buchen auch die Detailseite geöffnet, weil die Zeile den Klick des eigenen Knopfs mit aufgefangen hat.',
      'Auf der Detailseite sind Stornieren und Löschen ins „…"-Menü gewandert — davor standen vier gleich gewichtete Knöpfe nebeneinander. Richtung wird jetzt als Badge dargestellt, einheitlich mit der Liste.',
    ],
  },
  {
    version: '3.428.0',
    date: '2026-07-31T00:15',
    title: 'Neues Modul „Verbesserungen": Fehler und Wünsche direkt im ERP melden',
    tag: 'feature',
    changes: [
      'Unter System gibt es jetzt „Verbesserungen". Jeder kann melden, was nicht funktioniert oder besser sein könnte — und sieht dort auch, was aus der eigenen Meldung geworden ist. Bisher lief das über Zuruf und Teams-Nachrichten, wo es verloren ging.',
      'Das Formular fragt nicht nach einer „Beschreibung", sondern getrennt nach Ist-Zustand, Erwartung und Zusatzkontext — und die Fragen wechseln je nachdem, ob es ein Fehler oder ein Wunsch ist. Bei einem Fehler heißt das dritte Feld „Schritte zum Nachstellen", bei einem Wunsch „Was bringt das?". Zu jedem Feld steht ein Hinweis und ein ausformuliertes Beispiel.',
      'Eine Anzeige unter dem Formular sagt live, wie gut die Meldung bearbeitbar ist, und nennt konkret, was noch fehlt („Schritte zum Nachstellen sind sehr knapp", „Bereich fehlt"). Ganz dünne Meldungen („geht nicht") lassen sich nicht absenden — die Mindestangaben prüft auch der Server, nicht nur das Formular.',
      'Seite, App-Version und Browser werden automatisch mitgeschickt. Das sind genau die Angaben, nach denen sonst jedes Mal zurückgefragt werden musste.',
      'Jede Meldung hat einen Verlauf: Rückfragen und Antworten stehen am Vorgang. Setzt die Verwaltung eine Meldung auf „Rückfrage offen", wird der Melder benachrichtigt und sieht am Vorgang, dass Angaben fehlen; ergänzt er sie, wandert die Meldung automatisch zurück in die Bearbeitung.',
      'Für die Systemverwaltung: Gesamtliste mit Filtern nach Art, Status, Bereich und Einordnung, Spalte „Detailgrad", interne Notizen (für den Melder nicht sichtbar) sowie Status, Einordnung und Abschluss-Notiz je Meldung. Neue Meldungen und Antworten kommen als Benachrichtigung.',
      'Ohne das Verwaltungsrecht sieht man ausschließlich die eigenen Meldungen — das filtert der Server, nicht die Oberfläche.',
    ],
  },
  {
    version: '3.427.0',
    date: '2026-07-31T00:00',
    title: 'Zahlungsplan im Verkaufsmodul löst Anzahlungs-/Schlussrechnung ab',
    tag: 'feature',
    changes: [
      'Neu im Auftrags-Editor: der Reiter „Zahlungsplan" legt VORHER fest, wie sich der Auftragswert auf Teilzahlungen verteilt — statt wie bisher bei jeder Anzahlung neu und ohne Obergrenze zu rechnen. Vorlagen 10/30/50/10, 30/70 oder 100 % setzen die Positionen mit einem Klick, jede Position lässt sich einzeln anpassen (Bezeichnung, Auslöser als Datum oder Meilenstein, Prozentsatz oder Festbetrag).',
      '„Rechnung erzeugen" je Position baut automatisch die komplette Rechnung: Kopfzeile mit dem vollen Auftragswert, darunter eine Abzugszeile pro übriger Position — bereits gestellte mit Rechnungsnummer und Datum, noch offene mit ihrem Auslöser. Der Rechnungsbetrag ergibt sich als Differenz, ganz ohne Handrechnen. Ein Live-Summen-Banner zeigt sofort, ob der Plan zum Auftragswert passt; passt er nicht, blockiert eine Fakturasperre das Erzeugen der nächsten Rechnung.',
      'Positionen werden standardmäßig in Reihenfolge fakturiert (abschaltbar in den Firmeneinstellungen) — eine spätere Position lässt sich nicht vorziehen, solange eine frühere noch offen ist. Eine Gutschrift auf eine so erzeugte Rechnung gibt die Position automatisch wieder frei und verteilt den Rest neu.',
      'Der alte Weg (Anzahlungs-/Schlussrechnung über eigene Knöpfe, ohne Plan) ist entfernt — ebenso der direkte „Auftrag → Rechnung"-Umwandlungsweg, der am Zahlungsplan vorbeiging. Bestehende Anzahlungs-/Schlussrechnungen wurden automatisch in Zahlungsplan-Positionen übernommen, nichts geht dabei verloren.',
    ],
  },
  {
    version: '3.426.0',
    date: '2026-07-30T23:45',
    title: 'Transporte: Detailseite mit Brotkrumen zurück, Zeilen wieder anklickbar',
    tag: 'fix',
    changes: [
      'Beim Verschlanken des Transportmoduls ist die Detailseite mit entfallen — und damit die Brotkrumen und der Weg zurück. Das war zu viel des Guten: eine Transportnummer muss verlinkbar sein. Die Seite ist wieder da, schlank: Kopfdaten, Termin, Bezug, die vier Aktionen.',
      'Ein Klick auf eine Zeile in der Übersicht öffnet den Transport wieder. Klicks aus dem Aktionsmenü lösen dabei keine Navigation aus.',
      'Kommt man aus dem Wareneingang („Angeliefert mit"), führen die Brotkrumen zurück zum Beleg — mit dessen Nummer, nicht auf die Transportliste.',
    ],
  },
  {
    version: '3.425.0',
    date: '2026-07-30T23:00',
    title: 'Transporte auf das Wesentliche reduziert: erfassen, verknüpfen, terminieren',
    tag: 'improvement',
    changes: [
      'Das Transportmodul war für einen Ausbau gebaut, den es noch nicht gibt: LKWs, Container, Collis in Container verladen, Gewichts- und Maßsummen, Verladenachweis-Fotos, Ladelisten-PDF, fünfstufige Statuskette. Gebraucht wird ein Bruchteil davon — deshalb bleibt vorerst nur der Kern.',
      'Was geht: eingehende Anlieferungen und ausgehende Abholungen erfassen, Bestellungen (eingehend) bzw. ein Projekt (ausgehend) verknüpfen, Termin mit Datum und Uhrzeit setzen, abschließen oder stornieren. Alles auf einer Seite, Anlegen und Bearbeiten im selben Dialog.',
      'Die Liste zeigt standardmäßig nur offene Transporte — mit Zähler und „Alle anzeigen", wie in der Kommissionierliste. Je Zeile gibt es genau einen Knopf für den nächsten Schritt: bei einer Anlieferung „Als angekommen buchen", bei einer Abholung „Als abgefahren buchen".',
      'Die Bestellungen eines eingehenden Transports hingen bisher am einzelnen LKW. Sie sind jetzt am Transport selbst — bestehende Zuordnungen wurden übernommen, es geht nichts verloren.',
      'Wichtig: Es wurde nichts gelöscht. Bereits verladene Collis, LKW- und Container-Daten sowie die Verladefotos stehen unverändert in der Datenbank. Wenn der Ladeplan später gebraucht wird, wird darauf aufgesetzt statt neu erfasst.',
      'Am Wareneingang steht unter „Angeliefert mit" jetzt der Termin statt der Fahrzeugliste; der Link führt auf die Transportübersicht, dort ist der Transport über seine Nummer sofort gefunden.',
    ],
  },
  {
    version: '3.424.0',
    date: '2026-07-30T22:45',
    title: 'Materialbedarf zieht ins Modul „Material" — mit Übersicht über alle Projekte',
    tag: 'feature',
    changes: [
      'Der Materialbedarf war bisher im Projekt versteckt (Warenwirtschaft → Bedarf / Material). Wer wissen wollte, wo überall noch etwas offen ist, musste Projekt für Projekt durchklicken. Jetzt gibt es links unter „Material" den eigenen Punkt „Materialbedarf".',
      'Die erste Ansicht ist eine Übersicht: eine Zeile je Projekt mit Bedarf — Projektnummer, Name, Kunde, Listen-Status und wie viele Positionen offen, bestellt, gedeckt oder geliefert sind. Projekte mit offenem Bedarf stehen oben, darunter die zuletzt bearbeiteten. Über der Liste steht, wie viele Projekte gerade offenen Bedarf haben.',
      'Ein Klick auf eine Zeile öffnet die gewohnte Bedarfsliste dieses Projekts — mit allem, was sie vorher konnte: Import aus Excel/CSV, Reservieren, Bestellung erzeugen, Quelle je Position, Export und den Vollbild-Editor. An den Funktionen selbst wurde nichts geändert.',
      'Suchen und Filtern in der Übersicht: nach Projektnummer, Name oder Kunde, wahlweise eingegrenzt auf „nur mit offenem Bedarf" oder auf einen Listen-Status.',
      'Der Unter-Tab „Bedarf / Material" im Projekt ist damit entfallen; die Warenwirtschaft im Projekt zeigt weiterhin Bestellungen, Lager und Kommissionierung. „Schließen" im Vollbild führt jetzt zurück in die Bedarfsliste unter Material.',
      'Zum Namen: bewusst „Materialbedarf" und nicht „Stücklisten" — eine Stückliste ist im System die Zusammensetzung eines Artikels (am Artikel hinterlegt). Der Materialbedarf ist das Gegenstück dazu: was ein Projekt braucht. Eine Engineering-Stückliste bleibt der übliche Import in so eine Bedarfsliste.',
    ],
  },
  {
    version: '3.423.0',
    date: '2026-07-30T22:30',
    title: 'Bau-Tagesbericht via Mail senden',
    tag: 'feature',
    changes: [
      'Der Bau-Tagesbericht (Montage & Service) ließ sich bisher nur als PDF herunterladen. Jetzt lässt sich am Bericht eine E-Mail-Adresse hinterlegen (Ansprechpartner vor Ort, je Einsatz unterschiedlich) — der neue Knopf „Via Mail senden" neben dem PDF-Download lädt den unterschriebenen Tagesbericht herunter und öffnet gleichzeitig das lokale E-Mail-Programm mit vorbefülltem Empfänger, Betreff und Text (dasselbe Verfahren wie schon bei Verkaufs-/Einkaufsbelegen — das PDF muss von Hand angehängt werden, ein Browser kann das aus Sicherheitsgründen nicht automatisch).',
      'Das Sekretariat bekommt automatisch eine Benachrichtigung, sobald der Knopf gedrückt wurde — dieselbe Glocke, die auch bei Artikelanträgen oder Wareneingang informiert.',
    ],
  },
  {
    version: '3.422.0',
    date: '2026-07-30T22:00',
    title: 'Kommissionierung: abgeschlossene Aufträge sind standardmäßig ausgeblendet',
    tag: 'improvement',
    changes: [
      'Die Kommissionierliste öffnet jetzt mit „Offene Aufträge": abgeschlossene und stornierte stehen nicht mehr drin. Vorher wuchs die Liste mit jedem fertigen Auftrag, und die zwei offenen gingen zwischen dem Archiv unter.',
      'Ausgeblendet heißt nicht verschwunden: unter der Liste steht, wie viele Aufträge der Filter versteckt, daneben ein „Alle anzeigen". Über das Status-Feld ist weiterhin jeder einzelne Status wählbar.',
      'Das Status-Feld war unvollständig — „Kommissioniert" und „Storniert" fehlten ganz, man konnte also gar nicht danach filtern. Jetzt stehen alle sechs Status zur Auswahl.',
      'Ist in der gewählten Auswahl nichts zu sehen, sagt die Seite auch warum — vorher stand dort „Keine Kommissionieraufträge", selbst wenn es welche gab und nur der Filter sie versteckte.',
    ],
  },
  {
    version: '3.421.0',
    date: '2026-07-30T21:45',
    title: 'Fix: Standardlieferant beim Artikel-Import (Büroware/NDJSON)',
    tag: 'fix',
    changes: [
      'Beim Import von Artikeln aus der Büroware wurde der Standardlieferant bisher nie übernommen — die Quellspalte enthält eine Lieferantennummer, wurde aber wie ein Name behandelt und landete nirgends.',
      'Die Lieferantennummer wird jetzt gegen die Kunden-/Lieferantenstammdaten aufgelöst: der Artikel bekommt den passenden Lieferanten samt Namen gesetzt, genau wie das bei Bestellungen schon funktioniert.',
    ],
  },
  {
    version: '3.420.0',
    date: '2026-07-30T21:30',
    title: 'Projekt-Analyse: Bestellungen jetzt positionsgenau statt Beleg-Kopf',
    tag: 'fix',
    changes: [
      'Die Auswertung „Analyse → Abfrage: Projekte" hat bei „bestellt" bisher das Projekt vom Beleg-Kopf einer Bestellung genommen. Eine gepoolte Bestellung (mehrere Projekte in einer Sammelbestellung beim selben Lieferanten, wie es die Beschaffung schon länger erlaubt) hat aber gar kein Kopf-Projekt — sie fehlte in der Projekt-Analyse dadurch komplett, und die einzelnen Projekte wirkten fälschlich günstiger eingekauft, als sie waren.',
      'Die Auswertung liest jetzt je Bestellposition das Positions-Projekt (falls gesetzt), sonst das Kopf-Projekt als Rückfallwert — genau wie es die Bestellung selbst schon seit Längerem je Zeile erlaubt.',
    ],
  },
  {
    version: '3.419.0',
    date: '2026-07-30T21:15',
    title: 'Langtext: Text unterstreichen — und die Formatierungshilfe nur noch auf Zuruf',
    tag: 'improvement',
    changes: [
      'Im Positions-Langtext lässt sich Text jetzt auch unterstreichen: __so__ geschrieben, unterstrichen gedruckt. Zwei Unterstriche bewusst statt einem — Angaben wie „DN100_PN16_Edelstahl" sind im Langtext Alltag und sollen nichts auszeichnen.',
      'Unterstreichen ist mit dem Rest kombinierbar: **__fett und unterstrichen__** oder #m#__farbig unterstrichen__#m#. Ein offener Marker gilt wie gehabt bis zum Zeilenende.',
      'Die Zeile mit den Formatierungscodes stand dauerhaft unter jedem Langtext-Feld — bei drei geöffneten Positionen dreimal dasselbe. Jetzt steht dort nur „Formatierungshilfe"; die vollständige Liste erscheint beim Darüberfahren, am Tablet beim Antippen und per Tastatur beim Fokussieren.',
      'Nebenbei: Ein Klick auf die Checkbox „Artikelnummer nicht drucken" hat zusätzlich den Cursor ins Langtext-Feld gesetzt — das lag an einer verschachtelten Feldbeschriftung und ist behoben.',
    ],
  },
  {
    version: '3.419.1',
    date: '2026-07-30T21:00',
    title: 'Zwei neue Rechte: Datei-Upload bei Artikeln, Auftragswert einzeln steuerbar',
    tag: 'feature',
    changes: [
      'Neues Recht „Datei hochladen" (inventory.uploadfile): Zeichnungen bei einem Artikel hochzuladen ist jetzt ein eigenes Recht, ZUSÄTZLICH zu „Artikel & Lager ändern" nötig. Bestehende Rollen mit „Artikel & Lager ändern" haben das neue Recht automatisch mitbekommen — es ändert sich also nichts, bis ein Admin es gezielt jemandem entzieht.',
      'Neues Recht „Auftragswert" (projects.viewordervalue) im Projekt-Modul: steuert den Lesezugriff auf den Auftragswert eines Projekts unabhängig vom bisherigen Recht „Kaufmännisch (Projekt)" (projects.viewfinancials). Wer nur den Auftragswert sehen soll, aber nicht die Kalkulation/Transferpreise, bekommt jetzt nur dieses eine Recht statt des ganzen Kaufmännisch-Tabs. Gilt auch für den PDF-Projektbericht.',
    ],
  },
  {
    version: '3.418.0',
    date: '2026-07-30T20:30',
    title: 'Dynamische Umsatz- und Lieferantenstatistik in CRM und Artikel',
    tag: 'feature',
    changes: [
      'CRM und Artikel & Lager hatten eigene, dünne Analyse-Seiten: feste Kennzahlen fürs laufende Kalenderjahr, kein Zeitraum, kein Vorjahresvergleich, kein Durchklicken zu einem Kunden oder Artikel. Sie zeigen jetzt dieselbe dynamische Auswertung, die es unter „Analyse → Belege/Artikel" schon gibt: wählbarer Zeitraum (letzte 6/12/24 Monate, laufendes/letztes Jahr), Vorjahresvergleich, eine Tabelle der stärksten Kunden bzw. Artikel mit Klick zum Eingrenzen, und Export als Excel.',
      'Beschaffung bekommt damit erstmals eine echte Lieferantenstatistik — vorher gab es nur einen Balken „Volumen nach Lieferant" ohne Zeitraum. Jetzt derselbe dynamische Bericht wie bei den Kunden, nur auf Einkaufsbelege eingegrenzt.',
      'Neu, weil es bisher fehlte: eine echte Artikel-Umsatzstatistik. Die Artikel-Auswertung kannte bisher nur Lagerbewegungen (was wurde ein-/ausgelagert) — jetzt zusätzlich, welcher Umsatz mit einem Artikel tatsächlich gemacht wurde (aus den Rechnungen, nicht aus Angeboten oder unfakturierten Aufträgen — sonst würde ein Auftrag und die daraus entstandene Rechnung doppelt zählen). Die Liste der meistverkauften Artikel richtet sich jetzt danach, was sich verkauft hat, nicht mehr nur danach, was am meisten aus dem Lager ging.',
      'Die bisherigen Kennzahlen der vier Seiten bleiben erhalten (Adressstruktur, Lagerwert, Angebots-Trichter, Bestellstatus) — sie zeigen den aktuellen Stand aus den Stammdaten und ergänzen die neue, zeitraumbezogene Auswertung darunter.',
      'Die Auswertung ist wie bisher an das Recht „Auswertungen & Kennzahlen" gebunden — ohne dieses Recht steht dort ein Hinweis statt eines Fehlers.',
    ],
  },
  {
    version: '3.417.0',
    date: '2026-07-30T19:15',
    title: 'Wareneingang: Klick auf eine Lieferung zeigt erst die Positionen',
    tag: 'improvement',
    changes: [
      'Ein Klick auf eine erwartete Lieferung hat bisher sofort eine Wareneingangsprüfung gestartet und den Beleg angelegt. Wer nur nachsehen wollte, was da eigentlich kommt, hatte damit einen Beleg erzeugt — und weil je Bestellung nur eine Prüfung offen sein darf, stand der auch noch dem nächsten Versuch im Weg.',
      'Jetzt öffnet der Klick eine Vorschau mit allen Positionen der Bestellung: Artikelnummer, Bezeichnung, Langtext und je Zeile bestellt / geliefert / offen. Schon vollständig gelieferte Positionen bleiben sichtbar und sind als „vollständig" gekennzeichnet — an der Rampe ist „was fehlt noch?" und „was ist schon da?" dieselbe Frage.',
      'Die Prüfung startet weiterhin über den Knopf am Zeilenende — und zusätzlich unten in der Vorschau. Der Weg wird also nicht länger, er beginnt nur nicht mehr aus Versehen.',
      'Überschriften und Textzeilen der Bestellung stehen in der Vorschau mit drin, damit sie sich liest wie der Beleg selbst; Mengen zeigen sie keine. Weicht das Projekt einer Position vom Projekt der Bestellung ab, steht es an der Zeile.',
      'Am Handy ist die Karte geteilt: Tippen auf die Angaben zeigt die Vorschau, die Prüfung hat einen eigenen Knopf. Die Positionen werden erst beim Öffnen geladen — eine Liste mit 50 Bestellungen soll nicht 50 Positionslisten mitschleppen.',
      'Nebenbei behoben: Ein Klick in einem Fenster, das aus einer Tabellenzeile geöffnet wurde, zählte in handgebauten Listen noch als Klick auf die Zeile darunter. Das ist jetzt für alle Tabellen der Anwendung ausgeschlossen, nicht mehr nur für die großen Listen.',
    ],
  },
  {
    version: '3.416.0',
    date: '2026-07-30T18:45',
    title: 'Baubericht fragt nur noch, was zum Einsatz passt',
    tag: 'improvement',
    changes: [
      'Der Baustellenbericht blendet Felder aus, die zum Einsatz nicht gehören: Ort und Entfernung der Unterkunft erscheinen nur, wenn es eine Unterkunft gab; „Unterweisung am/durch" nur, wenn eine stattfand; Teilung und Antriebskette nur, wenn ein Kettentyp eingetragen ist. Bei einer Wartung ohne Übernachtung sind das fünf Fragen weniger.',
      'Der Fortschrittsbalken zählt nur noch, was der Monteur vor Ort beantworten kann. Angaben, die aus dem Büro kommen (Betrieb seit, letzter Service, letzte Wartung durch PROBIG, Betriebsdauer, Temperatur des Mediums, chemische Belastung), sind weiterhin sichtbar, zählen aber nicht mehr mit — vorher war „100 %" auf der Baustelle unerreichbar und der Balken damit wertlos.',
      'Wichtig fürs Dokument: Ausblenden betrifft nur die Eingabe. Ein Wert, der schon erfasst war, bleibt gespeichert und steht weiter im PDF — auch wenn die zugehörige Frage später anders beantwortet wird.',
    ],
  },
  {
    version: '3.415.0',
    date: '2026-07-30T18:30',
    title: 'Bau-Tagesbericht: Unterschriftsfeld auf Vollbild vergrößern',
    tag: 'improvement',
    changes: [
      'Im Unterschriftsdialog gibt es „Vergrößern": das Unterschriftsfeld nutzt dann den ganzen Bildschirm, die Eingabefelder verschwinden. Gedacht für den Moment, in dem der Monteur das iPad weitergibt — Rolle, Name und Ort füllt er vorher aus, der Kunde sieht nur noch die Fläche.',
      'Zur Kontrolle steht über dem Feld eine Zeile „Unterschrift von … als … · Ort", damit auch im Vollbild klar ist, was unterschrieben wird.',
      'Ein bereits gezogener Schriftzug bleibt beim Vergrößern und Verkleinern erhalten und wird ohne Verzerrung eingepasst.',
      'Dabei ist ein älterer Fehler mitbehoben: Bisher löschte jede Größenänderung der Fläche den Schriftzug — also auch das Drehen des iPads zwischen Hoch- und Querformat.',
      'Fehlt der Name beim Übernehmen, schaltet der Dialog automatisch zurück auf klein: sonst zeigte der Hinweis auf ein ausgeblendetes Feld.',
    ],
  },
  {
    version: '3.414.0',
    date: '2026-07-30T18:00',
    title: 'Bau-Tagesbericht: Personenzeile auf dem Handy zwei- statt dreizeilig',
    tag: 'improvement',
    changes: [
      'Im Bau-Tagesbericht stand auf dem Handy je Person der Papierkorb allein auf einer dritten Zeile. Jetzt teilen sich Rolle, Stunden und Löschen die zweite Zeile — bei drei Monteuren spart das gut einen halben Bildschirm. Auf dem iPad bleibt alles wie gehabt in einer Zeile.',
      'Die Bildschirm-Prüfung des Berichts (`tools/design-review/review-baubericht.mjs`) hat den Tagesbericht bisher gar nicht gemessen: Die Tageskarten blieben zugeklappt, und die Testdaten für das eingesetzte Personal hatten falsche Feldnamen, sodass die Personenzeile leer blieb. Beides ist behoben — die Prüfung klappt jetzt alle Tage auf und weist nach, dass Unterschrift, Fotostreifen und Personenzeile wirklich im Bild waren.',
      'Zusätzlich meldet die Prüfung jetzt Elemente, die über den rechten Rand hinausragen (die waagrecht scrollbare Sprungleiste ausgenommen) und schlägt fehl, statt die Messwerte nur auszugeben.',
    ],
  },
  {
    version: '3.413.0',
    date: '2026-07-30T17:45',
    title: 'Baubericht: Komma-Eingabe rechnete falsch — behoben',
    tag: 'fix',
    changes: [
      'Behoben, und zwar wichtig: In den Zahlenfeldern des Baustellenberichts (Kilometer, Entfernung, Temperatur, Maße, Räumer/Balken je Becken) hat der Browser das Komma stillschweigend verschluckt. Aus getippt „2,5" wurde der Wert 25 — der Zehnfache. Auf dem Handy fiel das kaum auf.',
      'Jetzt wird die deutsche Schreibweise richtig gelesen: „2,5" ist 2,5, „1.250" ist 1250 (Tausenderpunkt), und der englische Punkt wird weiterhin verstanden. Gespeicherte Werte werden mit Komma angezeigt.',
      'Eine unlesbare Eingabe löscht nicht mehr den vorhandenen Wert: das Feld meldet „Bitte eine Zahl eingeben, z. B. 2,5. Der bisherige Wert bleibt gespeichert." Vorher wurde in diesem Fall stillschweigend „keine Angabe" gespeichert — die Angabe war also weg.',
      'Der Bau-Tagesbericht (Stunden, Reisezeit, Temperatur) war nie betroffen, dort wurde die Kommaeingabe von Anfang an richtig behandelt.',
      'Fotos im Bericht werden nur noch nach Rückfrage gelöscht — anders als Tage, Personalzeilen und Listeneinträge lässt sich ein Foto nicht zurückholen. Ein unterschriebener Tagesbericht ist ohnehin gesperrt und kann gar nicht gelöscht werden.',
    ],
  },
  {
    version: '3.412.0',
    date: '2026-07-30T17:15',
    title: 'Wareneingang: nach Liefertermin sortiert, durchsuchbar — Reklamation bekommt die Fotos',
    tag: 'improvement',
    changes: [
      'Erwartete Lieferungen stehen jetzt in der Reihenfolge, in der sie jemanden interessieren: nach zugesagtem Liefertermin, das Überfällige zuerst. Bestellungen ohne Termin rutschen ans Ende — die sind nicht dringend, sondern unbestimmt. Vorher kam die Liste in Bestellreihenfolge, und der seit einer Woche fällige LKW stand irgendwo in der Mitte.',
      'Überfälliges ist als solches markiert („überfällig seit 5 Tagen", rot) statt nur als Datum. Darüber filtern zwei Knöpfe mit Zähler: „Überfällig" und „Prüfung läuft" — man sieht die Zahl, ohne zu klicken.',
      'Die erwarteten Lieferungen sind am Rechner eine Tabelle mit Spalten (Lieferant, Bestellung, Projekt, offene Positionen, Termin) statt einer Textzeile. Am Tablet bleiben es Karten mit großen Zielen — an der Rampe wird getippt, nicht geklickt.',
      'Statt eines blauen Textes am Zeilenende steht dort jetzt ein richtiger Knopf: „Prüfung starten" bzw. „Fortsetzen".',
      'Das Register „Wareneingänge" hat endlich eine Suche — über Belegnummer, Lieferschein-Nummer, Bestellnummer und Lieferant. Der Status-Filter ist von einem Dropdown auf Knöpfe mit Zählern umgestellt, wie bei den Transporten.',
      'Die Belegtabelle zeigt zusätzlich die Lieferschein-Nummer, das Verhältnis der Abweichungen („2 von 3") und den jüngsten Schritt („Gebucht 28.07.2026") statt stur des Anlagedatums.',
      'Beide Listen endeten bisher stumm bei 50 Einträgen — wer den 51. suchte, hielt ihn für verschwunden. Jetzt steht unter der Liste, wie viele es insgesamt sind, mit „Mehr laden".',
      'Reklamation aus dem Wareneingang: Die Fotos der Lieferung werden jetzt wirklich in den QM-Prozess übernommen, nicht nur im Text erwähnt. Alle Fotos des Belegs landen als Anhang am Prozess — auch das Bild der Palette an der Rampe, das bei einem Transportschaden meist der eigentliche Beweis ist.',
      'Bewusst eine Kopie, kein Verweis: Der QM-Prozess geht zum Lieferanten und muss die Bilder selbst tragen, auch wenn am Wareneingang später ein Foto gelöscht wird. Fotos, die erst nach der Reklamation entstehen, hängt man direkt im QM-Prozess an.',
      'Ein Hinweis in der Prüfstrecke („Fehlt noch: Foto") war bisher schwarz statt rot — ein Farbfehler, jetzt behoben.',
    ],
  },
  {
    version: '3.411.0',
    date: '2026-07-30T16:30',
    title: 'Duplizieren jetzt auch im Einkauf',
    tag: 'feature',
    changes: [
      'Anfragen, Bestellungen und Eingangsrechnungen lassen sich duplizieren — im Zeilenmenü der Beschaffungsliste, genau wie im Verkauf. Die Kopie übernimmt Lieferant, Konditionen, Lieferbedingung, Notiz, alle Positionen samt Positions-Projekten und Zuschnittlängen sowie die Druck-Schalter des Belegs.',
      'Die Kopie startet immer als Entwurf mit eigener Belegnummer — auch wenn die Vorlage schon bestellt oder geliefert war. Anschließend öffnet sich die Kopie direkt im Editor.',
      'Bewusst NICHT mitkopiert wird der Wareneingang: gelieferte Mengen und die Verknüpfung zum Projektbedarf bleiben bei der Vorlage. Eine Kopie darf keine Bedarfsmengen ein zweites Mal zurückmelden.',
      'Liefertermin und Fälligkeit werden neu berechnet, behalten aber den Abstand der Vorlage: aus einer drei Wochen alten Bestellung mit „Liefertermin in 10 Tagen" wird eine Kopie mit Liefertermin in 10 Tagen — nicht eine mit Termin in der Vergangenheit.',
    ],
  },
  {
    version: '3.410.0',
    date: '2026-07-30T15:00',
    title: 'Behoben: Nach dem Schließen der Vorschau landete man im Beleg',
    tag: 'fix',
    changes: [
      'Wer die Beleg-Vorschau geschlossen hat, stand plötzlich im Beleg-Editor — obwohl er nur schauen wollte. Grund: Klicks im Vorschau-Fenster wurden von der Tabellenzeile darunter mitgezählt, weil das Fenster technisch zur Zeile gehört.',
      'Behoben an der Wurzel: eine Tabellenzeile öffnet den Datensatz nur noch, wenn der Klick tatsächlich in der Zeile passiert ist. Das gilt für alle Listen der Anwendung.',
      'Damit sind auch die Zeilenmenüs sauber: „Status ändern" ändert nur den Status und öffnet nicht zusätzlich den Beleg.',
    ],
  },
  {
    version: '3.409.0',
    date: '2026-07-30T14:00',
    title: 'Baubericht neu für iPad und Handy — und zwei sinnlose Felder weg',
    tag: 'improvement',
    changes: [
      'Der Baustellenbericht war auf dem iPhone elf Bildschirme lang, mit 63 Eingabefeldern auf einer Seite. Jetzt ist jeder Abschnitt eine zusammenklappbare Karte mit Ausfüllstand („3/9" bzw. „fertig") — der ganze Bericht passt als Übersicht auf einen Bildschirm, und man sieht sofort, was noch fehlt.',
      'Oben eine Sprungleiste: ein Tipp führt direkt zum Abschnitt und klappt ihn auf. Daneben ein Fortschrittsbalken über den gesamten Bericht. Beim Öffnen ist der erste unfertige Abschnitt schon aufgeklappt.',
      'Ja/Nein-Angaben sind jetzt zwei große Knöpfe (grün/rot) statt eines Auswahlfelds: ein Tipp statt öffnen–wählen–schließen. Erneutes Tippen setzt die Angabe wieder auf „offen". Kurze Auswahllisten (Beckenform, Neubau/Umbau, Reinigungsgrad …) stehen als Knopfreihe da, ohne Dropdown.',
      'Alle Eingabefelder sind auf Fingergröße gebracht (mindestens 44 Punkte) — vorher waren 19 Ziele kleiner. Die Kopfzeile ist auf dem Handy auf eine Zeile geschrumpft, die Projektdaten sind eingeklappt und zeigen geschlossen Kunde und Ort.',
      'Auch „Baustellenverlauf" und „Listen & Fotos" sind jetzt solche Karten: geschlossen zeigen sie eine Kurzfassung (Tage und Arbeitsstunden bzw. Anzahl der Einträge), aufgeklappt arbeiten sie wie bisher. Beide sind zusätzlich über die Sprungleiste erreichbar.',
      'Entfernt: „Baujahr" (gehört zur Anlage bzw. zum Projekt, nicht in jeden Einsatzbericht) und „Schicht" im Tagesbericht (Freitext neben „Arbeit von/bis", der leer blieb oder ihm widersprach). Die bisher dort erfassten Werte werden mit dem Update gelöscht.',
    ],
  },
  {
    version: '3.408.0',
    date: '2026-07-30T13:30',
    title: 'Nachtrag: PDF-Vorschau in Safari zeigte „Unexpected server response (0)"',
    tag: 'fix',
    changes: [
      'Der erste Anlauf reichte nicht: die Vorschau meldete in Safari „PDF konnte nicht angezeigt werden: Unexpected server response (0)". Grund war der Zwischenschritt über einen internen Datei-Link — den durfte die Anzeige aus zwei unabhängigen Gründen nicht laden (Sicherheitsregeln der Seite und eine Safari-Eigenheit).',
      'Die Vorschau holt das PDF jetzt direkt vom Server und übergibt die Daten unmittelbar an die Anzeige — ohne Zwischenschritt. Damit ist die Ursache weg, nicht nur umgangen.',
      'Nebeneffekt: Belege werden nicht mehr doppelt geladen. Vorher holte die Seite das PDF einmal für die Anzeige und beim Herunterladen ein zweites Mal.',
      'Warum es vorher nur in Produktion auffiel: die genannten Sicherheitsregeln setzt erst der Server der laufenden Anwendung — bei einem Test auf dem Entwicklungsrechner greifen sie nicht.',
    ],
  },
  {
    version: '3.407.0',
    date: '2026-07-30T11:30',
    title: 'PDF-Vorschauen funktionieren jetzt auch in Safari',
    tag: 'fix',
    changes: [
      'Behoben: In Safari (Mac, iPhone, iPad) blieb jede PDF-Vorschau leer — die Beleg-Vorschau in der Liste, die Vorschau im eigenen Tab, die Dokument-Vorschau im Dateimanager und die Zeichnungs-Vorschau am Artikel. Ursache: Safari zeigt PDFs grundsätzlich nicht an, wenn sie aus einem geschützten Download stammen und in einen Rahmen eingebettet werden.',
      'Die Seiten werden jetzt von ProERP selbst gezeichnet (pdf.js) statt an den Browser-Betrachter übergeben. Damit sieht die Vorschau in jedem Browser gleich aus und funktioniert auch auf dem iPad auf der Baustelle.',
      'Mehrseitige Belege stehen untereinander und lassen sich durchscrollen; die Seiten passen sich der Fensterbreite an und werden für Bildschirme mit hoher Auflösung scharf gezeichnet.',
      'Das Zusatzpaket dafür wird erst geladen, wenn wirklich eine Vorschau geöffnet wird — der normale Seitenaufbau bleibt unverändert schnell.',
      'Herunterladen und „Via Mail senden" waren nie betroffen und funktionieren wie bisher.',
    ],
  },
  {
    version: '3.406.0',
    date: '2026-07-30T10:00',
    title: 'Baustellenbericht: Listen und Fotos — die Word-Vorlage ist abgelöst',
    tag: 'feature',
    changes: [
      'Der Baustellenbericht hat jetzt Listen: Störungen, fehlende Teile, zu Erledigendes, Bericht, Infos, Fremdfirmen vor Ort, vertretene Firmen, Kundeneinschätzung, Diskussionsthemen und Infos für den Vertrieb — die letzten Blöcke der Word-Vorlage.',
      'Erfasst wird in einer Zeile: Liste wählen, Text tippen, Enter. Bei Störungen, fehlenden Teilen und Zu-Erledigen kommen Verantwortlicher und Frist dazu, und man kann sie abhaken; die Überschrift zeigt, wie viele noch offen sind. Bei einer Kundeneinschätzung gibt es das bewusst nicht — die ist nie „erledigt".',
      'Listen, in denen nichts steht, erscheinen gar nicht erst — weder auf dem iPad noch im PDF.',
      'Baustellenfotos: mit der Kamera aufnehmen, Bildunterschrift dazu. Ein Foto lässt sich direkt an eine Störung hängen, dann steht das Schadensbild beim Eintrag statt in einer großen Sammelgalerie.',
      'Im PDF stehen die Listen mit Kästchen (abgehakt = Haken), Verantwortlichem und Frist. Die Fotos kommen als Raster mit Bildunterschrift dazu — begrenzt auf zwölf, damit der Bericht per Mail versendbar bleibt; weitere werden im Dokument benannt und bleiben im ERP abrufbar.',
      'Ein Eintrag mit angehängten Fotos lässt sich nicht versehentlich löschen — es kommt eine klare Meldung statt einer Fehlermeldung aus der Datenbank.',
    ],
  },
  {
    version: '3.405.0',
    date: '2026-07-30T09:00',
    title: 'Verkaufsbelege duplizieren, Beleg-Vorschau direkt aus der Liste',
    tag: 'feature',
    changes: [
      'Verkaufsbelege lassen sich duplizieren: im Zeilenmenü der Belegliste („…") gibt es den Punkt „Duplizieren". Die Kopie übernimmt alle Kopfdaten und Positionen, bekommt eine eigene Belegnummer und startet als Entwurf — anschließend öffnet sich die Kopie direkt im Editor.',
      'Mitkopiert wird alles, was am Beleg hängt: Langtexte, Baugruppen, Zuschnittlängen, Rabatte, Lieferbedingungen, Notiz und die pro Beleg gesetzten Druck-Schalter der Belegeinstellungen.',
      '„Gültig bis" und die Fälligkeit werden neu berechnet, aber mit der Spanne der Vorlage: ein Angebot mit 30 Tagen Gültigkeit ist auch als Kopie 30 Tage gültig — nicht vom ersten Tag an abgelaufen.',
      'Eine Freigabe wird bewusst NICHT mitkopiert: die Kopie ist ein eigener Beleg und muss eigenständig durch die Vier-Augen-Freigabe. Ebenso bleibt die Kopie ohne die Datei-Anhänge der Vorlage.',
      'Nicht duplizierbar sind Anzahlungs-, Schluss- und Stornorechnungen sowie Kommissionierbelege — die entstehen aus ihrem Vorgängerbeleg (nur dort werden z. B. die Anzahlungen verrechnet). Der Menüpunkt erscheint dort gar nicht; ein Versuch über die Schnittstelle wird mit Hinweis auf den richtigen Weg abgelehnt.',
      'Neue Spalte in Verkauf und Beschaffung: ein kleiner Vorschau-Knopf je Zeile zeigt das Beleg-PDF in einem großen Fenster, ohne die Liste zu verlassen. Von dort aus lässt sich der Beleg herunterladen oder in einem neuen Tab öffnen (dort gibt es zusätzlich „Via Mail senden").',
      'Die Vorschau lädt das PDF erst beim Öffnen — eine Liste mit 100 Zeilen erzeugt also weiterhin nur eine Abfrage, nicht 100.',
    ],
  },
  {
    version: '3.404.0',
    date: '2026-07-29T20:30',
    title: 'Wareneingang: Reklamation auf Knopfdruck, Anlieferung sichtbar',
    tag: 'feature',
    changes: [
      'Bei einer abweichenden Position (zu wenig, zu viel, beschädigt) legt ein Knopf am Beleg eine Reklamation als QM-Prozess an — die Problembeschreibung ist schon ausgefüllt: Beleg- und Bestellnummer, Lieferant, Lieferschein, Artikel, bestellte/offene/gezählte Menge, Befund, Bemerkung, Anzahl Fotos und Projekt.',
      'Voreingestellt als 4D-Report; für größere Fälle lässt sich das im QM auf 8D umstellen. Je Position gibt es genau eine Reklamation (die zweite wird abgelehnt), dafür ist sie auch nach dem Buchen noch möglich — ein Schaden fällt oft erst später auf.',
      'Die Reklamation darf das Büro anlegen (Recht „QM-Prozess anlegen"), nicht der Prüfende: wer den Schaden dokumentiert, führt nicht auch die Reklamation. Am Beleg steht danach die QM-Nummer als Link.',
      'Neu am Beleg: „Angeliefert mit" — welcher eingehende Transport die Bestellung gebracht hat, mit Fahrzeug, Spedition und Ankunftszeit, verlinkt auf den Transport.',
      'Umgekehrt zeigt der eingehende Transport jetzt die Wareneingänge zu seinen zugeordneten Bestellungen, mit Status und Zahl der Abweichungen.',
    ],
  },
  {
    version: '3.403.0',
    date: '2026-07-29T20:00',
    title: 'Baustellenbericht: Technik, Zustand und Betrieb der Anlage',
    tag: 'feature',
    changes: [
      'Der Baustellenbericht hat drei neue Abschnitte, die den Rest der Word-Vorlage abdecken. „Technik vor Ort": Becken-Bezeichnung, Räumer und Balken je Becken, Kettentyp (NCS/Loop Chain), Teilung, Antriebskette, Medium, Temperatur des Mediums und Meerwasser-Kennzeichen.',
      '„Zustand & Umgebung": Beckenform, Material (mehrfach wählbar), Neubau/Umbau, Wände parallel, Bodenwellen, Bodenabstand, Dehnfugen, Rampe, Pumpensumpf, Vouten, Sand, Prallbleche, chemische Belastung, Reinigungsgrad, gefüllt, Überdachung und geschlossener Raum.',
      '„Betrieb": Betriebsdauer, Betrieb seit, letzter Service und letzte Wartung durch PROBIG.',
      'Alle drei Abschnitte erscheinen automatisch auch im PDF — mit ausgeschriebenen Bezeichnungen („Loop Chain" statt „LOOP_CHAIN"), Einheiten und Ja/Nein statt Rohwerten.',
      'Hinweise zu einzelnen Feldern sind jetzt auch dort sichtbar, wo es keinen Platzhalter gibt. Bei „Geschlossener Raum" steht damit endlich der Hinweis auf Befahrerlaubnis und Gaswarner am Feld.',
      'Was am Projekt steht — Anzahl Becken, Maße, Wasserspiegel, Anlagentyp —, wird weiterhin nur angezeigt und nicht doppelt erfasst.',
    ],
  },
  {
    version: '3.402.0',
    date: '2026-07-29T19:30',
    title: 'Wareneingang: Prüfprotokoll als PDF',
    tag: 'feature',
    changes: [
      'Jeder Wareneingang lässt sich als Prüfprotokoll drucken: Kopf mit Beleg-Nummer, Lieferant und Status, Stammdaten (Bestellung, Lieferschein, Projekt, Zeitpunkte), die geprüften Positionen mit bestellter, offener und gezählter Menge samt Langtext — und die Abweichungen im Klartext, inklusive der Folge („wird NICHT eingebucht").',
      'Unten stehen zwei Unterschriftszeilen: geprüft (Lager/Technik) und gebucht (Sekretariat) — dieselbe Zweiteilung wie im Ablauf.',
      'Bewusst ohne Preise: das Protokoll geht bei einer Reklamation an den Lieferanten und dokumentiert Mengen und Zustand. Der Briefkopf kommt von der Gesellschaft der Bestellung.',
      'Der Knopf „Prüfprotokoll" steht am Beleg in jedem Status — auch bei gebuchten und verworfenen Prüfungen bleibt der Nachweis abrufbar.',
    ],
  },
  {
    version: '3.401.0',
    date: '2026-07-29T19:00',
    title: 'Artikel-Suche: echtes Vollbild, spürbar schneller, Suche in den Warengruppen',
    tag: 'improvement',
    changes: [
      'Die Artikel-Suche nutzt jetzt den ganzen Bildschirm (vorher blieb ein Rand stehen) und das Suchfeld sitzt mittig im Kopf.',
      'Deutlich schnelleres Öffnen: die Trefferliste baut nur noch die sichtbaren Zeilen auf. Bei rund 1.600 Artikeln stehen etwa 18 Zeilen im Fenster statt 1.600 — der Dialog ist in etwa vier Zehntelsekunden da, unabhängig von der Größe des Artikelstamms.',
      'Behoben: Bei vielen Artikeln liefen Trefferliste und Warengruppen-Spalte unter die Fußzeile und wurden unten abgeschnitten.',
      'Die Warengruppen-Spalte hat ein eigenes Suchfeld — Bezeichnung oder Nummer eingeben, ↵ springt in die erste gefundene Gruppe. Bei vielen Warengruppen entfällt damit das Scrollen.',
      'Sucht man in einer Warengruppe und dort ist nichts dabei, obwohl es woanders Treffer gibt, erscheint jetzt „In allen Warengruppen suchen" — der noch gesetzte Gruppenfilter war bisher die häufigste Ursache für ein scheinbar erfolgloses Suchen.',
      'Tastatur überarbeitet: ↑/↓ wählt den Artikel, Bild ↑/Bild ↓ blättert seitenweise, Pos1/Ende springen an Anfang und Ende, ⌥/Alt+↑/↓ wechselt die Warengruppe. Die Zeilen sind absichtlich nicht einzeln per Tabulator erreichbar — bei tausenden Artikeln wären es sonst genauso viele Tab-Stopps.',
      'In der Vorschau führt „Artikelstamm öffnen" direkt zum Artikel — in einem neuen Tab, damit der Beleg mit seinen noch nicht gespeicherten Positionen offen bleibt.',
      'Bei sehr großen Warengruppen zeigt der Variantenvergleich bis zu 12 Varianten rund um die markierte und nennt den Rest, statt hunderte Zeilen aufzubauen.',
    ],
  },
  {
    version: '3.400.0',
    date: '2026-07-29T17:30',
    title: 'Neue Artikel-Suche: Warengruppen, große Vorschau, Variantenvergleich',
    tag: 'feature',
    changes: [
      'Die Artikel-Suche im Beleg-Editor ist jetzt ein fast bildschirmfüllender Dialog mit drei Spalten: links die Warengruppen, in der Mitte die Treffer, rechts dauerhaft die Vorschau des markierten Artikels. Das Auge-Symbol je Zeile entfällt — man muss die Vorschau nicht mehr einzeln öffnen.',
      'Die Trefferliste ist zweizeilig: Nummer und Bezeichnung stehen vollständig da (vorher wurden lange Namen mitten im Wort abgeschnitten), darunter freier Bestand mit Ampel und Hersteller, rechts EK- und VK-Preis nebeneinander.',
      'Kernstück ist der Variantenvergleich in der Vorschau: bei sechs Artikeln „Kunststoff-Kette NCS600/NCS650/NCS720S/…" steht je Variante nur noch der unterscheidende Teil des Namens, mit EK und VK direkt daneben. Der gemeinsame Namensanfang wird automatisch erkannt und weggelassen. Ein Klick springt zur Variante.',
      'Links navigiert man ohne Tippen durch die Warengruppen (echte Warengruppen-Namen aus dem Stamm, mit Trefferzahl); Artikel ohne Warengruppe sammelt ein eigener Eintrag. Warengruppen ohne Treffer bleiben sichtbar, damit man beim Suchen nicht die Orientierung verliert.',
      'Tastatur: ↑/↓ wählt den Artikel, ↵ übernimmt, Esc schließt. Was in der Zelle schon getippt war, steht als Suchbegriff bereits im Feld.',
    ],
  },
  {
    version: '3.399.0',
    date: '2026-07-29T15:30',
    title: 'Vier-Augen-Freigabe: Historie bleibt erhalten, Aufhebung nur bei echter Änderung',
    tag: 'improvement',
    changes: [
      'Wird ein freigegebener Beleg bearbeitet, verschwindet die Freigabe-Historie nicht mehr. Sie bleibt vollständig stehen und bekommt einen zusätzlichen Eintrag: wer wann etwas geändert hat, welche Bereiche betroffen waren (z. B. „Positionen, Termine") und dass die Freigabe dadurch erloschen ist.',
      'Die Freigabe erlischt außerdem nur noch, wenn der Beleg wirklich vom freigegebenen Stand abweicht. Den Editor öffnen und ohne Änderung speichern — oder ein Feld auf denselben Wert setzen — lässt die Freigabe jetzt bestehen; bisher genügte das Speichern an sich.',
      'Als inhaltliche Abweichung zählt alles, was auf dem Beleg steht oder Geld bewegt: Positionen, Summen, Kunde, Adressen, Termine, Lieferbedingungen, Steuer, Belegtexte, Gesellschaft und die Auswahl der gedruckten Felder. Projektzuordnung, Sachbearbeiter und die interne Notiz zählen bewusst nicht — das ist interne Organisation.',
      'Wird ein Beleg geändert, während eine Freigabe noch aussteht, bleibt die Anforderung offen und die Änderung erscheint als eigener Eintrag in der Historie — die Freigeber sehen so, dass sich der Stand seit ihrer Anfrage bewegt hat.',
      'Auch das bewusste Zurückziehen einer Freigabe („Ändern") steht jetzt als eigener Punkt in der Historie.',
    ],
  },
  {
    version: '3.398.0',
    date: '2026-07-29T15:00',
    title: 'Enter in der Artikel-Spalte öffnet die Artikel-Suche',
    tag: 'improvement',
    changes: [
      'Enter in der Artikel-Spalte führt jetzt immer zur Artikel-Suche, wenn die Eingabe nicht eindeutig ist — auch bei leerem Feld. Vorher sprang der Cursor dort stumm in die nächste Zeile, und die Suche war nur über die Lupe erreichbar. Unverändert: ist ein Vorschlag markiert oder die Artikelnummer exakt getroffen, wird der Artikel direkt übernommen.',
    ],
  },
  {
    version: '3.397.0',
    date: '2026-07-29T14:00',
    title: 'Bau-Tagesbericht mit Unterschrift auf dem iPad',
    tag: 'feature',
    changes: [
      'Jeder Tag im Baustellenverlauf ist jetzt ein vollwertiger Bau-Tagesbericht — mit laufender Nummer, Temperatur, Schicht, Behinderungen/Erschwernissen, besonderen Vorkommnissen und Regieleistungen. Bewusst kein zweites Formular: der Monteur trägt den Tag einmal ein, sonst stünden im Gesamtbericht und auf dem Tagesblatt irgendwann verschiedene Stunden.',
      'Das eingesetzte Personal wird namentlich erfasst — die Monteure des Einsatzes stehen als Knopf bereit, Rolle (Leitmonteur, Monteur, Schweißer, Elektriker, Kranführer …) und Stunden je Person daneben. Im PDF erscheint zusätzlich die gewohnte Gruppenaufstellung („3× Monteur, 24 Std."), die dafür niemand pflegen muss.',
      'Sobald jemand namentlich erfasst ist, zählen dessen Stunden für die Summe; das grobe Feld „Monteure" verschwindet dann, damit nicht zwei Zahlen für denselben Aufwand nebeneinanderstehen.',
      'Die Witterung lässt sich jetzt mehrfach auswählen — „sonnig, aber windig" ist auf der Baustelle der Normalfall, und „Regen + Frost" ist ein Behinderungsgrund.',
      'Neu: Unterschreiben direkt auf dem iPad. Bauherr und Bauleiter unterschreiben mit Finger oder Stift, dazu Name und Ort; der Schriftzug landet im PDF des Tages. Der Ort ist mit dem Standort der Baustelle vorausgefüllt und lässt sich überschreiben, wenn woanders unterschrieben wird.',
      'Mit der Unterschrift ist der Tag gesperrt — Stunden, Personal und Texte lassen sich nicht mehr ändern. Sonst wäre die Gegenzeichnung wertlos. Zum Korrigieren zieht man die Unterschrift zurück, dann ist der Tag wieder offen.',
      'Jeder Tag hat einen eigenen PDF-Knopf: ein Blatt im Layout des gewohnten Durchschreibblocks, das auf eine Seite passt. Nicht ausgefüllte Blöcke bekommen Schreiblinien — so lässt sich das Blatt auch ausdrucken und vor Ort mit der Hand ergänzen.',
    ],
  },
  {
    version: '3.396.0',
    date: '2026-07-29T10:00',
    title: 'Projektname überall neben der Projektnummer — plus Langtext im Wareneingang',
    tag: 'improvement',
    changes: [
      'Wo bisher nur eine Projektnummer stand, steht jetzt auch der Projektname: Verkaufs- und Bestellliste, Kontext-Leiste am Beleg, QM-Prozesse, Lagerbewegungen, Artikel-Belege und -Reservierungen, Montage-Aufgaben und Wartungsplan, Beleg-Vorschau in der Suche sowie der komplette Wareneingang.',
      'Wareneingangsprüfung am Tablet: Jede Position zeigt jetzt den Langtext der Bestellposition (mit Zeilenumbrüchen — Maße, Werkstoff, Ausführung) und das Projekt der Position. In der Kopfzeile steht zusätzlich das Projekt der Bestellung.',
      'Der Wareneingangsbeleg hat eine Projekt-Spalte in der Positionstabelle und nennt das Projekt im Untertitel; die Belegliste zeigt es als eigene Spalte.',
      'Bei Belegen (Bestellung, Wareneingang) ist der Projektname mitgespeichert — ein alter Beleg behält den Namen von damals. Überall sonst wird er live nachgeschlagen, sodass eine Umbenennung sofort überall sichtbar ist.',
    ],
  },
  {
    version: '3.395.0',
    date: '2026-07-29T09:30',
    title: 'Fix: ⌘/Strg+⇧+Enter („Neue Position") reagierte nicht',
    tag: 'fix',
    changes: [
      'War der Fokus bereits in einer Positionszelle (insbesondere im Artikel-Feld), fing die dortige Enter-/Pfeiltasten-Logik das Kürzel ab, bevor es das neue globale ⌘/Strg+⇧+Enter erreichte — sichtbar wurde entweder gar nichts oder ein Sprung in die falsche Zeile. Die zellenlokalen Kürzel lassen ⌘/Strg-Kombinationen jetzt konsequent durch.',
    ],
  },
  {
    version: '3.394.0',
    date: '2026-07-29T09:00',
    title: 'Baustellenbericht: tageweiser Verlauf für die Montage',
    tag: 'feature',
    changes: [
      'Der Baustellenbericht hat einen neuen Abschnitt „Baustellenverlauf": je Tag Datum, Wetter, Arbeit von/bis, Stunden, Reisezeit, Anzahl Monteure und die ausgeführten Arbeiten — genau die Tabelle aus der Word-Vorlage.',
      'Die Tage sind nach Kalenderwoche gegliedert (ein Einsatz dauert schnell drei Wochen), mit Wochensumme je Woche. „Tag hinzufügen" schlägt automatisch den Folgetag vor, beim ersten Tag den geplanten Beginn des Einsatzes.',
      'Trägt man „Arbeit von" und „Arbeit bis" ein, wird die Stundenzahl vorgeschlagen — aber nur, solange noch keine eingetragen ist: die Spanne kennt keine Pausen, die getippte Zahl bleibt maßgeblich.',
      'Oben im Abschnitt stehen die Summen: Tage, Arbeitsstunden, Reisestunden und Personenstunden (Stunden × Anzahl Monteure). Sie werden immer gerechnet und lassen sich nicht übertippen.',
      'Alles wird wie im übrigen Bericht automatisch beim Verlassen des Feldes gespeichert; die Bedienelemente sind für das iPad auf Fingergröße gebracht.',
      'Der Verlauf steht auch im PDF — als Tabelle mit wiederholtem Tabellenkopf über Seitenwechsel hinweg und einer Summenzeile am Ende.',
      'Ein fertiggestellter Bericht ist auch beim Verlauf gesperrt; zum Nachtragen muss er wie gewohnt erst wieder geöffnet werden.',
    ],
  },
  {
    version: '3.393.0',
    date: '2026-07-29T07:00',
    title: 'Benachrichtigungen führen jetzt direkt zum Vorgang, nicht nur zur Seite',
    tag: 'improvement',
    changes: [
      'Ein Klick (oder Tipp auf die Push-Meldung) landet jetzt genau dort, wo die Benachrichtigung herkam: Bei einer Aufgabe öffnet sich das Projekt im Register „Checkliste → Aufgaben" und die betreffende Aufgabe wird kurz hervorgehoben — vorher landete man auf der Projekt-Übersicht und musste die Aufgabe selbst suchen.',
      'Urlaubs- und Fehlzeitanträge führten bisher auf den Urlaubskalender, wo ein einzelner Antrag gar nicht auffindbar ist. Sie führen jetzt auf die Antragsliste, mit hervorgehobenem Antrag.',
      'Bei Reiseanträgen öffnet sich direkt die Detailansicht der gemeinten Reise, statt nur die Liste zu zeigen.',
      'Artikelanträge und alle anderen Listen heben die gemeinte Zeile hervor und scrollen sie in den Blick. Die Hervorhebung verschwindet nach ein paar Sekunden von selbst.',
      'Der Link bleibt in der Adresszeile stehen — Neuladen oder Weiterschicken führt wieder an dieselbe Stelle.',
    ],
  },
  {
    version: '3.392.0',
    date: '2026-07-29T06:15',
    title: 'Neues Beleg-Editor-Kürzel: Neue Position mit Fokus direkt in der Artikelsuche',
    tag: 'feature',
    changes: [
      'Zusätzliches Kürzel ⌘/Strg+⇧+Enter: legt eine neue Position an (bzw. nutzt die vorhandene leere Abschlusszeile) und springt mit dem Fokus direkt ins Artikel-Feld — sofort losschreiben und die Such-Trefferliste erscheint beim Tippen.',
    ],
  },
  {
    version: '3.391.0',
    date: '2026-07-28T18:00',
    title: 'Push-Einrichtung: falsches Schlüsselpaar wird jetzt benannt statt stillschweigend hingenommen',
    tag: 'fix',
    changes: [
      'Der häufigste Einrichtungsfehler bei Push ist ein Schlüsselpaar aus zwei verschiedenen Erzeugungsläufen — beide Werte sehen richtig aus, aber jede Benachrichtigung wird vom Push-Dienst abgewiesen, und auch Aus-/Einschalten am Gerät hilft nicht. Die Anwendung prüft die beiden Schlüssel jetzt beim Start gegeneinander und sagt im Klartext, wenn sie nicht zusammengehören — unter „Benachrichtigungen" und im Server-Log.',
      'Ebenfalls erkannt: abgeschnittene Schlüssel (beim Kopieren verloren gegangene Zeichen) und eine fehlerhafte Kontaktadresse. Letztere hätte bisher sogar den Start der gesamten Anwendung verhindert — ein Tippfehler in einer einzigen Einstellung hätte ProERP lahmgelegt.',
    ],
  },
  {
    version: '3.390.0',
    date: '2026-07-28T17:00',
    title: 'Fix: Push kam nicht an — veraltetes Geräte-Abo, dazu ein Test-Knopf',
    tag: 'fix',
    changes: [
      'Push-Benachrichtigungen kamen unter Umständen nicht an, obwohl der Schalter „eingeschaltet" zeigte. Ursache: War auf dem Gerät schon ein Abo aus einer früheren Einrichtung vorhanden, wurde es ungeprüft weiterverwendet — der Push-Dienst lehnte dann jede Zustellung ab, weil es zu einem anderen Schlüssel gehörte. Jetzt wird das alte Abo erkannt, verworfen und sauber neu angelegt.',
      'Neuer Knopf „Test" neben dem Schalter: schickt eine Testbenachrichtigung an die eigenen Geräte. Kommt sie nicht an, steht dort der echte Grund im Klartext — falscher Schlüssel, kein angemeldetes Gerät, oder fehlender Internetzugang des Servers zum Push-Dienst. Damit lässt sich die Einrichtung allein prüfen, ohne eine zweite Person.',
      'Im Server-Log steht jetzt bei jedem Versand eine Zeile „Push: n/m zugestellt". Vorher war nicht zu unterscheiden, ob nichts zu senden war oder alles still gescheitert ist.',
    ],
  },
  {
    version: '3.389.0',
    date: '2026-07-28T16:30',
    title: 'Wareneingang am Tablet: Lieferung prüfen, Sekretariat bucht',
    tag: 'feature',
    changes: [
      'Neuer Menüpunkt „Wareneingang" (Logistik) mit zwei Registern: „Erwartete Lieferungen" zeigt alle Bestellungen mit offener Menge — antippen startet die Prüfung, läuft schon eine, geht es dort weiter. Daneben die Liste aller Wareneingangsbelege mit Status und Abweichungszähler.',
      'Prüfstrecke im Vollbild, für das Tablet gebaut: eine Karte je Position mit großen Plus/Minus-Feldern, vorbelegt mit der offenen Restmenge — im Normalfall hakt man nur ab. Oben die Lieferschein-Nummer, unten der Fortschritt.',
      'Abweichung erkennt die App selbst aus der gezählten Menge (zu wenig/zu viel). „Beschädigt" meldet der Mitarbeiter mit einem Knopf; ein Tap zurück leitet die Abweichung wieder aus den Mengen ab.',
      'Foto direkt aus der Kamera (auf dem Tablet öffnet sich die rückwärtige Kamera), zugeordnet zur betroffenen Position. Fehlt bei einer Abweichung Foto oder Bemerkung, steht das an der Position UND unten in einer Leiste — abschließen lässt sich die Prüfung dann nicht.',
      'Der Beleg zeigt den Ablauf als Stufenleiste (Prüfung → Geprüft → Gebucht) mit Datum, dazu Positionen, Fotogalerie und Verlauf.',
      'Buchen bleibt beim Sekretariat: der Knopf „Lagerzugang buchen" erscheint nur mit dem entsprechenden Recht und nennt vorher, wie viele Positionen gebucht werden und wie viele beschädigt außen vor bleiben.',
    ],
  },
  {
    version: '3.388.0',
    date: '2026-07-28T15:30',
    title: 'Wareneingang: Prüfung an der Rampe (Grundlage im Hintergrund)',
    tag: 'feature',
    changes: [
      'Neuer Beleg „Wareneingang" (Nummer WE-JJJJ-NNNN): eine Prüfung je Lieferung zu einer Bestellung, mit Lieferschein-Nummer, gezählten Mengen je Position, Abweichungsart, Bemerkung und Fotos.',
      'Zwei Stufen wie beim Warenausgang: Lager/Technik prüfen und schließen ab, das Sekretariat bucht den Lagerzugang. Eigene Rechte dafür (prüfen und buchen sind getrennt), in den Standardrollen bereits verteilt.',
      'Abweichungen werden automatisch aus der gezählten Menge erkannt (zu wenig/zu viel). Beschädigte Ware muss der Mitarbeiter selbst melden — sie gilt als angekommen, wird aber NICHT in den Bestand gebucht und die Bestellposition bleibt offen.',
      'Bei einer Abweichung sind Foto (und bei Überlieferung/Schaden zusätzlich eine Bemerkung) Pflicht, sonst lässt sich die Prüfung nicht abschließen.',
      'Gebucht wird über denselben Weg wie bisher im Bestell-Dialog — Lagerzugang, Rückmeldung an die Projekt-Materialliste, Doppelbuchungsschutz und Protokoll bleiben unverändert.',
      'Die Oberfläche für das Tablet folgt im nächsten Schritt; sichtbar ist bisher noch nichts in der App.',
    ],
  },
  {
    version: '3.387.0',
    date: '2026-07-28T15:00',
    title: 'Push-Benachrichtigungen aufs Handy — auch wenn ProERP nicht offen ist',
    tag: 'feature',
    changes: [
      'ProERP kann jetzt echte Push-Benachrichtigungen aufs Gerät schicken: Freigabe-Anfragen, zugewiesene Aufgaben, entschiedene Urlaubs- und Reiseanträge, Artikelanträge — alles, was bisher nur in der Glocke stand, kommt auf Wunsch auch dann an, wenn die App gar nicht geöffnet ist. Einschalten unter „Benachrichtigungen", je Gerät einzeln (Handy, Tablet und Rechner können unabhängig voneinander angemeldet sein).',
      'Auf iPhone und iPad gibt es Push nur für die installierte App. Wer ProERP dort im normalen Safari-Tab öffnet, sieht statt eines toten Schalters die Anleitung: Teilen → „Zum Home-Bildschirm" → von dort öffnen. Danach erscheint der Schalter.',
      'Die Benachrichtigung zeigt denselben Text wie die Glocke und springt beim Antippen an die richtige Stelle — ein bereits offenes ProERP-Fenster wird dabei wiederverwendet statt einen neuen Tab zu öffnen. Mehrfache Meldungen zum selben Vorgang ersetzen einander, statt den Sperrbildschirm zuzumüllen.',
      'Push ist bewusst Beiwerk: Kommt eine Nachricht nicht durch, passiert im Hintergrund nichts Schlimmes — die Glocke bleibt die verlässliche Quelle, und keine Freigabe oder Buchung scheitert daran. Geräte, die sich abgemeldet haben (App gelöscht, Browserdaten geleert), räumt das System selbstständig weg.',
      'Für die Administration: Die Funktion ist erst aktiv, sobald ein Schlüsselpaar hinterlegt ist (Runbook „Push-Benachrichtigungen aktivieren"). Vorher zeigt die Oberfläche den Schalter gar nicht erst an.',
    ],
  },
  {
    version: '3.386.0',
    date: '2026-07-28T13:30',
    title: 'Neue Tastaturkürzel im Beleg-Editor (Belegblatt/Werkbank, Verkauf/Einkauf)',
    tag: 'feature',
    changes: [
      'Position duplizieren (⌘/Strg+⇧+D), löschen (⌘/Strg+⇧+X), Überschrift einfügen (⌘/Strg+⇧+H), Seitenumbruch einfügen (⌘/Strg+⇧+B) und ins Positionsraster springen (⌘/Strg+⇧+↓) — wirkt einheitlich in Belegblatt und Werkbank, für Verkaufs- und Einkaufsbelege gleichermaßen.',
      'Alle Kürzel stehen wie gewohnt im Spickzettel („?").',
    ],
  },
  {
    version: '3.385.0',
    date: '2026-07-28T13:00',
    title: 'Transporte: überarbeitete Oberfläche — Ablauf auf einen Blick, Kennzahlen, weniger Knöpfe',
    tag: 'improvement',
    changes: [
      'Der Ladeplan zeigt oben eine Stufenleiste (Entwurf → Geplant → Verladen → Abgefahren, bei eingehenden Transporten „Angekommen"/„Entladen abgeschlossen") mit dem jeweiligen Datum — vorher musste man Status-Badge und drei Datumsfelder einzeln zusammenlesen.',
      'Die Ladung steht jetzt als Kennzahlen im Kopf (LKWs, Container, Collis, Brutto- und Nettogewicht) statt als Fließtext; bei eingehenden Transporten die Zahl der zugeordneten Bestellungen.',
      'Die Transport-Liste bekommt Status-Chips mit Zählern: ein Tap filtert, und man sieht sofort, wie viele Transporte geplant, verladen oder abgefahren sind. Die Richtung ist ein Segment-Schalter (Alle / Ausgehend / Eingehend).',
      'Überfällige Termine sind in der Liste rot markiert (geplanter Termin in der Vergangenheit, Transport noch nicht abgefahren) — auf dem Handy mit ausgeschriebenem Hinweis.',
      'Je LKW bündelt ein „…"-Menü die Aktionen (Ladeliste, Fahrzeugdaten, Container, Entfernen) statt bis zu vier gleichrangiger Knöpfe; die Ladeliste in Deutsch/Englisch liegt ebenfalls in einem Menü. Die Ladung je LKW erscheint als kompakte Zahlen-Chips.',
      'Beim Verladen lassen sich jetzt alle freien Collis auf einmal auswählen; darunter steht, wie viele ausgewählt sind und wie schwer sie zusammen sind. Die Zeilen sind größer und damit auf dem Tablet besser triffbar.',
      'Nachfragen vor dem Entfernen eines LKW oder Containers laufen über einen richtigen Dialog statt über das Browser-Fenster.',
    ],
  },
  {
    version: '3.384.0',
    date: '2026-07-28T12:00',
    title: 'Bestell-PDF: Projekt-Bezug im Positions-Langtext (abschaltbar)',
    tag: 'feature',
    changes: [
      'Trägt eine Bestellposition ein eigenes Projekt, druckt das Bestell-PDF Projektnummer und -name jetzt als kleine Zusatzzeile über dem Positions-Langtext.',
      'Neuer Haken in den Belegeinstellungen („Weitere Belegangaben") schaltet den Ausdruck pro Bestellung ab — Standard: anzeigen.',
    ],
  },
  {
    version: '3.383.0',
    date: '2026-07-28T01:00',
    title: 'Fix: Belege wandeln ging gar nicht mehr („Internal Server Error")',
    tag: 'fix',
    changes: [
      'Das Umwandeln eines Belegs (Angebot → Auftrag, Auftrag → Rechnung/Lieferschein, Storno) endete ausnahmslos in einem „Internal Server Error". Ursache war die Sperre, die doppelte Folgebelege bei Doppelklick verhindert: Sie wurde technisch als Abfrage statt als Anweisung ausgeführt, und die Datenbank liefert dafür kein Ergebnis, das sich auslesen ließe. Eine Zeile — aber sie hat jede Umwandlung lahmgelegt. Im Einkauf steht dieselbe Sperre seit jeher korrekt.',
      'Warum das keiner der bestehenden Tests gefunden hat: Die Tests des Verkaufsmoduls arbeiten mit einer nachgebauten Datenbank-Schicht und können Fehler auf SQL-Ebene prinzipiell nicht sehen. Neu ist deshalb ein Test, der über HTTP gegen eine echte Datenbank wandelt — inklusive großem Angebot (60 Positionen), zweiter Wandlung (muss sauber abgelehnt werden), entfernter Gesellschaft und hinterherhinkendem Nummernkreis.',
      'Der Freigabe-Punkt in der Belegliste ist auf Wunsch wieder entfernt — der Freigabestand steht weiterhin im Beleg selbst und in der Freigabe-Historie.',
    ],
  },
  {
    version: '3.382.0',
    date: '2026-07-28T00:15',
    title: 'Verkauf: Freigabe erlischt bei Änderung, Freigabe-Ampel in der Liste, Positionen kopieren, Umwandeln-Fix',
    tag: 'feature',
    changes: [
      'Vier-Augen: Wird ein bereits freigegebener Beleg noch einmal geändert, erlischt die Freigabe und der Prozess beginnt von vorne. Beim Öffnen eines freigegebenen Belegs kommt der Hinweis „Dieser Beleg wurde bereits freigegeben" mit „Ändern" (löscht die bestehende Freigabe) und „Schliessen" (lässt alles, wie es ist) — bis dahin ist der Beleg schreibgeschützt.',
      'Der Reset greift auch bei Änderungen an den Positionen und unabhängig von der Oberfläche (serverseitiges Sicherheitsnetz). Ein reiner Statuswechsel auf „versendet" zählt nicht als Änderung — dafür wurde ja freigegeben. Freigeber und Anforderer bekommen eine Benachrichtigung, dass ihre Freigabe erloschen ist.',
      'In der Belegliste zeigt ein sehr kleiner Punkt vor der Belegnummer den Freigabestand: grün freigegeben, orange wartet auf Freigabe, rot nicht freigegeben (kräftig rot = abgelehnt). Der Tooltip nennt den Zustand im Klartext.',
      'Im Belegblatt lassen sich die Positionen jetzt in die Zwischenablage kopieren — per „Umschalt+Enter", über „Positionen kopieren" in der Fußzeile oder über „Kopieren" in der Auswahl-Leiste (dann nur die markierten Zeilen). Das Format ist dasselbe wie beim Einfügen aus Excel, das Kopierte lässt sich also direkt in einen anderen Beleg oder nach Excel einfügen.',
      'Fix: Ein Angebot ließ sich unter Umständen nicht in einen Auftrag wandeln und die Oberfläche zeigte nur „Umwandeln fehlgeschlagen." — die Meldung des Servers wurde verschluckt. Jetzt steht dort der echte Grund. Zusätzlich sind die drei Stolpersteine beseitigt, die das Wandeln blockieren konnten: eine entfernte Gesellschaft und ein gelöschter Kunde brechen den Vorgang nicht mehr ab (die Belegdaten liegen ohnehin als Kopie am Beleg), und ein Nummernkreis, der dem Bestand hinterherhinkt, wird automatisch weitergedreht statt in einer Doppelnummer zu enden.',
      '„Beleg wandeln" erscheint im Editor nur noch mit der Berechtigung dafür — vorher führte der Knopf ohne Recht in eine unverständliche Fehlermeldung.',
    ],
  },
  {
    version: '3.381.0',
    date: '2026-07-27T23:45',
    title: 'Fix: Bestellpool jetzt direkt bearbeitbar (ohne Status-Umweg über Entwurf)',
    tag: 'fix',
    changes: [
      'Bestellungen im Bestellpool ließen sich bisher nicht bearbeiten, ohne den Status auf „Entwurf" zu stellen — was die Bestellung aber sofort aus dem Pool herausnahm und unter „Bestellungen" erscheinen ließ. Das ist behoben: Positionen, Mengen, Preise und Kopfdaten (Termine, Notiz, Sachbearbeiter …) lassen sich jetzt direkt im Bestellpool ändern, ohne den Status anzufassen. Die Freigabe aus dem Pool bleibt eine bewusste, separate Aktion.',
      'Als Folge davon funktioniert jetzt auch der Artikel-Langtext bei Bestellungen wie im Verkauf: Beim Auswählen eines Artikels wird sein Langtext übernommen und bleibt frei editierbar (über das Aufklapp-Symbol an der Position) — bisher war das durch dieselbe Bearbeitungssperre blockiert.',
    ],
  },
  {
    version: '3.380.0',
    date: '2026-07-27T23:30',
    title: 'Buchhaltung und Anmeldung deutlich besser durch Tests abgesichert',
    tag: 'improvement',
    changes: [
      'Die beiden empfindlichsten Bereiche der Anwendung — Buchhaltung und Anmeldung/Berechtigungen — sind jetzt weit besser durch automatische Tests abgedeckt: Buchhaltung von 62 % auf 88 %, Anmeldung von 47 % auf 61 %. Rein interne Absicherung, keine sichtbare Änderung.',
      'Abgesichert sind unter anderem: die Wirtschaftlichkeits-Berechnung eines Projekts (Auftragswert, Marge, Fakturierungsgrad, Zahlungseingang), die Sitzungs-Tokens der Anmeldung samt Abwehr manipulierter oder unsignierter Tokens, und dass ein Token aus dem ersten Login-Schritt NICHT als vollwertige Sitzung durchgeht — der zweite Faktor lässt sich damit nicht umgehen.',
      'Ebenfalls neu geprüft: dass jede Datenbank-Abfrage in Zahlungen, Kassabuch und Benutzerverwaltung auf den eigenen Mandanten filtert und gelöschte Datensätze ausblendet. Dafür gibt es technisch kein automatisches Netz — fiel so eine Prüfung bei einer Umbauarbeit weg, hätte es niemand bemerkt.',
      'Und: Jeder Buchhaltungs-Endpunkt muss eine Rechteprüfung tragen, die auch wirklich ein Finanz-Recht ist. Eine vergessene Prüfung wäre besonders heimtückisch — alles würde fehlerfrei funktionieren, aber jeder angemeldete Nutzer käme an Zahlungen und Kassabuch.',
    ],
  },
  {
    version: '3.379.0',
    date: '2026-07-27T22:45',
    title: 'Versand → Transporte: eingehende Transporte mit Bestellbezug, Projektbezug, moderner Termin-Picker',
    tag: 'feature',
    changes: [
      'Das Modul „Versand" heißt jetzt „Transporte" (Nav-Gruppe Logistik, `/transporte`) und erfasst neben ausgehenden jetzt auch eingehende Transporte (Wareneingang-Logistik) — die Richtung wird beim Anlegen gewählt und bleibt danach fix.',
      'Eingehende Transporte ordnen je LKW eine oder mehrere Bestellungen zu (Suche über Bestellnummer/Lieferant, Hinzufügen/Entfernen) — statt Collis, wie bei ausgehenden Transporten. Der Status-Ablauf ist derselbe, nur die Beschriftung passt sich an: „Angekommen" statt „Verladen", „Entladen abgeschlossen" statt „Abgefahren".',
      'Ausgehende Transporte brauchen jetzt ein Projekt. Das Kandidaten-Panel zeigt standardmäßig nur Collis dieses Projekts — über „Collis von einem anderen Projekt wählen" lässt sich der Filter für die aktuelle Suche gezielt aufheben, ohne den Normalfall zu verwässern.',
      'Die Lieferadresse (bzw. bei eingehend die Lieferantenadresse) wird automatisch aus dem Projekt- bzw. Bestellbezug vorgeschlagen, sobald ausgewählt — bleibt danach frei editierbar.',
      'Der Termin (geplante Abfahrt/Ankunft) hat jetzt einen modernen Kalender-Picker mit Uhrzeit statt des einfachen Datumsfelds ohne Uhrzeit.',
    ],
  },
  {
    version: '3.378.0',
    date: '2026-07-27T22:30',
    title: 'Baustellenbericht: Ausgabe als PDF und „Fertigstellen"',
    tag: 'feature',
    changes: [
      'Der Baustellenbericht lässt sich jetzt als PDF ausgeben — mit Firmen-Briefkopf, den Projekt-Kopfdaten und allen ausgefüllten Abschnitten. Damit ist der Bericht versand- und ablagefähig; das Word-Dokument wird dafür nicht mehr gebraucht.',
      'Neu ist der Knopf „Fertigstellen": Er schließt den Bericht ab und schreibt die Projektangaben mit dem aktuellen Stand fest. Ändert später jemand das Projekt, bleibt der abgeschlossene Bericht so, wie er unterschrieben wurde. Danach ist er gesperrt; zum Korrigieren gibt es „Wieder öffnen".',
      'Kleinigkeiten aus der Druckprüfung: Das Baujahr erscheint jetzt als „1998" statt „1.998", und lange Angaben wie Kundenname, Ansprechpartner oder die Monteurliste werden im PDF nicht mehr abgeschnitten.',
    ],
  },
  {
    version: '3.377.0',
    date: '2026-07-27T22:00',
    title: 'Suche findet Nummern jetzt auch ohne Bindestriche und Punkte',
    tag: 'feature',
    changes: [
      'Neu: Wer „P1001" eingibt, findet das Projekt „P-1001". Wer „123456789012" eingibt, findet den Artikel „1234.56789.012". Bisher musste man die Trennzeichen genau so tippen, wie sie gespeichert sind — was bei Artikelnummern kaum jemand tut.',
      'Das gilt für Projektcodes, Kunden- und Artikelnummern, Verkaufsbelege, Bestellungen, Fertigungsaufträge, Montageaufgaben, Wartungen, Rechnungsnummern und Personalnummern.',
      'Teilstücke funktionieren ebenfalls: „5678901" findet „1234.56789.012".',
      'Auch abweichende Trennzeichen sind abgedeckt: „1234-56789-012" findet „1234.56789.012". Ebenso die UID ohne Leerzeichen („ATU12345678" findet „ATU 12345678") und Telefonnummern.',
      'Die Zusatzsuche greift nur, wenn die Eingabe wie eine Nummer aussieht — mit Buchstaben ab vier Zeichen und drei Ziffern, rein numerisch ab sechs Ziffern. Die bloße Eingabe „0001" löst sie also nicht aus, denn die würde in jeder Liste den ersten Datensatz jedes Jahres treffen. Eine normale Wortsuche wird nicht langsamer.',
      'Gefundene Nummern-Treffer stehen immer VORNE in der Trefferliste. Ohne das könnten sie von aktuelleren Freitext-Treffern verdrängt werden — der gesuchte Datensatz wäre gefunden und trotzdem nicht sichtbar.',
    ],
  },
  {
    version: '3.376.0',
    date: '2026-07-27T21:15',
    title: 'Neu: Baustellenbericht digital (Schritt 1)',
    tag: 'feature',
    changes: [
      'Der Baustellenbericht („PROBIG Baustelle") lässt sich jetzt direkt im ERP ausfüllen statt in Word: In der Montage-Liste öffnet der Knopf „Bericht" eine Vollbild-Seite, die für das iPad auf der Baustelle gemacht ist. Jede Eingabe wird automatisch gespeichert, sobald man das Feld verlässt — oben rechts sieht man jederzeit, ob alles gesichert ist.',
      'In diesem ersten Schritt enthalten: Baujahr, gefahrene Kilometer und Fazit, der Zweck des Besuchs (Montage, Wartung, Abnahme … als antippbare Auswahl), Anreise und Unterkunft sowie der Sicherheitsblock (Baustellenaudit, Unterweisung, Sicherheitskontrolle).',
      'Angaben, die schon am Projekt stehen — Auftraggeber, Ansprechpartner, Projektleitung, Anzahl Becken, Beckenmaße, Wasserspiegel, Anlagentyp — werden oben angezeigt und müssen nicht erneut eingetippt werden. Geändert werden sie weiterhin am Projekt; ein Link führt direkt dorthin.',
      'Als Nächstes folgen: Ausgabe als PDF, der tageweise Baustellenverlauf, die Becken- und Zustandsangaben sowie die Listen (Störungen, fehlende Teile, zu erledigen).',
    ],
  },
  {
    version: '3.375.0',
    date: '2026-07-27T20:30',
    title: 'Neu: Versand — Ladeplan für Collis, Container und LKWs',
    tag: 'feature',
    changes: [
      'Neuer Navigationspunkt „Versand" (unter Logistik): Eine Sendung bündelt Collis aus mehreren Kommissionieraufträgen — also auch aus verschiedenen Projekten — auf einen oder mehrere LKWs. Die Liste zeigt Ziel, Status, die beteiligten Projekte und die Ladung.',
      'Ladeplan: Pro Sendung legst du LKWs an (Kennzeichen, Spedition, Fahrer als Freitext) und darin optional Container. Collis kommen entweder in einen Container ODER liegen lose direkt auf dem LKW — für lange Teile wie Rohre oder Rahmen, die in keinen Container passen.',
      'Collis zuordnen: Ein Panel zeigt alle noch nicht verladenen Collis (mit Projekt und Kommissionierauftrag, damit sie unterscheidbar sind). Auswählen, Ziel wählen, verladen. „Ausladen" macht ein Colli wieder frei. Ein Colli kann nur in EINER Sendung liegen — ein Umhängen aus einer anderen Sendung wird abgelehnt statt still ausgeführt.',
      'LKW-Fotos als Verladenachweis: Pro LKW lassen sich Fotos hinterlegen — damit ist später belegbar, in welchem LKW welche Collis verladen wurden. Fotos können nie gelöscht werden und sind auch nach dem Verladen noch möglich.',
      'Gewichte und Maße werden automatisch summiert (Colli → Container → LKW → Sendung), inklusive Container-Leergewicht. Fehlt bei einem Colli das Gewicht, wird die Summe als Untergrenze mit „≥" gekennzeichnet, statt eine zu kleine Zahl wie die echte Gesamtlast aussehen zu lassen.',
      'Ladeliste als PDF — für die ganze Sendung oder für einen einzelnen LKW, jeweils auf Deutsch und Englisch.',
      'Status-Ablauf Entwurf → Geplant → Verladen → Abgefahren. Ab „Verladen" ist der Ladeplan gesperrt, damit belegbar bleibt, was tatsächlich verladen wurde; das Wiederöffnen ist ein eigenes Recht und wird protokolliert.',
      'Hinweis: Fotos an einem LKW sind erst möglich, sobald dort mindestens ein Colli liegt (das Foto wird intern am Kommissionierauftrag des Ladeguts verankert).',
    ],
  },
  {
    version: '3.374.0',
    date: '2026-07-27T19:30',
    title: 'Kommissionieren am Tablet: Colli-Übersicht mit Spitzname und Foto je Packstück',
    tag: 'feature',
    changes: [
      'Der Knopf „Collis" in der Kopfzeile öffnet jetzt eine Übersicht aller Packstücke des Auftrags (mit Anzahl am Knopf) — bisher legte er kommentarlos ein weiteres Colli an. Neue Collis werden weiterhin dort angelegt, nur eben sichtbar aus der Liste heraus.',
      'Jedes Colli lässt sich mit einem frei wählbaren Spitznamen versehen („Große Holzkiste") — damit beim Packen nichts verwechselt wird. Die Colli-Nummer bleibt daneben bestehen, Beleg und Packliste beziehen sich weiterhin darauf. Der Spitzname ist auch im Auftrag unter „Packstücke" sichtbar und änderbar.',
      'Je Colli lässt sich direkt aus der Liste ein Foto aufnehmen; bereits vorhandene Fotos erscheinen als Vorschau. Collis ohne Foto sind markiert — praktisch, weil zum Abschließen jedes Packstück mindestens ein Foto braucht.',
      'Die Liste zeigt außerdem, wie viele Positionen auf dem jeweiligen Colli liegen (inklusive der auf mehrere Collis aufgeteilten Positionen).',
    ],
  },
  {
    version: '3.373.0',
    date: '2026-07-27T19:00',
    title: 'Suchmaschine ausgebaut — die Suche läuft direkt auf der Datenbank',
    tag: 'improvement',
    changes: [
      'Die App hatte einen separaten Suchdienst (Meilisearch) eingebaut. Die Prüfung hat ergeben: Er läuft seit dem Serverumzug Ende Juni gar nicht mehr — gesucht wurde die ganze Zeit über die Datenbank, und es ist niemandem aufgefallen. Der ungenutzte Dienst ist jetzt vollständig entfernt: ein Baustein weniger, der gepflegt werden muss und der so aussah, als wäre er in Betrieb.',
      'Für die Suche selbst ändert sich dadurch nichts — sie hat schon vorher so gearbeitet. Ein Vorteil kommt hinzu: Ohne Suchindex können Treffer nicht mehr veralten, jede Suche liest den aktuellen Stand.',
      'Ebenfalls entfallen: die Funktion „Suchindex neu aufbauen". Es gibt keinen Index mehr, der nachgezogen werden müsste. Das zugehörige Recht ist aus der Rechte-Übersicht verschwunden.',
      'Offen und bewusst benannt: Eine Tippfehler-Toleranz („Begen" findet „Beggen") kann die Datenbanksuche nicht. Falls das im Alltag fehlt, lässt sich das direkt in PostgreSQL nachrüsten — ohne einen zweiten Dienst.',
    ],
  },
  {
    version: '3.372.0',
    date: '2026-07-27T17:00',
    title: 'Fehlerhafte Server-Einstellungen fallen jetzt beim Start auf, nicht im Betrieb',
    tag: 'improvement',
    changes: [
      'Die Anwendung prüft beim Start ihre Einstellungen. Ein Zahlendreher oder Tippfehler in einer Server-Einstellung führte bisher dazu, dass der Wert stillschweigend ignoriert wurde — der Fehler fiel erst Tage später im laufenden Betrieb auf, und dann als Rätsel.',
      'Beispiel aus der Praxis: Steht in der Mandanten-Kennung ein falscher Wert, filtert jede Abfrage auf einen nicht existierenden Mandanten — die Anwendung erscheint dann komplett leer, ohne jede Fehlermeldung. Solche Fälle brechen den Start jetzt ab, mit klarem Hinweis auf die betroffene Einstellung.',
      'Bei einem Update ist das die sichere Richtung: Bricht der Start ab, läuft die bisherige Version einfach weiter, statt mit falschen Werten hochzufahren.',
      'Weniger kritische Fälle erzeugen nur eine Warnung im Protokoll und lassen den Start durch — etwa eine halb ausgefüllte Anbindung an die Dateiablage, die dann unbemerkt in den Rückfallmodus geht.',
      'In den Meldungen stehen ausschließlich die Namen der Einstellungen, niemals deren Werte — Passwörter landen so nicht im Protokoll.',
    ],
  },
  {
    version: '3.371.0',
    date: '2026-07-27T16:00',
    title: 'Referenzen-Karte: Detail-Vorschau erscheint wieder, neuer Hover-Tooltip',
    tag: 'fix',
    changes: [
      'Behoben: Auf der Referenzen-Karte öffnete der Klick auf einen Pin die Detail-Vorschau unsichtbar HINTER der Karte — die Seite wirkte dabei „eingefroren", weil sich gleichzeitig nichts mehr anklicken ließ (erst Escape half). Grund war eine Ebenen-Kollision mit der Kartenbibliothek; betroffen waren auch die Filter-Ausklappmenüs und Hinweis-Meldungen auf der Karte. Alle erscheinen jetzt korrekt über der Karte.',
      'Neu: Beim Überfahren eines Pins mit der Maus erscheint ein Tooltip mit Kunde und Standort — so lässt sich eine Referenz erkennen, ohne sie anzuklicken.',
    ],
  },
  {
    version: '3.370.0',
    date: '2026-07-27T15:00',
    title: 'App-Cockpit zeigt, ob die 2FA-Geheimnisse verschlüsselt sind',
    tag: 'feature',
    changes: [
      'Neuer Baustein im App-Cockpit: „2FA-Geheimnisse (Verschlüsselung)". Er zeigt, ob der Verschlüsselungs-Schlüssel auf dem Server hinterlegt ist und wie viele Konten ihr Authenticator-Geheimnis noch unverschlüsselt in der Datenbank liegen haben.',
      'Beides gehört zusammen: Bereits vorhandene Geheimnisse werden NICHT nachträglich verschlüsselt — das passiert nur, wenn jemand seine 2FA neu einrichtet. Ein hinterlegter Schlüssel allein bedeutet also nicht, dass nichts mehr im Klartext steht. Genau das war bisher von außen nicht erkennbar.',
      'Die Ampel steht auf Grün, wenn der Schlüssel gesetzt ist und kein Konto mehr im Klartext liegt; auf Gelb, solange ein Rest offen ist — mit Angabe, wie viele Konten betroffen sind.',
    ],
  },
  {
    version: '3.369.0',
    date: '2026-07-27T13:00',
    title: 'Belege tragen jetzt den Briefkopf der richtigen Gesellschaft',
    tag: 'fix',
    changes: [
      'Kommissionierbeleg, Lieferschein und Packliste wurden bisher IMMER mit der Standard-Gesellschaft als Absender gedruckt — auch wenn das Projekt zu einer anderen Gesellschaft gehörte. Die Belegnummer war schon richtig (sie enthält die Ziffer der Gesellschaft), nur der Briefkopf nicht. Da diese Belege zum Kunden gehen, war das der wichtigste Fall.',
      'Ebenfalls korrigiert: Projektbericht und QM-Report nehmen jetzt die Gesellschaft des zugehörigen Projekts.',
      'Gibt es am Projekt keine Gesellschaft, bleibt es wie bisher bei der Standard-Gesellschaft. Eine inaktiv gestellte Gesellschaft druckt weiterhin auf Altbelegen — damit ein nachgedrucktes Dokument denselben Absender zeigt wie das Original.',
      'Referenzliste, Bedarfsvorschau und Kassabuch behalten bewusst die Standard-Gesellschaft: Die Referenzliste wertet Projekte mehrerer Gesellschaften gemeinsam aus, und Bedarfsvorschau wie Kassabuch tragen im Datenmodell (noch) keine Gesellschaft.',
    ],
  },
  {
    version: '3.368.0',
    date: '2026-07-27T11:00',
    title: 'Automatischer Klick-Test der Anwendung vor jeder Veröffentlichung',
    tag: 'improvement',
    changes: [
      'Neu: Ein Testroboter bedient die Anwendung vor jeder Veröffentlichung wie ein Nutzer und meldet Fehler, bevor sie jemanden treffen. Er ruft die neun wichtigsten Seiten auf (Dashboard, Projekte, Verkauf, CRM, Beschaffung, Kommissionierung, Buchhaltung, Personen, Administration) und schlägt Alarm, sobald eine davon nicht mehr sauber erscheint.',
      'Zusätzlich geprüft: Ohne Anmeldung ist wirklich nichts von der Anwendung sichtbar, die Suche (⌘K) öffnet sich, und die Vier-Augen-Freigabe im Beleg-Editor verhält sich richtig — inklusive der Regel, dass der Ersteller seinen eigenen Beleg nicht selbst freigeben kann.',
      'Rein interne Absicherung, keine sichtbare Änderung in der Anwendung. Bisher konnte ein Anzeigefehler erst auffallen, wenn ihn jemand im Alltag entdeckt hat.',
    ],
  },
  {
    version: '3.367.0',
    date: '2026-07-24T15:00',
    title: 'Rollenänderungen brauchen jetzt die Freigabe eines zweiten Administrators',
    tag: 'feature',
    changes: [
      'Ändert ein Administrator die Rollen eines Benutzers, wirkt das nicht mehr sofort: Die Änderung wird als Antrag eingereicht und erst wirksam, wenn ein ZWEITER Administrator sie freigibt (Vier-Augen-Prinzip). Damit kann sich niemand mehr im Alleingang Rechte verschaffen.',
      'Offene Anträge stehen oben in der Rollen-Verwaltung: „Freigeben" oder „Ablehnen" (mit Begründung). Den eigenen Antrag kann man nicht selbst freigeben — nur zurückziehen. Alle Beteiligten werden über die Glocke benachrichtigt.',
      'Schutz gegen Selbst-Aussperrung: Gibt es nur einen einzigen Administrator, greift die Rollenänderung weiterhin direkt — sonst könnte sie niemand freigeben.',
      'Passwort- und 2FA-Zurücksetzen bleibt bewusst sofort wirksam (im Notfall muss schnell geholfen werden können), löst aber weiterhin eine Benachrichtigung an die übrigen Administratoren aus.',
    ],
  },
  {
    version: '3.366.0',
    date: '2026-07-24T13:00',
    title: 'Vier-Augen-Transparenz: Admins erfahren von Eingriffen an fremden Konten',
    tag: 'feature',
    changes: [
      'Ändert ein Administrator die Rollen eines anderen Benutzers oder setzt dessen Passwort bzw. zweiten Faktor (2FA) zurück, bekommen alle übrigen Administratoren jetzt automatisch eine Benachrichtigung (Glocke) — mit Angabe, wer was an welchem Konto geändert hat.',
      'Bewusst als Meldung statt als Sperre: Ein Passwort-Reset muss im Notfall sofort möglich sein (jemand ist ausgesperrt), darf aber nicht unbemerkt bleiben. Zusammen mit dem Änderungsprotokoll ist damit jeder Eingriff in fremde Konten nachvollziehbar.',
      'Keine Meldung bei Änderungen am eigenen Konto und nicht an das betroffene Konto selbst (das merkt den Eingriff ohnehin direkt).',
    ],
  },
  {
    version: '3.365.0',
    date: '2026-07-24T12:15',
    title: 'Sicherheit: 2FA-Geheimnisse verschlüsselbar, Berechtigungsprüfung abgesichert',
    tag: 'improvement',
    changes: [
      'Die Authenticator-Geheimnisse (2FA/TOTP) können jetzt verschlüsselt in der Datenbank abgelegt werden (AES-256-GCM). Aktiviert wird das über einen Schlüssel in der Server-Konfiguration; ohne Schlüssel bleibt alles wie bisher (rückwärtskompatibel), bereits vorhandene Geheimnisse bleiben lesbar.',
      'Die zentrale Berechtigungsprüfung (welche Aktion darf wer) ist jetzt durch automatische Tests abgesichert — inklusive der Fälle „Recht fehlt → 403" und „ein Recht gewährt nicht automatisch ein anderes". Rein interne Absicherung, keine sichtbare Änderung.',
    ],
  },
  {
    version: '3.364.0',
    date: '2026-07-24T11:30',
    title: 'Datenschutz & Revisionssicherheit: Audit-Log wird aufbewahrt, Gesundheitsdaten geschützt',
    tag: 'improvement',
    changes: [
      'Das Audit-Log (Änderungsprotokoll über Buchhaltungs-, Projekt-, Bestell- und Personaldaten) wird jetzt unbefristet aufbewahrt statt nach 30 Tagen automatisch gelöscht — entsprechend der gesetzlichen Aufbewahrungspflicht (GoBD/§147 AO) und der revisionssicheren („unveränderlichen") Auslegung des Protokolls.',
      'Der Freitext-Grund einer Abwesenheit (kann Gesundheitsangaben wie Krankmeldung/Arzt enthalten, Art. 9 DSGVO) wird bei Änderungen nicht mehr im Klartext ins Änderungsprotokoll geschrieben — protokolliert wird nur noch, dass der Grund geändert wurde.',
    ],
  },
  {
    version: '3.363.0',
    date: '2026-07-24T10:30',
    title: 'Digitales Kommissionieren: kumulierte Liste nach Hauptwarengruppe sortiert',
    tag: 'improvement',
    changes: [
      'Im kumulierten Modus des digitalen Kommissionierens sind die Artikel jetzt aufsteigend nach ihrer Hauptwarengruppe geordnet — genau wie auf dem gedruckten „Kommissionierbeleg kumuliert", sodass sich Tablet-Ansicht und Ausdruck 1:1 abgleichen lassen.',
      'Innerhalb einer Hauptwarengruppe wird zusätzlich nach Artikelnummer sortiert (numerisch); Freitext-Positionen ohne Artikel erscheinen am Ende.',
    ],
  },
  {
    version: '3.362.0',
    date: '2026-07-24T10:00',
    title: 'Sicherheits-Audit: Härtung gegen Angreifer',
    tag: 'fix',
    changes: [
      'Datei-Downloads (Anhänge, Zeichnungen, Fotos) sind gegen eingeschleuste Skripte gehärtet: hochgeladene SVG/HTML-Dateien werden nicht mehr im Browser dargestellt, sondern nur noch als Download ausgeliefert (verhindert Stored-XSS/Token-Diebstahl).',
      'Der serverseitige Foto-Import lädt nur noch von öffentlich erreichbaren Adressen — interne Dienste/Cloud-Metadaten sind blockiert (SSRF-Schutz).',
      'Nach einem Passwortwechsel oder einem Passwort-/2FA-Reset durch die Verwaltung werden bestehende Anmeldungen sofort beendet — ein evtl. gestohlenes Sitzungs-Token läuft nicht mehr bis zu 12 Stunden weiter. Hinweis: Nach dem Ändern des eigenen Passworts muss man sich neu anmelden.',
      'Serverfehler geben keine internen Details (Datenbank-/Pfadangaben) mehr an den Client; die Fehlermeldung bleibt mit Ablaufverfolgung nur im Server-Log.',
      'Weitere Härtung: Datei-Uploads mit Größen-/Anzahllimits, gedeckelter Referenzen-Export, zusätzliche Sicherheits-Header, aktualisierte Abhängigkeiten. Details siehe docs/security-audit-2026-07-24.md.',
    ],
  },
  {
    version: '3.361.0',
    date: '2026-07-24T09:45',
    title: 'Digitales Kommissionieren: Packstück-Aufteilung auch im kumulierten Modus',
    tag: 'feature',
    changes: [
      'Im Fokus einer kumulierten Artikel-Gruppe lässt sich die Menge jetzt genauso auf mehrere Collis aufteilen wie zuvor bei Einzelpositionen (1:1-Modus) — „Aufteilen" öffnet die Tranchen-Liste (Menge × Colli).',
      'Die für die ganze Gruppe erfasste Aufteilung wird automatisch und exakt auf die einzelnen Bedarfszeilen verteilt (die letzte Position nimmt den Rest auf) — die Summen je Colli stimmen dabei genau, und alle Positionen werden in einem Zug atomar gespeichert.',
      'Eine aufgeteilte Gruppe wird in der Liste mit „mehrere Collis" gekennzeichnet; „Aufteilung aufheben" setzt die ganze Gruppe wieder auf eine einfache Packstück-Zuordnung zurück.',
    ],
  },
  {
    version: '3.360.0',
    date: '2026-07-24T09:30',
    title: 'Vier-Augen-Freigabe: nachvollziehbare Historie am Beleg',
    tag: 'improvement',
    changes: [
      'Jeder Verkaufsbeleg zeigt jetzt eine lesbare Freigabe-Historie: wer wann die Freigabe angefordert hat, welche Personen als Freigeber gewählt wurden und wer wann freigegeben bzw. mit welcher Begründung abgelehnt hat — mit Klarnamen statt Kürzeln.',
      'Auch Ablehnungen sind vollständig nachvollziehbar: Ablehnende Person und Zeitpunkt werden zusätzlich am Beleg gespeichert (bisher nur im Audit-Log).',
      'Der Audit-Log bleibt weiterhin die vollständige, unveränderliche Spur aller Freigabe-Aktionen — die Anzeige am Beleg ist die schnelle Übersicht direkt im Vorgang.',
    ],
  },
  {
    version: '3.359.0',
    date: '2026-07-23T23:55',
    title: 'Neu: Vier-Augen-Freigabe für Verkaufsbelege',
    tag: 'feature',
    changes: [
      'Auf jedem Angebot, jeder Auftragsbestätigung und Rechnung gibt es im Editor jetzt einen Button „4-Augen": Damit kannst du vor dem Versand eine Freigabe anfordern und dabei selbst eine oder mehrere Personen als Freigeber auswählen. Die Gewählten bekommen eine Benachrichtigung (Glocke).',
      'Eine Freigabe genügt: Ein gewählter Freigeber (oder ein Admin) gibt den Beleg frei oder lehnt ihn mit Begründung ab. Der Ersteller kann seinen eigenen Beleg nicht selbst freigeben (Vier-Augen). Nach einer Ablehnung landet der Beleg wieder im Entwurf und kann überarbeitet und erneut angefordert werden.',
      'Solange eine Freigabe aussteht oder abgelehnt wurde, lässt sich der Beleg nicht auf „Versendet" setzen. Die Freigabe ist freiwillig (opt-in) — wer sie nicht anfordert, arbeitet wie bisher. Bearbeiten und PDF-Export bleiben jederzeit möglich.',
      'Freigabeberechtigt sind standardmäßig Administratoren, die Projektleitung und Sekretariat+ — weitere Rollen kann ein Admin über die Rechteverwaltung („Verkaufsbeleg-Freigabe") ergänzen.',
    ],
  },
  {
    version: '3.358.0',
    date: '2026-07-23T23:30',
    title: 'Reisekosten: Grenzübertritt klarer erfasst',
    tag: 'improvement',
    changes: [
      'Der Grenzübertritt gehört zur Reise, nicht zu jedem einzelnen Tag: „Grenze Hinfahrt" erscheint jetzt nur noch am ersten, „Grenze Rückfahrt" nur am letzten Reisetag (bei eintägiger Reise weiterhin beides).',
      'Zwischentage zeigen keinen Grenzübertritt mehr, sondern einen kurzen Hinweis — das beseitigt die irreführenden Doppelfelder, bei denen unklar war, was passiert, wenn man die Grenze nur in eine Richtung überquert.',
    ],
  },
  {
    version: '3.357.0',
    date: '2026-07-23T23:15',
    title: 'Zeit-Import: Gesellschaften mappen + ProERP-Projekt per Suche wählen',
    tag: 'improvement',
    changes: [
      'Firmen werden jetzt sauber zugeordnet: eigener Abschnitt „Firmen → Gesellschaften" mit Auswahl je Firma (AT/DE erkennt der Abgleich automatisch über den Ländercode; „PROBIG Holding"/„PRO GmbH" ordnest du selbst zu).',
      'Projekte lassen sich jetzt gezielt mit einem bestehenden ProERP-Projekt verknüpfen — über ein Such-Pop-up („ProERP-Projekt wählen …") statt nur der automatisch erkannten Zuordnung. Alternativ weiterhin: als neues Zeit-Projekt anlegen oder überspringen.',
    ],
  },
  {
    version: '3.356.0',
    date: '2026-07-23T22:35',
    title: 'Benachrichtigungen bei Urlaubs- und Reiseanträgen',
    tag: 'feature',
    changes: [
      'Ein neuer Urlaubs-/Fehlzeit- oder Reiseantrag benachrichtigt jetzt automatisch alle, die ihn genehmigen dürfen (leave.manage bzw. travel.manage) — sichtbar in der Glocke.',
      'Wird ein Antrag genehmigt oder abgelehnt, bekommt der Antragsteller eine Benachrichtigung über die Entscheidung.',
    ],
  },
  {
    version: '3.355.0',
    date: '2026-07-23T22:15',
    title: 'Zeiterfassung: Import aus der Alt-App — Zuordnen & Importieren (Schritt 2)',
    tag: 'feature',
    changes: [
      'Aus dem Prüfbericht wird jetzt ein echter Import: Funktions-Postfächer per Auswahl einem Mitarbeiter zuordnen und je Projekt entscheiden — bestehendes Zeit-Projekt, mit ProERP-Projekt verknüpfen, neu als Zeit-Projekt anlegen oder überspringen (Vorbelegung aus dem automatischen Abgleich).',
      'Der Import ist wiederholbar: Jede Zeile bekommt einen festen Schlüssel aus ihren Quelldaten — ein erneuter Lauf legt keine Dubletten an, sondern überspringt bereits importierte Einträge.',
      'Zeiten (lokale AT/DE-Zeit) werden korrekt nach UTC umgerechnet, Kategorie/Tätigkeit/Kostengruppe/Firma als Snapshot übernommen und — sofern ein Verrechnungssatz hinterlegt ist — die Ist-Kosten berechnet. Am Ende zeigt ein Ergebnis, wie viele Einträge angelegt, übersprungen oder als Dublette erkannt wurden.',
    ],
  },
  {
    version: '3.353.0',
    date: '2026-07-23T21:15',
    title: 'Zeitraum-Auswahl im „Booking.com"-Stil überall',
    tag: 'improvement',
    changes: [
      'Überall, wo ein Von–Bis-Zeitraum gewählt wird, gibt es jetzt den durchgängigen Kalender-Zeitraum-Picker (zwei Monate nebeneinander, Klick auf Start und Ende) statt zweier getrennter Datumsfelder.',
      'Umgestellt: Fehlzeiten-Antrag (Urlaub/Krankheit), Lager-Berichte, Zeiterfassungs-Liste & -Analyse, Audit-Log, Lagerbewegungen, Montage-Termin und die Belegsuche beim Kunden.',
    ],
  },
  {
    version: '3.352.0',
    date: '2026-07-23T21:00',
    title: 'Benachrichtigungen: eigene Seite, Filter, Wegwischen, Toast bei Neuem',
    tag: 'feature',
    changes: [
      'Neue Seite „Benachrichtigungen" (über „Alle ansehen" in der Glocke): volle Historie statt nur der letzten 10, mit Filter (nur ungelesene / nach Typ) und „Mehr laden".',
      'Einzelne Benachrichtigungen lassen sich jetzt wegwischen (löschen) — nicht nur „alle als gelesen".',
      'Kommt während der Nutzung eine neue Benachrichtigung rein, erscheint kurz ein Hinweis (Toast), statt dass man es erst beim Blick auf die Glocke merkt.',
    ],
  },
  {
    version: '3.351.0',
    date: '2026-07-23T20:00',
    title: 'Zeiterfassung: Import aus der Alt-App — Trockenlauf/Analyse',
    tag: 'feature',
    changes: [
      'Neuer Bereich „Zeit-Import" (unter „Personal & Zeit", nur für Zeiterfassungs-Admins): Man lädt den CSV-Export der alten Zeit-App hoch und bekommt einen Prüfbericht — es wird noch NICHTS gespeichert (Schritt 1, der eigentliche Import folgt).',
      'Der Bericht zeigt die Eckdaten (Zeilen, Stunden, Zeitraum, laufende Timer) und den automatischen Abgleich: Kategorien, Tätigkeiten, Kostengruppen und Firmen werden per Name direkt erkannt.',
      'Für die zwei Dimensionen, die eine Zuordnung brauchen, listet der Bericht Klartext auf: welche Nutzer-E-Mails zu einem Mitarbeiter passen (und welche nicht, z. B. Funktions-Postfächer), und welche Projektnamen zu einem bestehenden ProERP- oder Zeit-Projekt passen bzw. neu angelegt werden müssten.',
    ],
  },
  {
    version: '3.350.0',
    date: '2026-07-23T19:30',
    title: 'Referenzen-Karte neu gestaltet + Detail-Vorschau',
    tag: 'improvement',
    changes: [
      'Die Karte im neuen, aufgeräumten Design: bildschirmfüllend mit schwebender Suche, Filter-Panel und Trefferzähler, umschaltbaren Kartenthemen (Hell / Dunkel / Satellit) und einer Legende.',
      'Marker jetzt nach Kategorie eingefärbt und beschriftet — Kommunal (M, blau), Industriell (I, orange), Kläranlage (STP, lila) — mit dezentem Leuchteffekt.',
      'Klick auf einen Marker (oder eine Listenzeile) öffnet jetzt eine Detail-Vorschau als Fenster statt sofort zur Projektseite zu springen — von dort führt „Zum Projekt öffnen" bei Bedarf weiter.',
    ],
  },
  {
    version: '3.349.0',
    date: '2026-07-23T18:30',
    title: 'Referenzen überarbeitet: Karte lädt jetzt, Vollbild, DE/EN-PDF',
    tag: 'improvement',
    changes: [
      'Die Referenzen-Karte blieb grau, weil die Sicherheitsrichtlinie (CSP) die Kartenkacheln blockierte — behoben: OpenStreetMap und Satellit laden jetzt.',
      'Neu: Die Karte lässt sich als Vollbild in einem neuen Tab öffnen (Button „Vollbild") — ideal für Präsentationen; die aktiven Filter werden übernommen.',
      'PDF-Export der Referenzenliste jetzt zweisprachig: getrennte Buttons „PDF (DE)" und „PDF (EN)".',
      'Referenzen-Modul rundum aufgeräumt: Umschalter Liste/Karte direkt auf der Seite (Filter bleiben beim Wechsel erhalten), eine Kennzahlen-Zeile (Treffer, Länder, Anlagentypen, Produkte) und eine entfernbare Chip-Leiste für aktive Filter.',
    ],
  },
  {
    version: '3.348.0',
    date: '2026-07-23T17:00',
    title: 'Referenzen: interaktive Karte + PDF-Export',
    tag: 'feature',
    changes: [
      'Neue Kartenansicht „Referenzen → Karte": alle Referenzen mit hinterlegten Koordinaten auf einer interaktiven Karte (Standard-/Satellitenansicht umschaltbar), Marker nach Anlagentyp eingefärbt, Klick öffnet die Projekt-Detailseite.',
      'Neuer Button „Als PDF exportieren" auf der Referenzen-Liste: erzeugt eine schön formatierte, mehrseitige PDF-Tabelle der aktuell gefilterten Referenzen (mit Firmen-Briefkopf).',
    ],
  },
  {
    version: '3.347.0',
    date: '2026-07-23T16:45',
    title: 'Kommissionieren: erfasste Menge aus der Aufteilung + klarerer „Weiter"-Knopf',
    tag: 'improvement',
    changes: [
      'Teilt man eine Position im Fokus auf mehrere Packstücke auf, ist die erfasste Menge jetzt automatisch die Summe der Tranchen (z. B. 300 + 4 = 304) — vorher stand daneben ein getrennter, widersprüchlicher Wert.',
      'Der Abschluss-Knopf sagt jetzt genau, was gespeichert wird, statt „Erledigt & Weiter" (das heimlich die volle Menge gesetzt hat): „✓ Komplett · weiter" (Standard, ein Tap = volle Menge), „Teilmenge 304 m · weiter" oder — bei Aufteilung — die Summe. Darüber ein klarer Status „Vollständig" / „Teilmenge · Rest bleibt offen".',
      '„Nur Teilmenge?" blendet einen +/−-Stepper ein, „Alles" setzt schnell auf die volle Menge zurück. Die Position wird mit genau der angezeigten Menge gespeichert, dann geht es zur nächsten offenen Position.',
    ],
  },
  {
    version: '3.346.0',
    date: '2026-07-23T16:00',
    title: 'Referenzen: eigener Navigationsbereich, „Branche" vereinheitlicht',
    tag: 'improvement',
    changes: [
      '„Referenzen" hat jetzt einen eigenen Abschnitt in der Seitenleiste, statt unter „Vertrieb & Projekte" mitzulaufen.',
      'Die Felder „Branche" (Freitext) und „Projektgruppe" auf der Projektdaten-Seite standen doppelt und redundant nebeneinander — sie sind jetzt ein einziges Feld „Branche" (Kommunal/Industrie), auch in der Referenzen-Liste, im Export und im Projektbericht-PDF.',
    ],
  },
  {
    version: '3.345.0',
    date: '2026-07-23T15:30',
    title: 'Neu: „Referenzen" — Vertriebs-Ansicht auf die Projektdaten',
    tag: 'feature',
    changes: [
      'Neuer Navigationspunkt „Referenzen" mit einer vertriebsorientierten Projektliste: Land-Mehrfachauswahl, Projektgruppe, Anlagentyp, Produkt, Baujahr-Zeitraum sowie ein Abmessungs-Filter (Länge/Breite/Höhe). Zeilen führen auf die bestehende Projekt-Detailseite — Anlegen/Bearbeiten bleibt weiterhin über „Projekte".',
      'Projekt-Detailseite: neuer Tab „Notizen" (Verlauf mit Autor und Zeitstempel) sowie eine „Ähnliche Referenzen"-Karte in der Übersicht (gleiches Land + gleicher Anlagentyp). Der Datei-Tab (jetzt „Dokumente") ist wieder eingeblendet.',
      'Import-Endpunkt zur nachträglichen Anreicherung bestehender Projekte mit historischen Referenzdaten aus der bisherigen Referenz-Datenbank (Match per Projekt-Nummer).',
    ],
  },
  {
    version: '3.344.0',
    date: '2026-07-23T15:15',
    title: 'Kommissionieren am Tablet: kompakte Liste + Fokus zum Bearbeiten (iPad mini)',
    tag: 'improvement',
    changes: [
      'Die Vollbild-Kommissionierung ist jetzt auf das iPad mini optimiert: Die Positionen erscheinen als kompakte Liste — statt 2–3 sieht man 8–10 gleichzeitig, mit Status-Einfärbung, Herkunfts-Chip (Lager/bestellt), Foto- und Colli-Hinweisen. Das große Häkchen erledigt eine Position weiterhin mit einem Tap direkt aus der Liste.',
      'Ein Tap auf die Zeile öffnet die Position im Fokus: groß, mit Langtext, Herkunft und großen Bedienelementen (Menge per +/−-Stepper statt Tastatur, Packstück, Aufteilung/Tranchen, Foto, Lager-Vermerk). „Erledigt & Weiter" meldet voll zurück und springt zur nächsten offenen Position; „Liste" führt zurück zum Überblick.',
      'Gleiche Funktionen wie zuvor (kumuliert/1:1, Filter, Suche, Position/Colli hinzufügen, Abschließen) — nur neu angeordnet. Die volle Beschaffungsinfo (Lieferant, Bestellnummer, Liefertermin) steht jetzt im Fokus; in der Liste genügt ein kurzer „Bestellt"-Hinweis mit Lieferant.',
    ],
  },
  {
    version: '3.343.0',
    date: '2026-07-23T15:00',
    title: 'Dashboard selbst zusammenstellen: Widgets ein-/ausblenden und anordnen',
    tag: 'feature',
    changes: [
      'Über „Anpassen" oben rechts im Dashboard lässt sich jetzt festlegen, welche Widgets angezeigt werden und in welcher Reihenfolge — per Häkchen ein-/ausblenden und am Griff-Symbol in die gewünschte Reihenfolge ziehen.',
      'Die Anordnung ist am Benutzerkonto gespeichert und gilt geräteübergreifend. Es lassen sich nur Widgets wählen, die du ohnehin sehen darfst — Berechtigungen bleiben unberührt, neue Widgets tauchen automatisch am Ende auf.',
    ],
  },
  {
    version: '3.342.0',
    date: '2026-07-23T14:00',
    title: 'Dashboard: Widget „Meine Montage-Einsätze"',
    tag: 'feature',
    changes: [
      'Monteure sehen jetzt ihre eigenen offenen Montage-Einsätze direkt im Dashboard — mit Projekt, Ort und Termin (nächster zuerst). Es werden nur Einsätze angezeigt, denen du als Monteur zugeordnet bist (serverseitig über deinen verknüpften Mitarbeiter aufgelöst).',
    ],
  },
  {
    version: '3.341.0',
    date: '2026-07-23T13:00',
    title: 'Dashboard: Freigabe-/Genehmigungs-Widgets, QM & Benachrichtigungen',
    tag: 'feature',
    changes: [
      'Neue „Wartet auf mich"-Widgets, je nach Berechtigung eingeblendet: Kommissionierung-Freigabe, Bestellpool-Freigabe, Artikelnummern-Freigabe, Urlaubs- und Reise-Genehmigungen. Jedes zeigt die offene Warteschlange und verlinkt direkt zur Bearbeitung.',
      'Neues Widget „Meine QM-Prozesse" (nur die dir zugewiesenen, offene zuerst) und „Benachrichtigungen" (deine neuesten, ungelesene markiert).',
      'Alle Widgets erscheinen nur, wenn du die passende Berechtigung hast — und feuern ihre Abfrage auch erst dann.',
    ],
  },
  {
    version: '3.340.0',
    date: '2026-07-23T12:00',
    title: 'Dashboard: neues Widget „Meine offenen Aufgaben"',
    tag: 'feature',
    changes: [
      'Das Dashboard zeigt jetzt ganz oben deine offenen Projektaufgaben über alle Projekte hinweg — mit Projekt, Titel und Deadline (überfällig/bald fällig farblich markiert), ein Klick springt ins Projekt. Es werden ausschließlich die dir zugewiesenen Aufgaben angezeigt (serverseitig gefiltert).',
    ],
  },
  {
    version: '3.339.0',
    date: '2026-07-23T11:00',
    title: 'Dashboard: personalisiert nach Berechtigung, Finanzzahlen nur für Berechtigte',
    tag: 'feature',
    changes: [
      'Das Start-Dashboard zeigt jetzt nur noch, was der angemeldete Nutzer sehen darf. Finanz-Kacheln und -Widgets (Umsatz, Umsatzverlauf, Offene Posten, Überfällige Rechnungen, Auftragswerte in der Projektgesundheit) erscheinen ausschließlich mit der neuen Berechtigung „Finanz-Dashboard" (standardmäßig nur Administration). Bisher sah jeder alle Kacheln — bei fehlender Berechtigung mit irreführenden „0,00 €".',
      'Operative Kacheln (aktive Projekte, offene Projektaufgaben, offene Bestellungen, fällige Wartungen, Artikel unter Mindestbestand) hängen jetzt jeweils an der passenden Modul-Berechtigung und werden ausgeblendet statt genullt, wenn sie fehlen.',
      'Das Dashboard basiert jetzt auf einer Widget-Registry — Grundlage für das kommende benutzerdefinierte Dashboard, bei dem Widgets frei hinzugefügt und angeordnet werden können (ADR 0062).',
    ],
  },
  {
    version: '3.338.0',
    date: '2026-07-23T10:00',
    title: 'Projekt-Finanzdaten nur noch für Berechtigte sichtbar',
    tag: 'fix',
    changes: [
      'Auftragswert, Plan-Verkaufspreis, IC-Transferpreis, IC-Gewinnsplit und die Gemeinkosten-Prozentsätze eines Projekts werden jetzt serverseitig nur noch an Nutzer mit Finanz-Berechtigung ausgeliefert. Bisher waren diese Felder über die allgemeine Projektliste/-detail für jeden mit Projekt-Leserecht abrufbar, obwohl die eigentlichen Kalkulations-Ansichten sie längst absichern (ADR 0062). Vorbereitung für das personalisierte Dashboard.',
    ],
  },
  {
    version: '3.337.0',
    date: '2026-07-23T09:45',
    title: 'Neu: Eigene Reihenfolge für Module in der Navigation',
    tag: 'feature',
    changes: [
      'Im selben "Module verwalten"-Dialog lässt sich jedes Modul jetzt auch per Ziehen am Griff-Symbol in eine eigene Reihenfolge bringen — Sidebar und mobile "Mehr"-Übersicht übernehmen die Sortierung direkt.',
      'Wie das Ausblenden ist auch die Reihenfolge am Benutzerkonto gespeichert (geräteübergreifend) und rein optisch — Berechtigungen, Direktaufruf und Suche sind davon unberührt.',
      'Neu hinzugekommene oder umbenannte Module fallen nicht aus der Navigation: sie werden automatisch ans Ende der eigenen Reihenfolge angehängt.',
    ],
  },
  {
    version: '3.336.0',
    date: '2026-07-23T09:30',
    title: 'Laufender Timer (schwebende Anzeige): jetzt ein-/ausklappbar',
    tag: 'improvement',
    changes: [
      'Der schwebende Timer unten rechts lässt sich per Pfeil-Button zu einer kompakten Anzeige (Uhrzeit-Symbol + Dauer) zusammenklappen — praktisch, wenn er Inhalte darunter verdeckt. Ein Klick auf die kompakte Anzeige klappt ihn wieder auf.',
      'Der Zustand (auf-/zugeklappt) wird geräteweit gemerkt und bleibt auch nach einem Neuladen der Seite erhalten.',
    ],
  },
  {
    version: '3.335.0',
    date: '2026-07-23T06:20',
    title: 'Kommissionier-Auftrag: Projektname in der Kopfzeile',
    tag: 'improvement',
    changes: [
      'Die Detail-/Übersichtsseite eines Kommissionierauftrags zeigt jetzt Projektname und -code direkt unter der KO-Nummer — man sieht auf einen Blick, zu welchem Projekt der Auftrag gehört, statt sich die KO-Nummer merken zu müssen (parallel zur Kommissionier-Ansicht am Tablet, die das bereits zeigt).',
      'Nebenbei einen überflüssigen Extra-Ladevorgang entfernt: Projektname/-code kommen bereits als Join mit dem Auftrag mit, statt das ganze Projekt zusätzlich nachzuladen.',
    ],
  },
  {
    version: '3.334.0',
    date: '2026-07-22T19:30',
    title: 'Digitales Kommissionieren: Render-Last pro Klick reduziert (v. a. spürbar auf Lager-iPads)',
    tag: 'fix',
    changes: [
      'Trotz des optimistischen Updates (v3.332.0) fühlte sich Kommissionieren auf schwächerer/älterer Hardware (Lager-iPad) weiterhin träge an, weil bei jedem Klick die komplette Positionsliste neu gerendert wurde statt nur der betroffenen Zeile. Gruppen- und Einzelpositions-Zeilen sind jetzt memoisiert und rendern nur noch neu, wenn sich ihre eigenen Daten tatsächlich ändern.',
      'Foto-Anzahl je Position wird jetzt einmal für die ganze Liste vorberechnet statt bei jeder Zeile einzeln aus allen Fotos gefiltert.',
    ],
  },
  {
    version: '3.333.0',
    date: '2026-07-22T19:00',
    title: 'Neu: Eigene Module ausblenden (Navigations-Einstellung)',
    tag: 'feature',
    changes: [
      'Über "Module verwalten" im Benutzermenü (Desktop-Sidebar-Footer und mobiles Kontomenü) kann jeder Nutzer einzelne Navigations-Bereiche für sich selbst aus Sidebar und mobiler "Mehr"-Übersicht ausblenden — z. B. Module, die man nie braucht.',
      'Die Einstellung ist am Benutzerkonto gespeichert, gilt also geräteübergreifend (nicht nur im aktuellen Browser wie Design/Textgröße).',
      'Rein die Anzeige in der Navigation ist betroffen: direkter Aufruf einer Seite und die ⌘K-Suche funktionieren für ausgeblendete Bereiche unverändert — die Einstellung ist keine Berechtigung.',
    ],
  },
  {
    version: '3.332.0',
    date: '2026-07-22T18:00',
    title: 'Digitales Kommissionieren: spürbar schneller, Projektname in der Kopfzeile, Brotkrumen von der Liste',
    tag: 'fix',
    changes: [
      'Jeder Klick beim digitalen Kommissionieren (Häkchen, Menge, Packstück, Aufteilung) fühlte sich träge an, weil erst der Server-Roundtrip UND ein anschließender Refetch abgewartet wurden, bevor sich die Zeile änderte. Die Zeile aktualisiert sich jetzt sofort (optimistisches Update), ein Fehler macht die Änderung sichtbar rückgängig, der Abgleich mit dem Server läuft unauffällig im Hintergrund weiter.',
      'Der Projektname steht jetzt neben der Kommissioniernummer in der Kopfzeile der Vollbild-Kommissionierung — bisher stand dort nur die Nummer.',
      'Öffnet man einen Kommissionierauftrag über die Kommissionierungs-Liste (Modul „Kommissionierung"), zeigt der Brotkrumen jetzt „Kommissionierung" statt „Projekte / Projekt" und führt beim Zurückgehen wieder genau dorthin. Kommt man stattdessen über das Projekt, bleibt der bisherige Projekt-Pfad.',
    ],
  },
  {
    version: '3.331.0',
    date: '2026-07-22T17:15',
    title: 'Digitales Kommissionieren: Status-Filter als Dropdown, Freitext-Suche, POS-Nr. aufklappbar, Aufteilung als Mini-Liste; Brotkrumen-Fix',
    tag: 'improvement',
    changes: [
      'Der Status-Filter (Alle/Offen/Teilweise fertig/Erledigt) ist jetzt ein Dropdown statt vier einzelner Buttons — spart Platz, gleiche Höhe wie die Ansichts-Umschaltung; beide sitzen jetzt in einer Zeile.',
      'Neues Such-Icon unter dem Speicher-Status: auf-/zuklappbare Freitext-Suche über Artikelnummer und Bezeichnung, filtert die Positionsliste wie der Status-Filter (ohne den Fortschritts-Zähler zu beeinflussen).',
      'Die POS-Nr. sitzt jetzt unterhalb des Langtextes statt neben der Artikelnummer und ist auf-/zuklappbar (Standard: zugeklappt) — reduziert die Zeilenhöhe im Regelfall.',
      'Die Aufteilung auf mehrere Packstücke (Tranchen) ist jetzt eine Mini-Liste: je Zeile eine Anzahl plus eine Colli-Auswahl (Dropdown), Zeilen lassen sich frei hinzufügen und entfernen — statt eines festen Feldes pro angelegtem Packstück.',
      'Bug behoben: Der Brotkrumen-Pfad einer Kommissionierauftrags-Seite führte beim Projekt-Glied auf die Projekt-Übersicht statt zurück in den Kommissionierung-Unter-Tab der Warenwirtschaft, aus dem der Auftrag stammt (kontextuelle Navigation, analog zum Material-Vollbild).',
    ],
  },
  {
    version: '3.330.0',
    date: '2026-07-22T16:45',
    title: 'Kommissionierung: Hochformat für die kumulierte Liste, Aufteilen auf mehrere Packstücke, POS-Nr. und Status-Filter am Tablet',
    tag: 'feature',
    changes: [
      'Der kumulierte Kommissionierbeleg wird wieder im Hochformat gedruckt (der 1:1-Beleg bleibt Querformat, ADR 0060 Nachtrag) — in der Praxis übersichtlicher, da er weniger Spalten hat als der 1:1-Beleg. Der kombinierte PDF-Export (kumuliert + 1:1 in einem Dokument) übernimmt automatisch beide Formate, ohne eigenen Code dafür: erst die Hochformat-, dann die Querformat-Seiten.',
      'Neu: eine Kommissionierposition lässt sich beim digitalen Kommissionieren (1:1-Ansicht) auf mehrere Packstücke aufteilen ("Tranchen") — für Positionen, die physisch auf mehrere Collis verteilt werden. Die Position bleibt dabei EINE Zeile mit stabiler POS-Nr. (passt weiter zum bereits gedruckten Beleg), nur die Menge wird je Packstück hinterlegt. Die Packliste-PDF zeigt eine aufgeteilte Position entsprechend einmal je betroffenem Packstück mit der dort gepackten Teilmenge (ADR 0061).',
      'Digitales Kommissionieren zeigt jetzt die POS-Nr. jeder Position (im 1:1-Modus je Zeile, im kumulierten Modus als Liste der zugrundeliegenden Positionsnummern) — dieselbe Nummerierung wie auf dem Ausdruck, zum schnellen Abgleich.',
      'Neuer Status-Filter (Alle/Offen/Teilweise fertig/Erledigt) über der Positionsliste. Zeilen sind jetzt dreifarbig markiert: Erledigt = grün (unverändert), teilweise abgeschlossen = orange (neu), offen = ohne Einfärbung.',
    ],
  },
  {
    version: '3.329.0',
    date: '2026-07-22T16:15',
    title: 'Neu: Eigene Textgrößen-Einstellung (Normal/Groß/Sehr groß)',
    tag: 'feature',
    changes: [
      'iOS gibt die System-Textgröße (Dynamic Type) nicht an Webinhalte weiter — als Ersatz gibt es jetzt einen eigenen Regler in der Topbar ("Aa"-Symbol, neben dem Hell/Dunkel-Umschalter): drei Stufen, wird gespeichert und skaliert die ganze App.',
      'Dafür rund 90 Stellen mit fest codierter, sehr kleiner Schrift (9–13px) auf mitskalierende Einheiten umgestellt — bei „Normal" sieht nichts anders aus, bei „Groß"/„Sehr groß" wachsen jetzt aber auch diese Stellen mit statt starr zu bleiben.',
    ],
  },
  {
    version: '3.328.0',
    date: '2026-07-22T16:00',
    title: 'iPad mini: Tablet-Ansicht statt Handy-Ansicht',
    tag: 'fix',
    changes: [
      'Das iPad mini (744px Breite im Hochformat) lag unter der bisherigen Schwelle von 768px, ab der die App auf die Tablet-Ansicht (Sidebar + Tabellen) statt der Handy-Ansicht (Bottom-Navigation) umschaltet — dadurch wirkte die App auf dem iPad mini unnötig gequetscht/klein. Die Schwelle für genau diesen Wechsel liegt jetzt bei 744px; überall sonst in der App bleibt es unverändert.',
    ],
  },
  {
    version: '3.327.0',
    date: '2026-07-22T15:20',
    title: 'Bug behoben: Ablängungen (Artikel-Varianten) beim Erfassen einer Position nicht wählbar',
    tag: 'fix',
    changes: [
      'Der Artikel-Wähler beim manuellen Anlegen einer Position (Materialbedarf-Vollbild, Bedarfstabelle, Kommissionierung) lud den Artikelstamm bisher ohne Ablängungen (materialisierte Längen-Varianten, ADR 0040) — bereits gelagerte Fixlängen eines ablängbaren Artikels tauchten dort gar nicht erst auf. Die Beleg-Editoren (Verkauf/Einkauf) waren davon nicht betroffen.',
    ],
  },
  {
    version: '3.326.0',
    date: '2026-07-22T15:10',
    title: 'Materialbedarf-Vollbild: Tabelle nach Spalten sortierbar',
    tag: 'improvement',
    changes: [
      'Jede Spalte der Materialbedarf-Tabelle (Artikel-Nr., Bezeichnung, Baugruppe, Bedarf, Lager, Frei, Reserviert, Bestellt, Erhalten, Offen, Status, Lieferant) lässt sich jetzt per Klick auf den Spaltenkopf sortieren — Klick dreht die Richtung um, ein dritter Klick auf dieselbe Spalte hebt die Sortierung wieder auf.',
      'Nutzt dieselbe Sortier-Logik wie die eingebettete Materialtabelle im Projekt-Tab (materialGrid.sortItems). Solange sortiert ist, ist das manuelle Verschieben per Ziehgriff deaktiviert (ergibt auf sortierten Zeilen keinen Sinn) — ohne Sortierung bleibt die gewohnte, per Drag & Drop gespeicherte Reihenfolge wie bisher aktiv.',
    ],
  },
  {
    version: '3.325.0',
    date: '2026-07-22T15:05',
    title: 'Bug behoben: Projekt ließ sich nicht mehr bearbeiten, wenn ein früherer Projektleiter deaktiviert wurde',
    tag: 'fix',
    changes: [
      'Wurde ein Mitarbeiter, der einmal als Projektleitung eingetragen war, später deaktiviert oder gelöscht, ließ sich das Projekt danach über den „Bearbeiten"-Dialog gar nicht mehr speichern — egal welches Feld geändert wurde. Grund: Der Dialog sendet die Projektleitung bei jeder Speicherung unverändert mit, und das Backend lehnte den kompletten Speichervorgang mit „Unbekannter oder inaktiver Benutzer in der Projektleitung." ab, sobald auch nur einer der (unsichtbaren) hinterlegten Nutzer nicht mehr aktiv war.',
      'Die Projektleitung entfernt jetzt beim Speichern still nicht mehr aktive Nutzer aus der Liste, statt die gesamte Änderung abzulehnen — konsistent mit der Anzeige, die solche Nutzer ohnehin schon ausblendet.',
    ],
  },
  {
    version: '3.324.0',
    date: '2026-07-22T14:20',
    title: 'PDF-Belege: Langtext von Artikelpositionen reicht jetzt bis in die Menge-Spalte',
    tag: 'fix',
    changes: [
      'Der Langtext einer Position (z. B. „Bestehend aus: …"-Aufzählungen) stand nur in der schmalen Beschreibungs-Spalte zur Verfügung, obwohl er auf einer eigenen Zeile unter Bezeichnung/Menge/Einzelpreis steht und mit deren Werten gar nicht kollidiert — dadurch brach der Text unnötig oft um.',
      'Der Langtext nutzt jetzt die Breite bis zur nächsten Spalte nach der Menge (Einzelpreis-Spalte, sonst Seitenrand bzw. „Beschaffung"-Spalte) — global für alle Beleg-PDFs: Angebot, Auftragsbestätigung, Rechnung, Anzahlungs-/Schlussrechnung, Pro-Forma-Rechnung, Lieferschein und Gutschrift (sales-pdf.service.ts), Bestellung (purchase-pdf.service.ts) sowie Kommissionierbeleg 1:1 und kumuliert (picking-pdf.service.ts). Die Bezeichnung selbst und Positions-Notiz-Referenzen bleiben unverändert in ihrer bisherigen Spaltenbreite.',
    ],
  },
  {
    version: '3.323.0',
    date: '2026-07-22T14:15',
    title: 'Kommissionierung (Tablet): Herkunfts-Hinweis „Aus Lager" vs. „Bestellt bei …"',
    tag: 'improvement',
    changes: [
      'Jede Position auf der Kommissionier-Ansicht zeigt jetzt auf einen Blick, ob sie aus dem Lager entnommen wird oder bestellt ist — bei bestellten Positionen mit Lieferant, Bestellnummer und Liefertermin, direkt unter der Artikelbezeichnung.',
      'Im kumulierten Modus (mehrere Bedarfszeilen zu einem Artikel zusammengefasst) erscheint der Hinweis, sobald mindestens eine der zusammengefassten Positionen bestellt ist — dieselbe Beschaffungs-Information wie die „Beschaffung"-Spalte des gedruckten Kommissionierbelegs.',
    ],
  },
  {
    version: '3.322.0',
    date: '2026-07-22T13:30',
    title: 'Kommissionierbeleg: Beschaffungs-Hinweis, Querformat, kombinierter Export; Packliste-EN-Fix',
    tag: 'improvement',
    changes: [
      'Kommissionierbeleg (1:1 und kumuliert) zeigt jetzt eine „Beschaffung"-Spalte: bei bestellten Positionen (statt Lagerentnahme) erscheinen Lieferant, Bestellnummer und Liefertermin. Dafür sind beide Belege auf Querformat umgestellt — schafft Platz für die neue Spalte, ohne die übrigen zu verkleinern.',
      'Neuer Button „Beleg kombiniert": ein PDF-Download mit erst der kumulierten, dann der 1:1-Liste, statt zwei getrennter Exporte.',
      'Bug behoben: Die englische Packliste druckte Positions-Bezeichnungen bisher auf Deutsch. Sie nutzt jetzt den gepflegten englischen Artikelnamen, mit Rückfall auf Deutsch, wenn keine Übersetzung hinterlegt ist.',
    ],
  },
  {
    version: '3.321.0',
    date: '2026-07-22T12:25',
    title: 'Bug behoben: „In den Bestellkorb" (Sammelauswahl) meldete fälschlich „keine offenen Positionen"',
    tag: 'fix',
    changes: [
      'Die Sammelaktion „In den Bestellkorb" prüfte zusätzlich zur offenen Menge, ob die Quelle einer Position exakt auf „Bestellen" steht — der Einzelzeilen-Cart-Button (dieselbe Aktion, nur pro Zeile) tut das nicht und lässt jede Position mit offenem Bedarf zu. War die Quelle einer ausgewählten Position „Lager" (System-Vorschlag oder manuell gewählt), verweigerte die Sammelaktion sie stillschweigend — bei nur einer Auswahl kam dann die Meldung „Keine offenen, zu bestellenden Positionen.", obwohl die Zeile einzeln bestellbar gewesen wäre.',
      'Sammelaktion prüft jetzt nur noch die offene Menge, genau wie der Einzelzeilen-Button.',
    ],
  },
  {
    version: '3.320.0',
    date: '2026-07-22T12:10',
    title: 'Materialbedarf-Vollbild: Sammelaktion „In den Bestellkorb" umbenannt',
    tag: 'fix',
    changes: [
      'Die Sammelauswahl-Leiste hieß bisher „Zur Bestellung hinzufügen" — anders als die gleichbedeutende Einzelzeilen-Aktion („In den Bestellkorb"), was den Eindruck erweckte, die Sammelaktion fehle. Jetzt einheitlich „In den Bestellkorb" benannt (Funktion unverändert).',
    ],
  },
  {
    version: '3.319.0',
    date: '2026-07-22T12:00',
    title: 'Materialbedarf-Vollbild: Sammelauswahl kann jetzt auch reservieren',
    tag: 'improvement',
    changes: [
      'Die Sammelauswahl-Leiste bekommt „Freien Lagerbestand reservieren" — reserviert für alle ausgewählten Positionen mit freiem Bestand auf einmal, statt jede Zeile einzeln über die Aktion-Spalte reservieren zu müssen. Positionen ohne freien Bestand werden dabei übersprungen.',
    ],
  },
  {
    version: '3.318.0',
    date: '2026-07-22T11:50',
    title: 'Belege speichern: hängender Speichern-Button behoben',
    tag: 'fix',
    changes: [
      'Nach dem Speichern eines Belegs (Angebot, Auftrag, Rechnung, Lieferschein, Bestellung) konnte der „Speichern"-Button trotz Erfolgsmeldung („Beleg gespeichert.") im Lade-/Spinner-Zustand hängen bleiben, statt wieder normal anzuzeigen.',
      'Ursache war eine fragile, selbstgebaute Zustands-Synchronisierung, die auf einen indirekten Umweg wartete, um nach dem Speichern neu zu laden. Ersetzt durch einen direkten, robusteren Mechanismus (bereits vorhandene, eindeutige Kennung des Belegstands als Schlüssel) — betrifft Verkaufs- und Einkaufsbelege gleichermaßen.',
    ],
  },
  {
    version: '3.317.0',
    date: '2026-07-22T11:45',
    title: 'Kommissionierung: Liefertermin des Projekts jetzt in der Übersicht',
    tag: 'improvement',
    changes: [
      'Die Kommissionierungs-Übersicht (Logistik → Kommissionierung) zeigt jetzt zusätzlich den Liefertermin des zugehörigen Projekts — direkt neben Projekt/Nummer/Status, ohne erst ins Projekt wechseln zu müssen.',
    ],
  },
  {
    version: '3.316.0',
    date: '2026-07-22T11:40',
    title: 'Bug behoben: Datenbank-Tool zeigte keine Tabellen mehr an',
    tag: 'fix',
    changes: [
      'Ursache: Eine kürzlich entfernte Tabelle (Beschaffungs-Regeln) stand noch in der internen Tabellen-Liste des Daten-Editors. Da die Zeilenzahl aller Tabellen in EINER gemeinsamen Abfrage ermittelt wird, ließ die eine fehlende Tabelle die komplette Liste scheitern — das Tabelle-Dropdown blieb leer, ohne Fehlermeldung.',
      'Die Tabellen-Liste wird jetzt vorab gegen die tatsächlich vorhandenen Datenbank-Tabellen abgeglichen: eine einzelne veraltete Eintragung fällt künftig nur noch selbst raus, statt das ganze Werkzeug lahmzulegen. Schlägt das Laden der Liste trotzdem fehl, zeigt die Seite jetzt eine Fehlermeldung statt einer leeren Tabelle.',
    ],
  },
  {
    version: '3.315.0',
    date: '2026-07-22T11:15',
    title: 'Projekt-Material: Spalte „Beleg" zeigt verknüpfte Bestellung + Liefertermin',
    tag: 'feature',
    changes: [
      'Die Bedarfstabelle (Projekt → Warenwirtschaft → Bedarf/Material) hat jetzt eine Spalte „Beleg": zeigt die verknüpfte Bestellung als klickbaren Link (öffnet den Beleg) inkl. voraussichtlichem Liefertermin — bisher war das nur über den Vollbild-Editor indirekt ersichtlich.',
      'Technisch: eine neue, gebündelte Abfrage lädt die Bestellungs-Verknüpfungen für alle Positionen eines Projekts auf einmal (kein Einzel-Request je Zeile).',
    ],
  },
  {
    version: '3.314.0',
    date: '2026-07-22T11:00',
    title: 'Materialbedarf-Vollbild: adaptive Spalten, Lieferant ausfüllen, Sammelaktionen',
    tag: 'improvement',
    changes: [
      'Spaltenbreiten passen sich jetzt proportional an die verfügbare Fensterbreite an — alle Spalten inkl. „Aktionen" bleiben sichtbar, kein horizontales Scrollen mehr nötig. Manuelles Verbreitern/Verschmälern einer Spalte nimmt die Breite von der Nachbarspalte (Summe bleibt konstant).',
      'Spalte „Lieferant": per Ausfüll-Punkt (wie bei Bedarf/Baugruppe/Notiz) lässt sich ein zugewiesener Lieferant durch Ziehen auf nachfolgende Zeilen übertragen.',
      'Sammelauswahl-Leiste um „Lieferant zuweisen" und „Zur Bestellung hinzufügen" erweitert — wirkt auf alle markierten Positionen, nicht nur auf „Löschen".',
      '„Schließen" im Vollbild-Modus springt jetzt kontextuell zurück auf den Warenwirtschaft-Tab der Projektseite statt auf die Übersicht.',
    ],
  },
  {
    version: '3.313.0',
    date: '2026-07-21T21:00',
    title: 'Belege speichern: Performance-Bug behoben (war teils sehr langsam)',
    tag: 'fix',
    changes: [
      'Jedes Speichern einer Beleg-Position (Angebot, Auftrag, Rechnung, Lieferschein, Bestellung …) hat bisher für jeden im Beleg vorkommenden Artikel eine eigene Datenbank-Abfrage ausgelöst statt einer einzigen für alle zusammen — bei Belegen mit vielen unterschiedlichen Artikeln (Beleggewicht wird daraus berechnet) summierte sich das auf Sekunden bis teils deutlich länger je Speichervorgang.',
      'Betraf jede Positions-Änderung (anlegen, bearbeiten, löschen) in Verkaufs- UND Einkaufsbelegen gleichermaßen — jetzt eine einzige Abfrage für alle Artikel eines Belegs, Speichern ist wieder spürbar schnell, unabhängig von der Anzahl unterschiedlicher Artikel im Beleg.',
    ],
  },
  {
    version: '3.311.0',
    date: '2026-07-21T20:30',
    title: 'iPad/iPhone quer: Sidebar jetzt ganz einklappbar (echtes Vollbild)',
    tag: 'improvement',
    changes: [
      'Auf Touch-Geräten (iPad, iPhone im Querformat) klappte die Sidebar beim Einklappen bisher nur auf eine schmale Icon-Leiste zusammen — jetzt verschwindet sie beim Klick auf den Einklappen-Button ganz, die aktuelle Seite bekommt den vollen Bildschirm. Auf Maus-Geräten (Desktop) bleibt die Icon-Leiste wie gewohnt erhalten.',
    ],
  },
  {
    version: '3.310.0',
    date: '2026-07-21T20:00',
    title: 'Bug behoben: Checkboxen auf Touch-Geräten verzerrt',
    tag: 'fix',
    changes: [
      'Auf iPad/Touch-Geräten wurden Checkboxen (z. B. „Archivierte anzeigen") zu einem hohen, schmalen Rechteck statt einem Quadrat verzerrt. Ursache: eine globale Regel hebt Tippziele auf Touch-Geräten auf ≥44px an — traf aber versehentlich auch Checkboxen, weil Radix sie technisch als `<button>` rendert. Checkboxen lösen große Tippziele bereits selbst über eine unsichtbar vergrößerte Trefferfläche, jetzt von der globalen Regel ausgenommen.',
    ],
  },
  {
    version: '3.309.0',
    date: '2026-07-21T19:45',
    title: 'iPad-Ansicht: Suchfeld-Höhe korrigiert, Sidebar klappt auf Tablet-Breite automatisch ein',
    tag: 'fix',
    changes: [
      'Filter-Zeilen mit Suchfeld + Dropdown (Artikel-Belege, Lagerbewegungen, Projekt-Beschaffung) zeigten das Suchfeld minimal kleiner als die Dropdowns daneben — behoben, beide sind jetzt gleich hoch.',
      'Auf schmalen Tablet-Breiten (Hochformat-iPad) blieb die Sidebar bisher immer ausgeklappt (mit Textlabels), auch nach manuellem Einklappen + Neuladen — dafür fehlte Listen-Toolbars (Suche/Filter/Buttons) der Platz für eine Zeile. Ohne explizite Nutzer-Entscheidung klappt die Sidebar jetzt unterhalb von 1024px automatisch zu Icons ein.',
    ],
  },
  {
    version: '3.308.0',
    date: '2026-07-21T17:30',
    title: 'Nummernkreise: echte Belegnummer-Vorschau statt nacktem Zähler',
    tag: 'improvement',
    changes: [
      'Einstellungen → Nummernkreis zeigt in der Spalte „Nächste Nummer" jetzt die tatsächlich formatierte Belegnummer (z. B. „RE-2613001") statt nur des rohen Zählerstands — mit dem Zähler als kleiner Zusatzangabe darunter.',
      'Der Korrektur-Dialog zeigt beim Tippen live, welche Belegnummer als Nächstes vergeben wird — Feedback in Echtzeit statt einer abstrakten Zahl.',
    ],
  },
  {
    version: '3.307.0',
    date: '2026-07-21T17:00',
    title: 'Neues, einheitliches Belegnummern-Schema',
    tag: 'improvement',
    changes: [
      'Alle Belegarten bekommen jetzt ein einheitliches Nummernformat: Präfix, 2-stelliges Jahr, Gesellschafts-Ziffer (1=AT/2=DE) und Typ-Ziffer, z. B. „RE-2613001" für die erste Rechnung der Gesellschaft AT im Jahr 2026. Rechnung, Anzahlungsrechnung und Schlussrechnung teilen sich bewusst denselben Zähler.',
      'Angebot, Kommissionierauftrag und Einkauf (Anfrage/Bestellung/Eingangsrechnung) sind jetzt ebenfalls Gesellschafts-gebunden statt tenant-weit gezählt — die laufenden Nummern laufen dabei nahtlos vom bisherigen Stand weiter, kein Reset.',
      'Präfix-Änderungen: Auftrag „AB" → „AF", Gutschrift/Storno „ST" → „GS", Einkauf-Anfrage „AN" → „AE" (Kollision mit Angebot). Bereits vergebene Belegnummern bleiben unverändert gültig — betrifft nur neue Belege.',
    ],
  },
  {
    version: '3.306.0',
    date: '2026-07-21T16:50',
    title: 'Artikel-Belege: jetzt nach Art, Status und Nummer filterbar',
    tag: 'improvement',
    changes: [
      'Das Register „Belege" am Artikel (alle Angebote, Aufträge, Bestellungen, Kommissionieraufträge und Fertigungsaufträge mit diesem Artikel) hatte bisher keine Filter — bei Standardartikeln mit vielen Belegen wurde die Liste unübersichtlich.',
      'Neu: eine Suche über Nummer/Kunde-Lieferant/Projekt sowie zwei Dropdowns für Art und Status, jeweils mit den tatsächlich vorkommenden Werten befüllt.',
    ],
  },
  {
    version: '3.305.0',
    date: '2026-07-21T16:40',
    title: 'Personen-Tabelle: Spalten sind jetzt sortierbar',
    tag: 'improvement',
    changes: [
      'Personal → Personen → Tab „Mitarbeiter": Pers.-Nr., Name, Position, E-Mail und Status lassen sich per Klick auf den Spaltenkopf sortieren (Pfeil zeigt die Richtung, nochmal klicken dreht um). Standard weiterhin alphabetisch nach Name.',
    ],
  },
  {
    version: '3.304.0',
    date: '2026-07-21T16:05',
    title: 'Bug behoben: „Eintrag existiert bereits" beim Anlegen einer Person',
    tag: 'fix',
    changes: [
      'Ursache gefunden: eine gelöschte Mitarbeiter-Zeile blockierte ihre Personalnummer bzw. ihren Login-User dauerhaft für neue Mitarbeiter, weil der Eindeutigkeits-Check in der Datenbank gelöschte Zeilen nicht ausgenommen hat — der freundliche Vorab-Check im Hintergrund tat das zwar schon, der eigentliche Speichervorgang aber nicht.',
      'Beide Eindeutigkeits-Regeln (Personalnummer, Login-User je Tenant) gelten jetzt nur noch für aktive Mitarbeiter. Zusätzlich meldet ein Verknüpfungs-Versuch mit einem bereits vergebenen Login-User jetzt eine klare Fehlermeldung statt „Eintrag existiert bereits".',
    ],
  },
  {
    version: '3.303.0',
    date: '2026-07-21T15:30',
    title: 'Artikel-Auswahl in Belegpositionen: jetzt auch EK-Preis sichtbar',
    tag: 'improvement',
    changes: [
      'Die Artikel-Auswahl-Tabelle einer Belegposition zeigte bisher nur den VK-Preis. Jetzt steht daneben auch der EK-Preis (manueller Einkaufspreis, sonst der letzte Bezugspreis aus Einkaufsbelegen) — hilfreich beim Prüfen der Marge direkt bei der Artikelwahl.',
    ],
  },
  {
    version: '3.302.0',
    date: '2026-07-21T15:15',
    title: 'Beschaffungs-Regeln entfernt — Vorschlag ist jetzt fest einprogrammiert',
    tag: 'improvement',
    changes: [
      'Der Tab „Beschaffung" unter Einstellungen ist entfernt — das konfigurierbare Regelwerk (Artikel-Tag → Quelle-Verhalten) war für die zwei tatsächlich genutzten Tags („Projektbezogen"/„Standardartikel") unnötig komplex.',
      'Der Beschaffungs-Vorschlag im Projekt-Tab „Material" bleibt unverändert im Verhalten (aktuell gab es ohnehin keine Regeln): Artikel mit Tag „Projektbezogen" werden immer zur Bestellung vorgeschlagen, alle anderen nach Lagerdeckung.',
    ],
  },
  {
    version: '3.301.0',
    date: '2026-07-21T15:00',
    title: 'Kostenstelle wandert von Mitarbeiter zu Position',
    tag: 'improvement',
    changes: [
      'Kostenstelle und Position bildeten in der Praxis dieselbe Auswahl doppelt ab. Die Kostenstelle wird jetzt einmal je Position gepflegt (Personal → Positionen) statt je Mitarbeiter — der Mitarbeiter-Dialog zeigt sie nur noch als Hinweis bei der gewählten Position an.',
      'Zeiteinträge übernehmen die Kostenstelle weiterhin automatisch, jetzt über die Position des Mitarbeiters. Bestehende Positionen mit einem zur Kostenstelle passenden Namen (z. B. „Sekretariat") wurden automatisch verknüpft, alle anderen sind einmalig in der Positionen-Verwaltung nachzupflegen.',
    ],
  },
  {
    version: '3.300.0',
    date: '2026-07-21T14:30',
    title: 'Artikel-Vorschau: Infotext jetzt auf Deutsch UND Englisch',
    tag: 'fix',
    changes: [
      'Die neue Artikel-Vorschau (Auge-Button in der Artikel-Auswahl-Tabelle) zeigte den Infotext bisher nur auf Deutsch und ohne Beschriftung. Jetzt stehen beide Sprachen — klar als „Infotext (DE)" und „Infotext (EN)" beschriftet — untereinander in der Vorschau.',
    ],
  },
  {
    version: '3.299.0',
    date: '2026-07-21T14:15',
    title: 'Artikel-Auswahl in Belegpositionen: Vorschau-Button',
    tag: 'improvement',
    changes: [
      'In der Artikel-Auswahl-Tabelle einer Belegposition (Angebot, Bestellung …) gibt es jetzt ganz rechts je Zeile einen kleinen Vorschau-Button (Auge) — zeigt Bestand, Verfügbarkeit, Typ, Hersteller und Preis, ohne den Artikel erst auf die Position übernehmen zu müssen.',
    ],
  },
  {
    version: '3.297.0',
    date: '2026-07-21T14:00',
    title: 'Abläng-Anhang im Beleg-PDF: nur noch die reine Länge',
    tag: 'improvement',
    changes: [
      'Der gedruckte Anhang unter einer abgelängten Artikelnummer (Sub-Artikel, z. B. GFK-Balken) zeigt jetzt nur noch die reine Länge (z. B. „4920") statt „L = 4920 mm" — bricht die Artikelnummer selbst in eine zweite Zeile um, steht direkt darunter einfach die Länge, ohne Label oder Einheit.',
      'Betrifft neu angelegte oder in der Länge geänderte Sub-Artikel; bereits gedruckte Belege und noch nicht angefasste Bestandsvarianten behalten ihren bisherigen Anhang.',
    ],
  },
  {
    version: '3.296.0',
    date: '2026-07-21T13:30',
    title: 'Angebot/Rechnung/Lieferschein: lange Artikelnummern nicht mehr abgeschnitten',
    tag: 'fix',
    changes: [
      'Lange Artikelnummern mit eingebautem Maß (z. B. „2020.10001.108-4920") wickelten in der schmalen ART-NR.-Spalte des PDFs um — der darunter gedruckte Abläng-Hinweis („L = 4920 mm") stand dabei fest auf der zweiten Zeile und überlagerte den umgebrochenen Rest der Nummer, sodass sie kaputt/abgeschnitten aussah.',
      'Die ART-NR.-Spalte ist jetzt breiter (passt die meisten Artikelnummern auf eine Zeile) und der Abläng-Hinweis wird an der tatsächlich gemessenen Höhe der Artikelnummer ausgerichtet statt an einem festen Versatz — bricht die Nummer trotzdem um, bleibt beides sauber getrennt lesbar.',
    ],
  },
  {
    version: '3.295.0',
    date: '2026-07-21T13:00',
    title: 'Neu: Inventur (Artikel & Lager)',
    tag: 'feature',
    changes: [
      'Neues Werkzeug unter „Artikel & Lager → Inventur": Stichtags-Bestandsaufnahmen anlegen, zählen und abschließen. Positionen kommen entweder komplett per Hauptwarengruppe (oder „alle Lagerartikel") in die Zählliste oder einzeln per Artikelsuche dazu.',
      'Je Position wird die Soll-Menge zum Zeitpunkt der Aufnahme festgehalten und die gezählte Ist-Menge erfasst (mobil bedienbar). Beim Abschließen — erst möglich, wenn wirklich jede Position gezählt ist — werden für alle Positionen mit Abweichung automatisch Korrekturbuchungen erstellt; bei Übereinstimmung passiert nichts. Kopf und Positionen sind danach unveränderlich.',
      'Neue Berechtigung „inventory.count": Zählen (Inventur anlegen, Positionen erfassen) ist davon abhängig, Abschließen/Löschen bleibt an die bestehende Bestandskorrektur-Berechtigung gekoppelt — Lagerpersonal kann zählen, ohne generelle Korrekturrechte zu haben.',
    ],
  },
  {
    version: '3.294.0',
    date: '2026-07-21T12:30',
    title: 'Artikel-Anträge: Dubletten-Schutz für offene Anträge repariert',
    tag: 'fix',
    changes: [
      'Zwei offene Anträge (auch von verschiedenen Technikern) auf dieselbe Zeichnung + Material konnten unbemerkt gleichzeitig entstehen — der Konflikt fiel bisher erst bei der Freigabe des zweiten auf, oft erst nach Tagen, mit einer schwer nachvollziehbaren Fehlermeldung.',
      'Ursache: der bestehende Datenbank-Schutz griff auf der endgültigen Artikelnummer, die aber seit der letzten Umstellung (Nummer entsteht erst bei der Freigabe) bei offenen Anträgen immer leer ist — der Schutz war seither wirkungslos.',
      'Jetzt wird beim Anlegen sofort geprüft, ob für dieselbe Zeichnung + Material schon ein offener Antrag existiert (klare Fehlermeldung), zusätzlich abgesichert durch einen neuen, tatsächlich wirksamen Datenbank-Schutz gegen gleichzeitiges Anlegen.',
    ],
  },
  {
    version: '3.292.0',
    date: '2026-07-21T11:00',
    title: 'Artikel: neues Register „Belege"',
    tag: 'feature',
    changes: [
      'Die Artikel-Detailseite zeigt jetzt im Register „Belege" alle Verkaufsbelege, Einkaufsbelege, Kommissionier- und Fertigungsaufträge, in denen der Artikel als Position vorkommt — eine flache Liste, neueste zuerst, mit Datum, Status, Menge, Projekt und Beleg-Gesamtbetrag.',
      'Mehrere Positionszeilen desselben Artikels im selben Beleg werden zu einer Zeile zusammengefasst (Menge summiert).',
    ],
  },
  {
    version: '3.291.0',
    date: '2026-07-21T10:30',
    title: 'Neue globale Berechtigung: Datenexport',
    tag: 'feature',
    changes: [
      'Neue Berechtigung „Datenexport" (export.download) steuert modulübergreifend, ob jemand CSV-Export-Buttons in Tabellen (Kundenliste, Artikelliste, Bestellliste, Kontakte, Zeichnungsnummern, Materialcodes, Warengruppen, Sperrlager, Selektionspool, Projekte, Zeiterfassungs-Analyse, Lieferadressen) sowie Excel-/CSV-Downloads in Reporting & Auswertungen nutzen darf.',
      'Geschäftsdokument-PDFs (Angebot, Rechnung, Lieferschein, Bestellung, Packliste, Kassabuch …) sind bewusst NICHT betroffen — die bleiben an ihrer bisherigen Modul-Berechtigung, da sie zum normalen Arbeitsablauf gehören.',
      'Rollout ohne Überraschung: Alle bestehenden Rollen behalten die Berechtigung zunächst (kein Verhalten ändert sich). Einschränkungen nimmt ein Administrator gezielt pro Rolle unter „Administration → Rollen" vor.',
    ],
  },
  {
    version: '3.290.0',
    date: '2026-07-21T10:00',
    title: 'Antrag bearbeiten/Details: modernisiertes Register-Modal',
    tag: 'improvement',
    changes: [
      'Der Dialog „Antrag bearbeiten"/„Antrag — Details"/„Antrag nachbessern" ist jetzt ein Register-Modal mit Icon-Navigation (Zeichnung & Nummer, Stammdaten, Infotexte, Zoll & Logistik, Preise, Lager) statt einer langen Liste mit Textabschnitten — wie beim Artikel-Stammdatendialog.',
      'Status-Hinweise (Rückfrage-Grund, Freigabe-Status, Link zum angelegten Artikel) sind jetzt immer oben sichtbar, unabhängig vom gewählten Register.',
    ],
  },
  {
    version: '3.288.0',
    date: '2026-07-21T09:30',
    title: 'Warengruppen: archivierte Codes reaktivieren',
    tag: 'feature',
    changes: [
      'Eine gelöschte Warengruppe verschwand bisher spurlos aus der Liste — der Code blieb aber für immer reserviert (wie bei Artikelnummern), sodass eine Neuanlage mit demselben Code an „Code ist bereits vergeben" scheiterte, ohne erkennbaren Grund.',
      'Neue Checkbox „Archivierte anzeigen" auf der Warengruppen-Seite (Technik) zeigt gelöschte Codes wieder an (mit „Archiviert"-Badge) — mit einem Klick auf „Reaktivieren" lässt sich die Warengruppe wiederherstellen, statt einen neuen Code vergeben zu müssen.',
    ],
  },
  {
    version: '3.287.0',
    date: '2026-07-21T09:00',
    title: 'Artikelnummer anlegen: Taric-Dropdown & Lieferanten-Pop-up statt Freitext',
    tag: 'improvement',
    changes: [
      'Das Feld „Beschaffung" ist aus dem Assistenten „Artikelnummer anlegen" entfernt — das Sekretariat legt das bei der Freigabe fest.',
      'Der Taric-Code ist jetzt ein Dropdown mit den gepflegten Taric-Codes statt Freitext, inklusive Link „Taric-Codes verwalten", der die bestehende Verwaltung direkt im Assistenten öffnet.',
      'Der „Standardlieferant" nutzt jetzt das gewohnte Pop-up-Modal mit Suche statt eines einfachen Dropdowns — wie an anderen Stellen der App.',
    ],
  },
  {
    version: '3.285.0',
    date: '2026-07-21T07:00',
    title: 'Personen: HR-Mitarbeiter und Benutzerkonten zusammengeführt',
    tag: 'improvement',
    changes: [
      '„HR" und „Benutzer & Rechte" waren zwei getrennte Listen mit teils denselben Personen, aber doppelt gepflegtem Namen/E-Mail (konnten auseinanderlaufen, z. B. unterschiedliche Schreibweise). Jetzt gibt es unter „Administration → Personen" eine gemeinsame Liste: Mitarbeiter-Stammdaten und Login/Rollen einer Person werden in einem Dialog zusammen bearbeitet, Name/E-Mail nur noch einmal eingegeben.',
      'Ein Mitarbeiter ohne Login (z. B. Monteur) oder ein Benutzerkonto ohne Mitarbeiter-Datensatz (z. B. externer Zugang) bleiben weiterhin möglich — fehlt eine Seite, lässt sie sich direkt im Dialog ergänzen und wird automatisch verknüpft, statt sie über eine separate Dropdown-Auswahl zu suchen.',
      'Die reine Rollen-/Berechtigungsverwaltung ist jetzt unter „Administration → Rollen" (vormals Teil von „Benutzer & Rechte").',
    ],
  },
  {
    version: '3.284.0',
    date: '2026-07-21T06:00',
    title: 'Login-/Ladeseite: kein Aufblitzen des Standard-Brandings mehr',
    tag: 'fix',
    changes: [
      'Bis die Antwort auf das eigene Branding (Logo, Login-Texte, Panel-Farbe) da war, zeigte die Anmelde-/Ladeseite kurz das eingebaute Standard-Branding ("P"/ProERP, dunkler Verlauf) — bei langsamer Verbindung oder Server-Kaltstart wurde dieser kurze Zwischenzustand sichtbar, obwohl längst ein eigenes Branding hinterlegt war.',
      'Das zuletzt geladene Branding wird jetzt zwischengespeichert und beim nächsten Laden sofort angezeigt, während im Hintergrund aktualisiert wird — kein Aufblitzen des Standards mehr. Betrifft auch das App-Logo in Topbar/Sidebar direkt nach dem Login.',
    ],
  },
  {
    version: '3.283.0',
    date: '2026-07-21T05:15',
    title: 'Rolling Forecast: „Fertigstellung" ist jetzt Plan-Ende',
    tag: 'improvement',
    changes: [
      'Die Spalte „Fertigstellung" bei den aktiven Projekten im Rolling Forecast schreibt/liest jetzt direkt das bestehende Projektfeld „Plan-Ende" — kein eigenes, separates Forecast-Feld mehr. Vermeidet doppelte Datenpflege: eine Änderung an einer Stelle reicht.',
      'Der Monat/Jahr-Auswähler überschreibt dabei einen bereits gesetzten Tag von Plan-Ende nicht mehr stillschweigend mit dem 1. des Monats — ein woanders gepflegtes taggenaues Datum bleibt erhalten.',
    ],
  },
  {
    version: '3.282.0',
    date: '2026-07-21T05:00',
    title: 'Projekt bearbeiten/anlegen: modernisiertes, größeres Formular',
    tag: 'improvement',
    changes: [
      'Der Dialog „Projekt bearbeiten"/„Neues Projekt" ist jetzt größer und in klar abgegrenzte Abschnitte (Stammdaten, Projektleitung & Termine, Kaufmännisch, Notizen) mit Icon-Überschriften gegliedert — wie der Projektdaten-Tab.',
      'Die Felder „Standort", „WSP (m)" und „Projektgruppe" sind aus dem Dialog entfernt (unnötig für die Neuanlage/Bearbeitung; Projektgruppe wird weiterhin in den Projektdaten gepflegt).',
      'Das Feld „Projektleiter" ist jetzt dieselbe Mehrfachauswahl wie „Projektleitung" im Projektdaten-Tab — mehrere echte Benutzer statt eines Freitextfelds.',
    ],
  },
  {
    version: '3.281.0',
    date: '2026-07-21T04:45',
    title: 'Zeichnungsnummern: Suche & Warengruppen-Filter',
    tag: 'improvement',
    changes: [
      'Die Zeichnungsnummern-Liste (Technik) hat jetzt ein Suchfeld (Nummer, Beschreibung oder Warengruppe) und einen Warengruppen-Filter — beide kombinierbar, als entfernbare Filter-Chips über der Tabelle.',
      'Den erklärenden Beschreibungstext unter dem Seitentitel entfernt — die Spaltenüberschriften und der Hinweistext im Anlege-Dialog reichen aus.',
    ],
  },
  {
    version: '3.280.0',
    date: '2026-07-21T04:30',
    title: 'Brotkrumen: für iPhone optimiert',
    tag: 'fix',
    changes: [
      'Die Brotkrumen-Navigation (z. B. „Projekte › P… › KO-…") bricht auf dem iPhone nicht mehr auf mehrere Zeilen um. Auf schmalen Screens wird der Pfad auf das direkte Eltern-Glied + die aktuelle Seite eingedampft (das Eltern-Glied bleibt antippbar), lange Namen werden gekürzt. Ab Tablet-/Desktop-Breite erscheint wieder der vollständige Pfad.',
    ],
  },
  {
    version: '3.279.0',
    date: '2026-07-21T04:00',
    title: 'Reporting: HF/NAL-Bericht deutlich schneller',
    tag: 'improvement',
    changes: [
      'Der HF/NAL-Bericht baute die Wirtschaftlichkeit der angezeigten Projekte bisher strikt nacheinander auf — bei 25 Projekten je Seite summierten sich die Wartezeiten spürbar. Die Berechnung läuft jetzt gebündelt parallel (mehrere Projekte gleichzeitig, mit begrenzter Last auf die Datenbank), die Seite ist dadurch merklich schneller.',
      'Zusätzlich ein fehlender Datenbank-Index auf den Bestellpositionen ergänzt: die projektbezogene Ist-Kosten-Ermittlung (Controlling, Wirtschaftlichkeit, HF/NAL) musste bislang je Projekt die ganze Positionstabelle durchsuchen — jetzt greift ein gezielter Index. Das beschleunigt nicht nur das Reporting, sondern auch den Kaufmännisch-Tab im Projekt.',
      'Die angezeigten Zahlen sind unverändert — es wurde ausschließlich die Geschwindigkeit verbessert.',
    ],
  },
  {
    version: '3.278.0',
    date: '2026-07-21T03:30',
    title: 'Rolling Forecast: aktive Projekte paginiert',
    tag: 'improvement',
    changes: [
      'Die Liste der aktiven Projekte im Rolling Forecast wird jetzt seitenweise geladen (25 je Seite) — die Monatswerte werden weiterhin über alle Projekte gerechnet. Dadurch bleibt die Seite auch bei vielen aktiven Projekten schnell. Der Excel-Export enthält weiterhin alle Projekte.',
    ],
  },
  {
    version: '3.277.0',
    date: '2026-07-21T03:00',
    title: 'Sperrlager: Artikel öffnet im selben Tab',
    tag: 'fix',
    changes: [
      'Ein Klick auf einen Sperrlager-Artikel öffnete bisher einen neuen Browser-Tab — jetzt navigiert er wie überall sonst im selben Tab zur Detailseite.',
    ],
  },
  {
    version: '3.276.0',
    date: '2026-07-21T02:45',
    title: 'Vertriebspipeline & Rolling Forecast',
    tag: 'feature',
    changes: [
      'Neue Seite „Vertriebspipeline" (Modul Vertrieb): voraussichtlich eingehende Projekte pflegen — mit Entity (Gesellschaft), Projektname, Projektvolumen, Auftragswahrscheinlichkeit und erwartetem Auftragseingang. Die gewichtete Summe (Volumen × Wahrscheinlichkeit) wird laufend mit angezeigt.',
      'Neues Register „Rolling Forecast" im Modul Reporting: zeigt die Pipeline sowie eine Tabelle der aktiven Projekte mit einer direkt editierbaren Spalte „Fertigstellung" (Monat/Jahr auswählbar) — ohne die echten Projekttermine zu verändern.',
      'Report „Erwarteter Umsatz je Monat": summiert aktive Projekte (voller Auftragswert im Fertigstellungs-Monat) und die Pipeline (Volumen × Auftragswahrscheinlichkeit im Monat des erwarteten Auftragseingangs) je Monat — inkl. Verlaufsbalken und Excel-Export (Monatsübersicht, aktive Projekte, Pipeline).',
    ],
  },
  {
    version: '3.275.0',
    date: '2026-07-21T02:30',
    title: 'Sperrlager: Anlegen-Dialog mit Autocomplete, Kürzel, Bewertung und Fotos vorab',
    tag: 'feature',
    changes: [
      'Beim Tippen in Material, Kategorie, Hersteller, Stellplatz und Beschreibung erscheinen jetzt Vorschläge aus bereits erfassten Artikeln — antippen füllt das Feld aus, freies Weiterschreiben bleibt jederzeit möglich. Eigens gebaut statt native Browser-Vorschläge, weil iPhone/iPad (90 % der Nutzung) diese sonst nicht anzeigen.',
      '„Kürzel" wird beim Anlegen automatisch mit den Initialen des angemeldeten Benutzers befüllt (Ersteller) — danach frei änderbar.',
      'Neues Feld „Bewertung (€/Stück)" — nur für Berechtigte mit dem neuen Recht „Sperrlager: Bewertung sehen" sichtbar (Standard: nur Administratoren), serverseitig für alle anderen ausgeblendet. Die Liste zeigt Berechtigten oben einen „Gesamtwert Sperrlager" (Menge × Bewertung über alle Artikel).',
      'Fotos lassen sich jetzt schon beim Anlegen auswählen (lokale Vorschau, entfernbar) — sie werden automatisch mit hochgeladen, sobald der Artikel gespeichert ist. Der bisherige Zwischenschritt „erst anlegen, dann Fotos ergänzen" entfällt.',
    ],
  },
  {
    version: '3.274.0',
    date: '2026-07-21T01:35',
    title: 'Sperrlager mobil: kompaktere Karten',
    tag: 'improvement',
    changes: [
      'Die Sperrlager-Liste zeigt die Detail-Felder je Artikel (Material, Abmessung, Menge, Stellplatz, Kategorie, Fotos) auf dem Handy jetzt in einem zweispaltigen Raster statt einzeln untereinander — die Karten sind dadurch etwa halb so hoch, was bei 51+ Artikeln deutlich weniger Scrollen bedeutet.',
    ],
  },
  {
    version: '3.273.0',
    date: '2026-07-21T01:30',
    title: 'Belege im Projekt: eigenes Hauptregister',
    tag: 'improvement',
    changes: [
      'Im Projekt sind „Belege" jetzt ein eigenständiges Hauptregister, nicht mehr ein Unterpunkt von „Warenwirtschaft & Belege". Das Register heißt jetzt nur noch „Warenwirtschaft" und bündelt weiterhin Bedarf/Material, Bestellungen, Lager und Kommissionierung.',
    ],
  },
  {
    version: '3.272.0',
    date: '2026-07-21T01:00',
    title: 'Angebots-PDF-Import: Pauschal-Position bekommt Artikelnummer „Angebot excel"',
    tag: 'improvement',
    changes: [
      'Wird ein Angebot als PDF importiert, trägt die automatisch angelegte Pauschal-Position (Angebotssumme laut importiertem PDF) jetzt die Artikelnummer „Angebot excel" — im Beleg-Editor sofort erkennbar, dass diese Zeile aus einem PDF-Import stammt und nicht aus dem Artikelstamm kommt. Auf dem gedruckten Beleg erscheint weiterhin keine Artikelnummer.',
    ],
  },
  {
    version: '3.271.0',
    date: '2026-07-21T00:30',
    title: 'Kommissionieren: Nachfrage zur Zeiterfassung',
    tag: 'improvement',
    changes: [
      'Der Button „Kommissionieren" startete bisher sofort und ungefragt einen Zeiterfassungs-Timer auf das Projekt. Jetzt kommt zuerst eine Nachfrage: „Mit Zeiterfassung" startet den Timer und öffnet die Kommissionier-Ansicht, „Ohne Zeiterfassung" öffnet sie direkt ohne Timer — so entsteht kein ungewollter Zeiteintrag mehr.',
    ],
  },
  {
    version: '3.270.0',
    date: '2026-07-21T00:00',
    title: 'Zeiterfassung: für Handy & Tablet optimiert',
    tag: 'improvement',
    changes: [
      'Das Zeitprotokoll wird auf Handy und Tablet (bis zur Desktop-Breite) als Karten-Liste statt als breite Tabelle angezeigt — vorher wurde die zehnspaltige Tabelle abgeschnitten. Jede Karte zeigt Projekt, Dauer, Datum, Uhrzeit-Spanne, Firma, Tätigkeit und Notiz; Tippen öffnet den Eintrag, Löschen bleibt direkt erreichbar.',
      'Die Kennzahlen-Kacheln (Stunden heute, diese Woche, aktive Projekte) schneiden ihren Wert nicht mehr ab (kein „0,0…" mehr) und ordnen sich auf schmalen Breiten in zwei bzw. eine Spalte an.',
    ],
  },
  {
    version: '3.269.0',
    date: '2026-07-20T23:30',
    title: 'Kommissionierauftrag: aufgeräumte Kopfleiste (iPad)',
    tag: 'improvement',
    changes: [
      'Die Detailseite eines Kommissionierauftrags hatte in der Kopfzeile bis zu sieben Buttons nebeneinander — auf dem iPad unübersichtlich. Die fünf Dokument-/Export-Buttons (Beleg, Beleg kumuliert, Packliste, Packliste EN, XLSX) sind jetzt in einem einzigen Menü „Dokumente" zusammengefasst (gruppiert nach Kommissionierbeleg, Packliste und XLSX).',
      'Sichtbar bleiben nur noch die eigentlichen Arbeitsschritte (z. B. „Kommissionieren", „Kommissionierung abschließen") plus das Dokumente-Menü — deutlich ruhiger und leichter zu treffen.',
    ],
  },
  {
    version: '3.268.0',
    date: '2026-07-20T23:00',
    title: 'Projektphasen: Gate-Spalte entfernt, Zeitraum als ein Datums-Picker',
    tag: 'improvement',
    changes: [
      'Die Spalte „Gate" in der Phasen-Tabelle (Projekt → Planung → Phasen) ist entfernt — die Übersicht zeigt jetzt nur noch #, Phase, Art, Status, Zeitraum und Aktionen.',
      'Im Dialog „Phase hinzufügen/bearbeiten" wird der Zeitraum nicht mehr über zwei getrennte Felder (Start/Ende) gewählt, sondern in einem gemeinsamen Datums-Picker im „Booking.com"-Stil: erster Klick setzt den Start, der zweite das Ende, die Tage dazwischen werden als Spanne hervorgehoben (zwei Monate nebeneinander, auf schmalen Bildschirmen einer).',
    ],
  },
  {
    version: '3.267.0',
    date: '2026-07-20T22:30',
    title: 'Kalkulations-PDF: eigenständige Berichte je Gesellschaft',
    tag: 'improvement',
    changes: [
      'Der Gesamt-Bericht enthält weiterhin alles (inkl. Intercompany-Sektion und Ergebnis je Gesellschaft). Die Berichte für Probig AT und Probig DE sind jetzt eigenständige Firmen-Berichte: Briefkopf, Logo und Firmenwortlaut der jeweiligen Gesellschaft (aus den Gesellschafts-Stammdaten) statt des Standard-Kopfs.',
      'Inhaltlich zeigt jeder Entity-Bericht nur die eigenen Dinge: Der AT-Bericht führt „Endkunde" und „Auftraggeber (intern)" auf, sein Erlös ist der fixe Verrechnungspreis (kein Kunden-Verkaufspreis, kein Vertriebs-GK), und die Intercompany-Details (Ziel-Split, Ergebnis der Schwestergesellschaft) fehlen bewusst. Der DE-Bericht zeigt seine Zukauf-Blöcke, den eigenen Vertriebs-GK-Satz und den Kunden-VK — ebenfalls ohne interne Zahlen der Schwester.',
      'Auf der Kennzahlen-Seite heißt der Verrechnungspreis je Sicht passend „Erlös (VP, fix)" bzw. „Zukauf (VP, fix)"; der Gesellschafts-Vergleich erscheint nur im Gesamt-Bericht.',
    ],
  },
  {
    version: '3.266.0',
    date: '2026-07-20T21:30',
    title: 'Kalkulations-PDF: Querformat + Kennzahlen-Seite mit Diagrammen',
    tag: 'improvement',
    changes: [
      'Der Kalkulationsbericht ist jetzt A4 quer — alle Spalten der Bereichs-Tabellen passen nebeneinander (Warengruppen, Entity, Marker, Satz, Std Plan/Ist, Plan, Ist, Abweichung), nichts wird mehr gequetscht oder umgebrochen.',
      'Saubere Seitenumbrüche: Abschnittsüberschriften stehen nie allein am Seitenende, Tabellenköpfe werden auf Folgeseiten wiederholt, und der Summenblock bleibt als Ganzes auf einer Seite.',
      'Neue Abschlussseite „Kennzahlen & Diagramme": KPI-Karten (Ergebnis SK/HK Ist, WES, HK-Abweichung, Verrechnungspreis), Balkendiagramm Plan vs. Ist je Block (Material, Arbeit, Sonder, HK, SK, Erlös), Kostenstruktur der Herstellkosten (Plan/Ist mit Prozentanteilen) und Ergebnis je Gesellschaft als grün/rote Balken.',
    ],
  },
  {
    version: '3.265.0',
    date: '2026-07-20T21:00',
    title: 'Diagramme: Linien tauchen nicht mehr unter 0',
    tag: 'fix',
    changes: [
      'Die Flächen-/Liniendiagramme (z. B. „Stunden je Tag" in der Zeit-Analyse und der Umsatzverlauf im Dashboard) haben durch die weiche Kurvenglättung teils unter die Nulllinie „durchgeschwungen", obwohl keine Werte negativ waren. Die Kurve nutzt jetzt eine überschwingfreie Glättung (monotone) und bleibt sauber auf/über 0.',
    ],
  },
  {
    version: '3.264.0',
    date: '2026-07-20T20:30',
    title: 'Kalkulation: druckbarer PDF-Bericht je Sicht',
    tag: 'feature',
    changes: [
      'Neuer „PDF"-Knopf im Kalkulations-Tab: öffnet den Kalkulationsbericht der gerade gewählten Sicht (Gesamt, Probig AT oder Probig DE) als PDF zum Drucken/Ablegen — mit Firmenkopf, Eckdaten (Projektgruppe, WSP, GK-Sätze, Plan-/Ist-VK), den drei Bereichs-Tabellen inkl. Marker („Ist manuell", „Ist ohne Zeile") und Zukauf-Blöcken in der Kunden-Sicht, dem vollständigen Summenblock (HK → SK → Ergebnisse, WES) und der Intercompany-Übersicht mit Ergebnis je Gesellschaft.',
      'PDF und Bildschirm nutzen dasselbe Rechenmodell — die Zahlen sind garantiert identisch.',
    ],
  },
  {
    version: '3.263.0',
    date: '2026-07-20T19:30',
    title: 'Kalkulation: Verrechnungspreis verhandelbar, Marge aliquot, Ist-VK im Kopf',
    tag: 'improvement',
    changes: [
      'Der Verrechnungspreis ist wieder verhandelbar: Feld in der Intercompany-Karte (leer = der automatische Vorschlag gilt; „Übernehmen (fixieren)" schreibt den Vorschlag fest; Pfeil-Knopf löscht den manuellen Wert wieder). Bei manuellem Preis ergibt sich die Marge als Verrechnungspreis minus AT-Plan-Kosten.',
      'Die Marge erscheint NICHT mehr als eigene Zeile, sondern wird aliquot auf die drei Zukauf-Blöcke (Material, Abwicklung, Sonderpositionen) aufgeschlagen — die Summe der Blöcke ist exakt der Verrechnungspreis.',
      'Der Verkaufspreis IST (= Auftragswert) ist jetzt direkt im Kalkulations-Kopf neben dem Plan-VK pflegbar.',
      'Die Intercompany-Karte zeigt an, welche Gesellschaft als Kunden-Gesellschaft gerechnet wird (gesteuert über das Projektfeld „Gesellschaft (Owner)") und warnt, wenn das Feld am Projekt fehlt — das war der Grund, warum die Zukauf-Zeilen bisher im falschen Reiter standen.',
    ],
  },
  {
    version: '3.262.0',
    date: '2026-07-20T18:00',
    title: 'Kalkulation: Intercompany-Verrechnung nach dem Controlling-Excel',
    tag: 'improvement',
    changes: [
      'Der Verrechnungspreis an die Schwestergesellschaft wird jetzt automatisch berechnet und ist Plan-fixiert: AT-Plan-Kosten (Material inkl. MGK, Abwicklung/Arbeit, Sonderpositionen) plus Margenaufteilung (Ziel-% vom Plan-Ergebnis auf Herstellkosten-Basis, Standard 70 %). Die manuelle Pflege des Verrechnungspreises entfällt.',
      'In der Probig-DE-Sicht erscheinen die Bestandteile als vier Plan-fixierte Zukauf-Zeilen in den Sondereinzelkosten — genau wie im bisherigen Controlling-Excel („Zukauf Material … (inkl. MGK)", „Zukauf Abwicklung …", „Zukauf Sonderpositionen …", „Margenaufteilung 70 % an …").',
      'Die Owner-Gesellschaft (DE) rechnet mit einem eigenen Vertriebs-GK-Satz (Standard 7,5 %) auf ihre Herstellkosten inkl. Zukäufe; die Liefer-Gesellschaft (AT) erlöst den fixen Verrechnungspreis ohne Vertriebs-GK — ihr Plan-Ergebnis ist exakt der Margenanteil. Die Summe der Entity-Ergebnisse entspricht auf HK-Ebene weiterhin exakt dem Gesamtergebnis.',
      'WES heißt jetzt „WES (o. GK) in %" und rechnet wie im Excel: Fertigungsmaterial OHNE Materialgemeinkosten geteilt durch den Verkaufspreis.',
    ],
  },
  {
    version: '3.261.0',
    date: '2026-07-20T17:30',
    title: 'App-Cockpit: Restore-Test & Backup-Übersicht',
    tag: 'feature',
    changes: [
      'Neuer Baustein „Restore-Test" im App-Cockpit: ein monatlicher Job spielt das jüngste Backup automatisch in eine Wegwerf-Datenbank, prüft die Tabellen und wirft sie wieder weg — die echte Datenbank wird dabei nie angefasst. Das Cockpit zeigt „Wiederherstellung getestet vor X · N Tabellen" mit Ampel. So ist sichtbar, dass Backups nicht nur laufen, sondern auch wirklich wiederherstellbar sind.',
      'Der Baustein „Backups" zeigt jetzt zusätzlich eine kurze Übersicht: Anzahl der vorhandenen Sicherungen, Größe der letzten Sicherung und das neueste Archiv.',
      'Ein echter Restore auf die Produktionsdatenbank ist bewusst NICHT als Knopf in der App umgesetzt — das wäre zu gefährlich (überschreibt Live-Daten). Wiederherstellungen laufen weiter kontrolliert über das Runbook.',
    ],
  },
  {
    version: '3.260.0',
    date: '2026-07-20T16:30',
    title: 'Kalkulation: Inline-Bearbeitung mit Auto-Save',
    tag: 'improvement',
    changes: [
      'Die Kalkulationszeilen werden jetzt direkt in der Tabelle bearbeitet — der separate „Bearbeiten"-Modus mit Speichern-Knopf ist weg. Änderungen (Bezeichnung, Warengruppen, Position, Satz/Stunden, Beträge, Entity, Ist-Override) speichern automatisch kurz nach der letzten Eingabe; der Status oben rechts zeigt „Speichert …" bzw. ungespeicherte Änderungen.',
      'Bearbeitet wird in der Sicht „Gesamt"; die Gesellschafts-Sichten (AT/DE) bleiben reine GuV-Ansichten. Die Summen rechnen live beim Tippen mit — jetzt inklusive der Systemzeilen für nicht zugeordnete Ist-Kosten, damit die Live-Vorschau exakt dem gespeicherten Stand entspricht.',
    ],
  },
  {
    version: '3.259.0',
    date: '2026-07-20T15:00',
    title: 'Neu: Projekt-Kalkulation (Vor-/Nachkalkulation) mit Intercompany-Sichten',
    tag: 'feature',
    changes: [
      'Der Bereich „Kaufmännisch" im Projekt ist jetzt die vollständige Kalkulation und ersetzt die bisherigen Register Controlling und Vorkalkulation (und damit die externe ProjektControlling-App): drei Bereiche — Material & Fremdleistung, Arbeit/Fertigung, Sondereinzelkosten — mit PLAN, IST und Abweichung je Zeile.',
      'Rechenschema wie gewohnt: Summe Material + Material-GK % = Material gesamt, + Arbeit + Sonderkosten = HERSTELLKOSTEN, + Vertriebs-GK % = SELBSTKOSTEN; dazu Erlös/Verkaufspreis, Ergebnis SK/HK und WES % — alles für Plan und Ist. GK-Sätze, Plan-Verkaufspreis und Kalkulations-Status (Vorkalkulation in Arbeit … Abgeschlossen & geprüft) werden direkt im Kopf gepflegt.',
      'Ist-Werte kommen automatisch aus dem ERP (Bestellungen + Lagerentnahmen je Hauptwarengruppe, Zeitbuchungen je Position) und lassen sich je Zeile manuell überschreiben („Ist manuell"-Marker, jederzeit auf den ERP-Wert zurücksetzbar). Nicht zugeordnete Ist-Kosten erscheinen als eigene Hinweiszeile, damit die Nachkalkulation vollständig bleibt.',
      'Intercompany: je Kalkulationszeile ist hinterlegt, in welcher Gesellschaft die Kosten anfallen. Sichten „Gesamt / Probig AT / Probig DE" zeigen die GuV je Gesellschaft (Erlös der Liefer-Gesellschaft = Verrechnungspreis, die Auftrags-Gesellschaft trägt ihn als Kostenblock). Verrechnungspreis mit Vorschlagsrechnung nach Ziel-Gewinnverteilung (z. B. 70/30) auf Knopfdruck übernehmbar.',
      'Editor mit Live-Vorschau (Summen rechnen beim Tippen mit), „Material aus Beleg" übernimmt die Auftragspositionen je Warengruppe in den Plan. Projektgruppe (Kommunal/Industrie) und WSP (m) sind neue Projektfelder im Formular und in den Projektdaten.',
    ],
  },
  {
    version: '3.258.0',
    date: '2026-07-20T14:00',
    title: 'Reporting HF/NAL: seitenweise laden (25 pro Seite)',
    tag: 'improvement',
    changes: [
      'Der HF/NAL-Bericht (Reporting) lädt jetzt seitenweise statt alles auf einmal — dadurch baut sich die Seite sofort auf, statt bei vielen Projekten hängenzubleiben. Angezeigt werden standardmäßig alle aktiven Projekte (Status ungleich geschlossen/storniert), je 25 pro Seite mit „Zurück/Weiter".',
      'Die Summen unter der Tabelle beziehen sich auf die aktuelle Seite. Der Excel-Export lädt weiterhin ALLE aktiven Projekte und weist die echten Gesamtsummen aus.',
    ],
  },
  {
    version: '3.257.0',
    date: '2026-07-20T13:30',
    title: 'App-Cockpit: fehlendes Backup wird als Warnung angezeigt',
    tag: 'improvement',
    changes: [
      'Ist noch gar kein Datenbank-Backup eingerichtet, zeigt das App-Cockpit den Baustein „Backups" jetzt gelb als Warnung („Kein Backup eingerichtet — Datensicherung fehlt!") und die Gesamtampel springt auf „Beeinträchtigt" — statt es unauffällig als graues „optional" zu behandeln. So bleibt das Fehlen einer Sicherung sichtbar, bis der Backup-Job läuft.',
    ],
  },
  {
    version: '3.256.0',
    date: '2026-07-20T13:30',
    title: 'Authenticator (TOTP) auch neben Passkey einrichten',
    tag: 'feature',
    changes: [
      'Unter „Sicherheit" (Benutzermenü) lässt sich der Authenticator (TOTP, 6-stelliger Code) jetzt jederzeit selbst einrichten — auch zusätzlich zu einem bestehenden Passkey. QR-Code scannen oder Schlüssel manuell eingeben, mit einem Code bestätigen, fertig.',
      'Beide zweiten Faktoren sind gleichrangig und getrennt verwaltbar (hinzufügen/entfernen). Der letzte verbleibende Faktor kann nicht entfernt werden, damit die Zwei-Faktor-Anmeldung nie ausfällt.',
    ],
  },
  {
    version: '3.255.0',
    date: '2026-07-20T13:00',
    title: 'Neu: App-Cockpit (System) — Gesundheit, Metriken & Backup-Status',
    tag: 'feature',
    changes: [
      'Neuer Bereich „App-Cockpit" unter System (nur für Administratoren): zeigt auf einen Blick die Gesundheit der Infrastruktur — Datenbank, Cache/Queue (Redis), Suche (Meilisearch) und Dateispeicher — jeweils mit Ampel und Antwortzeit. Aktualisiert sich alle 15 Sekunden automatisch.',
      'Backup-Überwachung: Das nächtliche Datenbank-Backup meldet nach jedem Lauf Erfolg oder Fehler; das Cockpit zeigt „Letztes erfolgreiches Backup vor X" mit Ampel (grün frisch, gelb ein Lauf ausgefallen, rot veraltet/fehlgeschlagen). So ist sofort sichtbar, ob die Sicherungen laufen.',
      'Datenbestand als Kennzahlen (aktive Projekte, Geschäftspartner, Artikel, Verkaufsbelege, Bestellungen, offene Aufgaben, aktive Benutzer, Änderungen der letzten 24 h), Laufzeit-Infos (Version, Umgebung, Uptime, Speicher, DB-Größe) und die Auslastung der Hintergrund-Jobs (Queues).',
    ],
  },
  {
    version: '3.254.0',
    date: '2026-07-20T11:00',
    title: 'Projektbericht: Vorschau, alle Belege & neues Design',
    tag: 'improvement',
    changes: [
      'Der Button „Projektbericht" öffnet den Bericht jetzt als Vorschau in einem neuen Tab, statt ihn sofort herunterzuladen — so lässt er sich zuerst ansehen und bei Bedarf von dort speichern oder drucken.',
      'Der Bericht enthält jetzt alle mit dem Projekt verknüpften Belege: eine Finanzübersicht (Auftragswert, Verkauf brutto, Einkauf brutto), eine Tabelle aller Verkaufsbelege (Angebote, Aufträge, Rechnungen …) und aller Einkäufe/Bestellungen (mit Lieferant) — jeweils mit Datum, Status und Beträgen.',
      'Das Design wurde modernisiert: klarer Kopf mit Projektnummer und Status, ruhige Abschnittsüberschriften, dezente Info-Panels und übersichtliche Tabellen mit farblich hervorgehobenen Status — einheitlich mit den übrigen PDF-Berichten (z. B. Kassabuch).',
    ],
  },
  {
    version: '3.253.0',
    date: '2026-07-20T09:45',
    title: 'Kommissionieren startet die Zeiterfassung automatisch',
    tag: 'feature',
    changes: [
      'Beim Klick auf „Kommissionieren" im Kommissionierauftrag läuft jetzt automatisch ein Zeiterfassungs-Timer für das Projekt des Auftrags — Kategorie „Aufträge", Tätigkeit „Lager". So wird die Lagerzeit ohne Zusatzklicks erfasst.',
      'Läuft bereits ein anderer Timer, wird er automatisch gestoppt und auf den Kommissionier-Timer umgeschaltet. Läuft schon der richtige Timer, bleibt er unangetastet.',
      'Als Bemerkung wird die Auftragsnummer hinterlegt (z. B. „Kommissionierung KO-2026-0004"). Fehler beim Timer-Start blockieren das Kommissionieren nicht.',
    ],
  },
  {
    version: '3.252.0',
    date: '2026-07-20T09:30',
    title: 'Kommissionierung: Projekt in der Liste',
    tag: 'improvement',
    changes: [
      'Die Kommissionierungs-Übersicht zeigt jetzt zu jedem Auftrag das Projekt (Name + Code) als führende Spalte — die KO-Nummer allein merkt sich niemand.',
      'Die Suche findet Aufträge jetzt auch über den Projektnamen oder -Code, nicht mehr nur über die Auftragsnummer.',
    ],
  },
  {
    version: '3.251.0',
    date: '2026-07-20T09:00',
    title: 'Beschaffung: Mehrere Status gleichzeitig filtern',
    tag: 'improvement',
    changes: [
      'Der Status-Filter der Bestellliste (Beschaffung → Bestellungen) erlaubt jetzt Mehrfachauswahl: mehrere Status gleichzeitig ankreuzen (z. B. Entwurf UND Bestellt) statt nur einen. Ohne Auswahl werden weiterhin alle Status angezeigt.',
      'Jeder gewählte Status erscheint als eigener, einzeln entfernbarer Filter-Chip; „Auswahl zurücksetzen" leert den Filter. Die Auswahl steht in der URL — „Zurück" landet in derselben Ansicht, und auch der Export übernimmt die gewählten Status.',
    ],
  },
  {
    version: '3.250.0',
    date: '2026-07-19T12:45',
    title: 'iPad & iPhone: Prüfrunde über alle Seiten + Touch-Feinschliff',
    tag: 'improvement',
    changes: [
      'Alle 49 Seiten wurden auf iPhone und iPad (hoch/quer) durchgeprüft — Layout, Fehler, horizontales Scrollen und Touch-Zielgrößen. Keine Seite läuft über den Rand, keine stürzt ab.',
      'Kundenliste: ein versteckter Fehler beim Laden der Filterwerte (Länder/Branchen) konnte die Seite crashen — behoben.',
      'Touch-Feinschliff: Kleine Knöpfe (Zeilen-Aktionen in Listen, Tabellen-Sortierköpfe, „Artikel suchen" im Beleg-Editor, Jahr-Pfeile im Zeitplan) haben auf Touch-Geräten jetzt unsichtbar vergrößerte, fingerfreundliche Trefferflächen; die Filter-Häkchen in Artikel- und Ansprechpartner-Liste nutzen die Design-System-Checkbox.',
    ],
  },
  {
    version: '3.249.0',
    date: '2026-07-19T12:30',
    title: 'Buchhaltung: Digitales Kassabuch',
    tag: 'feature',
    changes: [
      'Neuer Tab „Kassabuch" in der Buchhaltung: Bareinnahmen und Barausgaben je Monat erfassen — mit Datum, Einnahme/Ausgabe, Betrag (Komma-Eingabe), Beschreibung, Belegnummer und Notiz. Jede Buchung kann bearbeitet und (mit Sicherheitsabfrage) gelöscht werden.',
      'Monatsverwaltung mit Übertrag: Jeder Monat wird mit einem Anfangsbestand initialisiert — der Vorschlag ist automatisch der Endbestand des Vormonats (Anfangsbestand + Einnahmen − Ausgaben). Der Anfangsbestand bleibt nachträglich korrigierbar (Stift auf der Karte).',
      'Vier Übersichtskarten (Anfangsbestand, Einnahmen grün, Ausgaben rot, aktueller Bestand) und eine Buchungstabelle mit laufendem Kassenstand nach jeder Buchung — wie im klassischen Kassabuch.',
      'Export für die Buchhaltung: formatiertes Kassabuch-PDF des Monats (für Steuerberater/Ablage) und Excel-Export aller Buchungen. Alle Änderungen werden im Audit-Log festgehalten.',
    ],
  },
  {
    version: '3.248.0',
    date: '2026-07-19T11:15',
    title: 'Stabilität: Absturz-Schutz für den Server & sichere Deploys',
    tag: 'improvement',
    changes: [
      'Absturz-Schutz: Ein einzelner interner Programmierfehler (vergessene Fehlerbehandlung) kann den Server nicht mehr für alle Benutzer beenden — der Fehler wird protokolliert und die App läuft weiter.',
      'Sauberes Herunterfahren: Bei Updates/Neustarts werden laufende Anfragen zu Ende bearbeitet und Datenbank-/Job-Verbindungen ordentlich geschlossen, statt hart gekappt zu werden.',
      'Sichere Deploys: Scheitert eine Datenbank-Migration beim Update, gibt es keinen Absturz-Kreislauf mehr — die bisherige Version läuft weiter, die Fehler-Logs bleiben erhalten und ein Hinweis zeigt direkt auf die Reparatur-Anleitung. (Zuvor führte das zweimal zum Komplettausfall.)',
    ],
  },
  {
    version: '3.247.0',
    date: '2026-07-19T11:00',
    title: 'PDF-Import: PDFs landen automatisch in den Anlagen',
    tag: 'improvement',
    changes: [
      'Beim Angebots-Import werden die hochgeladenen PDFs jetzt serverseitig und automatisch als Anlagen am Beleg gespeichert (im Dateimanager unter Kunde → Belege → Angebotsnummer sichtbar) — Import und Anlagen sind EIN Vorgang.',
      'Schlägt das Speichern der Anlagen fehl, wird der Import abgebrochen und das Angebot zurückgerollt — es kann kein importiertes Angebot ohne seine PDFs mehr entstehen.',
    ],
  },
  {
    version: '3.246.0',
    date: '2026-07-19T10:00',
    title: 'Verkauf: Angebots-PDFs importieren (Button „PDF importieren")',
    tag: 'feature',
    changes: [
      'Im Register Angebote können manuell erstellte Angebots-PDFs (deutsch + englisch) eingelesen werden: Angebotsnummer, Datum, Kunde, Betreff, Währung und Gesamtsumme werden automatisch erkannt und im Prüf-Dialog vorbefüllt — alles bleibt korrigierbar.',
      'Kundenzuordnung mit Vorschlägen aus dem CRM (automatischer Namensabgleich); fehlt der Kunde, kann er direkt aus dem Dialog neu angelegt werden. „Gültig bis" wird auch aus Begleit-PDFs (Angebotsbedingungen/AGB) übernommen.',
      'Das importierte Angebot übernimmt die Original-Nummer aus dem PDF (Duplikat-Prüfung verhindert doppelten Import), die Angebotssumme wird als Pauschal-Position geführt und alle hochgeladenen PDFs hängen als Anhänge am Beleg.',
    ],
  },
  {
    version: '3.245.0',
    date: '2026-07-17T16:30',
    title: 'Projektliste: Seitenblättern, Sammel-Bearbeitung & Archivierung',
    tag: 'feature',
    changes: [
      'Die Projektliste blättert jetzt seitenweise durch ALLE Projekte (100/250/500 pro Seite, Vor/Zurück) — nicht mehr nur die ersten Treffer. Zusätzlich: sortierbare Spalten, ein-/ausblendbare Spalten und CSV-Export.',
      'Sammel-Bearbeitung: Über „Auswählen" mehrere Projekte markieren (oder mit einem Klick alle zum Filter passenden über Seitengrenzen hinweg) und gemeinsam den Status ändern oder archivieren.',
      'Archivieren für Admins auch bei offenen Belegen: Beim (Sammel-)Archivieren gibt es für Administratoren die Option „Auch bei offenen Belegen archivieren" (Altbestand) — sie überspringt die Schutzabfrage gegen nicht stornierte Belege/offene Posten.',
    ],
  },
  {
    version: '3.244.0',
    date: '2026-07-17T16:15',
    title: 'PDF-Berichte: neues, modernes Design (Start: QM/8D-Report)',
    tag: 'improvement',
    changes: [
      'Die PDF-Berichte bekommen ein komplett neues, ruhigeres Design: klarer Kopf mit Nummer, Report-Typ und farbiger Status-Pille, dezentes Stammdaten-Panel statt vollflächiger Farbbalken.',
      'Der QM-/8D-Report ist der erste umgestellte Bericht: die D-Schritte erscheinen als nummerierte Blöcke (ausgefüllt = blau, offen = grau markiert), Kosten- und Zeiten-Tabellen mit dezenten Linien und Summenzeile (Kosten weiterhin je Währung getrennt).',
      'Grundlage ist ein gemeinsames PDF-Design-System, damit die weiteren Berichte (Projektbericht, Angebote, Bestellungen …) Schritt für Schritt im gleichen Stil folgen können.',
    ],
  },
  {
    version: '3.243.0',
    date: '2026-07-17T16:00',
    title: 'Projekt-Checkliste: NEIN zählt als erledigt + Zeilentitel bearbeitbar',
    tag: 'improvement',
    changes: [
      'Checkliste: Auch ein NEIN gilt jetzt als erledigt (grün) — manche Punkte werden schlicht nicht gebraucht. Datum und Zeichen werden dabei automatisch gestempelt; erst das Zurücksetzen auf „offen" entfernt den Stempel.',
      'Checkliste & Projektaufgaben: Der Titel jeder Zeile kann jetzt direkt in der Liste bearbeitet werden (Klick in den Text, Änderung wird beim Verlassen des Feldes gespeichert).',
    ],
  },
  {
    version: '3.242.0',
    date: '2026-07-17T15:30',
    title: 'Projekt-Ampel wird automatisch aus dem Liefertermin ermittelt',
    tag: 'feature',
    changes: [
      'Die Projekt-Ampel (Gesundheit) wird jetzt automatisch berechnet statt manuell gesetzt: Rot = Liefertermin überschritten, Gelb = Liefertermin in ≤ 14 Tagen oder Projekt pausiert, sonst Grün. Abgeschlossene und stornierte Projekte sind immer Grün.',
      'Als Termin zählt der Liefertermin, ersatzweise das geplante Ende; ohne beide bleibt die Ampel grün (nicht bewertbar). Die Auswertungen „Projektampel" und „kritische Projekte" (Dashboard/Analyse) spiegeln damit den echten Terminstatus.',
      'Hinweis: Die wirtschaftliche Dimension (Ist-Kosten gegen Auftragswert) fließt vorerst nicht ein — sie folgt separat.',
    ],
  },
  {
    version: '3.241.0',
    date: '2026-07-17T15:00',
    title: 'QM-Prozesse ist ein eigenständiges Modul',
    tag: 'improvement',
    changes: [
      'Die QM-Prozesse sind nicht mehr unter „Technik" eingegliedert, sondern bilden in der Navigation einen eigenen Bereich „Qualität". Ein Klick auf die Bereichs-Überschrift führt direkt zu den QM-Prozessen.',
    ],
  },
  {
    version: '3.240.0',
    date: '2026-07-17T14:30',
    title: 'Stabilitäts-Paket: Funktionsprüfung der App (Bugfixes)',
    tag: 'fix',
    changes: [
      'Aufgaben: Ein erneutes Speichern einer bereits erledigten Aufgabe überschreibt das ursprüngliche Erledigt-Datum nicht mehr; das Umsortieren der Checkliste wird jetzt im Audit-Log festgehalten.',
      'Beträge und Stunden: Deutsche Eingaben wie „1.500" oder „1.500,50" werden jetzt korrekt gelesen (vorher konnte „1.500" als 1,50 gespeichert werden); ungültige Eingaben zeigen einen Hinweis statt stumm zu verpuffen.',
      'Fehlende Rückmeldungen behoben: Fehlgeschlagenes Speichern (Haken, Zuständige, Termine, Löschen) zeigt jetzt überall eine Fehlermeldung; Listen zeigen bei Ladefehlern einen klaren Fehlerhinweis statt „keine Daten".',
      'Zeitplan/Timeline: Balken können beim Ziehen nicht mehr „verschwinden"; Phasen-Änderungen aus dem Projekt heraus aktualisieren die Zeitplan-Seite sofort; der QM-Report summiert Kosten je Währung getrennt.',
      'Montage: Einsatz-Ende kann nicht mehr vor dem Beginn liegen; der Kalender-Feed faltet Umlaute korrekt (RFC 5545), liefert nur noch die letzten 12 Monate und verträgt parallele Erst-Aufrufe; Doppelklick auf „Durchgeführt" verschiebt Wartungen nicht mehr doppelt.',
    ],
  },
  {
    version: '3.239.0',
    date: '2026-07-17T13:20',
    title: 'Checkliste: „Zeichen" wählt HR-Mitarbeiter',
    tag: 'improvement',
    changes: [
      'Das „Zeichen"-Feld je Checklisten-Punkt listet jetzt die HR-Mitarbeiter (statt der Login-Benutzer). Das Kürzel wird automatisch aus Vor- und Nachname gebildet (z. B. „Felix Perner" → „FP") — es muss nichts zusätzlich gepflegt werden.',
    ],
  },
  {
    version: '3.238.0',
    date: '2026-07-17T13:00',
    title: 'Projekt-Checkliste: getrennte JA/NEIN-Spalten & moderner Datums-Auswähler',
    tag: 'improvement',
    changes: [
      'JA und NEIN sind jetzt zwei getrennte Spalten: „noch nicht entschieden" ist dadurch klar von „NEIN" unterscheidbar. Ein erneuter Klick auf die aktive Seite setzt den Punkt wieder auf offen.',
      'Das Datumsfeld je Checklisten-Punkt nutzt jetzt den einheitlichen ProERP-Kalender statt des OS-eigenen Auswählers.',
    ],
  },
  {
    version: '3.237.0',
    date: '2026-07-17T12:45',
    title: 'Projekt: Register „Meilensteine" entfernt',
    tag: 'improvement',
    changes: [
      'Das Unter-Register „Meilensteine" unter „Planung" wurde entfernt — die Planung umfasst jetzt nur noch Zeitplan, Termine und Phasen.',
      'Bestehende Meilenstein-Daten bleiben in der Datenbank erhalten; nur die Ansicht wurde entfernt.',
    ],
  },
  {
    version: '3.236.0',
    date: '2026-07-17T12:15',
    title: 'Projekt: Projektdaten & Checkliste modernisiert, Datum/Zeichen editierbar',
    tag: 'improvement',
    changes: [
      'Der Tab „Projektdaten" wurde aufgeräumt: klare Abschnitts-Überschriften mit Icon und Kurzbeschreibung statt der grauen Balken, ruhigere Abstände und ein Hinweis, dass Änderungen automatisch gespeichert werden.',
      'Die Checkliste wurde modernisiert: Fortschrittsbalken mit Prozent, Kategorie-Blöcke mit Erledigt-Zähler, erledigte Zeilen dezent grün hinterlegt.',
      'Prozess-Fix: Datum und Zeichen (Benutzer) eines Checklisten-Punkts lassen sich jetzt nachträglich ändern — Datum über ein Datumsfeld, Zeichen über eine Benutzerauswahl. Beim Abhaken werden weiterhin heutiges Datum und der eigene Benutzer vorgestempelt.',
      'Registerstruktur: Aus dem Register „Aufgaben" wird „Checkliste"; „Aufgaben" ist jetzt ein Unterpunkt darin (Checkliste zuerst).',
    ],
  },
  {
    version: '3.235.0',
    date: '2026-07-17T11:45',
    title: 'Neues Modul: QM-Prozesse — 8D/4D/KVP (PM-App-Integration, Phase E)',
    tag: 'feature',
    changes: [
      'Neue Seite „QM-Prozesse" (Technik): Reklamationen als 8D-/4D-Report und Verbesserungen als KVP strukturiert abarbeiten — mit automatischer Nummer (QM-Jahr-Nr.), Projekt-/Kunden-Zuordnung und Verantwortlichem.',
      'Die D-Schritte (bei 8D: D1 Team bis D8 Abschluss) werden je Prozessart passend angezeigt und speichern automatisch beim Verlassen des Felds; Hilfetexte erklären jeden Schritt.',
      'Kosten und Arbeitszeiten je Prozess erfassen — mit laufender Summe; Datei-Anhänge (Fotos, Protokolle) liegen als normale Dokumente im Dateibereich des Projekts bzw. Kunden.',
      'Sichten „Meine Prozesse" und „Besprechung" wie in der PM-App; PDF-Report je Prozess im PROBIG-Layout; Prozessvorlagen über den Daten-Editor pflegbar.',
    ],
  },
  {
    version: '3.234.0',
    date: '2026-07-17T11:15',
    title: 'Moderner Datums-Auswähler auch in Projekten',
    tag: 'improvement',
    changes: [
      'Die Datumsfelder in den Projektdaten (Liefertermin von/bis) und bei den Projektaufgaben (Deadline) öffnen jetzt den einheitlichen ProERP-Kalender (Popover, deutsche Beschriftung, Wochenstart Montag, „Heute"/„Löschen") statt des systemabhängigen Browser-Kalenders.',
      'Damit nutzt die gesamte App durchgängig denselben modernen Datums-Auswähler.',
    ],
  },
  {
    version: '3.233.0',
    date: '2026-07-17T10:00',
    title: 'Zeit-Analyse: Filter nach Mitarbeiter',
    tag: 'improvement',
    changes: [
      'Die Zeit-Analyse lässt sich jetzt zusätzlich auf einen einzelnen Mitarbeiter eingrenzen — die Auswahl listet alle Mitarbeiter mit Zeitbuchungen.',
    ],
  },
  {
    version: '3.232.0',
    date: '2026-07-17T09:45',
    title: 'Moderner Datums-Auswähler statt Browser-Kalender',
    tag: 'improvement',
    changes: [
      'Die Datumsfelder in der Zeit-Analyse, im Reporting (Lager- & Artikelanalyse), im Änderungsprotokoll und in der Kommissionierung öffnen jetzt den einheitlichen ProERP-Kalender (deutsche Beschriftung, Wochenstart Montag, Tastatursteuerung) statt des OS-eigenen Auswählers.',
    ],
  },
  {
    version: '3.231.0',
    date: '2026-07-17T09:30',
    title: 'Montage: „Monteure" statt „Techniker"',
    tag: 'improvement',
    changes: [
      'Begriff korrigiert: Die Mitarbeiter im Montage-Einsatz heißen überall „Monteure" (Techniker = Büro/Konstruktion) — in Einsatzliste, Dialog, Timeline und Kalender-Feed.',
      'Die Monteur-Auswahl am Einsatz zeigt nur noch aktive Mitarbeiter mit der Stelle „Montage"/„Monteur" (solange keine Stellen gepflegt sind, alle aktiven Mitarbeiter).',
      'Die Plantafel zeigt jetzt ebenfalls die zugeteilten Monteure (nicht nur den Freitext).',
    ],
  },
  {
    version: '3.230.0',
    date: '2026-07-16T21:30',
    title: 'Montage: Techniker, Kategorien, Timeline & Kalender-Abo (PM-App-Integration, Phase D)',
    tag: 'feature',
    changes: [
      'Techniker am Einsatz sind jetzt echte Mitarbeiter (Mehrfachauswahl aus dem HR-Modul); für Fremdfirmen gibt es weiterhin ein Freitextfeld. Die Einsatzliste lässt sich nach Status, Kategorie und Techniker filtern.',
      'Neue Montage-Kategorien (Montage, Wartung, Inbetriebnahme, Reparatur, Service — im Daten-Editor erweiterbar) mit Farbcodierung in Liste und Timeline.',
      'Neuer Tab „Timeline": alle Einsätze als Gantt je Techniker im Kalenderwochen-Raster. Termine per Ziehen verschieben oder verlängern — gespeichert wird nach Bestätigung, mit Warnung bei Doppelbelegung eines Technikers.',
      'Kalender-Abo (iCal): Montage-Einsätze in Google Kalender, Outlook oder iPhone abonnieren — über eine geheime Feed-URL, ohne Google-Anmeldung. Die URL kann jederzeit neu erzeugt werden (alte Links werden ungültig).',
    ],
  },
  {
    version: '3.229.0',
    date: '2026-07-16T19:45',
    title: 'Projekte: Zeitplan mit Gantt & PDF-Export (PM-App-Integration, Phase C)',
    tag: 'feature',
    changes: [
      'Neue Seite „Zeitplan" (Vertrieb & Projekte): alle aktiven Projekte als Gantt im Kalenderwochen-Raster — Phasen farbcodiert, Heute-Linie, Liefertermin-Marker, Jahr umschaltbar.',
      'Termine direkt im Gantt ändern: Balken verschieben oder an den Kanten ziehen; gespeichert wird erst nach Bestätigung — mit Warnung bei Konflikten (Phase endet nach dem Liefertermin oder überholt die Reihenfolge).',
      'Zeitplan-Export als PDF in Deutsch und Englisch (A4 quer, KW-Raster, Legende, Marker auf der aktuellen Woche) — wie der Export der PM-App.',
      'Im Projekt-Detail zeigt „Planung" jetzt zuerst den Zeitplan des Projekts (eine Zeile je Phase, gleiche Bedienung).',
    ],
  },
  {
    version: '3.228.0',
    date: '2026-07-16T18:10',
    title: 'Projekte: Projektdaten-Tab & Projektbericht-PDF (PM-App-Integration, Phase B)',
    tag: 'feature',
    changes: [
      'Neuer Tab „Projektdaten" im Projekt-Detail: Auftrags-Stammdaten (Land, Produkt, Branche, Anlagentyp, Liefertermine, Incoterms, Verpackung, Zahlungsbedingungen, technische Eckdaten) mit Auto-Speichern je Feld — wie in der PM-App.',
      'Projektleitung als echte Benutzer: Mehrfachauswahl mit Kürzel-Chips (Kürzel aus der Benutzerverwaltung).',
      'Ansprechpartner und Lieferadresse kommen direkt aus dem CRM (Auswahl aus den Kontakten/Lieferadressen des Projektkunden); Telefon und Adressblock werden automatisch übernommen.',
      'Neuer Kopf-Button „Projektbericht": erzeugt das Projekt-PDF im Layout der PM-App (Übersicht, Termine & Lieferung, Adressen, technische Daten, Projekt-Checkliste, Montagen, Aufgaben).',
    ],
  },
  {
    version: '3.227.0',
    date: '2026-07-16T16:15',
    title: 'Projektaufgaben: automatische Benachrichtigungen',
    tag: 'feature',
    changes: [
      'Zuständige werden jetzt automatisch benachrichtigt (Glocke oben rechts): sofort bei der Zuweisung einer Aufgabe, einmalig wenn eine Aufgabe in den nächsten 7 Tagen fällig wird und einmalig, wenn sie überfällig ist — mit Direktlink ins Projekt.',
      'Keine Doppel-Meldungen: Jede Stufe wird pro Aufgabe nur einmal gemeldet; ändert sich Termin oder Zuständiger, wird erneut (an die richtige Person) gemeldet.',
      'Der Prüf-Lauf startet automatisch mit der Anwendung und läuft alle 30 Minuten — ohne zusätzliche Infrastruktur.',
    ],
  },
  {
    version: '3.226.0',
    date: '2026-07-16T15:45',
    title: 'Projekte: Aufgaben & Checkliste (PM-App-Integration, Phase A)',
    tag: 'feature',
    changes: [
      'Neuer Projekt-Tab „Aufgaben" mit den Registern Aufgaben und Checkliste — der Kern der bisherigen PROBIG-Projektmanagement-App ist damit in ProERP.',
      'Aufgaben: Erledigt-Haken, Zuständige per Benutzer-Kürzel, Deadline mit Ampel (überfällig rot, fällig in ≤ 7 Tagen orange), Umsortieren per Ziehen, „Projektaufgaben löschen" mit Bestätigung und „Standard-Aufgaben laden".',
      'Checkliste: Punkte je Kategorie (Übergabe, Bestellung, Technik, Zukauf, Lieferung, Montage, Controlling) mit JA/NEIN, automatischem Datum-/Zeichen-Stempel (eigenes Kürzel) und Lieferant/Prozess bei Zukauf-Punkten; Standard-Checkliste per Klick ladbar, freie Zusatzpunkte je Kategorie.',
      'Benutzer haben jetzt ein Kürzel (Benutzerverwaltung) — es erscheint in Zuweisungen und als Checklisten-Zeichen.',
      'Dashboard: neue Karte „Offene Projektaufgaben" mit Hinweis auf kritische Deadlines (≤ 7 Tage).',
      'Standard-Vorlagen (Aufgaben + Checkliste) werden automatisch aus dem Alt-App-Standard vorbefüllt und sind über den Daten-Editor pflegbar.',
    ],
  },
  {
    version: '3.225.0',
    date: '2026-07-16T14:30',
    title: 'Zeiterfassung: Admin-Analyse über alle Mitarbeiter',
    tag: 'feature',
    changes: [
      'Neue Seite „Zeit-Analyse" (unter „Personal & Zeit", nur für Zeiterfassungs-Administratoren): tenant-weite Auswertung der erfassten Zeiten über alle Mitarbeiter — anders als das persönliche Dashboard, das nur die eigenen Stunden zeigt.',
      'Kennzahlen (Gesamtstunden, Kosten, Mitarbeiter, Buchungen) plus Aufschlüsselung der Stunden nach Mitarbeiter, Projekt, Kategorie und Gesellschaft sowie ein Tagesverlauf.',
      'Zeitraum wählbar (Dieser/Letzter Monat, Letzte 7/30 Tage oder frei) und optional auf eine Gesellschaft eingegrenzt; die Mitarbeiter-Tabelle mit Stunden und Kosten lässt sich als CSV exportieren.',
      'Erreichbar über die Navigation oder den Button „Analyse" auf dem Zeiterfassungs-Dashboard.',
    ],
  },
  {
    version: '3.224.0',
    date: '2026-07-16T14:00',
    title: 'Technik: Seitenleiste flach — nur noch Überschrift + vier Seiten',
    tag: 'improvement',
    changes: [
      'Der zusätzliche aufklappbare Eintrag „Technik" in der Seitenleiste entfällt. Es gibt jetzt nur noch die Bereichs-Überschrift „Technik" und direkt darunter die vier Seiten (Zeichnungsnummern, Anträge, Materialcodes, Warengruppen) — kein doppeltes „Technik" mehr.',
      'Ein Klick auf die Überschrift „Technik" lädt weiterhin die Zeichnungsnummern; der Zähler offener Anträge sitzt jetzt direkt am Eintrag „Anträge".',
    ],
  },
  {
    version: '3.223.0',
    date: '2026-07-16T13:50',
    title: 'Modul „Engineering" heißt jetzt „Technik" — aufgeräumt auf vier Seiten',
    tag: 'improvement',
    changes: [
      'Das Modul „Engineering" heißt in der Oberfläche jetzt „Technik". Es hat nur noch vier Seiten: Zeichnungsnummern, Anträge, Materialcodes und Warengruppen — die frühere Übersichtsseite entfällt.',
      'Ein Klick auf die Überschrift „Technik" in der Seitenleiste lädt direkt die Zeichnungsnummern.',
      'Auf der Zeichnungsnummern-Seite sitzen jetzt alle Aktionen: „Neue Zeichnungsnummer" legt nur eine Zeichnungsnummer an; „Artikelnummer anlegen" startet den Assistenten, der Zeichnungs- und Artikelnummer in einem Schritt erzeugt; aus einer bestehenden Zeile lässt sich per „Artikel beantragen" direkt ein Artikel dazu anlegen. Die Taric-Codes-Verwaltung ist ebenfalls hier erreichbar.',
      'Alte Lesezeichen auf „/engineering/zeichnungsnummern" funktionieren weiterhin.',
    ],
  },
  {
    version: '3.222.0',
    date: '2026-07-16T13:45',
    title: 'Reporting: neue Berichtsgruppe „Lager- & Artikelanalyse"',
    tag: 'feature',
    changes: [
      'Neuer Reiter „Lager & Artikel" im Reporting mit sechs Berichten: Bestandsbewertung, Reichweiten-/Ladenhüter-Analyse, Mindestbestand-Unterschreitung, Bestandsbewegungen, ABC-/Verbrauchsanalyse und Verfügbarkeit (ATP). Je Bericht ein eigenes Parameter-Formular (Zeitraum/Stichtag, Bewertung, Schwellen, Filter) und eine Ergebnistabelle mit Kennzahlen.',
      'Jeder Bericht lässt sich als CSV, Excel (XLSX, mit echten summierbaren Zahlen) oder PDF exportieren.',
      'Bestandsbewertung wahlweise mit gleitendem Ø-Einkaufspreis oder Standard-EK und wahlweise zum Stichtag (historischer Bestand aus den Lagerbewegungen rekonstruiert); ABC-Analyse wahlweise auf Verbrauchs- oder Umsatzbasis.',
      'Jeder erzeugte Bericht wird in einer Lauf-Historie protokolliert (revisionssicher) und als Ereignis im System gemeldet.',
    ],
  },
  {
    version: '3.221.0',
    date: '2026-07-16T13:30',
    title: 'CRM-Kunden: standardmäßig nur aktive',
    tag: 'improvement',
    changes: [
      'Die Kundenliste zeigt jetzt standardmäßig nur aktive Kunden — inaktive (deaktivierte) sind ausgeblendet, bis man den Status-Filter umstellt.',
      'Der Status-Filter hat weiterhin „Aktiv", „Inaktiv" und neu „Alle Status"; bei „Inaktiv"/„Alle" erscheint ein entfernbarer Filter-Chip, der zurück auf den Default (nur aktiv) führt.',
    ],
  },
  {
    version: '3.220.0',
    date: '2026-07-16T13:15',
    title: 'Engineering: Artikelnummer wird erst bei der Freigabe vergeben',
    tag: 'fix',
    changes: [
      'Beim Anlegen eines Artikel-Antrags wird die Artikelnummer nicht mehr sofort reserviert — sie entsteht erst bei der Freigabe und wird genau dann verbindlich auf Eindeutigkeit geprüft und der Artikel angelegt. Das behebt einen Fehler, bei dem eine Nummer, die zu einem gelöschten Artikel gehörte, fälschlich als frei galt und der Antrag sich später nicht freigeben ließ.',
      'Die Eindeutigkeitsprüfung zählt jetzt auch gelöschte Artikel/Nummern mit: einmal vergebene Nummern bleiben dauerhaft belegt und werden nicht wiederverwendet (auch die laufende Zeichnungsnummer springt nach dem Löschen der höchsten Nummer nicht mehr zurück).',
      'Offene Anträge zeigen die Artikelnummer als „vorläufig" an (aus Zeichnung + Material) — die endgültige Nummer erscheint nach der Freigabe.',
    ],
  },
  {
    version: '3.219.0',
    date: '2026-07-16T13:00',
    title: 'Kommissionierung: Packstücke aufsteigend · Beleg-Menü einzeilig',
    tag: 'improvement',
    changes: [
      'Packstücke (Colli) werden in der Kommissionierung jetzt immer aufsteigend sortiert (Colli 1, 2, 3 …) — in der Ansicht, auf der Packliste (PDF) und im XLSX-Export.',
      'Im Positions-Menü des Beleg-Editors („Weitere Optionen") bricht der Eintrag „Als Seitenumbruch" (und „Als Überschrift") nicht mehr auf zwei Zeilen um — das Menü passt sich der Textbreite an.',
    ],
  },
  {
    version: '3.218.0',
    date: '2026-07-16T12:15',
    title: 'Kommando-Palette: Schnellaktionen zum Anlegen',
    tag: 'feature',
    changes: [
      'Die ⌘K-Palette hat jetzt eine Gruppe „Aktionen": „Neues Projekt", „Beleg anlegen (Verkauf)", „Neuer Kunde / Lieferant" und „Neue Bestellung (Einkauf)". Ein Klick springt auf die jeweilige Seite und öffnet dort direkt den Anlegen-Dialog.',
      'Die Aktionen erscheinen nur mit dem passenden Anlege-Recht.',
    ],
  },
  {
    version: '3.217.0',
    date: '2026-07-16T12:00',
    title: 'Artikelliste: Spalte „Beschaffung" entfernt',
    tag: 'improvement',
    changes: [
      'Die Spalte „Beschaffung" (Eigenfertigung/Zukauf) ist aus der Artikelliste entfernt — die Klassifizierung erfolgt jetzt über die Spalte „Typ" (Standardartikel/Projektbezogen).',
      'Der zugehörige Filter „Alle Beschaffung" wurde ebenfalls entfernt; der CSV-Export führt statt „Beschaffung" nun die Spalte „Typ".',
      'Das Feld bleibt am Artikel selbst erhalten (Detailseite/Formular) — nur die Listenspalte ist weg.',
    ],
  },
  {
    version: '3.216.0',
    date: '2026-07-16T11:15',
    title: 'Kommando-Palette: ⌘K sucht UND steuert, plus Kürzel-Spickzettel (?)',
    tag: 'feature',
    changes: [
      'Die ⌘K-Palette kann jetzt mehr als suchen: bei leerer Eingabe (oder mit führendem „>") zeigt sie Befehle. „Navigation" springt zu jeder Seite/jedem Bereich (nach Berechtigung gefiltert), „Ansicht" schaltet Design hell/dunkel, Seitenleiste und die Kürzel-Übersicht.',
      'Neuer Spickzettel aller Tastenkürzel: mit „?" (außerhalb von Eingabefeldern) aufrufbar oder über die Palette. Gespeist aus einem zentralen Kürzel-Register.',
      'Wie gewohnt: ↑/↓ wählen, ↵ ausführen, Esc schließt. Beim Tippen eines normalen Begriffs sucht die Palette weiter modulübergreifend.',
    ],
  },
  {
    version: '3.215.0',
    date: '2026-07-16T11:00',
    title: 'Artikelliste: Bestand als reine Ampel (ohne „nachbestellen"-Text)',
    tag: 'improvement',
    changes: [
      'Die Spalte „Bestand" in der Artikelliste zeigt den Status jetzt nur noch über die farbige Ampel (grün/orange/rot) neben der Menge — der zusätzliche Text „nachbestellen" bei niedrigem Bestand entfällt, die Zeile bleibt dadurch ruhiger.',
    ],
  },
  {
    version: '3.214.0',
    date: '2026-07-16T10:30',
    title: 'CRM: mehr Filter, Ansprechpartner-Mobil & Lieferadressen verwalten',
    tag: 'feature',
    changes: [
      'Kundenliste: deutlich mehr Filter — zusätzlich zu Typ und Status jetzt nach betreuender Gesellschaft (Owner, inkl. „Ohne Zuordnung"), Land und Branche. Die Länder- und Branchen-Auswahl wird serverseitig aus dem Bestand ermittelt (mit Trefferanzahl) und kommt ohne das Laden aller Datensätze aus; jeder aktive Filter erscheint als Chip und wirkt auch auf den Export.',
      'Ansprechpartner: Der Bearbeiten-Dialog trennt jetzt Festnetz und Mobilnummer (bisher nur ein Telefonfeld) — beide werden gepflegt und in der Ansprechpartner-Liste angezeigt.',
      'Lieferadressen (Kunden-Detail): neue Schaltflächen „Lieferadresse hinzufügen" (eigenständige Adresse anlegen und direkt verknüpfen) und „Lieferadresse verknüpfen" (bestehende Lieferadresse diesem Kunden zuordnen) — vorher waren Lieferadressen nur lesbar.',
    ],
  },
  {
    version: '3.213.0',
    date: '2026-07-16T10:00',
    title: 'Neuer Bereich „Selektionspool" — gespeicherte, bearbeitbare Auswahllisten',
    tag: 'feature',
    changes: [
      'Neue Seite „Selektionspool" (unter „Übersicht"): benannte, team-weit geteilte Auswahllisten für Kunden, Belege oder Artikel — anders als der bisherige Analyse-Reiter bleiben sie gespeichert und lassen sich später weiter bearbeiten.',
      'Pro Pool: aufgelöste Live-Daten der enthaltenen Datensätze mit art-spezifischen Spalten und Kennzahlen-Kacheln, CSV-Export sowie Mehrfach-Auswahl zum Entfernen (entfernt nur die Pool-Zugehörigkeit, nie den echten Datensatz).',
      'Datensätze per Filter-Suche finden und einzeln oder als komplette Trefferliste in den Pool übernehmen; Belege wahlweise aus Verkauf oder Einkauf.',
      'Pools anlegen, umbenennen und löschen — Umbenennen/Löschen nur für den Ersteller bzw. Administratoren sichtbar. Zugriff über die Berechtigung „selectionpool.manage".',
    ],
  },
  {
    version: '3.212.0',
    date: '2026-07-14T18:00',
    title: 'Neuer Tab „Nummernkreis" in den Einstellungen + automatische Projekt-Nummern',
    tag: 'feature',
    changes: [
      'Belege (Angebot/Auftrag/Rechnung/Lieferschein/etc.), Kommissionieraufträge und Projekte vergeben ihre laufende Nummer jetzt über einen persistenten Zählerstand statt einer Live-Zählung der vorhandenen Belege — behebt eine bisherige Kollisionsgefahr, falls jemals ein Beleg gelöscht wurde.',
      'Neuer Tab „Nummernkreis" in den Einstellungen zeigt jeden Zählerstand (Beleg-/Entitätsart, Gesellschaft falls gebunden, Jahr, aktueller/nächster Wert) und erlaubt eine manuelle Korrektur — für den Fall, dass sich mal jemand bei einer Nummer vertan hat.',
      'Projekte bekommen jetzt ebenfalls eine automatische Nummer (P-2026-0001), wenn beim Anlegen kein eigener Code angegeben wird — ein selbst gewählter Code funktioniert weiterhin unverändert.',
    ],
  },
  {
    version: '3.211.0',
    date: '2026-07-14T15:00',
    title: 'Analyse: neuer Reiter „Selektionspool" für Kunde-/Artikel-Filterabfragen',
    tag: 'feature',
    changes: [
      'Neuer Reiter „Selektionspool" im Modul Analyse: Umschalter zwischen Kunde und Artikel wechselt das komplette Filter-Formular. Kunde: Ort, PLZ, Land, Gesellschaft und Zeitraum filtern, Ergebnis inkl. Umsatz je Kunde im gewählten Zeitraum (Rechnungs-Belege) — z. B. „Umsatz von allen Kunden deren Adresse in Hamburg ist".',
      'Artikel-Modus: Volltextsuche (Nummer/Name/Beschreibung) plus ein „Material"-Feld, das sowohl den Material-Code am Ende der Artikelnummer als auch die Artikel-Textfelder selbst durchsucht — ein Treffer in einem der beiden reicht (z. B. „alle Artikel bei denen Material POM vorkommt"). Gesellschaft/Zeitraum sind hier deaktiviert mit Hinweis, da Artikel gesellschaftsübergreifende Stammdaten sind.',
      'Ergebnis immer als Kennzahlen-Kacheln (Anzahl + Umsatz- bzw. Lagerwert-Summe) über einer Tabelle mit CSV-Export — keine reine Liste ohne Kennzahlen und umgekehrt.',
      'Bewusst kuratierte Filter je Entität statt einer generischen, frei kombinierbaren Abfrage-Engine (ADR 0046) — Sicherheitsrisiko einer nutzerdefinierten Datenbankabfrage ohne automatische Mandanten-Prüfung wäre sonst zu hoch.',
    ],
  },
  {
    version: '3.210.0',
    date: '2026-07-14T14:00',
    title: 'Kommissionierung: Baugruppen-Druck, Lagerausgang-Freigabe getrennt, neue Belege',
    tag: 'feature',
    changes: [
      'Baugruppen sind in der Vollbild-Bedarfsliste jetzt per Drag & Drop als Ganzes umsortierbar (nicht nur einzelne Positionen) — die Gruppen-Ansicht sortierte bisher außerdem fälschlich alphabetisch statt nach Positions-Reihenfolge, das ist mitbehoben.',
      'Kommissionierung abschließen (Lagerist) und Lagerausgang buchen (Admin/Sekretariat) sind jetzt zwei getrennte Schritte mit getrennter Berechtigung — bisher löste ein Lagerist mit Kommissionier-Recht automatisch auch die Lagerbuchung mit aus.',
      'Kommissionierbeleg neu gestaltet: Hochformat, Baugruppen-Überschriften, Checkbox zum Abhaken statt einer offenen Linie — plus eine neue zweite Fassung „kumuliert", die gleiche Artikel zusammenfasst und nach Hauptwarengruppe sortiert.',
      'Lieferscheine drucken jetzt ebenfalls Baugruppen-Überschriften, wenn sie aus einem Kommissionierauftrag entstanden sind.',
      'Digitales Kommissionieren am Tablet: neuer Umschalter zwischen „Kumuliert" (bisheriges Verhalten) und „1:1" (Einzelpositionen mit Baugruppen-Bändern) — nützlich, wenn gleiche Artikel unterschiedliche Lager-Vermerke tragen.',
    ],
  },
  {
    version: '3.209.0',
    date: '2026-07-14T13:00',
    title: 'Kommissionierung: Fotos sauber zugeordnet, Abschluss-Prüfung je Packstück',
    tag: 'fix',
    changes: [
      'Der Reiter „Fotos" im Kommissionierauftrag ist jetzt eine reine Galerie (gruppiert nach Packstück bzw. Position, Alt-Fotos ohne Zuordnung unter „Ohne Zuordnung") — der bisherige freie Upload-Button dort erzeugte Fotos ohne jede Zuordnung und wurde entfernt. Fotos entstehen weiterhin über die Packstück-Karte (Register „Packstücke") bzw. die Positions-Zeile beim Kommissionieren am Tablet.',
      'Abschluss-Voraussetzung verschärft (ADR 0043 §7): Sind Packstücke angelegt, braucht jedes einzelne mindestens ein Foto, bevor die Kommissionierung abgeschlossen werden kann — die Fehlermeldung nennt die betroffenen Packstücke namentlich. Ohne Packstücke gilt weiterhin die einfache Ein-Foto-Regel für den ganzen Auftrag.',
    ],
  },
  {
    version: '3.208.0',
    date: '2026-07-14T12:00',
    title: 'Analyse: Drill-downs, Artikel-Detail, Vorjahresvergleich & XLSX-Export',
    tag: 'feature',
    changes: [
      'Drill-down in allen Abfrage-Tabs: Klick auf eine Zeile der Top-Tabelle grenzt die Auswertung ein (Artikel, Kunde/Lieferant, Projekt) — ein Chip neben den Filtern hebt die Eingrenzung wieder auf.',
      'Artikel-Tiefenanalyse: Bei gewähltem Artikel erscheinen drei zusätzliche Auswertungen — Preisentwicklung (Ø Einkaufspreis je Monat aus Bestellungen), Bestandsverlauf am Monatsende (aus allen Lagerbewegungen rekonstruiert) und Einkauf nach Lieferant.',
      'Belege: Umschalter zwischen Wert (€) und Anzahl sowie Vorjahresvergleich — der gleiche Zeitraum ein Jahr zurück wird als zweite Fläche über den aktuellen gelegt, inkl. Vorjahres-Kennzahl.',
      'Projekte: dritte Zeitreihe „Entnommen" (projektbezogene Lagerabgänge, bewertet mit dem EK-Snapshot) in Chart, Kennzahlen und Top-Tabelle; Klick auf ein Projekt startet die Einzelprojekt-Analyse.',
      'Jede Abfrage lässt sich als Excel-Datei exportieren (Blatt „Monate" mit der Zeitreihe + Top-Blatt, Beträge als summierbare Euro-Zahlen).',
    ],
  },
  {
    version: '3.207.0',
    date: '2026-07-14T11:00',
    title: 'iPad-Optimierung: Tippziele, Register & Drag & Drop',
    tag: 'improvement',
    changes: [
      'Tippziele auf Touch-Geräten (iPad) angehoben: Haupt- und Unter-Register (auch verschachtelte wie Projekt → Kaufmännisch → Controlling/Vorkalkulation), die Sidebar-Bereichspfeile, der Sidebar-Ein-/Ausklapp-Schalter und die Aufklapp-Pfeile in Tabellen sind jetzt bequem tippbar — auf der Maus bleibt alles wie gewohnt kompakt.',
      'Der Beleg-Editor (Verkauf/Einkauf): Zeilen-Auswahl, der Zieh-Griff zum Umsortieren und die Zeilen-Aktionen (Duplizieren, Langtext, Menü) waren bisher nur bei Maus-Hover sichtbar — auf dem iPad also unerreichbar. Jetzt dauerhaft sichtbar/bedienbar auf Touch-Geräten.',
      'Vollbild-Materialbedarf: Zeilen per Ziehen umsortieren funktionierte technisch nur mit der Maus (HTML5-Drag) und nicht mit dem Finger — auf Zeiger-Ereignisse umgestellt, funktioniert jetzt auch auf dem iPad.',
      'Zwei-Spalten-Dialoge (z. B. Artikel-/Kunden-Stammdaten) zeigen die Register-Leiste im iPad-Hochformat jetzt oben statt seitlich gequetscht — mehr Platz für mehrspaltige Formularfelder; ab iPad-Querformat/Desktop bleibt die gewohnte Zwei-Spalten-Ansicht.',
    ],
  },
  {
    version: '3.206.0',
    date: '2026-07-14T10:30',
    title: 'Analyse: gezielte Abfragen über Artikel, Belege & Projekte',
    tag: 'feature',
    changes: [
      'Die Analyse-Seite hat neben dem Cockpit drei neue Abfrage-Bereiche (Tabs): „Artikel", „Belege" und „Projekte" — serverseitig ausgewertet über ein neues Analyse-Backend (kein 200er-Listen-Deckel mehr, ADR 0042).',
      'Artikel: Zugang/Abgang je Monat als Flächen-Chart (Lagerbewegungen), Abgangswert über den EK-Snapshot, Top-Artikel inkl. bestellter Menge/Wert. Filterbar nach Zeitraum und einzelnem Artikel.',
      'Belege: Netto-Wert je Monat, gestapelt nach Belegart — wahlweise Verkauf oder Einkauf, filterbar nach Belegart; dazu Kennzahlen (Anzahl, Σ Netto, Ø je Beleg) und Top-Kunden bzw. -Lieferanten.',
      'Projekte: projektbezogen fakturiert vs. bestellt je Monat als Flächen-Chart, filterbar nach Projektstatus; Top-Projekte mit Auftragswert, Faktura- und Bestellwert (verlinkt ins Projekt).',
      'Zeitraum je Abfrage wählbar: letzte 6/12/24 Monate, laufendes Jahr oder letztes Jahr.',
    ],
  },
  {
    version: '3.205.0',
    date: '2026-07-14T09:00',
    title: 'Materialbedarf im Projekt: aufgeräumte Übersicht, Bearbeiten im Vollbild',
    tag: 'improvement',
    changes: [
      'Die Bedarfstabelle im Projekt ist jetzt eine reine, aufgeräumte Übersicht: filtern, nach Status filtern, gruppieren (Baugruppe/Lieferant), sortieren, Spalten ein-/ausblenden und als Excel/PDF exportieren — ohne Bearbeitungs-Bedienelemente in jeder Zeile.',
      'Bearbeiten und Beschaffung (Mengen/Baugruppe/Notiz ändern, Lieferant wählen, reservieren, bestellen, Positionen anlegen/importieren) laufen über den Button „Maximieren zum Bearbeiten" im Vollbild-Editor.',
      'Dezenter Verfügbarkeits-Punkt in der Spalte „Frei" (grün/amber/rot) zeigt auf einen Blick, ob der offene Bedarf aus dem freien Lagerbestand gedeckt werden kann.',
    ],
  },
  {
    version: '3.204.0',
    date: '2026-07-14T08:30',
    title: 'Vollbild-Materialbedarf: Fixieren, Ausfüllen, Export & mehr',
    tag: 'feature',
    changes: [
      'Spalten links fixieren: Standard sind Artikel-Nr. + Bezeichnung. Ein Pin-Symbol im Spaltenkopf setzt die Fixiergrenze (bleibt beim horizontalen Scrollen stehen), erneuter Klick hebt auf. Auswahl bleibt pro Browser gespeichert.',
      'Excel-artiges Ausfüllen: an der aktiven Zelle (Bedarf/Baugruppe/Notiz) erscheint ein Ausfüll-Punkt — nach unten ziehen kopiert den Wert in die überstrichenen Zeilen.',
      'Export: neuer Button für Excel (XLSX) und PDF (Druckansicht) der aktuellen Ansicht — sichtbare Spalten und gefilterte/gruppierte Positionen in Anzeigereihenfolge.',
      'Beim Zeilen-Verschieben zeigt eine klare Einfüge-Linie, wohin die Zeile kommt; die gezogene Zeile wird abgeblendet.',
      'Speichern-Button und Strg/Cmd+S bestätigen das (ohnehin sofortige) Speichern und übernehmen die gerade offene Zelle.',
      'Ganze Gruppen mit einem Haken im Gruppenkopf markieren (für Sammelaktionen). Dezenter Verfügbarkeits-Punkt in der Spalte „Frei" (grün deckt / amber teilweise / rot offen). Lieferant-Auswahl zeigt nur noch als Lieferant getaggte Adressen.',
    ],
  },
  {
    version: '3.203.0',
    date: '2026-07-13T23:30',
    title: 'Feinere Rechte: „Artikelstamm-Light" ohne Preise + Kaufmännisch-Tab nur für Leitung',
    tag: 'feature',
    changes: [
      'Neues Recht „Preise sehen" (inventory.viewprices): Ohne dieses Recht zeigt der Artikelstamm nur Lagerstand & Stammdaten, aber keine EK-/VK-Preise, Staffelpreise oder Preishistorie („Artikelstamm-Light"). Die Preise fallen serverseitig weg — auch Export/API zeigen sie nicht. Gedacht für Technik und Lager & Logistik.',
      'Neues Recht „Kaufmännisch (Projekt)" (projects.viewfinancials): Der Projekt-Tab „Kaufmännisch" (Controlling + Vorkalkulation) ist nur noch mit diesem Recht sichtbar — vorgesehen für Geschäftsleitung und Sekretariat+. Ohne das Recht ist der Tab komplett weg.',
      'Beide Rechte lassen sich pro Rolle unter „Administration → Benutzer & Rechte" vergeben. Standard-Rollen: Verkauf/Beschaffung/Sekretariat/Projektleitung sehen Preise; Technik und Lager nicht. Sekretariat+ und Admin sehen alles.',
    ],
  },
  {
    version: '3.202.1',
    date: '2026-07-13T23:10',
    title: 'Vollbild-Materialbedarf: ruhigeres Raster',
    tag: 'improvement',
    changes: [
      'Das Raster im Vollbild-Editor ist wieder aufgeräumt: Der Lieferant erscheint als Text (Klick öffnet die Auswahl) statt als Auswahlfeld in jeder Zeile, und die Zeilen-Aktionen (Reservieren, Bestellen, Reservierung aufheben, Löschen) sind eine schmale Spalte mit dezenten Icon-Schaltflächen. Alle Funktionen bleiben erhalten.',
    ],
  },
  {
    version: '3.202.0',
    date: '2026-07-13T22:40',
    title: 'Vollbild-Materialbedarf: alle Funktionen der Normalansicht',
    tag: 'feature',
    changes: [
      'Der Vollbild-Editor („Maximieren") kann jetzt alles wie die normale Bedarfsansicht — bei gleichbleibendem Tabellen-Design: Lieferant je Zeile wählen, reservieren, in den Bestellkorb legen, Reservierung aufheben und Position löschen (Aktions-Spalte je Zeile).',
      'Zusätzlich: Filtern nach Status (Chips „Offen/Teilweise/Bestellt/Gedeckt/Geliefert"), Mehrfachauswahl mit Sammellöschen, Positionen manuell anlegen, Stückliste importieren, „Bedarf aus Beleg" übernehmen und aus dem Bestellkorb Bestellungen je Lieferant erzeugen.',
      'Bearbeiten (Bedarf/Baugruppe/Notiz), Gruppieren (Baugruppe/Lieferant), Spalten ein-/ausblenden & -breiten und das dauerhafte Umsortieren per Drag & Drop bleiben erhalten.',
    ],
  },
  {
    version: '3.201.1',
    date: '2026-07-13T22:10',
    title: 'Vollbild-Materialbedarf: Zell-Eingaben gehen nicht mehr verloren',
    tag: 'fix',
    changes: [
      'Im Vollbild-Editor wurde eine gerade bearbeitete Zelle verworfen, sobald das Speichern einer Nachbarzelle abgeschlossen war (die Zeile wurde intern neu aufgebaut, Fokus und Eingabe gingen verloren). Dadurch wurde z. B. eine Baugruppe oder Bedarfsmenge scheinbar „nicht gespeichert". Zellen behalten ihre Eingabe jetzt zuverlässig, bis sie selbst verlassen werden.',
    ],
  },
  {
    version: '3.201.0',
    date: '2026-07-13T22:00',
    title: 'Navigation folgt den Leserechten — kein Menüpunkt ohne Zugriff',
    tag: 'improvement',
    changes: [
      'Menüpunkte erscheinen jetzt nur noch, wenn man das jeweilige Modul lesen darf: Projekte, Verkauf, CRM, Artikel & Lager, Beschaffung, Montage & Wartung, Buchhaltung, Einstellungen und der HR-Bereich sind an ihr Leserecht (z. B. „sales.read", „accounting.read") gekoppelt — fehlt es, ist der Punkt weg. Rechtfreie Landeseiten (Dashboard, Update-Log) bleiben immer sichtbar.',
      'Ein Bereich (z. B. „Material", „Finanzen") verschwindet komplett, wenn keiner seiner Punkte sichtbar ist. Administratoren sehen wie bisher alles.',
    ],
  },
  {
    version: '3.200.0',
    date: '2026-07-13T21:45',
    title: 'Materialbedarf: Vollbild-Tabelle („Maximieren“) in eigenem Tab',
    tag: 'feature',
    changes: [
      'Neuer Button „Maximieren" im Materialbedarf öffnet die Positionen als Vollbild-Tabelle in einem eigenen Browser-Tab (Excel-artiges Raster mit Zeilennummern).',
      'Dort lässt sich filtern, nach Baugruppe oder Lieferant gruppieren und die Zeilenreihenfolge per Drag & Drop (Griff ⋮⋮) dauerhaft ändern — die neue Reihenfolge wird gespeichert (nur in der ungruppierten, ungefilterten Ansicht, weil nur dort eindeutig).',
      'Bedarf, Baugruppe und Notiz sind direkt in der Zelle bearbeitbar (Speichern bei ↵ oder beim Verlassen).',
      'Spalten lassen sich ein-/ausblenden und in der Breite anpassen (per Ziehen am Spaltenrand); die Auswahl und Breiten bleiben pro Browser gespeichert, „Layout" setzt sie zurück.',
    ],
  },
  {
    version: '3.199.0',
    date: '2026-07-13T21:20',
    title: 'Materialbedarf: Baugruppe-Spalte in der Tabelle',
    tag: 'feature',
    changes: [
      'Die Bedarfstabelle hat eine neue Spalte „Baugruppe" (zwischen Artikel und Bedarf). Sie zeigt die beim Import erfasste Freitext-Baugruppe und lässt sich wie Bedarf und Notiz direkt in der Zelle bearbeiten (inkl. Nach-unten-Ausfüllen und Einfügen einer Spalte aus Excel).',
      'Nach der Baugruppe lässt sich sortieren; Zeilen ohne Baugruppe rutschen dabei ans Ende. Die Spalte kann über „Spalten" ein-/ausgeblendet werden.',
    ],
  },
  {
    version: '3.198.0',
    date: '2026-07-13T21:00',
    title: 'Material-Import: breiteres Fenster, Baugruppe & Artikel-Zuordnung',
    tag: 'feature',
    changes: [
      'Das Fenster „Stückliste importieren" im Projekt-Materialbedarf ist deutlich breiter und zeigt die Vorschau jetzt mit einer Baugruppe-Spalte.',
      'Import unterstützt eine Baugruppe (Freitext, z. B. „Kunststoffkomponenten"): entweder als 4. Spalte je Zeile (Artikel-Nr., Menge, Bezeichnung, Baugruppe) oder über das Feld „Baugruppe für alle Zeilen", das alle Zeilen ohne eigene Baugruppe-Spalte befüllt.',
      'Wird eine Artikelnummer nicht im Artikelstamm gefunden, lässt sich der Zeile direkt in der Vorschau der richtige Artikel zuordnen („Artikel zuordnen") — die Zeile wird dadurch importierbar, ohne die Datei zu ändern.',
      'Hochgeladene Dateien (CSV/Excel) landen ebenfalls zuerst in der Vorschau, statt sofort importiert zu werden — so lassen sich unbekannte Artikel vorher zuordnen.',
    ],
  },
  {
    version: '3.197.1',
    date: '2026-07-13T19:30',
    title: 'CRM-Verlauf: Notizen/E-Mails/Termine nicht mehr durchgestrichen',
    tag: 'fix',
    changes: [
      'Im CRM-Verlauf werden Notizen, E-Mails und Termine nicht mehr durchgestrichen dargestellt. Der Durchstreich-Stil ist jetzt ausschließlich erledigten Aufgaben vorbehalten (To-do-Haken) — importierte Historieneinträge mit gesetztem „erledigt"-Kennzeichen erschienen bisher fälschlich durchgestrichen.',
    ],
  },
  {
    version: '3.197.0',
    date: '2026-07-13T19:15',
    title: 'Artikel: Warengruppe & Hauptwarengruppe getrennt, Artikelnummer gesperrt',
    tag: 'improvement',
    changes: [
      'Im Artikel-Dialog gibt es jetzt zwei getrennte Felder: „Warengruppe" (4-stellig, z. B. 1100 — dieselbe wie im Engineering) und „Hauptwarengruppe" (die Tausenderstelle, z. B. 1000). Die Hauptwarengruppe wird automatisch aus der Warengruppe abgeleitet.',
      'Die Warengruppe wird beim Anlegen automatisch aus den ersten vier Ziffern der Artikelnummer vorbelegt, bleibt aber jederzeit von Hand anpassbar.',
      'Die Artikelnummer eines bestehenden Artikels lässt sich nicht mehr ändern — an ihr hängen als Schnappschuss tausende Positionen und Belege. Das Feld ist beim Bearbeiten gesperrt (nur beim Neuanlegen frei).',
    ],
  },
  {
    version: '3.196.0',
    date: '2026-07-13T19:00',
    title: 'Projekt- & Lieferant-Auswahlen: alle Einträge auffindbar (kein 200er-Deckel)',
    tag: 'fix',
    changes: [
      'Überall dort, wo ein Projekt gewählt wird (Beleg anlegen, Verkaufs- und Bestell-Belegblatt inkl. je Position, Bestellungen-Filter, Wartungs- und Montageauftrag, Zeit-Stammdaten), werden jetzt ALLE Projekte geladen — vorher fehlten Projekte jenseits der ersten 200 und waren nicht wählbar.',
      'Die Projektauswahl in Wartungs-/Montageauftrag und Zeit-Stammdaten ist zudem ein durchsuchbares Auswahlfenster statt eines langen Dropdowns.',
      'Auch die Lieferanten-Auswahl im Engineering-Artikelantrag (Dialog + Assistent) zeigt wieder alle Lieferanten.',
      'Die Projektfilter in den Lagerbewegungen und im Dateimanager berücksichtigen nun ebenfalls alle Projekte.',
    ],
  },
  {
    version: '3.195.0',
    date: '2026-07-13T18:30',
    title: 'PDF-Druckfelder pro Beleg wählbar + Gewicht manuell überschreibbar',
    tag: 'feature',
    changes: [
      'Unter „Weitere Belegangaben" gibt es jetzt „Felder auf dem PDF": pro Beleg (Verkauf & Einkauf) lässt sich einzeln an-/abwählen, welche optionalen Felder gedruckt werden — z. B. Gültig bis, Fällig, Sachbearbeiter, Kunden-/USt-IdNr., Bezugstext, Projekt, Liefertermin, Lieferzeit, Incoterm, Gewicht, Zahlungs-/Skonto-Text, Notiz. Pflichtinhalte (Empfänger, Belegdatum, Positionen, Summen) bleiben immer.',
      'Das Beleggewicht ist jetzt manuell überschreibbar (Feld „Gewicht (kg)" bei den Lieferangaben). Leer = automatisch aus den Artikelgewichten berechnet wie bisher; ein eingetragener Wert gewinnt und wird gedruckt.',
    ],
  },
  {
    version: '3.194.1',
    date: '2026-07-13T17:30',
    title: 'Anhänge-Bereich im Beleg-Editor ein-/ausklappbar',
    tag: 'improvement',
    changes: [
      'Der Anhänge-Bereich unten im Verkaufs-/Einkaufs-Editor lässt sich jetzt ein- und ausklappen; standardmäßig ist er zugeklappt. Der Zähler im Kopf zeigt weiterhin, ob und wie viele Dateien hängen, und „Dateien hinzufügen" klappt bei Bedarf automatisch auf.',
    ],
  },
  {
    version: '3.194.0',
    date: '2026-07-13T17:00',
    title: 'Langtext mit Formatierung im PDF (fett & Farben)',
    tag: 'feature',
    changes: [
      'Im Positions-Langtext (Verkauf & Einkauf) lassen sich jetzt einzelne Stellen hervorheben, die genau so aufs PDF drucken: **fett** macht fett, und Farbmarker färben den Text — #m#…#m# magenta sowie r (rot), g (grün), b (blau), o (orange).',
      'Ein Farbmarker wirkt zwischen zwei gleichen Marken (z. B. #m#Achtung#m#) oder bis zum Zeilenende, wenn er offen bleibt; fett und Farbe sind kombinierbar (#m#**Achtung**#m#). Ein kurzer Hinweis mit den Codes steht direkt unter dem Langtext-Feld.',
    ],
  },
  {
    version: '3.193.1',
    date: '2026-07-13T16:30',
    title: 'Beleg-Editor: Überschriften & Seitenumbrüche wieder speicherbar (Verkauf & Einkauf)',
    tag: 'fix',
    changes: [
      'Behoben: Beim Speichern eines Verkaufs-/Einkaufsbelegs brach der Vorgang mit „Validation failed — description: String must contain at least 1 character" ab, sobald eine bestehende Position (z. B. eine Überschrift) geleert wurde — dadurch ließen sich Überschriften und Seitenumbrüche in der Praxis nicht mehr zuverlässig ergänzen.',
      'Ursache: Eine bestehende Position mit gelöschtem Text wurde mit leerer Beschreibung an den Server geschickt, statt gelöscht zu werden. Jetzt wird eine geleerte Position beim Speichern entfernt; nur Positionen mit Inhalt (bzw. Seitenumbrüche) werden gespeichert — die gesendete Menge stimmt wieder mit der Editor-Prüfung überein.',
    ],
  },
  {
    version: '3.193.0',
    date: '2026-07-13T16:00',
    title: 'Artikel: Standardlieferant wieder wählbar (alle Lieferanten auffindbar)',
    tag: 'fix',
    changes: [
      'Im Artikel-Dialog werden Lieferanten jetzt serverseitig durchsucht — auch alphabetisch spätere Lieferanten (vorher ab dem ~200. Kontakt „verschwunden") sind wieder wähl- und auffindbar.',
      'Der bereits gesetzte Standardlieferant wird beim Öffnen korrekt angezeigt (auch wenn er nicht in den ersten Treffern steckt) und geht beim Speichern nicht mehr verloren.',
      'Der Standardlieferant lässt sich jetzt auch wieder entfernen („kein Standardlieferant").',
    ],
  },
  {
    version: '3.192.0',
    date: '2026-07-13T15:00',
    title: 'Zeiterfassung auf einer Seite + Zeiteintrag mit einmaligem Datum',
    tag: 'improvement',
    changes: [
      'Der Menüpunkt „Zeiterfassung" führt jetzt direkt aufs Dashboard — Dashboard und Zeitprotokoll liegen zusammen auf einer Seite (keine Untergruppen mehr): oben Timer und Kennzahlen, darunter das komplette, filterbare Zeitprotokoll zum Anlegen/Bearbeiten.',
      'Beim Zeiteintrag wird das Datum nur noch EINMAL gewählt: darunter „Von → Bis" als zwei Uhrzeit-Felder mit sofortiger Dauer-Anzeige. Endet die Zeit vor dem Start, wird sie als Folgetag erkannt (Schicht über Nacht, mit Hinweis).',
      'Der Menüpunkt „Fehlzeiten" heißt jetzt kurz „FZ".',
    ],
  },
  {
    version: '3.191.0',
    date: '2026-07-13T13:00',
    title: 'Materialbedarf: Spalten ein-/ausblenden + Auswahl-Fenster aufgeräumt',
    tag: 'improvement',
    changes: [
      'Neue Schaltfläche „Spalten" über der Bedarfstabelle: Bedarf, Lager, Frei, Reserviert, Bestellt, Erhalten, Offen, Deckung, Status und Lieferant lassen sich einzeln ein- oder ausblenden (Auswahl wird pro Nutzer gemerkt).',
      'Auswahl-Fenster (Artikel/Geschäftspartner): Spalteninhalte sind jetzt durchgängig linksbündig; im Artikel-Fenster hat die Spalte „Bezeichnung" mehr Platz.',
    ],
  },
  {
    version: '3.190.0',
    date: '2026-07-13T12:00',
    title: 'Bestellpool zählt nicht als „bestellt" + Auswahl-Fenster verbessert',
    tag: 'fix',
    changes: [
      'Fix: Bestellungen im Bestellpool markieren den Projekt-Bedarf nicht mehr als „bestellt" — die Position bleibt offen, bis die Bestellung freigegeben oder zusammengefasst wird. Bisher erschien der Bedarf fälschlich als bestellt/unterwegs, obwohl die Bestellung erst im Pool lag.',
      'Lieferanten-Auswahl im Materialbedarf: Das Fenster durchsucht jetzt alle Geschäftspartner serverseitig — auch Lieferanten, die vorher (ab dem 500. Kontakt) fehlten, sind wieder wählbar.',
      'Artikel-Auswahl beim Positions-Hinzufügen: Die dritte Spalte zeigt jetzt den Artikel-Langtext statt des VK-Preises (und die Suche berücksichtigt ihn).',
    ],
  },
  {
    version: '3.189.0',
    date: '2026-07-13T11:00',
    title: 'Materialbedarf: Lieferanten-Auswahl als Suchfenster + Bestellpool-Hinweis',
    tag: 'improvement',
    changes: [
      'Der Lieferant je Bedarfsposition wird jetzt über ein durchsuchbares Auswahl-Fenster (Name/Nummer/Ort) gewählt statt über ein enges Inline-Dropdown.',
      'Die Spalte „Deckung" in der Bedarfstabelle ist etwas schmaler — mehr Platz für die übrigen Spalten.',
      'Im Projekt-Tab „Bestellungen" weist ein Hinweis darauf hin, wie viele Bestellungen dieses Projekts noch im Bestellpool liegen — mit direktem Sprung dorthin.',
    ],
  },
  {
    version: '3.188.0',
    date: '2026-07-13T09:00',
    title: 'Bestellpool: Projekt-Bestellungen sammeln & zusammenfassen',
    tag: 'feature',
    changes: [
      'Neuer Reiter „Bestellpool" in der Beschaffung. Aus dem Projekt-Bedarf erzeugte Bestellungen (Aktion „Bestellung erzeugen") landen jetzt im Bestellpool statt direkt unter „Bestellungen".',
      'Im Bestellpool lassen sich mehrere Bestellungen desselben Lieferanten auswählen und mit „Zusammenfassen" zu einer Sammelbestellung bündeln (Positionen werden 1:1 übernommen) — oder einzeln über „Freigeben" nach „Bestellungen" übernehmen.',
      'Manuell über „Neu: Bestellung" angelegte Bestellungen bleiben wie bisher direkt unter „Bestellungen".',
    ],
  },
  {
    version: '3.187.0',
    date: '2026-07-12T18:30',
    title: 'Großer Stabilitäts-Release: ~30 Bugfixes aus dem Prozess-Audit',
    tag: 'fix',
    changes: [
      'Beschaffung: Stornierte/gelöschte Bestellungen und geänderte Entwurfs-Positionen buchen die bestellte Menge am Materialbedarf jetzt zurück — Positionen fallen wieder auf „Offen" und werden nachbestellt. „Bestellung erzeugen" ist gegen Doppelklick und Abbrüche abgesichert.',
      'Material: Reservieren übernimmt nie mehr als den offenen Bedarf; doppeltes Freigeben kann den Zähler nicht mehr ins Minus ziehen; Stücklisten-Import mit „ersetzen" wird geblockt, solange Bestellungen laufen; Ablängmaße wandern beim Beleg-Wandeln mit.',
      'Verkauf/Buchhaltung: Bezahlte und stornierte Belege sind terminal (kein Zurücksetzen auf Entwurf); Zahlungen auf Entwürfe/Stornos werden abgelehnt; die Schlussrechnung verrechnet nur tatsächlich gestellte Anzahlungen; Doppel-Fakturierung ist auf allen Wegen gesperrt; Positionspreise müssen zur Belegwährung passen; die OP-Liste kappt nicht mehr bei 500 Belegen.',
      'Produktion/Lager: Fertigmeldungen sind transaktional (kein Doppelklick-Doppelbuchen mehr); das Projekt-Controlling verrechnet Kommissionier-Stornos korrekt (keine Phantom-Materialkosten); die Bewegungs-Übersicht lädt seitenweise nach statt still bei 500 abzuschneiden.',
      'Materialbedarf-Tabelle: Deckungs-Spalte zeigt gelieferte Positionen korrekt mit 100 % (statt 0 %); Esc verwirft die Zellen-Eingabe wirklich; Excel-Einfügen/Ausfüllen schreibt nicht mehr in eingeklappte Gruppen; fehlgeschlagene Speicherungen setzen die Zelle sichtbar zurück.',
      'HR: Urlaub über den Jahreswechsel wird je Jahr korrekt gezählt; das Zeitprotokoll-Filter „Bis" schließt den Tag jetzt ein.',
      'Engineering: Reservierte Artikelnummern sind jetzt auch in der Datenbank eindeutig abgesichert (parallele/zurückgegebene Anträge können keine Dublette mehr erzeugen).',
      'Eingaben: Mengenfelder verstehen deutsche Tausenderpunkte durchgängig richtig („1.250" ist zwölfhundertfünfzig, nicht 1,25); Datums-Vorbelegungen nutzen die lokale Zeit statt UTC; das Leeren von Projektfeldern wird wirklich gespeichert.',
      'Sicherheit: Produktivstart erzwingt die App-Anmeldung (Basic-Auth allein genügt nicht mehr, außer explizit freigeschaltet); der Migrations-Reset löscht nur noch mandantenbezogen; Benutzer-/Zahlungsziel-Verzeichnisse erfordern eine Anmeldung.',
    ],
  },
  {
    version: '3.186.0',
    date: '2026-07-10T16:30',
    title: 'Mobile-Feinschliff: Karten statt Tabellen',
    tag: 'improvement',
    changes: [
      'Kommissionierung, Fehlzeiten (eigene Anträge, Verwaltung und Urlaubs-Analyse) und das Audit-Log zeigen auf dem Handy jetzt kompakte Karten statt seitlich scrollender Tabellen — wie die übrigen Listen der App.',
      'Der Lösch-Knopf in der HR-Mitarbeiterliste hat auf dem Handy jetzt eine volle Touchfläche.',
    ],
  },
  {
    version: '3.185.0',
    date: '2026-07-10T16:00',
    title: 'ProERP aufs iPhone/iPad installierbar (Web-App)',
    tag: 'feature',
    changes: [
      'ProERP lässt sich jetzt als App auf iPhone und iPad installieren: In Safari „Teilen" → „Zum Home-Bildschirm" — die App startet dann im Vollbild ohne Browser-Leiste, mit eigenem App-Icon (dunkle „P"-Kachel) und passender Statusleiste in Hell/Dunkel.',
      'Web-App-Manifest, App-Icons (inkl. maskierbarer Android-Variante) und Favicon ergänzt.',
      'Bewusst ohne Offline-Modus: ERP-Daten müssen aktuell sein — ein veralteter Cache wäre riskanter als eine kurze Fehlermeldung ohne Netz.',
    ],
  },
  {
    version: '3.184.0',
    date: '2026-07-10T15:30',
    title: 'Eingeklappte Sidebar repariert & iPhone-Safe-Area',
    tag: 'fix',
    changes: [
      'Im eingeklappten (Icon-)Modus der Sidebar waren einige Icons nicht klickbar: Die unsichtbare Bereichs-Überschrift lag über dem ersten Icon jeder Gruppe und fing die Klicks ab. Behoben — alle Icons sind wieder erreichbar.',
      'Menüpunkte mit Unterpunkten (z. B. CRM, Engineering, Artikel & Lager) führten eingeklappt ins Leere; sie verlinken jetzt direkt auf ihre Übersichtsseite.',
      'Das Benutzer-Kürzel unten links (z. B. „FP") wurde eingeklappt abgeschnitten — es ist jetzt kleiner und sitzt mittig im Icon-Streifen.',
      'iPhone: Die App respektiert jetzt die Safe-Area (Notch/Home-Indicator) — die untere Tab-Leiste bekommt auf neueren iPhones den korrekten Abstand (viewport-fit=cover).',
    ],
  },
  {
    version: '3.183.0',
    date: '2026-07-10T11:45',
    title: 'Materialbedarf: Tabelle 1:1 nach dem Design „Gruppen-Gliederung"',
    tag: 'feature',
    changes: [
      'Die Materialbedarf-Tabelle folgt jetzt exakt dem abgestimmten Excel-artigen Entwurf: dichtes Raster mit allen Mengenspalten nebeneinander — Position, Artikel, Bedarf, Lager, Frei, Reserv., Bestellt, Erhalten, Offen, Deckung, Status, Lieferant, Aktion.',
      'Nach Lieferant gruppiert mit spaltenbündigen Zwischensummen je Block (Σ Bedarf/Reserv./Bestellt/Erhalten/Offen + Deckungsbalken + „N offen"/„komplett") und einer Gesamtzeile am Tabellenfuß.',
      'Neue Toolbar mit Status-Filter-Chips (Alle/Offen/Teilweise/Bestellt/Gedeckt/Geliefert samt Zähler), Umschalter „Gruppieren: Lieferant/Keine" und „Auf-/Zuklappen".',
      'Eigene Status-Spalte (Offen/Teilweise/Bestellt/Gedeckt/Geliefert) und eine KPI-Fußzeile mit Status-Verteilung und Σ offener Menge; jede Spalte ist sortierbar. Das frühere Cockpit-Panel entfällt.',
    ],
  },
  {
    version: '3.182.0',
    date: '2026-07-10T11:15',
    title: 'Materialbedarf: gruppiertes Raster + Beschaffungs-Cockpit oben',
    tag: 'feature',
    changes: [
      'Standard-Ansicht ist jetzt nach Lieferant gruppiert (Gruppen-Gliederung): einklappbare Blöcke je Lieferant mit Deckungs-% und einem „Bestellung erzeugen"-Knopf je Gruppe.',
      'Neues Cockpit-Panel oben: Klick auf „Details" einer Position zeigt über der Tabelle die Beschaffungs-Übersicht — Deckungsbalken, Mengen-Aufschlüsselung (Bedarf/Lager/frei/reserviert/bestellt/erhalten/offen), Vorschlag mit nächster Aktion (reservieren/bestellen), Lieferant-Wahl und die verknüpften Belege.',
    ],
  },
  {
    version: '3.181.0',
    date: '2026-07-10T10:30',
    title: 'Belegblatt: Dateiablagezone für Anhänge (Verkauf & Einkauf)',
    tag: 'feature',
    changes: [
      'Im Belegblatt (Verkauf & Einkauf) gibt es unten eine neue Zone „Anhänge": Dokumente, Anfragen oder Zeichnungen lassen sich per Klick oder Drag & Drop an den Beleg hängen (PDF, Bilder, Office-Dateien).',
      'Jeder Anhang kann direkt per Knopfdruck geöffnet (neuer Tab) oder heruntergeladen werden; Entfernen ist mit Änderungsrecht möglich.',
      'Die Anhänge werden als reguläre Dateien gespeichert und erscheinen dadurch auch im zentralen Dateimanager — im Ordner des zugehörigen Kunden bzw. Lieferanten unter „Belege / <Belegnummer>".',
    ],
  },
  {
    version: '3.180.0',
    date: '2026-07-10T10:00',
    title: 'Einheiten überall lesbar (Stk/m/kg statt PIECE/METER)',
    tag: 'fix',
    changes: [
      'Artikel-Einheiten werden jetzt durchgängig mit dem hinterlegten deutschen Kürzel angezeigt (Stk, m, kg, l, m², Std) statt mit dem technischen Wert (PIECE, METER …) — in Listen, Bestandsbewegungen, Materialbedarf, Forecast, Kommissionierung, Sperrlager und CSV-/Excel-Exporten.',
      'Wichtig: Auch auf den Belegen (Angebot, Rechnung, Bestellung, Kommissionier-/Packliste, Bedarfs-PDF) steht die Einheit jetzt lesbar — bei englischen Belegen entsprechend englisch (pcs, h).',
      'Technisch als eine zentrale Quelle in @proerp/shared gebündelt (vorher vier uneinheitliche Kopien).',
    ],
  },
  {
    version: '3.179.0',
    date: '2026-07-10T09:00',
    title: 'Projekt-Tabs „Bestellungen" & „Lager": Suche, Filter, Sortierung',
    tag: 'feature',
    changes: [
      'Der Tab „Bestellungen" (Projekt → Warenwirtschaft & Belege) hat jetzt eine Suche (Nummer/Lieferant), Filter nach Status und Lieferant sowie sortierbare Spalten (Nummer, Lieferant, Bestelldatum, Netto, Status) — Standard: neueste zuerst.',
      'Der Tab „Lager" (Artikelbewegungen) hat jetzt eine Suche (Artikel/Herkunft/Notiz), einen Filter nach Art (Zugang/Abgang/Korrektur) und sortierbare Spalten (Datum, Art, Artikel, Menge, Herkunft). Gilt auch für die globale Lager-Übersicht und das Artikel-Detail.',
      'Neuer wiederverwendbarer Baustein für Client-seitiges Sortieren (stabil, leere Werte ans Ende) mit sortierbarem Spaltenkopf.',
    ],
  },
  {
    version: '3.178.0',
    date: '2026-07-09T16:00',
    title: 'Sicherheits-Härtung (Security-Review)',
    tag: 'fix',
    changes: [
      'Zwei-Faktor-Schutz gehärtet: Ein zweiter Faktor lässt sich beim Login nur noch einrichten, wenn noch keiner existiert — zusätzliche Faktoren nur aus einer angemeldeten Sitzung (Sicherheitseinstellungen). Verhindert die Übernahme eines Kontos allein mit dem Passwort.',
      'Dokumenten-Upload: Dateinamen mit Pfadangaben werden abgewiesen (Schutz vor dem Ausbruch aus dem Dokumentenordner).',
      'Datei-Vorschau: SVG- und HTML-Dateien werden nicht mehr aktiv im Browser ausgeführt, sondern sicher als Download ausgeliefert.',
      'CSV-/Excel-Exporte sind gegen Formel-Einschleusung abgesichert (Zellen mit =, +, -, @ werden neutralisiert).',
      'Notfall-DB-Tool: Passkey-/2FA-Tabelle ist jetzt vollständig gesperrt.',
      'Produktivstart bricht ab, wenn weder App-Login noch das Zugangs-Gate konfiguriert ist (kein versehentlich offener Betrieb).',
    ],
  },
  {
    version: '3.177.0',
    date: '2026-07-09T14:30',
    title: 'Bug-Fixes: USt-Satz, negative Positionen, Sub-Artikel-Konsistenz, Fehleranzeigen',
    tag: 'fix',
    changes: [
      'USt-Fix: Wird die Steuerbefreiung eines Belegs wieder aufgehoben, stellt das System jetzt automatisch den Standardsatz der ausstellenden Gesellschaft wieder her — bisher blieb der Beleg still auf 0 % USt (falsche Rechnungssumme). Gilt für den Beleg-Editor und den Anlegen-Dialog.',
      'Belege mit negativen Mengen oder Preisen (Gutschriften, Anzahlungsabzug der Schlussrechnung) lassen sich wieder speichern — die Positionsprüfung hatte sie fälschlich blockiert.',
      'Frei erfasste Artikelnummern ohne Katalog-Verknüpfung (z. B. „SONDER-1") gehen beim Speichern nicht mehr verloren (Verkauf + Einkauf).',
      'Sub-Artikel: Beim Umbenennen des Mutterartikels (Nummer/Name) ziehen die Sub-Artikel jetzt automatisch nach; beim Archivieren des Mutterartikels werden sie mit deaktiviert; die VK-Staffel (Meterpreise) wird nicht mehr fälschlich am Stück-Sub-Artikel angezeigt.',
      'Positions-Änderungen an Verkaufsbelegen erzeugen jetzt Audit-Log-Einträge (wie im Einkauf).',
      'Fehler beim Laden von Listen (Verkauf, Bestellungen, Kontakte, Forecasts, Lieferadressen) werden jetzt als Fehlermeldung mit Referenz angezeigt statt als „Keine Einträge".',
      'Mobil: Aufklappbare Tabellenzeilen (z. B. Sub-Artikel in der Artikelliste) funktionieren jetzt auch in der Karten-Ansicht.',
      'Sub-Artikel-Dialog: Nach „Abbrechen" und erneutem Öffnen werden wieder die gespeicherten Werte angezeigt statt der verworfenen Eingaben.',
    ],
  },
  {
    version: '3.176.0',
    date: '2026-07-09T13:20',
    title: 'Materialbedarf: Sammelaktionen + Gruppieren nach Lieferant',
    tag: 'feature',
    changes: [
      'Mehrfachauswahl: Positionen per Checkbox auswählen (oder alle über den Spaltenkopf). Eine Sammelaktions-Leiste bietet dann: Quelle setzen (Lager/Bestellen), reservieren, Bedarf mit einem Faktor skalieren und löschen (mit Rückfrage).',
      'Gruppieren nach Lieferant: Über „Ansicht → Gruppierung" lässt sich die Liste in einklappbare Lieferanten-Blöcke gliedern (mit Positions- und Offen-Zähler je Block) — passend zur Sammelbestellung.',
      'Der Excel-Flow (sortieren, inline bearbeiten, Sammelauswahl) ist jetzt durch einen Integrationstest abgesichert.',
    ],
  },
  {
    version: '3.175.0',
    date: '2026-07-09T12:45',
    title: 'Materialbedarf: sortieren, Deckungsbalken, Excel-Inline-Bearbeitung',
    tag: 'feature',
    changes: [
      'Sortierbare Spalten: Klick auf einen Spaltenkopf sortiert (Artikelnummer, Bedarf, Deckung, Lagerbestand, Frei verfügbar), erneuter Klick dreht die Richtung. Positionen ohne Lagerbestand landen stabil am Ende.',
      'Neue Spalte „Deckung": ein Balken je Zeile zeigt auf einen Blick erhalten (grün) · aus Lager reserviert (hell) · bestellt (cyan) · offen (Rest), mit Status und offener Menge darunter.',
      'Excel-Inline-Bearbeitung: Bedarfsmenge (und optional die Notiz) direkt in der Zelle ändern, mit ↑/↓/↵ zwischen Zeilen navigieren, den Ausfüll-Punkt nach unten ziehen und eine Spalte aus Excel per ⌘V einfügen.',
      'Ansicht-Menü: Zeilendichte (Normal/Kompakt) und abschaltbare Spalten (Deckung, Lagerbestand, Frei verfügbar, Lieferant, Notiz) — pro Browser gemerkt. Kennzahlen-Fußzeile (Positionen · offen · gedeckt · geliefert) wie eine Excel-Statusleiste.',
    ],
  },
  {
    version: '3.174.0',
    date: '2026-07-09T12:15',
    title: 'Navigation: größere Bereichs-Überschriften, kompaktere eingeklappte Gruppen',
    tag: 'improvement',
    changes: [
      'Die Bereichs-Überschriften in der linken Navigation sind jetzt größer (15 px) und dadurch besser als Sektions-Header erkennbar.',
      'Eingeklappte Bereiche rücken enger zusammen: Ist eine Gruppe zugeklappt, wird der vertikale Abstand zur nächsten Gruppe reduziert, sodass mehrere zugeklappte Bereiche als kompakte Liste erscheinen.',
    ],
  },
  {
    version: '3.173.0',
    date: '2026-07-09T11:45',
    title: 'Bedarfstabelle: Bestand & Lieferant je Zeile, Bestellkorb-Leiste',
    tag: 'improvement',
    changes: [
      'Die Bedarfstabelle im Projekt (Warenwirtschaft & Belege → Bedarf/Material) zeigt jetzt eigene Spalten für Artikelnummer, Bezeichnung, Bedarf, Lagerbestand und frei verfügbaren Bestand. Deckt der freie Bestand den offenen Bedarf, wird die Zelle grün markiert und „Reservieren" als empfohlene Aktion hervorgehoben.',
      'Neue Spalte „Lieferant": Dropdown aller Lieferanten, vorbelegt mit dem Standardlieferanten des Artikels. Eine abweichende Wahl wird an der Position gespeichert (mit Hinweis auf den Artikel-Standard) und steuert die Sammelbestellung.',
      'Bestellen ohne Dialog-Umweg: „Bestellen" in der Zeile legt die Position in den Bestellkorb. Eine fixierte Leiste unten zeigt live, wie viele Bestellungen bei welchen Lieferanten entstehen — ein Klick legt je Lieferant eine Bestellung an. Fehlt einer Position der Lieferant, wird das dort angezeigt.',
      'Die Tabelle ist filterbar: Suche (Nummer/Bezeichnung), Status (offener Bedarf, reserviert, bestellt, geliefert) und Lieferant. Der bisherige Lager/Bestellen-Umschalter entfällt — beide Aktionen stehen direkt in der Zeile.',
    ],
  },
  {
    version: '3.172.0',
    date: '2026-07-09T11:30',
    title: 'Belegblatt: Bezugstext fluchtet exakt mit Sachbearbeiter/Projekt',
    tag: 'fix',
    changes: [
      'Das Feld „Bezugstext" beginnt jetzt exakt auf derselben Höhe wie „Unser Zeichen / Sachbearbeiter" und „Projekt" rechts daneben — unabhängig davon, wie viele Zeilen Adresse oder Warnhinweis links einnehmen.',
    ],
  },
  {
    version: '3.171.0',
    date: '2026-07-09T11:00',
    title: 'Kunde nachträglich wechseln + Adressen aus dem CRM',
    tag: 'feature',
    changes: [
      'Unter „Weitere Belegangaben" (Belegblatt + Werkbank) lässt sich jetzt der Kunde eines Belegs nachträglich wechseln — solange der Beleg im Entwurf ist. Beim Speichern werden Anschrift, Kundennummer und USt-IdNr. automatisch aus dem CRM-Stamm des neuen Kunden übernommen.',
      'Rechnungs- und Lieferadresse (abweichend) können per Klick „Aus CRM" befüllt werden: ein Modal zeigt die Kundenanschrift und alle hinterlegten Lieferadressen zur Auswahl.',
    ],
  },
  {
    version: '3.170.0',
    date: '2026-07-09T10:30',
    title: 'Beleg anlegen: alle Belegblatt-Felder schon im Anlegen-Dialog',
    tag: 'improvement',
    changes: [
      'Der Dialog „Beleg anlegen" (Verkauf) bietet jetzt dieselben Kopf-Funktionen wie das Belegblatt: „Unser Zeichen / Sachbearbeiter" ist eine Nutzerauswahl (statt Freitext).',
      'Neuer einklappbarer Bereich „Lieferangaben & weitere Belegangaben": Bezugstext, Liefertermin, Lieferzeit, Incoterm, Erfüllungsort sowie abweichende Rechnungs- und Lieferadresse — damit lassen sich diese Angaben direkt beim Anlegen erfassen und nicht erst nachträglich im Belegblatt.',
    ],
  },
  {
    version: '3.169.0',
    date: '2026-07-09T10:00',
    title: 'Belegblatt: Bezugstext in die linke Spalte',
    tag: 'improvement',
    changes: [
      'Das Feld „Bezugstext" sitzt im Belegblatt jetzt in der linken Spalte direkt unter dem Adress-/Warnhinweis-Block und nimmt die volle Spaltenbreite ein (statt im schmalen Streifen über der Positionstabelle).',
    ],
  },
  {
    version: '3.168.0',
    date: '2026-07-09T09:15',
    title: 'Werkbank-Editor zieht mit dem Belegblatt gleich',
    tag: 'improvement',
    changes: [
      'Der Kopf-Streifen der Werkbank zeigt jetzt dieselben Felder wie der Referenzblock des Belegblatts — inkl. Sachbearbeiter-Auswahl und Projekt-Suchmodal; die USt-IdNr. des Kunden steht read-only bei der Anschrift.',
      'Incoterm + Erfüllungsort und alle übrigen Kopf-/Konditionsfelder sind über „Alle Belegdaten" gepflegt.',
      'Auch in der Werkbank gibt es jetzt „Vorlage einfügen" bei der Notiz / Zahlungsbedingung samt Formatierungs-Hinweis (### / **fett** / -).',
    ],
  },
  {
    version: '3.167.0',
    date: '2026-07-09T08:30',
    title: 'Zahlungsbedingungs-Vorlagen zum Einfügen',
    tag: 'feature',
    changes: [
      'Neben der Notiz/Zahlungsbedingung gibt es jetzt „Vorlage einfügen": ein Klick öffnet die tenant-weiten Vorlagen und fügt den gewählten Text (inkl. Formatierung) in die Notiz ein — in Verkauf und Einkauf.',
      'Vorlagen können direkt im Auswahlfenster aus dem aktuellen Notiztext angelegt und gelöscht werden (Recht „settings.update"); ohne dieses Recht lassen sich Vorlagen nur einfügen.',
      'Die Vorlagen sind zentral gepflegt und für alle Belege verfügbar — ideal für wiederkehrende Zahlungspläne (z. B. 30/65/5 im Anlagenbau).',
    ],
  },
  {
    version: '3.166.0',
    date: '2026-07-08T23:45',
    title: 'Zahlungsbedingungen mit Formatierung im Notizfeld',
    tag: 'feature',
    changes: [
      'Das Belegnotiz-Feld heißt jetzt „Notiz / Zahlungsbedingungen" — hier werden fortan die Zahlungsbedingungen erfasst (z. B. 30 % bei Bestellung / 65 % nach Lieferung / 5 % nach Installation).',
      'Das PDF unterstützt dabei einfache Formatierung: „### Überschrift" (auch ohne Leerzeichen) wird fett gedruckt, „**fett**" hebt Text im Satz hervor, „- " erzeugt Aufzählungspunkte — in Verkauf und Einkauf.',
      'Ein Hinweis unter dem Feld erklärt die Formatierung direkt im Editor.',
    ],
  },
  {
    version: '3.165.0',
    date: '2026-07-08T23:00',
    title: 'Sachbearbeiter als User-Auswahl am Beleg',
    tag: 'feature',
    changes: [
      '„Unser Zeichen / Sachbearbeiter" ist jetzt ein Dropdown aller aktiven App-Benutzer (Verkauf + Einkauf) statt eines Freitextfelds — die Auswahl wird als echte User-Verknüpfung gespeichert (Basis für spätere „meine Belege"-Filter).',
      'Beim Anlegen eines Belegs wird automatisch der eingeloggte Benutzer als Sachbearbeiter gesetzt; beim Wandeln wandert er in den Folgebeleg mit.',
      'Das PDF druckt weiterhin „Unser Zeichen: <Name>" — neu auch auf Bestellungen im Einkauf. Bestehende Freitext-Einträge bleiben erhalten und werden im Dropdown angezeigt, bis ein Benutzer gewählt wird.',
      'Neuer schlanker Endpunkt /v1/users/options (nur id + Name, für alle eingeloggten Nutzer) versorgt die Auswahl.',
    ],
  },
  {
    version: '3.164.0',
    date: '2026-07-08T22:00',
    title: 'Incoterm + Erfüllungsort ersetzen die Versandart',
    tag: 'feature',
    changes: [
      'Das Freitextfeld „Versandart" ist einem Incoterm-Dropdown (Incoterms 2020: EXW … DDP) gewichen; rechts daneben steht das neue Freitextfeld „Erfüllungsort" (z. B. DAP + „Kläranlage Wien").',
      'Das PDF druckt beides kombiniert im Lieferblock unten links (z. B. „Incoterm: DAP · Kläranlage Wien") — auch auf Bestellungen im Einkauf, die die Felder neu bekommen.',
      'Bestehende Versandart-Einträge wurden übernommen: erkennbare Incoterm-Codes (auch „DAP Wien") wandern ins Incoterm-Feld, alle übrigen Texte in den Erfüllungsort — nichts geht verloren.',
      'Beim Wandeln von Belegen (z. B. Anfrage → Bestellung) wandert die Lieferbedingung in den Folgebeleg mit.',
    ],
  },
  {
    version: '3.163.0',
    date: '2026-07-08T21:20',
    title: 'Sub-Artikel erben Stammdaten live vom Mutterartikel + eigener Bearbeiten-Dialog',
    tag: 'improvement',
    changes: [
      'Sub-Artikel (Ablängungen) übernehmen Bezeichnung, Beschreibung, Texte und Preise jetzt automatisch (live) vom Mutterartikel — ändert sich der Mutterartikel, ziehen alle Sub-Artikel nach.',
      'Neuer, reduzierter Bearbeiten-Dialog für Sub-Artikel: editierbar sind ausschließlich Ablängung (Länge), Gewicht und Lagerstand; die geerbten Stammdaten werden nur zur Info angezeigt.',
      'Erreichbar per Klick auf einen Sub-Artikel in der aufgeklappten Artikelübersicht sowie im Tab „Ablängungen" des Mutterartikels; die direkte Detailseite eines Sub-Artikels zeigt ebenfalls die reduzierte Ansicht.',
      'Der VK eines Sub-Artikels berechnet sich weiterhin aus dem Meterpreis des Mutterartikels × Länge (+ Rüstpauschale).',
    ],
  },
  {
    version: '3.162.0',
    date: '2026-07-08T21:10',
    title: 'Belegeditor: Kopf-Felder aufgeräumt (Verkauf + Einkauf)',
    tag: 'improvement',
    changes: [
      'USt-IdNr. und Kundennummer werden nur noch automatisch aus dem CRM-Stamm übernommen und im Beleg read-only angezeigt — sie sind nicht mehr im Belegblatt pflegbar (keine Doppelpflege).',
      '„Ihre Referenz" entfällt; der freie „Bezugstext" bleibt (beide Felder waren inhaltlich doppelt).',
      'Das Feld „Projekt" sitzt jetzt direkt neben „Unser Zeichen / Sachbearbeiter" und öffnet ein durchsuchbares Auswahl-Modal (Pop-up mit Suche) statt eines einfachen Dropdowns — auch im Einkauf.',
    ],
  },
  {
    version: '3.161.0',
    date: '2026-07-08T20:05',
    title: 'Belegfluss im Editor einklappbar',
    tag: 'improvement',
    changes: [
      'Der Belegfluss-Baum unten im Beleg-Editor (Verkauf + Einkauf) lässt sich jetzt über die Kopfzeile ein- und ausklappen.',
      'Der Zustand wird pro Nutzer gemerkt — einmal eingeklappt, bleibt der Belegfluss auch bei anderen Belegen eingeklappt.',
    ],
  },
  {
    version: '3.160.0',
    date: '2026-07-08T19:30',
    title: 'Belegblatt-Editor: Felder sitzen wie im Ausdruck',
    tag: 'improvement',
    changes: [
      'Die Eingabefelder im Editor „Belegblatt" liegen jetzt ungefähr dort, wo ihr Wert später im PDF erscheint — das erleichtert das Ausfüllen und Prüfen.',
      'Verkauf: Adresse + USt-IdNr. des Kunden links oben, Referenzblock (Gesellschaft, Belegdatum, Kundennummer, Gültig bis, Fällig, Unser Zeichen) rechts oben, „Ihre Referenz"/Bezug/Projekt als Betreffzeile über der Positionstabelle, Konditionen (USt, Zahlungsziel, Skonto) neben dem Summenblock, Lieferangaben (Liefertermin, Lieferzeit, Versandart) und Notiz in der Fußzone.',
      'Einkauf: Bestelldatum, Liefertermin und Fälligkeit rechts oben, Projekt über der Tabelle, USt-Satz an den Summen, Notiz in der Fußzone — passend zum Bestell-PDF.',
      'Selten genutzte Felder (abweichende Adressen, Sprache) bleiben unter „Weitere Belegangaben".',
    ],
  },
  {
    version: '3.159.0',
    date: '2026-07-08T18:45',
    title: 'Beleg-PDF: Adress- & Referenzblock überarbeitet',
    tag: 'improvement',
    changes: [
      'Adressblock: Das Land wird ausgeschrieben (z. B. „AT" → „Österreich", bei englischen Belegen „Austria"); die USt-IdNr. des Kunden steht jetzt direkt unter der Anschrift.',
      'Referenzblock (rechts) verschlankt: „Nr." entfällt (steht im Titel), USt-IdNr. ist in den Adressblock gewandert, „Fällig" wird nur noch auf Rechnungen gedruckt (nicht mehr auf Angeboten), „Ihre Referenz" steht jetzt unter dem Betreff und „Liefertermin" unten links. Damit ist auch die frühere Text-Überlappung behoben.',
      'Ab der zweiten Seite beginnt die Positionstabelle rund 1 cm höher (nutzt den freien Platz unter dem Briefkopf).',
      'Die Änderungen an Ländername und Seitenumbruch gelten auch für das Bestellungs-PDF im Einkauf.',
    ],
  },
  {
    version: '3.158.0',
    date: '2026-07-08T18:30',
    title: 'Sub-Artikel (Ablängungen) in der Artikelübersicht + direkt im Beleg wählbar',
    tag: 'improvement',
    changes: [
      'Die Artikelübersicht zeigt am Hauptartikel eine Spalte „Ablängungen" mit der Anzahl der Sub-Artikel; die Zeile lässt sich aufklappen und listet die Sub-Artikel (Nummer, Bezeichnung, VK, Bestand) darunter.',
      'Sub-Artikel sind jetzt direkt in der Belegerfassung (Verkauf + Einkauf) auswählbar — beim Auswählen wird die Basisnummer als Artikelnummer gesetzt und der Längen-Zusatz separat gedruckt (zweizeilig unter der Nummer).',
      'Das zuvor eingeführte „Länge (mm)"-Feld an der Belegposition entfällt wieder — Ablängungen werden über die Auswahl des Sub-Artikels erfasst, nicht mehr als Positions-Parameter.',
    ],
  },
  {
    version: '3.157.1',
    date: '2026-07-08T17:30',
    title: 'App-Logo oben links linksbündig',
    tag: 'improvement',
    changes: [
      'Das App-Logo oben links ist jetzt linksbündig (bündig mit den Navigationseinträgen) statt zentriert.',
    ],
  },
  {
    version: '3.157.0',
    date: '2026-07-08T17:00',
    title: 'Artikel-Ablängung: kundenspezifische Längen (z. B. GFK-Balken)',
    tag: 'feature',
    changes: [
      'Ein Artikel kann jetzt als „ablängbar" markiert werden (Artikel bearbeiten) — mit Preis je Meter und optionaler Rüstpauschale. Grundlage für kundenspezifische Längen.',
      'Neuer Tab „Ablängungen" am ablängbaren Artikel: feste Lagerlängen als eigene Artikel anlegen (eigener Bestand, Artikelnummer = Basisnummer + Länge in mm). Gleiche Länge = dieselbe Variante; ein Basisartikel mit Ablängungen ist gegen Löschen geschützt.',
      'In der Belegerfassung (Verkauf + Einkauf) lässt sich zu einem ablängbaren Artikel in der Positions-Detailzeile eine Länge (mm) eingeben — Preis und gedruckter Zusatz werden automatisch berechnet.',
      'Auf den Beleg-PDFs erscheint die Länge klein unterhalb der Artikelnummer (die Basisnummer bleibt oben, damit die Zeile nicht zu lang wird).',
    ],
  },
  {
    version: '3.156.0',
    date: '2026-07-08T16:30',
    title: 'Belege: „Vorschau" + Dropdown „Export & Mail"',
    tag: 'improvement',
    changes: [
      'Der PDF-Button in der Belegbearbeitung heißt jetzt „Vorschau" (öffnet den Beleg direkt im PDF-Viewer des Browsers).',
      '„Export & Mail" ist jetzt ein Aufklapp-Button mit zwei Einträgen: „Export" (PDF herunterladen, Dateiname = Belegnummer) und „Mail" (PDF herunterladen + Mail-Entwurf mit Empfänger, Betreff und Text) — ohne Umweg über eine separate Seite.',
    ],
  },
  {
    version: '3.155.1',
    date: '2026-07-08T16:30',
    title: 'Navigation: Bereichs-Überschriften präsenter',
    tag: 'improvement',
    changes: [
      'Die Gruppen-Überschriften in der Seitenleiste sind jetzt kräftiger gesetzt (größer, halbfett, volle Farbe statt blassem Grau) und heben sich klarer als Bereichs-Header ab; beim Überfahren wird die Verlinkung zur Bereichs-Übersicht durch eine Unterstreichung angedeutet.',
    ],
  },
  {
    version: '3.155.0',
    date: '2026-07-08T16:00',
    title: 'Navigation: Bereichs-Übersichten (klickbare Überschriften)',
    tag: 'improvement',
    changes: [
      'Ein Klick auf eine Gruppen-Überschrift in der Seitenleiste öffnet jetzt eine Bereichs-Übersicht: alle Module des Bereichs als Kacheln, inklusive Schnell-Links zu den Unterseiten — ein Landeplatz je Bereich.',
      'Das Chevron rechts neben der Überschrift klappt den Bereich weiterhin ein oder aus (Zustand wird pro Benutzer gemerkt); die aktive Seite klappt ihren Bereich automatisch auf.',
      '„Übersicht“ bleibt wie gewohnt ohne Überschrift; im schmalen Icon-Modus sind weiterhin alle Symbole erreichbar.',
    ],
  },
  {
    version: '3.154.0',
    date: '2026-07-08T15:30',
    title: 'Navigation: Bereiche ein- und ausklappbar',
    tag: 'improvement',
    changes: [
      'Die Gruppen-Überschriften in der Seitenleiste sind jetzt anklickbar und klappen ihren Bereich auf oder zu — so lässt sich die Navigation auf die gerade genutzten Bereiche eindampfen.',
      'Der Bereich mit der aktuell geöffneten Seite klappt automatisch auf; welche Bereiche ein- oder ausgeklappt sind, wird pro Benutzer gemerkt.',
      '„Übersicht“ bleibt immer sichtbar; im schmalen Icon-Modus der Seitenleiste sind weiterhin alle Symbole erreichbar.',
    ],
  },
  {
    version: '3.153.0',
    date: '2026-07-08T15:00',
    title: 'Belege: PDF-Vorschau repariert (zwei Buttons)',
    tag: 'fix',
    changes: [
      'Der „PDF"-Button in der Belegbearbeitung (Verkauf & Einkauf) öffnet den Beleg wieder direkt im PDF-Viewer des Browsers — wie früher. Die zuletzt eingeführte Vorschau-Seite zeigte in manchen Umgebungen nur einen grauen Bereich.',
      'Zusätzlicher Button „Export & Mail" führt weiterhin auf die Vorschau-Seite mit „Beleg speichern" und „Via Mail senden".',
    ],
  },
  {
    version: '3.152.1',
    date: '2026-07-08T15:00',
    title: 'App-Logo kleiner, Versionsnummer unten links',
    tag: 'improvement',
    changes: [
      'Das App-Logo oben links ist noch einmal 20 % kleiner (weiterhin per Regler in den Einstellungen skalierbar).',
      'Die Versionsnummer steht jetzt ganz unten links unter dem Benutzer statt direkt unter dem Logo.',
    ],
  },
  {
    version: '3.152.0',
    date: '2026-07-08T14:00',
    title: 'Reisekosten: schlankeres Erfassen & Admin-Selbstfreigabe',
    tag: 'improvement',
    changes: [
      'Der Dialog „Reise erfassen" wurde verschlankt: Das Feld „Gefahrene km" entfällt komplett (auch in Liste, Detail-Ansicht und PDF-Export) — Kilometer werden nicht mehr erfasst.',
      'Grenz-Zeiten nur bei Bedarf: „Grenze Hinfahrt/Rückfahrt" erscheinen erst, wenn „Grenze überquert" angehakt ist (für Auslandsreisen). Inlandsreisen bleiben aufgeräumt.',
      'Klarere Verpflegungs-Fragen: „Mittagessen erhalten?" und „Abendessen erhalten?" statt nur „Mittag"/„Abend".',
      'Neuer Reisetag übernimmt jetzt die Angaben des Vortags (Reiseziel, Zweck, Zeiten, Grenze, Verpflegung) und setzt das Datum automatisch einen Tag weiter — spart Tipparbeit bei mehrtägigen Reisen.',
      'Admins (admin.manage) dürfen ihre eigenen Reisen selbst freigeben; für alle anderen bleibt das Vier-Augen-Prinzip bestehen.',
    ],
  },
  {
    version: '3.151.1',
    date: '2026-07-08T13:40',
    title: 'App-Logo: Standardgröße halbiert, Version rechtsbündig',
    tag: 'improvement',
    changes: [
      'Die Standardgröße des App-Logos (100 %) ist halbiert — das Logo war in voller Breite zu groß. Über den Regler in den Einstellungen lässt es sich weiterhin frei skalieren.',
      'Die Versionsnummer darunter ist kleiner und rechtsbündig.',
    ],
  },
  {
    version: '3.151.0',
    date: '2026-07-08T13:00',
    title: 'App-Logo: Größe einstellbar',
    tag: 'feature',
    changes: [
      'Die Größe des App-Logos oben links lässt sich jetzt unter Einstellungen → Branding per Regler einstellen (50–300 %, Standard 100 %) — mit Live-Vorschau.',
    ],
  },
  {
    version: '3.150.1',
    date: '2026-07-08T12:00',
    title: 'App-Logo oben links größer',
    tag: 'improvement',
    changes: [
      'Das App-Logo oben links in der Navigation ist deutlich größer (rund dreifach); die Versionsnummer steht jetzt darunter statt daneben.',
      'Der zusätzliche Text „ProERP" neben dem Logo entfällt — das hinterlegte Logo steht für sich.',
    ],
  },
  {
    version: '3.150.0',
    date: '2026-07-08T10:30',
    title: 'App-Logo für die Oberfläche',
    tag: 'feature',
    changes: [
      'Unter Einstellungen → Branding lässt sich ein eigenes App-Logo hochladen (PNG/JPEG, max. 2 MB). Es erscheint oben links in der Navigation, auf der Anmeldeseite und im mobilen Kopf.',
      'Das App-Logo ist bewusst getrennt vom Beleg-Logo je Gesellschaft (Tab „Gesellschaften") — es betrifft nur die Bildschirm-Oberfläche, nicht die PDF-Belege.',
      'Ohne hinterlegtes Logo zeigt die App weiterhin das Standard-Zeichen „P".',
    ],
  },
  {
    version: '3.149.0',
    date: '2026-07-08T09:00',
    title: 'Zeitprotokoll: Einträge manuell anlegen',
    tag: 'feature',
    changes: [
      'Neuer Button „Neuer Eintrag" im Zeitprotokoll — legt einen Zeiteintrag direkt mit Start-/Endzeit an, ohne zuerst einen Timer starten und stoppen zu müssen (z. B. für nachträglich erfasste Stunden).',
      'Derselbe Dialog wie beim Bearbeiten (Firma, Kategorie, Projekt, Tätigkeit, Start/Ende, Bemerkung); beim Neuanlegen ist die Kategorie Pflicht, genau wie beim Timer-Start.',
    ],
  },
  {
    version: '3.148.0',
    date: '2026-07-07T15:10',
    title: 'BUG: Neu angelegte Projekte in der Zeiterfassung nicht wählbar',
    tag: 'fix',
    changes: [
      'Die Projekt-Auswahl beim Timer-Start suchte nur innerhalb der ersten 100 (alphabetisch sortierten) aktiven Projekte — weiter hinten stehende, u. a. gerade neu angelegte Projekte, waren dadurch nicht auffindbar, egal was man in die Suche eingegeben hat.',
      'Die Suche fragt jetzt bei jeder Eingabe den Server ab (statt nur die bereits geladenen 100 zu filtern) — alle aktiven Projekte sind damit wieder auswählbar.',
    ],
  },
  {
    version: '3.147.0',
    date: '2026-07-07T14:30',
    title: 'Verkauf: Beleg-Editor für Mobilgeräte optimiert',
    tag: 'improvement',
    changes: [
      'Die Editor-Kopfzeile bricht auf schmalen Screens jetzt sauber um (kein Überlappen von Titel/Status mehr); Zurück- und Schließen-Button bleiben immer erreichbar.',
      'Belegkopf-Felder (Gesellschaft, Belegdatum, gültig bis, Liefertermin, Zahlungsziel, USt-Satz) stehen auf Mobil einspaltig in voller Breite statt in einem zu engen Zwei-Spalten-Raster.',
      'Die Positionstabelle bleibt auf schmalen Screens voll funktionsfähig (Inline-Bearbeitung, Drag & Drop, Artikelsuche) und lässt sich statt gequetschter Spalten horizontal durchscrollen.',
      'Die Summenleiste (Werkbank-Ansicht) läuft nicht mehr über den Bildschirmrand — sie scrollt bei Bedarf horizontal, „Gesamtbetrag" bleibt erreichbar.',
      'Tab-Leisten mit vielen Reitern (z. B. Verkaufsbelege) zeigen jetzt eine dezente Fade-Kante, wenn noch weitere Tabs außerhalb des Bildschirms liegen.',
    ],
  },
  {
    version: '3.146.0',
    date: '2026-07-07T13:45',
    title: 'Verkauf + Einkauf: PDF-Vorschau mit „Beleg speichern" und „Via Mail senden"',
    tag: 'feature',
    changes: [
      'Der PDF-Button im Beleg-Editor (Verkauf + Einkauf) öffnet jetzt eine eigene Vorschau-Seite (neuer Tab) mit der PDF plus zwei Aktionen: „Beleg speichern" (Download, Dateiname = Belegnummer) und „Via Mail senden".',
      '„Via Mail senden" lädt das PDF herunter und öffnet gleichzeitig das lokale E-Mail-Programm mit vorbefülltem Betreff/Text (Empfänger = Kunden-/Lieferanten-E-Mail, falls hinterlegt) — das PDF muss aus dem Download-Ordner in den Entwurf gezogen werden, da kein Browser E-Mails per `mailto:` mit Anhang öffnen kann.',
    ],
  },
  {
    version: '3.145.0',
    date: '2026-07-07T12:30',
    title: 'Einkauf: alte Belegmaske entfernt',
    tag: 'fix',
    changes: [
      'Der alte, separate Bestellungs-Ansichtsbildschirm ist entfernt — ein Link/Klick auf eine Bestellung öffnet jetzt immer den Vollbild-Editor (Belegblatt/Werkbank), nie mehr die alte Maske.',
      'Projekt-Bezug und Belegfluss (Vorgänger-/Folgebelege wie Anfrage → Bestellung → Eingangsrechnung), bisher nur in der alten Maske sichtbar, erscheinen jetzt auch im neuen Editor.',
      'Eine doppelte, ungenutzte Route für den Bestell-Editor wurde entfernt.',
    ],
  },
  {
    version: '3.144.0',
    date: '2026-07-07T11:15',
    title: 'Verkauf: Belegliste nach Gesellschaft filterbar',
    tag: 'feature',
    changes: [
      'Neuer Filter „Gesellschaft" in der Verkaufsbelegliste (neben Suche, Jahr und Status) — zeigt nur Belege der ausgewählten Gesellschaft.',
    ],
  },
  {
    version: '3.143.0',
    date: '2026-07-07T10:00',
    title: 'Verkauf: Belegliste neu sortiert und Rechnungsarten zusammengefasst',
    tag: 'improvement',
    changes: [
      'Die Tabs der Verkaufsbelege sind neu geordnet: Angebote, Aufträge, Kommissionierungen, Lieferscheine, Rechnungen, Stornos, Proforma-Rechnung.',
      'Der Tab „Rechnungen" zeigt jetzt Rechnungen, Anzahlungsrechnungen und Schlussrechnungen gemeinsam (statt in drei getrennten Tabs) — je Zeile markiert eine Typ-Spalte RE (Rechnung), TR (Anzahlungsrechnung) oder SR (Schlussrechnung). Die Belegnummern selbst (RE-/AZ-/SR-Präfix) bleiben unverändert.',
      '„Neu: Rechnung" legt im Rechnungen-Tab weiterhin eine normale Rechnung an; Anzahlungs- und Schlussrechnungen entstehen wie bisher aus dem Auftrag.',
    ],
  },
  {
    version: '3.142.0',
    date: '2026-07-06T15:30',
    title: 'Neues Modul: Update-Log / Audit-Log',
    tag: 'feature',
    changes: [
      'Neue Seite „Audit-Log" unter Administration (nur für Administratoren): unveränderliches Protokoll aller Schreib-Operationen der App (Anlegen/Ändern/Archivieren/Löschen) mit Filter nach Objekttyp, Aktion, Benutzer und Zeitraum sowie Volltextsuche.',
      'Aufbewahrung automatisch auf 30 Tage begrenzt — ein Hintergrundjob löscht ältere Einträge täglich, unabhängig von Redis/BullMQ (Single-Server-Setup).',
      'Die bestehende Audit-Log-Schreiblogik (bisher intern, ohne Oberfläche) bleibt unverändert; neu ist ausschließlich die Leseseite (Berechtigung `audit.read`, ausdrücklich nicht Teil der „Sekretariat+"-Sonderbefugnisse).',
    ],
  },
  {
    version: '3.141.0',
    date: '2026-07-06T14:20',
    title: 'Umbau Zeiterfassung: neue Ebene „Kategorie"',
    tag: 'feature',
    changes: [
      'Neues Feld „Kategorie" zwischen Firma und Projekt: Firma → Kategorie → Projekt → Tätigkeit. Die 11 Kategorien (#Aufträge, #Vertrieb, #Marketing, #Buchhaltung, #Technik, #EDV, #Lager/Instandhaltung, #Büro, #ISO, #Neuentwicklung, #Pause) sind eigenständige, admin-gepflegte Stammdaten (neuer Tab „Kategorien" in den Zeiterfassung-Stammdaten) und einmalig mit den vorgegebenen Zeit-Projekten/Tätigkeiten vorbelegt.',
      'Jede Kategorie schränkt ein, welche Projekte wählbar sind: entweder eine feste Liste von Zeit-Projekten oder zusätzlich jedes echte ProERP-Projekt (#Aufträge/#Vertrieb/#Neuentwicklung). Unabhängig vom gewählten Projekt schränkt die Kategorie außerdem ein, welche Tätigkeiten (Unterkategorien) zur Auswahl stehen.',
      'Zeit-Projekte und Tätigkeiten mit gleichem Namen werden über mehrere Kategorien hinweg geteilt (z. B. „Diverses" bei #Technik/#EDV/#Lager-Instandhaltung/#Büro) statt dupliziert; Zeit-Projekte können jetzt auch firmenübergreifend angelegt werden (keine Gesellschaft fest zugeordnet).',
      'Die Kostenstelle bleibt unverändert automatisch aus dem Mitarbeiterstamm abgeleitet und komplett unabhängig von der neuen Kategorie. Bestehende Zeit-Projekte/Unterkategorien und historische Zeitbuchungen bleiben unangetastet — Alt-Einträge ohne Kategorie lassen sich weiterhin wie bisher bearbeiten.',
    ],
  },
  {
    version: '3.140.0',
    date: '2026-07-06T12:54',
    title: 'Universelle Suche: mehr Details in der Vorschau',
    tag: 'improvement',
    changes: [
      'Die Vorschau-Spalte (⌘K) zeigt jetzt die Fundstelle im Volltext mit hervorgehobenem Treffer, statt nur Titel/Untertitel/Status.',
      'Für Artikel, Kunden, Verkaufsbelege, Bestellungen und Projekte lädt die Vorschau zusätzlich die wichtigsten Kennzahlen live nach — z. B. Verkaufspreis/Lagerbestand bei Artikeln, Kontaktdaten bei Kunden, Netto/Brutto bei Belegen.',
    ],
  },
  {
    version: '3.139.0',
    date: '2026-07-06T12:33',
    title: 'BUGFIX: Erstes Zeit-Projekt ließ sich nicht anlegen + Zeitprotokoll-Klick',
    tag: 'fix',
    changes: [
      'War noch kein Zeit-Projekt bzw. keine Unterkategorie angelegt, öffnete der Dialog „+ Zeit-Projekt"/„+ Unterkategorie" gar nicht — der Dialog steckte in derselben bedingten Ausgabe wie die (bei leerer Liste ausgeblendete) Tabelle. Betroffene Tenants konnten ihr allererstes Zeit-Projekt nie über die UI anlegen.',
      'Im Zeitprotokoll öffnet ein Klick auf eine ganze Zeile jetzt den Bearbeiten-Dialog (wie schon in der mobilen Ansicht) — nicht mehr nur der kleine Stift ganz rechts.',
      'Das Update-Log zeigt ab sofort auch die Uhrzeit an, wenn ein Eintrag eine trägt (ältere Einträge ohne Uhrzeit bleiben unverändert).',
    ],
  },
  {
    version: '3.138.0',
    date: '2026-07-06',
    title: 'Bestell-Sammelkorb: Aufteilung nach Standardlieferant',
    tag: 'improvement',
    changes: [
      '„Bestellung erzeugen" fasste bisher alle ausgewählten Positionen in eine einzige Bestellung an einen manuell gewählten Lieferanten zusammen — auch wenn die Artikel eigentlich unterschiedliche Standardlieferanten haben.',
      'Der Dialog teilt den Sammelkorb jetzt automatisch nach dem Standardlieferanten jedes Artikels auf und legt pro Lieferant eine eigene Bestellung an; der Lieferant ist je Gruppe vorbefüllt, bleibt aber änderbar. Positionen ohne (auflösbaren) Standardlieferanten landen in einer eigenen Gruppe „Ohne Standardlieferant" mit Pflicht-Auswahl.',
    ],
  },
  {
    version: '3.137.0',
    date: '2026-07-06',
    title: 'Bedarfstabelle: eine sichtbare Aktion statt drei versteckter Klicks',
    tag: 'improvement',
    changes: [
      'Reservieren oder Bestellen lief bisher über drei unverbundene Stellen der Zeile: Chip anklicken (Menü nicht erkennbar), Quelle wählen, dann für Lager das Schloss-Symbol ganz rechts bzw. für Bestellen die Checkbox ganz links plus den Kopf-Button.',
      'Die Quelle Lager/Bestellen ist jetzt ein sichtbarer Umschalter, direkt daneben zeigt ein einzelner Button die eine sinnvolle nächste Aktion — „Reservieren" oder „Zur Bestellung" (wechselt zu „Im Sammelkorb", sobald ausgewählt). Ist eine Position bereits reserviert, bestellt oder geliefert, erscheint stattdessen der informative Status.',
    ],
  },
  {
    version: '3.136.0',
    date: '2026-07-06',
    title: 'BUGFIX: Zeile rutscht beim Hovern in der Belegerfassung',
    tag: 'fix',
    changes: [
      'Beim Überfahren einer Position mit der Maus sprang die ganze Zeile leicht nach rechts, weil die Zeilennummer beim Hovern durch den Verschiebe-Anfasser ersetzt wurde und dadurch die Tabellenspalte kurzzeitig breiter wurde.',
      'Zeilennummer und Anfasser überlagern sich jetzt in einem fest reservierten Bereich statt sich gegenseitig aus dem Layout zu nehmen — die Spaltenbreite bleibt beim Hovern konstant.',
    ],
  },
  {
    version: '3.135.0',
    date: '2026-07-06',
    title: 'BUGFIX: Gesellschaften-Auswahl für alle Rollen',
    tag: 'fix',
    changes: [
      'Die Gesellschaften-Liste (z. B. in Verkaufs-/Einkaufsbelegen, Projekt anlegen, Kundenformular) verlangte bisher dieselbe Berechtigung wie die Admin-Einstellungsseite — dadurch war die Auswahl für alle Rollen außer Administrator und Sekretariat+ leer bzw. nicht nutzbar.',
      'Lesen ist jetzt ein Basisrecht, das jede Rolle automatisch hat (wie die globale Suche); Anlegen/Ändern/Löschen einer Gesellschaft bleibt weiterhin zentral beim Administrator.',
    ],
  },
  {
    version: '3.134.0',
    date: '2026-07-06',
    title: 'Bedarfstabelle: ein Blick statt zwei Spalten für Lager/Bestellung',
    tag: 'improvement',
    changes: [
      'Die Spalten „Quelle" und „Status" sind zu einer Spalte „Beschaffung" verschmolzen: ein Symbol-Chip zeigt sofort, ob eine Position aus dem Lager kommt, bestellt und unterwegs ist (inkl. Liefertermin und Bestellnummer), teilweise oder vollständig geliefert wurde, oder noch offen ist.',
      'Der Chip ist zugleich der Umschalter für die manuelle Quelle-Wahl (Lager/Bestellen) — ein Klick öffnet die Auswahl, statt eines separaten Dropdowns.',
      'Die Tabelle zeigt dadurch weniger, aber aussagekräftigere Spalten; Detailzahlen (Reserviert/Bestellt/Eingegangen) bleiben über „Bezüge" pro Position abrufbar.',
    ],
  },
  {
    version: '3.133.0',
    date: '2026-07-06',
    title: 'Rechteverwaltung: Ansicht „Nach Modul" + Schutz vor Reset',
    tag: 'feature',
    changes: [
      'Neue Ansicht unter Admin → Rollen → „Nach Modul": links das Modul anklicken, rechts sehen und umschalten, welche Rolle welche Aktion darin darf — statt Rolle für Rolle durch alle Module zu klicken.',
      'BUGFIX (ADR 0038): Manuelle Rechte-Anpassungen an Systemrollen (z. B. „Sekretariat" zusätzlich ein Recht geben) überlebten bisher keinen Neustart/Deploy — der Rollen-Seeder hat sie beim nächsten Start stillschweigend zurückgesetzt. Angepasste Rollen werden jetzt als „Angepasst" markiert und vom Seeder in Ruhe gelassen; ein „Zurücksetzen"-Knopf holt bei Bedarf explizit wieder den Code-Standard.',
      'Globale Suche: Berechtigung „search.read" hat jetzt eine eigene, gut sichtbare Gruppe „Globale Suche" in der Rechte-Übersicht (vorher in der Admin-Gruppe versteckt) — Suchergebnisse bleiben weiterhin je Treffertyp nach Rolle gefiltert.',
    ],
  },
  {
    version: '3.132.0',
    date: '2026-07-06',
    title: 'Bedarfsliste: Excel-Zeilen direkt einfügen',
    tag: 'feature',
    changes: [
      'Im Import-Dialog der Materialliste (Projekt → Warenwirtschaft & Belege → Bedarf/Material) können jetzt Zeilen aus Excel direkt eingefügt werden (⌘V) — zusätzlich zum Datei-Upload.',
      'Eingefügte Zeilen werden sofort gegen den Artikelstamm abgeglichen: eine Vorschau zeigt je Zeile Artikel-Nr., Bezeichnung, Menge und ob der Artikel gefunden wurde, bevor irgendetwas übernommen wird.',
      'Nur die gültigen Zeilen lassen sich mit einem Klick übernehmen ("N Positionen übernehmen"); unbekannte Artikelnummern fallen vorher auf, statt danach aufgeräumt werden zu müssen.',
    ],
  },
  {
    version: '3.131.0',
    date: '2026-07-06',
    title: 'Reporting Auftragseingang: neue Spalten + Monats-Download',
    tag: 'improvement',
    changes: [
      'Je Auftrag jetzt mit Datum, Beleg, Kunde, Gesamtwert netto, Rohertrag und Gesellschaft — Rohertrag ist Netto abzüglich der EK-Kosten der Artikelpositionen (approximiert über den aktuellen EK-Preis).',
      'Jeder Monat lässt sich einzeln als Excel herunterladen, zusätzlich zum bestehenden Jahres-Export.',
    ],
  },
  {
    version: '3.130.0',
    date: '2026-07-06',
    title: 'Schutz gegen zeitgleiches Bearbeiten (Beleg & Artikel)',
    tag: 'feature',
    changes: [
      'Öffnet ein zweiter Nutzer denselben Beleg (Verkauf/Einkauf) oder Artikel zum Bearbeiten, sieht er einen Hinweis „Wird gerade von … bearbeitet" und das Formular schaltet auf Nur-Lesen — verhindert, dass Änderungen sich gegenseitig überschreiben.',
      'Die Sperre läuft automatisch nach kurzer Zeit ab, falls eine Sitzung abstürzt oder der Tab ohne Schließen verlassen wird — niemand bleibt dauerhaft ausgesperrt.',
    ],
  },
  {
    version: '3.129.0',
    date: '2026-07-05',
    title: 'Reporting: Auftragseingang bis auf den einzelnen Auftrag aufgegliedert',
    tag: 'improvement',
    changes: [
      'Jeder Monat im Report „Auftragseingang" lässt sich aufklappen und zeigt die einzelnen Aufträge (Beleg-Nr., Kunde, Datum, Netto) statt nur der Monatssumme.',
      'Der Excel-Export listet jeden Auftrag einzeln je Monat, mit Monats-Zwischensumme und Gesamtsumme.',
    ],
  },
  {
    version: '3.128.0',
    date: '2026-07-05',
    title: 'Belegkette im Verkaufs-Editor nach unten verschoben',
    tag: 'improvement',
    changes: [
      'Der Belegfluss-Baum sitzt jetzt im unteren Bereich des Beleg-Editors (unter Positionen/Summen) statt oben über dem Beleg.',
      'Oben bleiben die kurzen Absprünge zu Projekt und auslösender Kommissionierung erhalten.',
    ],
  },
  {
    version: '3.127.0',
    date: '2026-07-05',
    title: 'Modul Reporting',
    tag: 'feature',
    changes: [
      'Neues Menü „Reporting" (für Stakeholder & Buchhaltung) mit vier Auswertungen, je mit Excel-Export.',
      'Auftragseingang je Monat (Anzahl + Netto) mit Jahresauswahl.',
      'Umsatz nach Kunde und nach Land (aus Rechnungen).',
      'Lagerwert je Artikel (Bestand × EK) inkl. Gesamtwert.',
      'HF/NAL — halbfertige/nicht abrechenbare Leistungen je Projekt (Kosten − Fakturiert).',
    ],
  },
  {
    version: '3.126.0',
    date: '2026-07-05',
    title: 'Belegfluss & Verknüpfungen',
    tag: 'feature',
    changes: [
      'Herkunft der Lagerbewegungen ist jetzt klickbar: In der Lagerbewegungs-Übersicht und im Artikel-Tab „Lagerbewegungen" führt die Spalte „Herkunft" direkt zum auslösenden Beleg (Kommissionierung, Lieferschein, Wareneingang); die frühere Notiz bleibt als Sekundärzeile/Tooltip erhalten.',
      'Verkaufsbelege: Die Projekt-Spalte in der Belegliste und ein Projekt-Absprung im Beleg-Editor verlinken direkt aufs Projekt.',
      'Bestellungen: Die Detailseite zeigt jetzt Folgebelege (z. B. aus einer Anfrage entstandene Bestellung/Eingangsrechnung) sowie einen Absprung zum Vorgängerbeleg.',
      'Projekt-Material: Pro Position aufklappbare „Bezüge" mit den echten Verknüpfungen (verlinkte Bestellungen, Kommissionierungen und Reservierungen) statt artikelbasierter Heuristik.',
      'Lieferschein ↔ Kommissionierung: Im Editor eines Lieferscheins erscheint ein Hinweis „aus Kommissionierung …" mit Absprung zur auslösenden Kommissionierung.',
      'Belegfluss im Verkaufs-Editor jetzt direkt sichtbar (nicht nur im Menü) und vollständig: als Baum von der Wurzel bis zu allen Nachfahren, mit hervorgehobenem aktuellem Beleg.',
    ],
  },
  {
    version: '3.125.0',
    date: '2026-07-05',
    title: 'Artikel-Übersicht: irreführende Balken entfernt',
    tag: 'improvement',
    changes: [
      'Die Bestandskennzahlen (Bestand, Frei verfügbar, Reserviert, Kalk./Bestell-/Forecast-Bestand) zeigen jetzt nur Zahl + Bezeichnung ohne Fortschrittsbalken — diese Werte haben kein natürliches Maximum, ein voller Balken bei 0 oder negativem Bestand war irreführend.',
    ],
  },
  {
    version: '3.124.0',
    date: '2026-07-05',
    title: 'Sperrlager: Detailseite im neuen Tab',
    tag: 'improvement',
    changes: [
      'Klick auf einen Sperrlager-Eintrag öffnet jetzt eine eigene Detailseite in einem neuen Tab — mit allen Feldern und einer großen Bildergalerie (Foto anklicken = vergrößern) statt des kleinen Bearbeiten-Dialogs.',
      'Bearbeiten weiterhin per Button auf der Detailseite; Fotos hinzufügen/löschen direkt dort.',
    ],
  },
  {
    version: '3.123.0',
    date: '2026-07-05',
    title: 'Bedarfsvorschau-PDF mit Logo',
    tag: 'improvement',
    changes: [
      'Das PDF der Bedarfsvorschau trägt jetzt oben rechts das hinterlegte Firmenlogo (aus Gesellschaften/Branding) — wie die Kommissionierbelege.',
    ],
  },
  {
    version: '3.122.0',
    date: '2026-07-05',
    title: 'Weitere Audit-Funde behoben (MEDIUM/LOW)',
    tag: 'fix',
    changes: [
      'Einkauf: Der „letzte Bezugspreis" eines Artikels stammt nicht mehr aus gelöschten oder stornierten Bestellungen.',
      'Einkauf: Positionen einer Bestellung (Hinzufügen/Ändern/Löschen) werden jetzt im Audit-Log protokolliert, da sich der Bestellwert ändert.',
      'Einkauf: Bestellungen aus dem Projektbedarf übernehmen die Einkaufswährung der Artikel; bei Fremd- oder gemischten Währungen wird nicht mehr still in EUR gebucht.',
      'Zeiterfassung: Die Kosten beim Bearbeiten einer Buchung werden exakt (ganzzahlig, ohne Float) berechnet — identisch zum Anlegen.',
      'Projekte: Das Reservieren einer Materialposition wird jetzt im Audit-Log protokolliert (wie das Aufheben der Reservierung).',
      'Kommissionierung: Beim Wandeln in einen Lieferschein wird der Beleg vorab exklusiv beansprucht — ein Doppelklick erzeugt keinen verwaisten Lieferschein mehr.',
      'Kommissionierung: Nach Abschluss/Wiederöffnen werden Artikelbestände und Projektmaterial in der Oberfläche automatisch aktualisiert.',
      'Lager: Der CSV-Export der Lagerbewegungen zeigt EK- und Wertbeträge in voller Präzision (Sub-Cent-Preise werden nicht mehr abgeschnitten).',
      'Projekte: Ein Ladefehler in der Materialliste wird jetzt als Fehler mit „Erneut versuchen" angezeigt statt als leere Liste.',
      'Bedarfsvorschau & Kommissionierung: Einheiten werden lesbar dargestellt („Stk" statt „PIECE").',
      'Reisekosten: Kilometer mit deutschem Komma werden korrekt übernommen; ungültige Eingaben blockieren das Speichern statt still 0 zu setzen.',
      'Berechtigungen: In den Listen (Projekte, Artikel, Kunden, Beschaffung, Verkauf) sehen Nur-Lese-Nutzer keine Anlegen-/Bearbeiten-/Löschen-Aktionen mehr.',
    ],
  },
  {
    version: '3.121.0',
    date: '2026-07-05',
    title: 'Mehrere Korrektheits-/Sicherheitsfixes aus dem Prozess-Audit',
    tag: 'fix',
    changes: [
      'Globale Suche: Treffer werden jetzt streng nach Berechtigung gefiltert. Ohne das jeweilige Modul-Leserecht erscheinen keine Mitarbeiter-, Zahlungs- oder Dokument-Treffer mehr; das Suchen selbst erfordert das neue Basisrecht „search.read" (für alle Standardrollen freigeschaltet).',
      'Verkauf: Beim Wandeln von Belegen werden stornierte/abgelehnte Vorgänge abgelehnt und doppelte Folgebelege verhindert (kein zweiter Rechnungs-/Gutschrift-/Schlussrechnungsbeleg mehr durch Doppelklick).',
      'Verkauf: Der Warenausgang eines Lieferscheins wird bei gleichzeitigem Buchen nur noch einmal gebucht (kein doppelter Lagerabgang).',
      'Einkauf: Der Wareneingang bucht auch bei parallelem Abhaken nicht mehr doppelt (Menge und Lagerbestand bleiben korrekt).',
      'Lager: Bestandsbuchungen schreiben den Bestand jetzt relativ fort (Zugang/Abgang), sodass gleichzeitige Bewegungen sich nicht mehr gegenseitig überschreiben.',
      'Artikel & Projekte: Beträge mit mehr als zwei Nachkommastellen (z. B. kg-/m-Preise) lassen sich wieder speichern — auch bei unverändertem Feld.',
      'Kommissionierung: Beim Abschluss wird die Lager-Reservierung der kommissionierten Position aufgelöst; der frei verfügbare Bestand wird dadurch nicht mehr fälschlich negativ.',
    ],
  },
  {
    version: '3.120.0',
    date: '2026-07-05',
    title: 'Neues Modul „Sperrlager" (Logistik)',
    tag: 'feature',
    changes: [
      'Neuer Menüpunkt Logistik → Sperrlager: verwaltet Restmaterial ohne Stammdaten-Bezug (z. B. aus Projekten) nach Stellplatz und mit Fotos.',
      'Sperrlager-Artikel anlegen/bearbeiten mit allen Feldern (Beschreibung, Material, Abmessung, Menge/Einheit, Stellplatz, Kategorie, Hersteller, Kürzel, Bewertung, Bemerkung) inkl. Foto-Upload und Galerie mit Löschen je Foto.',
      'Bestand inkl. Fotos aus einer JSON-Datei des Alt-Systems importieren: Vorschau der gültigen Einträge, Dedup über die Herkunfts-ID, Einheit wird aus dem Alt-Feld abgeleitet. Fotos werden — wenn möglich — heruntergeladen und lokal gespeichert, sonst als Link übernommen.',
      'Liste mit Suche und Filtern nach Kategorie, Stellplatz und Hersteller, Sortierung, Paginierung und CSV-Export.',
      'Berechtigungen „Sperrlager" (Lesen/Anlegen/Ändern/Löschen) für Rollen Lager, Sekretariat, Technik und Nur-Lesen ergänzt.',
    ],
  },
  {
    version: '3.119.0',
    date: '2026-07-05',
    title: 'Datenbank-Editor: Warengruppen zeigten 0 Einträge',
    tag: 'fix',
    changes: [
      'In Administration → Datenbank war „Warengruppen" fälschlich mit der (leeren) Tabelle der Hauptwarengruppen verknüpft — daher immer 0. Jetzt zeigt „Warengruppen" die echte Tabelle (commodity_groups).',
      'Zusätzlich sichtbar/editierbar: Hauptwarengruppen, Materialcodes und Taric-Codes.',
    ],
  },
  {
    version: '3.118.0',
    date: '2026-07-05',
    title: 'Rollen löschen — auch Systemrollen',
    tag: 'improvement',
    changes: [
      'Unter Benutzer & Rechte → Rollen lassen sich jetzt alle Rollen löschen (außer der Administrator-Rolle) — bisher waren die geseedeten Systemrollen gesperrt. Mit Bestätigungsdialog, auch am Handy.',
      'Rollen, die noch Benutzern zugewiesen sind, können nicht gelöscht werden (erst dort entfernen).',
      'Eine bewusst gelöschte Systemrolle wird beim nächsten Start NICHT wieder angelegt — die Löschung bleibt bestehen.',
    ],
  },
  {
    version: '3.117.0',
    date: '2026-07-05',
    title: 'Warengruppen-/Material-Import: Alt-System-Format erkannt',
    tag: 'improvement',
    changes: [
      'Der CSV-Import (Engineering → Warengruppen / Materialcodes) erkennt jetzt Dateien mit Kopfzeile automatisch und ordnet die Spalten per Name zu — auch bei abweichender Reihenfolge oder Extraspalten wie „Aktiv"/„Erstellt am".',
      'Damit lassen sich Export-Dateien (Format „Code;Warengruppencode;Bezeichnung;Aktiv;Erstellt am" bzw. „Materialcode;Bezeichnung;Aktiv") direkt hochladen — ohne vorher umzusortieren.',
      'Die Spalte „Aktiv" (Ja/Nein) wird beim Import ausgewertet; ohne Kopfzeile gilt weiterhin die dokumentierte Reihenfolge Code, Name, (Hauptwarengruppe).',
    ],
  },
  {
    version: '3.116.0',
    date: '2026-07-05',
    title: 'Navigation neu gegliedert',
    tag: 'improvement',
    changes: [
      'Gruppe „Material & Fertigung" heißt jetzt „Material" (wir fertigen nicht selbst) und bündelt Artikel & Lager, Beschaffung und Bedarfsvorschau.',
      'Neue Gruppe „Montage & Service" (Montage & Wartung) als eigener Prozessschritt.',
      '„Logistik" bleibt eine eigene Gruppe mit der Kommissionierung — hier docken später Versand/Lieferscheine an.',
      'Reihenfolge folgt jetzt dem Ablauf: Vertrieb → Engineering → Material → Logistik → Montage → Finanzen.',
    ],
  },
  {
    version: '3.115.0',
    date: '2026-07-05',
    title: 'Projekt: Belege in die Warenwirtschaft verschoben',
    tag: 'improvement',
    changes: [
      'Im Projekt liegt das Unterregister „Belege" jetzt unter der Warenwirtschaft statt unter „Kaufmännisch" — Belege, Material, Bestellungen und Kommissionierung gehören fachlich zusammen.',
      'Das Hauptregister „Material" heißt deshalb neu „Warenwirtschaft & Belege". „Kaufmännisch" bündelt weiterhin Controlling und Vorkalkulation.',
    ],
  },
  {
    version: '3.114.0',
    date: '2026-07-05',
    title: 'Reisekosten komplett überarbeitet',
    tag: 'improvement',
    changes: [
      'Neuer Aufbau: Kennzahlen oben (Offen · Genehmigt · km im laufenden Jahr), darunter „Meine Reisen" — ab Desktop als Tabelle, am Handy als Kartenliste mit großen Tap-Zielen.',
      'Verwaltung: Summenzeile zum Filterergebnis (Reisen · km · Std), Stunden-Spalte, Prüfer-Name bei geprüften Reisen und eine mobile Kartenansicht mit prominentem „Prüfen"-Button.',
      'Detail- und Erfassungs-Dialog modernisiert: Kennzahl-Kacheln, Tages-Karten am Handy, klare Summen-Leiste — alle Funktionen (Erfassen, Bearbeiten, Genehmigen/Ablehnen, Monats-PDF) unverändert erhalten.',
    ],
  },
  {
    version: '3.113.0',
    date: '2026-07-05',
    title: 'Rollen & Rechte · Suche mit Filter & Vorschau · Projektseite entschlackt · neuer Zeit-Picker',
    tag: 'feature',
    changes: [
      'Neue Geschäftsrollen (ADR 0037): Sekretariat (operativ ohne Auswertungen/Finanzen), Sekretariat+ (alles außer Benutzer- & Rechteverwaltung), Technik (viel sehen, bearbeiten rund um Projekte/Engineering), Lager. Neue Berechtigung „Auswertungen & Kennzahlen" (analytics.read) schützt Analyse-Seiten und finanzielle Kennzahlen — server- und oberflächenseitig. Feinjustage jederzeit unter Administration → Benutzer & Rechte.',
      'Globale Suche (⌘K): Kategorie-Chips unter dem Suchfeld (Alle · je Modul mit Trefferzahl) und ein geteiltes Layout — links die Treffer, rechts eine Detailvorschau des markierten Eintrags mit „Öffnen"-Button.',
      'Projekt-Detailseite: statt 13 Registern jetzt 6 klare Bereiche (Übersicht · Kaufmännisch · Planung · Material · Montage · Stunden) mit Unterregistern — alle Inhalte bleiben unverändert erreichbar.',
      'Zeiterfassung: Start/Ende im Bearbeiten-Dialog mit modernem Datums-Picker + separatem Uhrzeitfeld statt des sperrigen Browser-Felds.',
    ],
  },
  {
    version: '3.112.0',
    date: '2026-07-05',
    title: 'Packliste auf Englisch · Brotkrumen in der Bedarfsvorschau',
    tag: 'improvement',
    changes: [
      'Die Packliste lässt sich jetzt zusätzlich als englische Version exportieren („Packliste EN") — Überschriften, Status, Einheiten und Datumsformat auf Englisch.',
      'Die Bedarfsvorschau-Detailseite hat jetzt dieselbe Brotkrumen-Navigation wie die übrigen Detailseiten (Bedarfsvorschau / Liste) statt des einfachen Zurück-Links.',
    ],
  },
  {
    version: '3.111.0',
    date: '2026-07-05',
    title: 'Zeiterfassung-Dashboard überarbeitet',
    tag: 'improvement',
    changes: [
      'Der laufende Timer sitzt jetzt ganz oben als „Hero" mit sekündlich tickender Dauer und einem direkten „Stoppen"-Button — kein Umweg mehr über den schwebenden Timer.',
      'Klarere Reihenfolge: Timer oben, darunter die Kennzahlen (heute / diese Woche / aktive Projekte), dann die letzten Einträge und die meistgenutzten Projekte.',
      'Letzte Einträge sind anklickbar und öffnen direkt den Bearbeiten-Dialog; laufende Einträge sind als solche markiert. Kopf mit Schnellzugriff auf Protokoll und (für Verwalter) Stammdaten.',
      'Auf dem Dashboard wird der schwebende Timer ausgeblendet, damit die Zeit nicht doppelt erscheint.',
    ],
  },
  {
    version: '3.110.0',
    date: '2026-07-05',
    title: 'Globale Suche (⌘K): größeres, moderneres Fenster',
    tag: 'improvement',
    changes: [
      'Das Suchfenster (⌘K / Strg+K) nimmt jetzt rund zwei Drittel des Bildschirms ein und ist deutlich höher — mehr Treffer auf einen Blick.',
      'Frischeres Design: größeres Suchfeld, Ergebnis-Zeilen mit Icon-Kachel und Untertitel, ein einladender Leerzustand und eine Fußzeile mit Tastatur-Hinweisen (↑↓ navigieren · ↵ öffnen · esc schließen) samt Trefferzahl.',
    ],
  },
  {
    version: '3.109.0',
    date: '2026-07-05',
    title: 'Einstellungen: Tab „Firma" entfernt · Branding wieder speicherbar',
    tag: 'fix',
    changes: [
      'Der Tab „Firma" unter Einstellungen ist entfallen — Firmenstammdaten werden über „Gesellschaften" gepflegt.',
      'Branding/Login-Einstellungen lassen sich wieder speichern: Der Speichern-Button war fälschlich an den (nun nicht mehr gepflegten) Firmennamen gekoppelt.',
    ],
  },
  {
    version: '3.108.0',
    date: '2026-07-05',
    title: 'Kommissionierbeleg-PDF: Artikel-Langtext je Position',
    tag: 'improvement',
    changes: [
      'Der Kommissionierbeleg (PDF) zeigt jetzt je Position den Artikel-Langtext (Infotext aus dem Artikelstamm) unter der Bezeichnung — der Lagerist erkennt auch im Ausdruck sofort, was einzupacken ist.',
    ],
  },
  {
    version: '3.107.0',
    date: '2026-07-05',
    title: 'Engineering-Assistent: volle Nummer sofort + vollständige Artikeldaten',
    tag: 'improvement',
    changes: [
      'Die vollständige Artikelnummer wird jetzt schon beim Anlegen angezeigt — auch bei einer neuen Zeichnung (die nächste laufende Nummer wird vorab ermittelt und beim Absenden reserviert). Kein „?????" mehr.',
      'Der Artikel-Schritt umfasst nun alle relevanten Felder (Stammdaten, Infotexte DE/EN, Standardlieferant, Zoll & Logistik, Preise & Lager) — der Techniker füllt möglichst viel aus, das Sekretariat gibt anschließend nur noch frei und ändert ggf. Kleinigkeiten.',
    ],
  },
  {
    version: '3.106.0',
    date: '2026-07-05',
    title: 'Engineering: geführter Assistent für Artikelnummern',
    tag: 'feature',
    changes: [
      'Neue Artikelnummern legt der Techniker jetzt über einen geführten Assistenten in 4 Schritten an: Zeichnungsnummer (neu über Warengruppe oder bestehende wählen) → Material → Artikeldaten → Prüfen. Zeichnungs- und Artikelnummer entstehen in einem Durchgang.',
      'Die resultierende Artikelnummer (XXXX.YYYYY.ZZZ) wird live angezeigt und bleibt eindeutig (Prüfung gegen Artikelstamm und offene Anträge). Der Artikel-Schritt ist schlank — Details wie EN-Bezeichnung, Hersteller oder EK/VK sind optional aufklappbar.',
      'Der bewährte Ablauf bleibt: erst nach Freigabe durch das Sekretariat (Vier-Augen) entsteht der echte Artikel im Artikelstamm. Bearbeiten/Prüfen offener Anträge wie gewohnt unter „Anträge".',
    ],
  },
  {
    version: '3.105.0',
    date: '2026-07-05',
    title: 'Verkauf: alter Tab „Kommissionierlisten" entfernt',
    tag: 'improvement',
    changes: [
      'Die alte Belegart „Kommissionierlisten" ist aus dem Verkauf verschwunden — sie wurde durch das eigene Modul „Kommissionierung" (Tab „Kommissionierung") ersetzt. „Beleg wandeln" bietet Kommissionierliste nicht mehr an.',
    ],
  },
  {
    version: '3.104.0',
    date: '2026-07-05',
    title: 'Prozess-Audit Kommissionierung: Bugfixes rund um Lieferschein, Bestand & Eingaben',
    tag: 'fix',
    changes: [
      'Ein Kommissionierbeleg kann nur noch EINMAL in einen Lieferschein gewandelt werden (keine Doppel-Lieferscheine mehr). Nach dem Wandeln erscheint statt „In Lieferschein wandeln" ein Button „Lieferschein öffnen".',
      'Ist bereits ein Lieferschein erstellt, lässt sich der Abschluss nicht mehr rückgängig machen — das verhindert widersprüchliche Bestände (Ware zurück im Lager, aber Lieferschein „versendet").',
      'Abschließen/Rückgängig sind jetzt gegen Doppelklick/Nebenläufigkeit abgesichert (Warenausgang kann nicht mehr doppelt gebucht werden).',
      'Kommissionier-Eingaben mit Dezimalpunkt (z. B. „48.6" auf der Tablet-Tastatur) werden korrekt gelesen — vorher wurde daraus fälschlich „486".',
      'Ein halb getippter Lager-Vermerk geht bei einer Hintergrund-Aktualisierung nicht mehr verloren.',
      'Über-Mengen (mehr gepackt als bestellt) gehen nicht mehr verloren, und Packstück-Gewichte werden grammgenau gespeichert (keine Rundungsverluste).',
    ],
  },
  {
    version: '3.103.0',
    date: '2026-07-03',
    title: 'Kommissionierung: lesbare Einheiten statt „PIECE"',
    tag: 'fix',
    changes: [
      'Die Artikel-Einheit wird jetzt überall lesbar dargestellt (z. B. „Stk" statt „PIECE") — in der Kommissionieransicht, der Auftrags-Detailseite, dem Kommissionierbeleg-/Packlisten-PDF und im Excel-Export.',
    ],
  },
  {
    version: '3.102.0',
    date: '2026-07-03',
    title: 'Kommissionieren: Artikel-Langtext auf größeren Karten',
    tag: 'improvement',
    changes: [
      'In der Vollbild-Kommissionierung zeigen die Artikelkarten jetzt den Artikel-Langtext (Infotext aus dem Artikelstamm) — der Lagerist sieht auf einen Blick, was einzupacken ist.',
      'Die Karten sind größer und übersichtlicher: Artikelnummer und Bezeichnung prominenter, Langtext in einem eigenen Feld, Texte werden vollständig umbrochen statt abgeschnitten.',
    ],
  },
  {
    version: '3.101.0',
    date: '2026-07-03',
    title: 'Kommissionierbeleg in Lieferschein wandeln',
    tag: 'feature',
    changes: [
      'Ein abgeschlossener Kommissionierbeleg lässt sich per Klick in einen Lieferschein (Verkauf) wandeln: Die kommissionierten Positionen und Mengen werden übernommen, der Kunde kommt aus dem Projekt.',
      'Der Warenausgang wurde bereits beim Abschließen der Kommissionierung gebucht — der Lieferschein wird daher direkt als „versendet" angelegt, ohne den Lagerbestand ein zweites Mal zu bebuchen.',
      'Nach dem Wandeln öffnet sich der Lieferschein direkt im Beleg-Editor.',
    ],
  },
  {
    version: '3.100.0',
    date: '2026-07-03',
    title: 'Kommissionierbelege im Verkauf sichtbar + Kommissionieransicht mobil optimiert',
    tag: 'improvement',
    changes: [
      'Im Modul „Verkauf" gibt es einen neuen Tab „Kommissionierung", der alle Kommissionierbelege (such-/sortierbar) auflistet — ein Klick öffnet den Auftrag. (Nächster Schritt: Kommissionierbelege in Lieferscheine wandeln.)',
      'Die Vollbild-Kommissionierung ist für Handy/Tablet optimiert: Die Kopfleiste läuft nicht mehr über den Rand — Titel/Speicherstatus oben, die Aktionen (Position, Colli, Abschließen) darunter in voller Breite.',
    ],
  },
  {
    version: '3.99.0',
    date: '2026-07-03',
    title: 'Kommissionier-PDFs: kleineres Logo, Seitenzahl unten rechts, Packliste im Hochformat mit Übersicht',
    tag: 'improvement',
    changes: [
      'Das Logo auf Kommissionierbeleg und Packliste ist um 50 % verkleinert. Die Seitenzahl steht jetzt unten rechts.',
      'Die Packliste ist im Hochformat: Seite 1 zeigt eine Übersicht aller Packstücke (Maße, Brutto/Tara/Netto, Anzahl Positionen, Gesamtsumme), danach folgen die Details je Packstück.',
      'Spaltenbreiten passen sich der Seite an und lange Texte werden umgebrochen statt abgeschnitten.',
    ],
  },
  {
    version: '3.98.0',
    date: '2026-07-03',
    title: 'Kommissionieren: Positionen selbst hinzufügen + Lager-Vermerk je Artikel',
    tag: 'improvement',
    changes: [
      'In der Vollbild-Kommissionierung lassen sich jetzt zusätzliche Positionen selbst hinzufügen („Position" → Artikel aus dem Stamm wählen, Menge erfassen).',
      'Je Artikel gibt es wieder ein Lager-Vermerk-Feld, in das der Lagerist Bemerkungen schreiben kann.',
    ],
  },
  {
    version: '3.97.0',
    date: '2026-07-03',
    title: 'Kommissionierung: Abschluss rückgängig machen · nur Logo auf Beleg/Packliste',
    tag: 'improvement',
    changes: [
      'Ein versehentlich abgeschlossener Kommissionierauftrag lässt sich wieder öffnen („Abschluss rückgängig"): Der gebuchte Warenausgang wird automatisch storniert (ins Lager zurückgebucht) und der Auftrag geht zurück auf „In Arbeit".',
      'Vor dem Abschließen gibt es jetzt eine Sicherheitsabfrage — so passiert es nicht mehr aus Versehen.',
      'Kommissionierbeleg und Packliste zeigen nur noch das hinterlegte Logo statt des gesamten Briefpapiers (weiterhin Querformat).',
    ],
  },
  {
    version: '3.96.0',
    date: '2026-07-03',
    title: 'Kommissionierung: Vollbild-Modus, Artikel kumuliert, Fotos je Position/Colli, Firmen-Branding auf PDFs',
    tag: 'feature',
    changes: [
      'Neue Vollbild-Kommissionieransicht (eigener, tablet-optimierter Bildschirm): große Häkchen, oben ein Speicher-Status („Alles gespeichert / Speichert … / Nicht gespeichert") und ein Fortschrittsbalken. Erreichbar über „Kommissionieren".',
      'Gleiche Artikel werden zu einer Zeile zusammengefasst (kumuliert): Sind 8 Bedarfszeilen derselbe Artikel, hakt man einmal die Gesamtmenge ab — spart Zeit beim Einpacken. Eine Teilmenge wird automatisch auf die einzelnen Bedarfszeilen verteilt.',
      'Fotos lassen sich jetzt gezielt einer Position (Artikel) und einem Packstück (Colli) zuordnen — nicht nur dem Auftrag allgemein.',
      'Kommissionierbeleg und Packliste tragen jetzt das Briefpapier/Branding der Gesellschaft (aus „Gesellschaften"), wie die übrigen Belege.',
      'Die Auftrags-Detailseite zeigt im Status „In Arbeit" eine kompakte, kumulierte Fortschrittsübersicht statt der bisherigen Einzelzeilen.',
    ],
  },
  {
    version: '3.95.0',
    date: '2026-07-03',
    title: 'Kommissionieren per Häkchen · Packstück-Gewichte in Kilogramm',
    tag: 'improvement',
    changes: [
      'Beim Kommissionieren wird eine Position jetzt mit einem einzigen Tap als erledigt abgehakt (Ist = Bedarf) — kein mühsames Hochklicken mehr (z. B. 100 m Kette). Für Teilmengen gibt es weiterhin ein kleines optionales Mengenfeld.',
      'Packstück-Gewichte werden in Kilogramm eingegeben und angezeigt (Brutto/Tara/Netto). Intern weiterhin grammgenau gespeichert — keine Rundungsfehler.',
    ],
  },
  {
    version: '3.94.0',
    date: '2026-07-03',
    title: 'Neuer Navigationsbereich „Logistik" für die Kommissionierung',
    tag: 'improvement',
    changes: [
      'Die Kommissionierung ist von „Material & Fertigung" in einen eigenen Menübereich „Logistik" umgezogen.',
    ],
  },
  {
    version: '3.93.0',
    date: '2026-07-03',
    title: 'Kommissionierung komplett: Tablet-Kommissionieren, Packstücke, Pflicht-Fotos, Warenausgang & Packliste',
    tag: 'feature',
    changes: [
      'Tablet-Kommissionierung: Nach der Freigabe „Kommissionierung starten" — der Lagerist meldet je Position die Ist-Menge über einen großen Mengen-Stepper zurück, trägt Lager-Vermerke ein und ordnet Positionen einem Packstück zu. Fertige Positionen werden grün markiert.',
      'Packstücke (Colli): Anlegen/Löschen mit Maßen (mm) und Gewichten (Brutto/Tara, g) — das Nettogewicht wird automatisch berechnet.',
      'Fotos zur Rückverfolgbarkeit: Direkt aus der Kamera aufnehmen (Tablet/iPhone), als Galerie sichtbar. Vor dem Abschließen ist mindestens ein Foto Pflicht.',
      'Abschließen bucht automatisch den Warenausgang je kommissionierter Position aufs Projekt (Lager-Abgang) — atomar in einer Transaktion.',
      'Dokumente: Kommissionierbeleg und Packliste als PDF (Querformat), Packliste zusätzlich als Excel-Datei.',
      'Neue Modulseite „Kommissionierung" (Material & Fertigung) mit projektübergreifender, filter-/suchbarer Auftragsliste. Rollen: Sekretariat/Projektleitung erstellen & geben frei, Lager kommissioniert.',
    ],
  },
  {
    version: '3.92.0',
    date: '2026-07-03',
    title: 'Kommissionierung: Admin-Selbstfreigabe + Brotkrumen-Navigation',
    tag: 'improvement',
    changes: [
      'Administratoren dürfen Kommissionieraufträge selbst freigeben — das Vier-Augen-Prinzip gilt weiterhin für alle anderen Nutzer (der Hinweistext entfällt für Admins).',
      'Die Auftragsseite hat jetzt die gewohnte Brotkrumen-Navigation (Projekte / Projekt / Auftragsnummer) statt des einfachen „zurück"-Links — konsistent mit den übrigen Detailseiten.',
    ],
  },
  {
    version: '3.91.0',
    date: '2026-07-03',
    title: 'Projekt-Material: Tabelle im Excel-Look mit direkt editierbarer Bedarfsmenge',
    tag: 'improvement',
    changes: [
      'Die Bedarf/Material-Tabelle wurde modernisiert und fühlt sich jetzt wie ein Tabellenblatt an: durchgängiges Zellraster, kompakte Zeilen und eine fixierte Kopfzeile beim Scrollen.',
      'Die Bedarfsmenge lässt sich direkt in der Zelle bearbeiten (deutsches Komma erlaubt) — Speichern mit Enter oder beim Verlassen des Feldes, Abbrechen mit Esc.',
      'Die Quelle (Bestellen/Lager) wird nahtlos in die Zelle integriert; leere Suchergebnisse zeigen einen dezenten „Keine Treffer"-Hinweis.',
    ],
  },
  {
    version: '3.90.0',
    date: '2026-07-03',
    title: 'Kommissionierung: Projekt-Register + Auftrag mit Freigabe & Vermerken',
    tag: 'feature',
    changes: [
      'Neues Projekt-Register „Kommissionierung": Aufträge des Projekts auflisten und per Klick öffnen; „Kommissionierauftrag erzeugen" übernimmt die offenen Positionen als Snapshot aus der Materialliste.',
      'Auftragsseite: Status auf einen Blick, Freigabe im Vier-Augen-Prinzip (der Ersteller kann nicht selbst freigeben) und pro Position ein Techniker-/Projektleiter-Vermerk (z. B. „KW34", „um 20 mm kürzen") samt Termin.',
      'Erster Teil des neuen Kommissionier-Moduls (ADR 0036); Tablet-Kommissionierung, Packstücke, Fotos und die automatische Packliste folgen in den nächsten Schritten.',
    ],
  },
  {
    version: '3.89.0',
    date: '2026-07-03',
    title: 'Projekt-Material: Techniker-Notizfeld, Suche/Filter, Bestellstatus je Position',
    tag: 'improvement',
    changes: [
      'Der Listen-Status (Engineering / Bereit / Bestellt) oben ist entfallen. Stattdessen gibt es ein Freitextfeld „Infos zur Materialliste", in das die Techniker Hinweise schreiben (z. B. „Förderkette erst in KW34", „Wandkonsole um 20 mm kürzen") — wird automatisch gespeichert.',
      'Die Materialliste ist jetzt durchsuchbar (Nummer/Bezeichnung) und filterbar (Alle / Offen / Bestellt / Geliefert).',
      'Sobald über die Liste bestellt wurde, erscheint je Position eine Info: „Bestellt N · erwartet am TT.MM.JJJJ · Bestellnummer" (mit Absprung zur Bestellung) sowie „geliefert N" nach Wareneingang.',
    ],
  },
  {
    version: '3.88.0',
    date: '2026-07-03',
    title: 'Navigation: HR unter „Administration"',
    tag: 'improvement',
    changes: [
      'Der Menüpunkt „HR" ist von „Personal & Zeit" in den Bereich „Administration" gewandert (als Unterpunkt). Der HR-Bereich selbst ist unverändert erreichbar.',
    ],
  },
  {
    version: '3.87.0',
    date: '2026-07-03',
    title: 'Mobile Navigation robuster für Tablet & iPhone',
    tag: 'improvement',
    changes: [
      'Die untere Navigationsleiste (Smartphone) sitzt jetzt oberhalb der iPhone-Home-Leiste (Safe-Area), ist höher und griffiger, mit klarem aktivem Reiter (Akzentbalken + Akzentfarbe) und direktem Druck-Feedback beim Antippen.',
      'Touch-Bedienung entschärft: kein Zoom-/Verzögerungs-Effekt beim Tippen mehr (touch-manipulation), größere Tap-Flächen im „Mehr"-Menü (Haupt- und Unterpunkte).',
    ],
  },
  {
    version: '3.86.0',
    date: '2026-07-03',
    title: 'DMS vorerst ausgeblendet',
    tag: 'improvement',
    changes: [
      'Das Dateien-/Dokumentenmanagement (DMS) ist vorerst ausgeblendet: der Navigationspunkt „Dateien" und das gleichnamige Register in der Projektansicht erscheinen nicht mehr. Die Funktion bleibt technisch bestehen und lässt sich jederzeit wieder einblenden.',
    ],
  },
  {
    version: '3.85.0',
    date: '2026-07-03',
    title: 'Artikel-Kit: Register „Stückliste" → „Artikel-Kit" (nur bei Kit-Artikeln)',
    tag: 'feature',
    changes: [
      'Das Register „Stückliste" heißt jetzt „Artikel-Kit" — ein Artikel-Kit ist ein Artikel, der aus mehreren Artikeln besteht.',
      'Das Register erscheint nur, wenn der Artikel im Artikelstamm als „Artikel-Kit" markiert ist. Die Markierung erfolgt über die neue Checkbox „Artikel-Kit (besteht aus mehreren Artikeln)" im Bearbeiten-/Anlegen-Dialog (Register Stammdaten).',
    ],
  },
  {
    version: '3.84.0',
    date: '2026-07-03',
    title: 'Artikel-Übersicht: Bestands-Kennzahlen als ruhige Stat-Kacheln',
    tag: 'improvement',
    changes: [
      'Die sechs Bestands-Kennzahlen (Bestand, Frei verfügbar, Reserviert, Kalk. Bestand, Bestellbestand, Forecast-Bestand) werden statt als Radial-Diagramme jetzt als minimalistische Kacheln im Stil des Auswertungs-Registers dargestellt: großer Zahlenwert + dünner Vergleichsbalken (gemeinsame Skala).',
    ],
  },
  {
    version: '3.83.0',
    date: '2026-07-03',
    title: 'Artikel-Bearbeiten: EK- und VK-Staffelpreise editierbar',
    tag: 'feature',
    changes: [
      'Im Artikel-Bearbeiten-Dialog (Register „Preise") lassen sich jetzt VK-Staffelpreise pflegen: Standard-VK ab Menge plus bis zu 5 weitere Preisstufen (Menge-ab + VK-Preis).',
      'EK-Staffelpreise (mengenabhängiger Einkaufspreis) können ebenfalls direkt im Dialog hinzugefügt und gelöscht werden — bisher nur im separaten Preise-Tab der Artikel-Detailseite. EK-Staffeln erscheinen nur beim Bearbeiten (nicht beim Neuanlegen, da ein gespeicherter Artikel nötig ist).',
    ],
  },
  {
    version: '3.82.0',
    date: '2026-07-03',
    title: 'Artikel-Übersicht: 6 Bestands-Gauges',
    tag: 'feature',
    changes: [
      'Der Übersicht-Tab des Artikels zeigt jetzt sechs Radial-Diagramme statt der einzelnen Lagerbestand-Kachel: Bestand, Frei verfügbar, Reserviert, Kalkulierter Bestand, Bestellbestand und Forecast-Bestand.',
      'Kalkulierter Bestand = Bestand + Bestellbestand − Reserviert (disponibler Bestand inkl. Zulauf). Alle Ringe teilen sich dieselbe Skala und sind damit direkt vergleichbar; ein negativer kalkulierter Bestand wird rot hervorgehoben.',
    ],
  },
  {
    version: '3.81.0',
    date: '2026-07-03',
    title: 'Artikel-Übersicht: Karte „Hauptzeichnung" entfernt',
    tag: 'improvement',
    changes: [
      'Die Karte „Hauptzeichnung" wurde aus dem Übersicht-Tab des Artikels entfernt — die Übersicht ist damit aufgeräumter. Zeichnungen bleiben unverändert im eigenen Tab „Zeichnungen" verfügbar (inkl. Hauptzeichnung festlegen).',
    ],
  },
  {
    version: '3.80.0',
    date: '2026-07-03',
    title: 'Bedarfsvorschau-PDF: Querformat + Analyse-Teil',
    tag: 'improvement',
    changes: [
      'Der PDF-Export der Bedarfsvorschau ist jetzt im Querformat (A4 quer) — mehr Platz für die Positionsspalten.',
      'Das PDF enthält zusätzlich eine Analyse-Seite: Kennzahlen (Positionen, verknüpfte Artikel, Projekte, nächster Termin), der kumulierte Bedarf je Artikel (Menge summiert, absteigend, mit frühestem Termin) und die Verteilung der Positionen auf die Projekte.',
    ],
  },
  {
    version: '3.79.0',
    date: '2026-07-03',
    title: 'Architektur-Härtung: Wareneingang atomar, Mandanten-Schutz, exakte Vorschau',
    tag: 'improvement',
    changes: [
      'Wareneingang ist jetzt vollständig transaktional: Lagerzugang, Positions-Rückmeldung, Projekt-Materialrückmeldung und Statuswechsel werden gemeinsam gebucht oder gemeinsam zurückgerollt. Bricht ein Schritt ab, driften Lager, Beleg und Projektbedarf nicht mehr auseinander.',
      'Offene-Posten-Liste zeigt keine Entwürfe mehr: Rechnungs- und Eingangsrechnungs-Entwürfe sind noch keine ausgestellte Forderung/Verbindlichkeit und blähen die Summen nicht länger auf.',
      'Datenbank-Schreibzugriffe sind mandantensicher gehärtet: Aktualisieren/Löschen prüft zusätzlich die Mandanten-Zugehörigkeit direkt in der Datenbank (Schutznetz gegen versehentliche mandantenübergreifende Änderungen).',
      'Die Live-Summenvorschau im Beleg-Editor rechnet jetzt exakt mit derselben Ganzzahl-Geldmathematik wie der Server (geteilter Code) — Vorschau und gespeicherter Beleg stimmen garantiert überein.',
    ],
  },
  {
    version: '3.78.0',
    date: '2026-07-02',
    title: 'Bedarfsvorschau: Export + Analyse · Artikel: Bestandsübersicht',
    tag: 'feature',
    changes: [
      'Bedarfslisten lassen sich exportieren: als PDF (gespeicherter Stand, druckfertige Tabelle), als Excel (.xlsx) oder als CSV — über den neuen „Export"-Knopf in der Detailansicht. Excel/CSV nehmen den aktuellen Rasterstand inklusive ungespeicherter Zeilen mit.',
      'Neue Analyse-Seite für die Bedarfsvorschau (Knopf „Analyse" in der Übersicht): Kennzahlen (Listen, Positionen, überfällige Termine, Artikel-Verknüpfung) plus Diagramme — Top-Artikel nach Menge, Positionen je Projekt, Liefertermine je Monat und Verteilung je Liste.',
      'Artikel-Bestandsübersicht: In der Artikel-Auswertung zeigt eine neue Karte Aktueller Bestand, Reserviert, Frei verfügbar, Bestellt (offener Zulauf mit Absprung zur Bestellung) und den Forecast-Bedarf aus der Bedarfsvorschau.',
      'Artikelliste um die Spalten „Bestellt" (offene Bestellmenge, Tooltip mit Bestellnummer und Termin) und „Forecast" (geplanter Bedarf) erweitert; der Wert „Reserviert" ist jetzt Teil der Artikel-API.',
    ],
  },
  {
    version: '3.77.0',
    date: '2026-07-02',
    title: 'Bug-Hunt: 12 Fixes — Zahlen/Geld, Wareneingang, Zahlungen, Beleg-Editor',
    tag: 'fix',
    changes: [
      'Mengen und Preise verstehen jetzt deutsche Tausenderpunkte: „1.500" ist 1500 (bisher wurde die Menge still als 1,5 gespeichert — bei 100 €/Stück wurden aus 150.000 € nur 150 €). Gilt im Beleg-Editor (Verkauf + Einkauf) und in der Bedarfsvorschau.',
      'Preise mit mehr als 2 Nachkommastellen (kg-/Meter-Preise, z. B. 2,3456 €) bleiben beim Bearbeiten erhalten — bisher wurden sie beim Öffnen des Belegs still auf 2 Stellen abgeschnitten.',
      'Ein Tippfehler im USt-Feld erzeugt keine 0-%-Rechnung mehr: ungültige oder leere Eingaben fallen auf den bisherigen Steuersatz zurück (steuerfrei geht nur explizit über „steuerfrei").',
      'Doppelklick auf „Speichern" legt Positionen nicht mehr doppelt an: nach erfolgreichem Speichern bleibt der Editor gesperrt, bis die frischen Daten geladen sind.',
      'Excel-Einfügen über eine bereits verknüpfte Artikelzeile übernimmt jetzt wirklich den neuen Artikel — bisher blieb intern der alte Artikel verknüpft (falscher Artikel auf PDF/Lager trotz richtiger Nummer in der Zelle).',
      'Wareneingang ist nur noch auf Bestellungen möglich: auf einer Eingangsrechnung oder Anfrage würde derselbe Bestand doppelt bzw. nie bestellte Ware eingebucht (Backend-Sperre + Button ausgeblendet).',
      'Belegstatus im Einkauf folgt jetzt auch im Backend dem Statusmodell je Belegart (z. B. kein „Geliefert" auf Eingangsrechnungen).',
      'Zahlungen müssen in der Belegwährung erfasst werden — eine USD-Zahlung auf eine EUR-Rechnung zählte bisher still als EUR und konnte die Rechnung fälschlich auf „bezahlt" setzen. Gleiches gilt für fixe Anzahlungsbeträge (Auftragswährung Pflicht).',
      'Anzahlungsbeträge aus Prozentsätzen werden kaufmännisch gerundet statt abgeschnitten.',
      'Bedarfsvorschau: ein Hintergrund-Refresh (z. B. direkt nach dem Speichern) überschreibt laufende Eingaben nicht mehr; die Artikel-Auswahl lädt jetzt alle Artikel (bisher max. 500).',
      'Projekt-Materialliste: Bestell-Rückmeldungen prüfen die Mandanten-Zugehörigkeit der Positionen; das Aufheben einer Reservierung erzeugt jetzt einen Audit-Log-Eintrag.',
      '„Positionen übernehmen": ein Ladefehler zeigt jetzt eine klare Fehlermeldung statt „Keine Belege".',
    ],
  },
  {
    version: '3.76.0',
    date: '2026-07-02',
    title: 'Belegerfassung: Positionen aus anderem Beleg übernehmen + Mehrfachauswahl',
    tag: 'feature',
    changes: [
      'Neuer Button „Positionen übernehmen" im Verkaufs-Beleg-Editor: einen anderen Beleg wählen, Positionen anhaken — sie werden inklusive Langtext, Menge, Preis und Rabatt als neue Zeilen angehängt.',
      'Mehrfachauswahl im Positionsraster: über die Checkbox je Zeile (bzw. „alle" im Kopf) lassen sich mehrere Positionen markieren und auf einmal löschen. Ausgewählte Zeilen sind farblich hervorgehoben.',
    ],
  },
  {
    version: '3.75.0',
    date: '2026-07-02',
    title: 'Bedarfsvorschau: Projekt-Autocomplete + Positionsfilter',
    tag: 'feature',
    changes: [
      'Im Bedarfsraster schlägt das Feld „Projekt (Freitext)" jetzt bereits verwendete Projekte vor (Autocomplete über alle Bedarfslisten hinweg) — konsistente Schreibweise ohne Tippen.',
      'Über der Liste gibt es ein Filterfeld: Positionen lassen sich nach Artikelnummer, Bezeichnung oder Projekt einschränken (Trefferzahl wird angezeigt). Der volle Bestand bleibt beim Speichern erhalten.',
    ],
  },
  {
    version: '3.74.0',
    date: '2026-07-02',
    title: 'Fix: „Auf Verfügbarkeit prüfen" erkennt Positionen + Notfall-DB-App mit Oberfläche',
    tag: 'fix',
    changes: [
      '„Auf Verfügbarkeit prüfen" meldete fälschlich „Keine Artikel-Positionen mit Menge zum Prüfen": die Belegpositionen wurden über den falschen Feldschlüssel gelesen. Jetzt wird der je Zeile bereits verknüpfte Artikel korrekt erkannt (mehrfach genutzte Artikel werden zusammengezählt).',
      'Das Notfall-DB-Admin-Image baut jetzt auch die Weboberfläche mit ein und liefert sie aus — bisher enthielt der Container nur die API, sodass beim Aufruf keine Oberfläche erschien.',
    ],
  },
  {
    version: '3.73.0',
    date: '2026-07-02',
    title: 'Beleg-Editor: „Auf Verfügbarkeit prüfen" + höhere Dialoge',
    tag: 'feature',
    changes: [
      'Im Beleg-Editor (Verkauf, Werkbank & Belegblatt) gibt es unten den Button „Auf Verfügbarkeit prüfen": er prüft je Artikel, ob die Menge aktuell frei verfügbar ist. Fehlt etwas, wird die Fehlmenge und der offene Zulauf angezeigt — welche Bestellung läuft und wann geliefert wird (mit Absprung zur Bestellung).',
      'Die Stammdaten-Dialoge (Kunde/Artikel) füllen jetzt ~80 % der Bildschirmhöhe — mehr Platz je Register, Inhalt scrollt bei Bedarf.',
    ],
  },
  {
    version: '3.72.0',
    date: '2026-07-02',
    title: 'Stammdaten-Dialoge: gleichbleibende Höhe je Register',
    tag: 'improvement',
    changes: [
      'Die Kunden- und Artikeldialoge haben jetzt eine feste Höhe (rund die Hälfte des Bildschirms) — beim Wechsel zwischen den Registern springt die Fenstergröße nicht mehr. Braucht ein Register mehr Platz, wird nur der Inhalt gescrollt.',
    ],
  },
  {
    version: '3.71.0',
    date: '2026-07-02',
    title: 'Buchhaltung: Eingangsrechnungen als Kreditoren (offene Posten)',
    tag: 'feature',
    changes: [
      'Die offenen Posten zeigen jetzt auch Kreditoren: Einkaufs-Eingangsrechnungen erscheinen neben den Kundenrechnungen. Neuer Filter „Debitoren / Kreditoren" trennt Forderungen und Verbindlichkeiten.',
      'Auf Eingangsrechnungen lassen sich Zahlungen erfassen (wie bei Kundenrechnungen); vollständig bezahlte verschwinden aus „offen".',
      'Bestellungen/Eingangsrechnungen haben jetzt ein Feld „Fälligkeit" (im Beleg-Editor) — überfällige Kreditoren werden in den offenen Posten markiert.',
    ],
  },
  {
    version: '3.70.0',
    date: '2026-07-02',
    title: 'Bedarfsvorschau: „Aus Bedarf bestellen"',
    tag: 'feature',
    changes: [
      'In der kumulierten Bedarfsansicht gibt es jetzt „Bestellung erzeugen": Lieferant wählen, Positionen anhaken — daraus entsteht eine Bestellung (oder Anfrage) mit den kumulierten Mengen; der Einkaufspreis kommt aus dem Artikelstamm. Danach landet man direkt im Beleg-Editor.',
    ],
  },
  {
    version: '3.69.0',
    date: '2026-07-02',
    title: 'Kontextbezogene Navigation: weitere Listen',
    tag: 'improvement',
    changes: [
      'Auch die CRM-Listen „Lieferadressen" und „Ansprechpartner" merken sich Suche, Filter, Sortierung und Seite in der Adresse — „Zurück" landet wieder in derselben Ansicht.',
    ],
  },
  {
    version: '3.68.0',
    date: '2026-07-02',
    title: 'Kontextbezogene Navigation: „Zurück" bleibt in der Ansicht',
    tag: 'improvement',
    changes: [
      'Filter, Tabs, Suche, Sortierung und Seite der großen Listen (Projekte, CRM-Kunden, Artikel, Beschaffung, Bedarfsvorschau — Verkauf bereits) stehen jetzt in der Adresse (URL). Öffnet man einen Eintrag und geht zurück, landet man exakt in derselben gefilterten Ansicht — inklusive Scroll-Position. Die Ansicht lässt sich so auch als Link teilen oder als Lesezeichen speichern.',
    ],
  },
  {
    version: '3.67.0',
    date: '2026-07-02',
    title: 'Bedarfsvorschau: echtes Tabellen-Gefühl',
    tag: 'improvement',
    changes: [
      'Die Bedarfsliste wird jetzt als kompakte Tabelle mit Gitterlinien erfasst (wie ein Excel-Sheet): einzeilige Zeilen, eigene Spalten für Artikel und Bezeichnung, Zeilennummer, und die Eingabefelder füllen die Zelle randlos aus.',
    ],
  },
  {
    version: '3.66.0',
    date: '2026-07-02',
    title: 'Bedarfsvorschau als Liste (Artikelbedarf mit Liefertermin)',
    tag: 'improvement',
    changes: [
      'Die Bedarfsvorschau ist jetzt eine Liste statt eines Perioden-Rasters: je Zeile ein Artikel (verknüpft) mit Menge, voraussichtlichem Liefertermin, Projekt-Referenz als Freitext und Notiz — wie ein smartes Excel-Sheet.',
      'Neue Ansicht „Kumuliert": fasst die Bedarfe je Artikel zusammen (Gesamtmenge, nächster Liefertermin, Anzahl Einzelbedarfe) — umschaltbar zwischen „Liste" und „Kumuliert".',
    ],
  },
  {
    version: '3.65.0',
    date: '2026-07-02',
    title: 'Neu: Bedarfsvorschau (Forecast)',
    tag: 'feature',
    changes: [
      'Neues Planungsmodul „Bedarfsvorschau": geschätzte Artikelmengen je Zeitraum erfassen — reine Planung, keine Bestellung. Erreichbar über „Bedarfsvorschau" im Menü (Material & Fertigung).',
      'Raster wählbar nach Monat, Quartal oder Jahr (Startperiode + Anzahl Perioden). Artikel als Zeilen, Perioden als Spalten, geschätzte Menge je Zelle.',
      'Das Projekt lässt sich als Freitext hinterlegen (kein fester Projektbezug).',
    ],
  },
  {
    version: '3.64.0',
    date: '2026-07-02',
    title: 'Einkauf: Belegfluss Anfrage → Bestellung → Eingangsrechnung',
    tag: 'feature',
    changes: [
      'Die Beschaffung kennt jetzt drei Belegarten: Anfrage, Bestellung und Eingangsrechnung — umschaltbar über Tabs, jede mit eigener fortlaufender Nummer (AN-/BE-/ER-).',
      '„Beleg wandeln": Eine Anfrage lässt sich per Klick in eine Bestellung wandeln, eine Bestellung in eine Eingangsrechnung — inklusive Übernahme aller Positionen und Verknüpfung zum Vorgängerbeleg (im Editor per „Vorgängerbeleg öffnen" erreichbar). Verfügbar als Zeilen-Aktion in der Liste und im Beleg-Editor.',
      'Status und Beschriftungen sind jetzt belegartgerecht (z. B. „Angefragt" bei Anfragen, „Gebucht" bei Eingangsrechnungen). Der Wareneingang bleibt der Bestellung vorbehalten.',
    ],
  },
  {
    version: '3.63.0',
    date: '2026-07-02',
    title: 'Stammdaten-Dialoge als Register',
    tag: 'improvement',
    changes: [
      'Die Abschnitte in den Kunden- und Artikeldialogen sind jetzt echte Register (Tabs): Ein Klick in der linken Leiste zeigt nur den gewählten Abschnitt — kein Durchscrollen mehr. Die Eingaben bleiben beim Wechsel zwischen Registern erhalten, ein Speichern erfasst alle Register.',
    ],
  },
  {
    version: '3.62.0',
    date: '2026-07-02',
    title: 'Neue Stammdaten-Dialoge mit Seitenleiste',
    tag: 'improvement',
    changes: [
      'Kunden- und Artikeldialog haben ein neues Zwei-Spalten-Layout: links eine Abschnitts-Navigation (Stammdaten, Preise, Lager …), rechts der Inhalt. Ein Klick springt zum Abschnitt, der gerade sichtbare Abschnitt wird in der Leiste hervorgehoben. Auf dem Handy wird die Leiste zu einer Chip-Zeile über dem Formular.',
      'Artikel · Preise: zeigt jetzt live die Marge, den Aufschlag und die Spanne aus Einkaufs- und Verkaufspreis — beim Bearbeiten zusätzlich Ø-EK und letzter Einkaufspreis.',
      'Artikel · Lager: beim Bearbeiten werden aktueller Bestand und frei verfügbare Menge direkt angezeigt.',
      'Artikel: neues Feld „Interne Notizen" (nur intern, erscheinen nie auf Belegen).',
    ],
  },
  {
    version: '3.61.0',
    date: '2026-07-02',
    title: 'Frischeres Kunden- und Artikelformular',
    tag: 'improvement',
    changes: [
      'Das Artikelformular hat das gleiche, aufgeräumte Karten-Layout wie das Kundenformular bekommen: Abschnitte („Stammdaten", „Preise", „Lager", „Zoll & Logistik", „Infotexte") stehen als klar abgegrenzte Karten mit Symbol nebeneinander.',
      'Der Artikeltyp wird jetzt als große, segmentierte Auswahl ganz oben gewählt (Standardartikel / Projektbezogen) — mit einer kurzen Erklärung, was der jeweilige Typ bedeutet.',
      'Kunden- und Artikelformular teilen sich jetzt denselben Abschnitts-Baustein — einheitliche Optik über beide Stammdaten-Masken.',
    ],
  },
  {
    version: '3.60.0',
    date: '2026-07-02',
    title: 'Schnell & wertvoll: Artikeltyp, Lager-Ampel, Projekt archivieren',
    tag: 'feature',
    changes: [
      'Artikelanlage: Statt frei vergebbarer Tags gibt es jetzt eine klare Auswahl „Projektbezogen" oder „Standardartikel". Standardartikel werden auf Lager gehalten, projektbezogene Teile pro Auftrag zugekauft.',
      'Lagerstands-Ampel: Ein kleiner Punkt (grün / orange / rot) zeigt in der Artikelliste und in der Belegerfassung auf einen Blick, ob ein Standardartikel ausreichend verfügbar ist oder nachbestellt werden muss (Meldebestand). Die angezeigte Verfügbarkeit berücksichtigt aktive Reservierungen.',
      'Anzahlungs- und Schlussrechnung: Die Sammel-/Abzugsposition verwendet jetzt automatisch den Artikel mit der Nummer „div." (sofern angelegt) — die Artikelnummer wird dabei nicht auf dem Beleg gedruckt.',
      'Kundenformular: Das nicht benötigte Feld „Intercompany" wurde entfernt. Die Zuordnung der eigenen Gesellschaft bleibt erhalten.',
      'Projekte lassen sich jetzt archivieren (Soft-Delete) und wieder reaktivieren. Ein Projekt mit noch offenen Belegen (Entwurf / versendet / angenommen) kann nicht archiviert werden. Über „Archivierte anzeigen" lassen sich archivierte Projekte einblenden.',
    ],
  },
  {
    version: '3.59.0',
    date: '2026-07-02',
    title: 'Produktion entfernt — alles läuft über den Einkauf',
    tag: 'improvement',
    changes: [
      'Der Menüpunkt „Produktion" wurde entfernt: Da nicht selbst gefertigt, sondern alles zugekauft/fremdgefertigt wird, gibt es keine eigenen Fertigungsaufträge mehr — Teile werden über die Beschaffung bestellt.',
      'Die Stücklisten-Pflege bleibt erhalten und ist jetzt direkt am Artikel unter dem Tab „Stückliste" zu finden (nützlich für die Materialbedarfs-Planung von Projekten).',
    ],
  },
  {
    version: '3.58.0',
    date: '2026-07-02',
    title: 'Beschaffung & Buchhaltung: weniger Doppelerfassung',
    tag: 'improvement',
    changes: [
      'Neuer Knopf „Bedarf aus Beleg übernehmen" im Projekt-Material: übernimmt die Artikel-Positionen des Auftrags automatisch als Materialbedarf (mehrfache Positionen desselben Artikels werden zusammengefasst, Freitextzeilen übersprungen). Ideal für Ersatzteil-/Katalogaufträge — danach direkt reservieren oder bestellen.',
      'Offene Posten (Buchhaltung): neue Such- und Filterleiste — nach Nummer oder Kunde suchen, nach Status (offen / überfällig / bezahlt) und Belegart (Rechnung / Anzahlung / Schluss) filtern. Der Filterzustand bleibt in der Adresse erhalten.',
    ],
  },
  {
    version: '3.57.0',
    date: '2026-07-02',
    title: 'Belege & Vorkalkulation: Feinschliff aus dem Praxis-Durchlauf',
    tag: 'improvement',
    changes: [
      'Belegpositionen erlauben jetzt negative Mengen und Preise — nötig für Gutschriften, Rabattzeilen und den Anzahlungsabzug in der Schlussrechnung.',
      'Positionen können die Artikelnummer im Druck ausblenden („Artikelnummer nicht drucken"); Anzahlungs- und Schlussrechnungs-Positionen nutzen das automatisch, sodass dort keine störende Nummer erscheint.',
      'Belegstatus sind jetzt je Belegart sinnvoll eingeschränkt: Angebote kennen nur Entwurf/Versendet/Angenommen/Abgelehnt (kein „Bezahlt"/„Storniert"), Rechnungen kein „Angenommen/Abgelehnt". „Bezahlt" wird weiterhin automatisch über den Zahlungseingang gesetzt.',
      'In der Belegliste gibt es je Zeile ein Aktionsmenü: Status direkt ändern und Belege umwandeln — ohne den Beleg erst öffnen zu müssen.',
      'Vorkalkulation: Plankosten lassen sich jetzt auch dann erfassen, wenn noch keine Warengruppen/Positionen als Stammdaten angelegt sind (vorher fehlte das Eingabefeld). Ladefehler werden jetzt sichtbar statt verschluckt.',
      'Neuer Knopf „Vorkalkulation aus Beleg übernehmen": erzeugt die Plan-Materialkosten automatisch aus den Auftragspositionen (Menge × Einkaufspreis, nach Warengruppe gruppiert).',
    ],
  },
  {
    version: '3.56.0',
    date: '2026-07-02',
    title: 'Produktion: Fertigungsaufträge mit Material-Reservierung',
    tag: 'feature',
    changes: [
      'Neue Seite „Produktion": Fertigungsaufträge anlegen (Artikel, Menge, Projekt, Termine) und als Liste mit Status-Filter und Suche verwalten.',
      'Detailansicht je Auftrag mit Materialliste aus der Stückliste: Bedarf, Lagerbestand, reservierte und verfügbare Menge je Komponente — Unterdeckungen werden rot markiert.',
      'Neuer Schritt „Freigeben": prüft die Materialverfügbarkeit und reserviert die benötigten Komponenten im Lager. Reicht der Bestand nicht, wird die Freigabe mit genauer Fehlmengen-Angabe abgelehnt.',
      '„Fertig melden" verbraucht die Reservierungen und bucht das Lager (Material-Abgang + Zugang des Fertigteils). Bei Materialmangel erscheint ein Warn-Dialog; auf Wunsch kann trotzdem gebucht werden.',
      'Alle Lagerbuchungen der Fertigung tragen jetzt das Projekt des Auftrags — die Materialkosten landen damit in der Projekt-Auswertung.',
      'Stücklisten-Pflege direkt am Artikel: neuer Tab „Stückliste" mit Komponenten hinzufügen, ändern und entfernen.',
      'Stornieren oder Löschen eines Auftrags gibt reserviertes Material automatisch wieder frei.',
    ],
  },
  {
    version: '3.55.0',
    date: '2026-07-02',
    title: 'Projekt-Wirtschaftlichkeit: Marge & Fakturierungsgrad auf einen Blick',
    tag: 'feature',
    changes: [
      'Neue Wirtschaftlichkeits-Karte oben im Übersichts-Tab jedes Projekts: Auftragswert, Ist-Kosten, Marge (Betrag und Prozent), Fakturiert (mit Fortschrittsbalken), Offen zu fakturieren und Bezahlt — die zentrale Anlagenbau-Kennzahl ohne Rechnen im Kopf.',
      'Der Auftragswert kommt automatisch aus den Auftragsbestätigungen des Projekts (stornierte zählen nicht); gibt es noch keine, greift der im Projekt hinterlegte Auftragswert — die Karte zeigt an, welche Quelle gilt.',
      'Fakturiert summiert Rechnungen, Anzahlungs- und Schlussrechnungen korrekt: Die Anzahlungs-Verrechnung in der Schlussrechnung wird nicht doppelt gezählt. „Bezahlt" zeigt die tatsächlichen Zahlungseingänge auf diese Rechnungen.',
      'Eine negative Marge (Projekt im Minus) wird deutlich rot hervorgehoben.',
      'Der Stunden-Tab zeigt jetzt je Zeitbuchung die Kosten (Stunden × Stundensatz) sowie eine Summenzeile mit Gesamtstunden und Gesamtkosten.',
    ],
  },
  {
    version: '3.54.0',
    date: '2026-07-02',
    title: 'Notfall-Datenbank-Zugriff (separate Admin-App)',
    tag: 'feature',
    changes: [
      'Neue, komplett eigenständige Admin-App für den Notfall: Läuft als eigener Dienst unter einer eigenen Adresse und funktioniert auch dann, wenn die Haupt-App gerade nicht erreichbar ist — so kommt man immer an die Daten.',
      'Nur für Administratoren: eigener Login (E-Mail + Passwort + Authenticator-Code), Zugriff nur mit voller Administrator-Berechtigung.',
      'Zeigt alle Datenbank-Tabellen zum Ansehen und Bearbeiten (Anlegen/Ändern/Löschen). Sensible Bereiche sind gesperrt: Benutzer, Rollen und Rechte sind nicht einsehbar/änderbar, das Änderungsprotokoll ist nur lesbar.',
      'Export und Import je Tabelle (CSV/NDJSON, Import mit Vorschau) sowie ein kompletter Datenbank-Download (Backup) und eine Wiederherstellung — Letztere nur nach ausdrücklicher Bestätigung.',
      'Jede Änderung über dieses Werkzeug wird im Änderungsprotokoll festgehalten.',
    ],
  },
  {
    version: '3.53.0',
    date: '2026-07-02',
    title: 'DMS: Projektordner benennen sich automatisch um',
    tag: 'improvement',
    changes: [
      'Wird ein Projekt umbenannt (oder seine Nummer geändert), zieht der zugehörige Dateien-Ordner jetzt automatisch mit — inklusive aller Unterordner und Dateien im Dateispeicher (Storage Box). Egal, über welchen Weg die Änderung passiert.',
      'Bestehende Projektordner, die noch das alte Format (nur Projektname) tragen, werden beim nächsten App-Start einmalig auf „Projektname_Projektnummer" umbenannt — Dateien und Ordnerstruktur bleiben vollständig erhalten.',
      'Jede automatische Umbenennung wird im Audit-Log festgehalten.',
    ],
  },
  {
    version: '3.52.0',
    date: '2026-07-02',
    title: 'DMS: Projektordner heißen jetzt „Name_Projektnummer"',
    tag: 'improvement',
    changes: [
      'Der Dateien-Ordner eines Projekts wird ab jetzt als „Projektname_Projektnummer" angelegt (z. B. „Förderanlage_P-2026-003") — so ist auch beim direkten Blick in den Dateispeicher (WebDAV/Storage Box) sofort klar, zu welchem Projekt ein Ordner gehört.',
      'Sonderzeichen, die in Datei-Pfaden Probleme machen (z. B. Schrägstriche), werden im Ordnernamen automatisch bereinigt.',
      'Bestehende Projektordner behalten vorerst ihren bisherigen Namen; die einmalige Umbenennung des Bestands folgt in einem separaten Schritt.',
    ],
  },
  {
    version: '3.51.0',
    date: '2026-07-01',
    title: 'Passkey als zweiter Faktor (Alternative zu TOTP)',
    tag: 'feature',
    changes: [
      'Passkeys (Face ID, Touch ID, Windows Hello, Sicherheitsschlüssel) können jetzt als zweiter Faktor genutzt werden — gleichrangig zu TOTP, nicht nur als Zusatz. Beim ersten Login wählt man frei zwischen Authenticator-App und Passkey.',
      'Wer bereits TOTP eingerichtet hat, kann jederzeit über „Sicherheit" im Benutzermenü einen oder mehrere Passkeys hinzufügen (z. B. iPhone zusätzlich zum Sicherheitsschlüssel) und wieder entfernen.',
      'Der letzte verbleibende zweite Faktor lässt sich nicht löschen — mindestens einer (TOTP oder Passkey) ist weiterhin Pflicht.',
      'Voraussetzung für den produktiven Einsatz: ein gültiges TLS-Zertifikat auf der Login-Domain (kein Self-Signed-Zertifikat) — WebAuthn verlangt das browserseitig.',
    ],
  },
  {
    version: '3.50.0',
    date: '2026-06-30',
    title: 'Hosting auf Hetzner dokumentiert + DB-Backup-Skript',
    tag: 'improvement',
    changes: [
      'Neues Hosting-Konzept festgehalten (ADR 0032): App, Postgres und Redis laufen self-hosted über Coolify auf einem Hetzner-Server (Standort Deutschland); DMS-Dateien und DB-Backups liegen auf einer Hetzner Storage Box — ganz ohne Object Storage und außerhalb des US CLOUD Act.',
      'Fertiges Backup-Skript (tools/backup/pg-backup-borg.sh) ergänzt: sichert die Datenbank täglich verschlüsselt und versioniert per BorgBackup auf die Storage Box (inkl. Aufbewahrungsregeln und Anleitung zum Wiederherstellen).',
      '.env.example modernisiert: Beispielwerte für die Storage-Box-Anbindung (WebDAV) und das Backup, Hinweis dass kein S3/Object-Storage mehr nötig ist.',
    ],
  },
  {
    version: '3.49.0',
    date: '2026-06-24',
    title: 'Benachrichtigungen für Artikel-Anträge',
    tag: 'feature',
    changes: [
      'Neue Glocke im Kopfbereich: zeigt die Anzahl ungelesener Benachrichtigungen und listet die neuesten auf. Ein Klick markiert sie als gelesen und springt direkt zur passenden Stelle (z. B. zur Anträge-Seite).',
      'Das Sekretariat wird über jeden neu eingereichten Artikel-Antrag benachrichtigt (der Antragsteller selbst nicht doppelt).',
      'Der Antragsteller wird benachrichtigt, sobald sein Antrag freigegeben, abgelehnt oder mit einer Rückfrage zur Nachbesserung zurückgegeben wurde.',
      'Zähler-Badge am Menüpunkt „Anträge" (und am zugeklappten Menüpunkt „Engineering") mit der Zahl offener Anträge — nur für Freigabeberechtigte.',
    ],
  },
  {
    version: '3.48.0',
    date: '2026-06-23',
    title: 'Timer: Projekt-Auswahl als Suchpopup mit Favoriten',
    tag: 'feature',
    changes: [
      'Die Projektauswahl im Timer öffnet jetzt ein durchsuchbares Popup (Tippen filtert, ↑/↓ navigiert, ↵ wählt) statt einer langen Auswahlliste — gruppiert in „Favoriten“, „Zuletzt verwendet“, „Projekte“ und „Zeit-Projekte“.',
      'Favoriten: Projekte lassen sich pro Benutzer mit dem Stern anpinnen und erscheinen dann ganz oben — ideal für die täglich genutzten Projekte.',
      '„Zuletzt verwendet“ zeigt automatisch die kürzlich bebuchten Projekte der gewählten Gesellschaft.',
      'Das gleiche Popup wird auch beim Bearbeiten eines Zeiteintrags genutzt.',
    ],
  },
  {
    version: '3.47.0',
    date: '2026-06-23',
    title: 'Verkauf: Zurück zur Liste behält Ansicht & Scrollposition',
    tag: 'improvement',
    changes: [
      'Im Verkauf landet man nach dem Schließen eines Belegs wieder genau dort, wo man war: gewählter Tab (Angebot/Auftrag/…), Suche, Geschäftsjahr, Status, Seite, Sortierung und Scrollposition bleiben erhalten.',
      'Die Listen-Ansicht steckt jetzt in der URL (Tab/Filter/Seite/Sortierung) — dadurch ist die Ansicht auch teil- und lesezeichenbar; „Zurück" (Schließen-Pfeil oder Browser-Zurück) führt zur exakt gleichen Liste.',
      'Allgemein: Beim Zurück-Navigieren wird die Scrollposition der Seite wiederhergestellt.',
    ],
  },
  {
    version: '3.46.0',
    date: '2026-06-23',
    title: 'Zeiterfassung: Firmen-Auswahl repariert & Oberfläche aufgefrischt',
    tag: 'fix',
    changes: [
      'Behoben: In der Zeiterfassung blieb die Firmen-Auswahl leer (und „Zeit-Projekt anlegen“ deaktiviert), weil die Gesellschaften nur mit der Berechtigung „Einstellungen lesen“ ladbar waren. Die Gesellschaften kommen jetzt über einen eigenen Zugang der Zeiterfassung — der Timer und die Stammdaten funktionieren für alle berechtigten Rollen.',
      'Dashboard aufgefrischt: klarere Kennzahlen-Karten, hervorgehobene „Läuft“-Karte bei laufendem Timer, Nutzungsbalken bei den meistgenutzten Projekten und deutlichere Hover-/Klick-Hinweise bei den letzten Einträgen.',
      'Feinschliff: bessere Touch-Flächen (Timer-Button und Protokoll-Einträge auf Mobilgeräten), sichtbarere Tastatur-Fokuszustände und ladende Platzhalter für das gesamte Dashboard.',
      'Aufgeräumt: Die HR-Seite enthält keine eigene „Zeiterfassung“ mehr (das war doppelt zur dedizierten Zeiterfassung). Der Bereich heißt jetzt „HR“ und bündelt Mitarbeiter und Positionen.',
    ],
  },
  {
    version: '3.45.0',
    date: '2026-06-23',
    title: 'Datenimport: NDJSON-Belege-Bündel, Einkauf & Gesellschafts-Zuordnung',
    tag: 'feature',
    changes: [
      'Neuer Import „Belege-Bündel (NDJSON)“ für Verkauf und Einkauf: eine Datei mit Belegen, die ihre Positionen verschachtelt enthalten (Kopf + `positionen` je Zeile). Der Import entpackt das serverseitig in Belege/Bestellungen + Positionen, faltet Beschreibungs-/Textzeilen in den Langtext der zugehörigen Artikelposition und ordnet Kunde/Lieferant, Projekt und Artikel automatisch zu. Idempotent (deterministische IDs) — erneuter Import legt keine Duplikate an. Beim Einkauf werden nur Bestellungen übernommen (EK-Rechnungen/Lieferscheine haben kein Zielmodell und werden übersprungen).',
      'Der normale Import (Kunden, Artikel …) akzeptiert jetzt auch NDJSON-Dateien (nicht mehr nur CSV) — Tabelle, Spalten und Filter werden wie gewohnt erkannt.',
      'Gesellschafts-Zuordnung beim Import: das Feld „gesellschaft“ (1 = Probig AT, 2 = Probig DE) setzt beim Beleg/der Bestellung die Gesellschaft und beim Kunden die Owner-Gesellschaft automatisch.',
      'Robustere Ländererkennung: der ISO-Ländercode wird aus dem Klartext-Ländernamen abgeleitet (z. B. „Deutschland“ → DE, „Malaysia“ → MY), auch wenn der Quell-Ländercode kein ISO-Code ist — wichtig für die korrekte Steuerfindung (Inland/EU/Drittland).',
    ],
  },
  {
    version: '3.44.0',
    date: '2026-06-23',
    title: 'Zeiterfassung nutzt echte Gesellschaften & Projekte (Hybrid)',
    tag: 'improvement',
    changes: [
      'Die Firma im Timer ist jetzt die echte Gesellschaft aus den Einstellungen (Einstellungen → Gesellschaften) — keine doppelte Firmen-Pflege mehr. Bei nur einer Gesellschaft wird sie automatisch gewählt.',
      'Projekte werden „hybrid“ geladen: Im Timer erscheinen sowohl die echten ProERP-Projekte der gewählten Gesellschaft als auch eigens angelegte Zeit-Projekte. Buchungen auf echte Projekte fließen direkt ins Projekt-Controlling.',
      'Unterkategorien: echte Projekte bieten alle aktiven Unterkategorien; Zeit-Projekte nur die individuell freigegebenen.',
      '„Zeit-Stammdaten“ verwaltet nur noch Unterkategorien und Zeit-Projekte (mit Gesellschaft, Unterkategorie-Freigaben und optionaler ProERP-Projekt-Verknüpfung). Die Gesellschaften-Pflege entfällt hier — sie liegt in den Einstellungen.',
      'Zeitprotokoll und Dashboard zeigen Projekt (echtes oder Zeit-Projekt) und Gesellschaft; gefiltert wird nach Gesellschaft.',
    ],
  },
  {
    version: '3.43.0',
    date: '2026-06-23',
    title: 'Datenbank: mehrere Tabellen auf einmal leeren',
    tag: 'feature',
    changes: [
      'Im Bereich „Datenbank“ gibt es neben „Tabelle leeren“ jetzt „Mehrere leeren“: Tabellen per Checkbox auswählen und ihre Inhalte gemeinsam löschen (nur Daten des eigenen Mandanten).',
      'Vorschau vor dem Löschen: zeigt die Gesamtzahl der betroffenen Zeilen und welche abhängigen Tabellen wegen Verknüpfungen automatisch mitgeleert werden (Fremdschlüssel-sichere Reihenfolge, alles in einer Transaktion).',
      'Sicherheitsabfrage: zum Auslösen muss das Wort „LÖSCHEN“ eingegeben werden. Jede geleerte Tabelle wird im Audit-Log protokolliert. Nur für Administratoren (Berechtigung „Daten schreiben“).',
    ],
  },
  {
    version: '3.42.0',
    date: '2026-06-23',
    title: 'Zeiterfassung: Timer-Menü überarbeitet & Zeit-Stammdaten',
    tag: 'improvement',
    changes: [
      'Timer-Menü neu strukturiert als Kaskade Firma → Projekt → Unterkategorie. Das Feld „Kategorie“ ist entfallen.',
      'Firma: Auswahl aus den angelegten Gesellschaften; ist nur eine Gesellschaft vorhanden, wird sie automatisch gewählt. Die Projektliste ist auf die gewählte Gesellschaft gefiltert.',
      'Unterkategorien sind jetzt eine eigenständige, admin-gepflegte Stammdaten-Tabelle. Pro Projekt wird festgelegt, auf welche Unterkategorien es Zugriff hat — im Timer erscheinen nur die für das Projekt freigegebenen Unterkategorien.',
      'Neuer Navigationspunkt „Zeit-Stammdaten“ (Berechtigung „Zeiterfassung verwalten“) zum Pflegen von Gesellschaften, Unterkategorien und Projekten inkl. Firmen-/Unterkategorie-Freigaben und optionaler Verknüpfung mit einem ProERP-Projekt.',
      'Die Kostenstelle ist nicht mehr im Timer wählbar: Sie wird am Mitarbeiter hinterlegt (HR/Admin) und beim Starten automatisch auf den Zeiteintrag übernommen.',
    ],
  },
  {
    version: '3.41.0',
    date: '2026-06-23',
    title: 'Datenimport: weniger Stolperfallen',
    tag: 'improvement',
    changes: [
      'Tabellenerkennung ist ehrlicher: Dateien, die nur über den Dateinamen „passen“ (z. B. „artikel_langtexte“, „artikel_zusatzdaten“), aber inhaltlich nicht zu einer Tabelle gehören, werden nicht mehr stillschweigend als „Artikel“ erkannt und mit 0 Zeilen importiert — stattdessen erscheint der Hinweis „Tabelle nicht sicher erkannt, bitte prüfen“.',
      'Übersprungene Zeilen werden jetzt im Import-Ergebnis ausgewiesen: Wird z. B. eine Belegposition ohne zugehörigen Beleg (oder ein Kontakt ohne Kunden) importiert, steht im Lauf „X Zeile(n) übersprungen — keine Pflicht-Verknüpfung gefunden“ statt eines kommentarlosen „0 importiert“. Tipp: erst die übergeordnete Tabelle (Belege/Kunden) importieren.',
      'CRM-Aktivitäten aus der Adressdatei werden automatisch zugeordnet (Kundennummer und Aktivitäten-Verlauf), ohne dass die Spalten von Hand gemappt werden müssen.',
    ],
  },
  {
    version: '3.40.0',
    date: '2026-06-23',
    title: 'Artikel-Workflow: Rückfrage statt Ablehnung & Techniker-Self-Service',
    tag: 'feature',
    changes: [
      'Das Sekretariat kann einen Antrag jetzt mit einem Kommentar „zur Nachbesserung" an den Antragsteller zurückgeben (Status „Rückfrage") — statt ihn hart abzulehnen. Der Techniker sieht die Rückfrage im Dialog, bessert nach und reicht mit „Erneut einreichen" wieder ein.',
      'Techniker-Self-Service: eigene offene Anträge (und Rückfragen) lassen sich selbst bearbeiten und über „Zurückziehen" stornieren — ohne das Sekretariat.',
      '„Meine Anträge"-Filter auf der Anträge-Seite (für Techniker standardmäßig aktiv, für das Sekretariat alle); eigene Anträge werden mit „Ich" gekennzeichnet.',
      'Neue Status „Rückfrage" und „Zurückgezogen" mit eigenen Markierungen und Filtern.',
    ],
  },
  {
    version: '3.39.0',
    date: '2026-06-23',
    title: 'Aussagekräftige Fehlermeldungen statt „Forbidden"',
    tag: 'improvement',
    changes: [
      'Schlägt eine Aktion fehl, zeigt die App jetzt den konkreten Grund statt nur „Forbidden" oder „Bad Request" — z. B. „Dir fehlt die Berechtigung „inventory.update" für diese Aktion." Validierungsfehler werden mit Feldnamen aufgeführt.',
      'Bei unerwarteten Serverfehlern (5xx) erhält die Meldung eine kurze Referenz-ID (z. B. „Referenz: ab12cd34"), die im Server-Log auffindbar ist — so lässt sich ein Fehler eindeutig zuordnen.',
      'Listen-Ladefehler (Artikel, Kunden, Projekte) nennen ebenfalls den Grund samt HTTP-Status.',
    ],
  },
  {
    version: '3.38.0',
    date: '2026-06-23',
    title: 'Engineering: Rollen-Workflow für Artikelanlage geschärft',
    tag: 'improvement',
    changes: [
      'Neue Rolle „Techniker“: darf Zeichnungsnummern und Artikel-Anträge selbst generieren (Lesen + Anlegen), aber nicht freigeben.',
      'Admins dürfen ihre eigenen Anträge jetzt selbst freigeben (Vier-Augen-Ausnahme nur für Admins). Für alle anderen bleibt: Antragsteller ≠ Freigeber.',
      'Der Artikelnummern-Massen-Import legt echte Artikel direkt an und ist daher jetzt nur noch für Admins zugänglich (vorher Sekretariat + Admin). Der normale Weg bleibt: Techniker beantragt → Sekretariat prüft auf Vollständigkeit und gibt frei.',
    ],
  },
  {
    version: '3.37.0',
    date: '2026-06-23',
    title: 'Navigation neu gegliedert (übersichtlicher)',
    tag: 'improvement',
    changes: [
      'Das Hauptmenü ist deutlich aufgeräumt: zusammengehörige Unterseiten sind zu aufklappbaren Einträgen gebündelt — „Engineering" (Übersicht, Zeichnungsnummern, Anträge, Warengruppen, Materialcodes) und „Administration" (Benutzer & Rechte, Import, Datenbank) statt vieler einzelner Punkte.',
      'Neue, klarere Bereiche: „Personal & Zeit" (HR, Zeiterfassung, Fehlzeiten, Reisekosten), „Finanzen" (Buchhaltung) und ein verschlankter „System"-Bereich.',
      'Berechtigungen wirken jetzt auch auf Unterpunkte: Man sieht nur die Einträge, für die man Rechte hat; leere Sammel-Einträge werden ausgeblendet. Gilt für Sidebar und mobiles Menü.',
    ],
  },
  {
    version: '3.36.0',
    date: '2026-06-23',
    title: 'Reisekostenabrechnung mit Monats-PDF',
    tag: 'feature',
    changes: [
      'Neuer Bereich „Reisekosten“: Mitarbeiter erfassen Reisen mit Bezeichnung, Land und beliebig vielen Reisetagen (Datum, Reiseziel, Reisezweck, Von-/Bis-Zeit, Grenzübertritte, gefahrene Kilometer, Mittag-/Abendessen). Tage lassen sich hinzufügen, bearbeiten und entfernen.',
      'Eigene Reisen als Kartenübersicht mit Status und Statistikkarten (ausstehend/genehmigt/abgelehnt); ausstehende Reisen sind bearbeit- und löschbar, jede Reise hat eine Detailansicht mit allen Reisetagen.',
      'Admin-Verwaltung (Berechtigung „Reisekosten verwalten“): alle Reisen filtern (Monat/Status/Mitarbeiter), genehmigen/ablehnen mit Notiz. Eigene Reisen kann man nicht selbst entscheiden.',
      'Monatsexport als PDF (A4 quer): nur genehmigte Reisen, gruppiert je Mitarbeiter (eigene Seite), Tabelle aller Reisetage inkl. Stunden/Kilometer/Verpflegung und Summenzeile.',
    ],
  },
  {
    version: '3.35.0',
    date: '2026-06-23',
    title: 'Fehlzeiten, Urlaubsverwaltung & Urlaubskalender',
    tag: 'feature',
    changes: [
      'Neuer Bereich „Fehlzeiten“: Mitarbeiter beantragen Urlaub, Krankheit oder Arzttermine. Urlaubstage werden als Arbeitstage (Mo–Fr, ohne österreichische Feiertage) berechnet; Arzttermine erfassen Start-/Endzeit.',
      'Eigene Anträge mit Status (Ausstehend/Genehmigt/Abgelehnt) und Statistikkarten; ausstehende Anträge lassen sich bearbeiten und löschen.',
      'Admin-Verwaltung (Berechtigung „Urlaub verwalten“): alle Anträge filtern, genehmigen/ablehnen mit Notiz. Eigene Anträge kann man nicht selbst entscheiden (Funktionstrennung).',
      'Urlaubsanalyse je Mitarbeiter: Anspruch, Resturlaub aus dem Vorjahr, genommene und verbleibende Tage, Krankheitstage und Arztstunden.',
      'Urlaubskalender als Monatsansicht mit Farbcodierung (Urlaub blau, Krankheit rot, Arzt gelb) und österreichischen Feiertagen; Klick auf einen Tag zeigt die Details.',
      'Im Mitarbeiter-Stamm lassen sich Urlaubsanspruch und Resturlaub pflegen.',
    ],
  },
  {
    version: '3.34.0',
    date: '2026-06-23',
    title: 'Zeiterfassung: Timer, Dashboard & Zeitprotokoll',
    tag: 'feature',
    changes: [
      'Neuer Bereich „Zeiterfassung“ (Verwaltung): Stoppuhr mit Auswahl Firma → Kategorie → Projekt → Unterkategorie, optionaler Bemerkung und Kostenstelle. Es kann immer nur ein Timer gleichzeitig laufen.',
      'Ein schwebender Timer ist auf jeder Seite sichtbar, zeigt die laufende Dauer sekundengenau und lässt sich direkt stoppen.',
      'Dashboard mit Kennzahlen (Stunden heute/diese Woche, aktive Projekte, laufender Timer), meistgenutzten Projekten und den letzten Einträgen.',
      'Zeitprotokoll mit Filtern (Zeitraum, Suche, Firma/Kategorie/Projekt) sowie Bearbeiten und Löschen der eigenen Einträge.',
      'Das Zeit-Projekt kann optional mit einem ProERP-Projekt verknüpft werden (Hybrid); Mitarbeiter werden über ihr Benutzerkonto zugeordnet.',
    ],
  },
  {
    version: '3.33.0',
    date: '2026-06-23',
    title: 'Engineering: Listen & CSV-Import für Warengruppen, Materialcodes und Artikelnummern',
    tag: 'feature',
    changes: [
      'Warengruppen und Materialcodes haben jetzt eigene Listen-Seiten (eigene Navigationspunkte) mit Sortierung, Spaltenwahl, CSV-Export sowie Anlegen/Bearbeiten/Löschen statt des bisherigen kombinierten Dialogs.',
      'Neuer CSV-Import für Warengruppen und Materialcodes: Datei hochladen oder Zeilen einfügen, mit Live-Vorschau (gültig/fehlerhaft je Zeile) und Ergebnis-Zusammenfassung. Bereits vorhandene Codes werden übersprungen.',
      'Bestands-Artikelnummern importieren (unter „Zeichnungsnummern“, nur Sekretariat/Admin): aus jeder Artikelnummer XXXX.YYYYY.ZZZ werden die Zeichnungsnummer abgeleitet/angelegt und ein echter Artikel erzeugt. Fehlende Warengruppen/Materialcodes werden pro Zeile gemeldet (kein Auto-Anlegen), bereits vorhandene Artikel übersprungen.',
      'CSV-Format flexibel: Trenner Semikolon, Tab oder Komma; optionale Kopfzeile; #-Kommentarzeilen.',
    ],
  },
  {
    version: '3.32.0',
    date: '2026-06-23',
    title: 'Mobile Navigation verbessert',
    tag: 'improvement',
    changes: [
      'Das „Mehr"-Menü in der mobilen Ansicht ist jetzt nach Bereichen gruppiert (wie die Desktop-Seitenleiste), zeigt Unterpunkte (z. B. CRM → Ansprechpartner) und ist bei vielen Einträgen scrollbar.',
      'Der „Mehr"-Tab wird jetzt hervorgehoben, wenn die aktuelle Seite nicht zu den vier Schnellzugriffen gehört — man sieht so, wo man sich befindet.',
    ],
  },
  {
    version: '3.31.1',
    date: '2026-06-23',
    title: 'Validierungsfehler liefern wieder 422 statt 500',
    tag: 'fix',
    changes: [
      'Ungültige Eingaben (z. B. Upload einer Nicht-PDF-Datei als Zeichnung) liefern jetzt zuverlässig eine 422-Antwort mit Feldfehlern statt eines 500-Serverfehlers — der Fehlerfilter erkennt Zod-Validierungsfehler robust (auch über Modulgrenzen hinweg).',
    ],
  },
  {
    version: '3.31.0',
    date: '2026-06-23',
    title: 'Engineering: Antrag-Details öffnen & offene Anträge bearbeiten',
    tag: 'feature',
    changes: [
      'Ein Klick auf eine Zeile in der Anträge-Liste öffnet jetzt die vollständigen Details des Antrags.',
      'Offene (noch nicht freigegebene) Anträge lassen sich bearbeiten: alle Artikel-Details (Name, EN-Name, Texte, Lieferant, Beschaffung, Zoll/Logistik, Preise, Lager, Artikelart) sind änderbar. Die reservierte Artikelnummer samt Zeichnung und Material bleibt fest.',
      'Bereits freigegebene oder abgelehnte Anträge werden schreibgeschützt angezeigt — inklusive Status, Ablehnungsgrund bzw. Link zum angelegten Artikel.',
      'Berechtigung „engineering.update": Das Sekretariat (und Admins) darf offene Anträge bearbeiten.',
    ],
  },
  {
    version: '3.30.2',
    date: '2026-06-23',
    title: 'Engineering: klare Begründung bei abgelehntem Artikel-Antrag',
    tag: 'fix',
    changes: [
      'Wird eine Artikelnummer bereits verwendet oder beantragt, zeigte das Einreichen nur „Antrag konnte nicht eingereicht werden (HTTP 409)". Jetzt erscheint die konkrete Begründung des Servers (z. B. „Artikelnummer 1100.10001.108 ist bereits beantragt." bzw. „… existiert bereits."), sodass klar ist, welche Nummer betroffen ist — der bestehende Antrag ist in der Liste auffindbar.',
      'Auch beim Freigeben/Ablehnen werden Server-Begründungen sprechend angezeigt (z. B. „Antragsteller darf nicht selbst freigeben." beim Vier-Augen-Prinzip).',
    ],
  },
  {
    version: '3.30.1',
    date: '2026-06-23',
    title: 'Engineering: Anträge-Liste lädt wieder zuverlässig',
    tag: 'fix',
    changes: [
      'Die Anträge-Liste konnte nach der Umstellung mit „HTTP 500" fehlschlagen, wenn noch Anträge aus der Vorversion vorhanden waren (unvollständige Daten) oder eine zugeordnete Warengruppe zwischenzeitlich gelöscht wurde. Die Liste ist jetzt robust: Der Warengruppen-Code wird direkt aus der reservierten Nummer abgeleitet, und unvollständige Alt-Anträge werden tolerant dargestellt statt das Laden abzubrechen.',
    ],
  },
  {
    version: '3.30.0',
    date: '2026-06-22',
    title: 'Engineering: Artikel-Antrag mit fester Nummer & vollständiger Anlage',
    tag: 'feature',
    changes: [
      'Die fertige Artikelnummer steht jetzt bereits beim Einreichen des Antrags fest (reserviert) — nicht mehr erst bei der Freigabe. Bei einer neuen Zeichnung wird die laufende Nummer sofort vergeben, bei einer bestehenden Zeichnung exakt übernommen. Die Vier-Augen-Freigabe ist dadurch ein reiner Vollständigkeits-Check, der den Artikel mit der reservierten Nummer anlegt.',
      'Eindeutigkeit wird schon beim Einreichen geprüft: Existiert die Artikelnummer bereits (als Artikel oder als offener Antrag), wird der Antrag mit klarer Meldung abgelehnt.',
      'Der Beantragen-Dialog erfasst nun alle Felder der normalen Artikel-Anlage (inkl. englischer Name, Hersteller, Standardlieferant, Beschaffung, Zoll/Logistik, Preise EK/VK, Lagerbestände) und eine Pflicht-Auswahl „Artikelart" (Projektbezogen/Standardartikel), die als Tag in den Artikel übernommen wird.',
      'Neuer Zeichnungs-Umschalter im Dialog: „neue Zeichnungsnummer" (Warengruppe wählen) oder „bestehende Zeichnungsnummer" — mit Live-Vorschau der entstehenden Artikelnummer.',
      'Die Anträge-Liste zeigt die reservierte Artikelnummer jetzt für alle Status (auch offene Anträge), sodass der Freigeber die fertige Nummer beim Prüfen sieht.',
    ],
  },
  {
    version: '3.29.0',
    date: '2026-06-22',
    title: 'Engineering: Artikel beantragen mit Vier-Augen-Freigabe',
    tag: 'feature',
    changes: [
      'Neuer Ablauf „Artikel beantragen": Ein Mitarbeiter beantragt eine neue Artikelnummer, das Sekretariat (Berechtigung „engineering.approve") gibt sie frei — erst dann wird ein echter Artikel mit der generierten Nummer angelegt. Eine Ablehnung legt nichts an.',
      'Beantragen-Dialog mit zwei Wegen: von Grund auf über eine Warengruppe (die Zeichnungsnummer wird erst bei der Freigabe vergeben) oder zu einer bestehenden Zeichnungsnummer (Texte werden vorbefüllt). Material, Beschreibung, Langtexte (DE/EN), Taric-Code, Lagereinheit, Referenz und interne Notiz erfassbar; optional „Aus bestehendem Artikel laden".',
      'Neue Seite „Anträge" im Modul Engineering: Liste aller Anträge mit Status (Offen/Freigegeben/Abgelehnt), Statusfilter und — nach Freigabe — der generierten Artikelnummer samt Link zum Artikel.',
      'Vier-Augen-Prinzip serverseitig erzwungen: Der Antragsteller kann den eigenen Antrag nicht selbst freigeben. Freigeben/Ablehnen (mit Pflicht-Begründung) sehen nur Nutzer mit der Freigabe-Berechtigung.',
      'Einstiege: „Artikel beantragen"-Button auf der Engineering-Startseite und je Zeile auf der Zeichnungsnummern-Seite.',
    ],
  },
  {
    version: '3.28.1',
    date: '2026-06-22',
    title: 'Versender (Gesellschaft) auch bei Angeboten wählbar',
    tag: 'fix',
    changes: [
      'Der Versender-Dropdown (Gesellschaft) erscheint jetzt im Editor bei allen Belegarten — auch bei Angeboten und Kommissionierlisten —, damit das richtige Briefpapier gewählt werden kann. Die Nummernkreise bleiben unverändert (geteilte Arten behalten ihr Format).',
    ],
  },
  {
    version: '3.28.0',
    date: '2026-06-22',
    title: 'Engineering: Zeichnungsnummern anlegen (Format XXXX.YYYYY)',
    tag: 'feature',
    changes: [
      'Neue Seite „Zeichnungsnummern" im Modul Engineering: Liste aller vergebenen Zeichnungsnummern (Nummer, Warengruppe, Beschreibung, Erstelldatum) mit Suche/CSV-Export.',
      'Anlegen neuer Zeichnungsnummern über einen Dialog — Warengruppe wählen, Beschreibung und Langtexte (DE/EN) erfassen, optionale Referenz und interne Notiz. Die laufende Nummer (5-stellig, Start 10000) wird je Warengruppe automatisch vergeben.',
      'Komfort: „Daten aus bestehender Zeichnungsnummer laden" befüllt die Textfelder aus einer ausgewählten bestehenden Nummer vor.',
      'Die Beantragung von Artikeln zu einer Zeichnungsnummer (mit Vier-Augen-Freigabe durch das Sekretariat) folgt im nächsten Schritt.',
    ],
  },
  {
    version: '3.27.1',
    date: '2026-06-22',
    title: 'Versender-Auswahl zuverlässig sichtbar',
    tag: 'fix',
    changes: [
      'Der Versender-Dropdown (Gesellschaft) im Beleg-Editor wird jetzt für alle gesellschaftsgebundenen Belege angezeigt — auch während die Gesellschaften noch geladen werden. Die aktuelle Gesellschaft des Belegs ist immer als Option vorhanden.',
    ],
  },
  {
    version: '3.27.0',
    date: '2026-06-22',
    title: 'Neues Modul „Engineering" (Stammdaten: Warengruppen, Materialien, Taric)',
    tag: 'feature',
    changes: [
      'Neue Navigationsgruppe „Engineering" mit eigener Seite — Grundlage für die künftige Generierung von Zeichnungs- und Artikelnummern (Format XXXX.YYYYY.ZZZ) direkt in ProERP statt über eine externe App.',
      'Stammdaten-Pflege über die Header-Buttons: Warengruppen (4-stelliger Code, optional einer Hauptwarengruppe zugeordnet), Materialien (3-stelliger Code) und Taric-Codes (Zolltarifnummer mit Beschreibung) — jeweils Anlegen, Bearbeiten und Löschen.',
      'Neue Berechtigungen engineering.read/create/update/approve sowie die Systemrolle „Sekretariat" (Lesen + spätere Vier-Augen-Freigabe).',
      'Die eigentliche Nummern-Generierung mit Vier-Augen-Freigabe folgt im nächsten Schritt.',
    ],
  },
  {
    version: '3.26.1',
    date: '2026-06-22',
    title: 'Versender (Gesellschaft) in der Beleg-Kopfleiste sichtbar',
    tag: 'fix',
    changes: [
      'Der Versender-Auswahl (Gesellschaft) steht jetzt direkt in der Kopfleiste des Beleg-Editors (Belegblatt und Werkbank) — vorher nur im aufklappbaren Bereich „Weitere Belegangaben" bzw. im Dialog „Alle Belegdaten" und dadurch leicht zu übersehen.',
    ],
  },
  {
    version: '3.26.0',
    date: '2026-06-22',
    title: 'Versender (Gesellschaft) je Beleg im Editor änderbar',
    tag: 'feature',
    changes: [
      'Im Beleg-Editor lässt sich der Versender (ausstellende Gesellschaft) jetzt per Dropdown unter „Beleginfo" ändern — auch nachträglich. Gilt für gesellschaftsgebundene Belege (Rechnung, Auftragsbestätigung, Lieferschein, An-/Schlussrechnung, Gutschrift, Pro-forma).',
      'Die Belegnummer bleibt beim Wechsel unverändert; das PDF nutzt anschließend Briefpapier und Firmendaten der gewählten Gesellschaft.',
    ],
  },
  {
    version: '3.25.1',
    date: '2026-06-22',
    title: 'Briefpapier eindeutig je Gesellschaft (falsches Briefpapier behoben)',
    tag: 'fix',
    changes: [
      'Logo und Briefpapier werden nur noch je Gesellschaft (Tab „Gesellschaften") verwaltet — der alte tenant-weite Upload im Tab „Branding" entfällt, weil er auf Belege keine Wirkung mehr hatte und für Verwirrung sorgte.',
      'Belege ohne zugeordnete Gesellschaft (z. B. Angebote) verwenden jetzt das Briefpapier der Standard-Gesellschaft statt der alten Firmenstammdaten — so wird durchgängig das richtige Briefpapier gezogen.',
    ],
  },
  {
    version: '3.25.0',
    date: '2026-06-22',
    title: 'Gesellschaft: Übernahme auf Bestandsbelege + richtiges Briefpapier',
    tag: 'feature',
    changes: [
      'Wird die betreuende Gesellschaft eines Kunden geändert, werden die bestehenden (gesellschaftsgebundenen) Belege dieses Kunden automatisch auf die neue Gesellschaft umgestellt — ohne Neunummerierung. Die Anzahl angepasster Belege wird gemeldet.',
      'Beim Belegausdruck wird jetzt durchgängig das Briefpapier (und die Firmendaten) der jeweiligen Gesellschaft verwendet — auch bei Bestellungen (z. B. die automatischen Intercompany-Bestellungen der DE-Gesellschaft).',
    ],
  },
  {
    version: '3.24.0',
    date: '2026-06-22',
    title: 'CRM: betreuende Gesellschaft je Kunde',
    tag: 'feature',
    changes: [
      'Im Kundenformular lässt sich jetzt die betreuende Gesellschaft (Owner) wählen — welche eigene Gesellschaft den Kunden bedient.',
      'Neue Belege übernehmen diese Gesellschaft automatisch als Vorschlag (Reihenfolge: manueller Override → Projekt-Owner → Kunden-Owner → Ableitung nach Land); manuell übersteuerbar.',
    ],
  },
  {
    version: '3.23.0',
    date: '2026-06-22',
    title: 'Mehr-Gesellschaft Phase 4: automatische Intercompany-Belege',
    tag: 'feature',
    changes: [
      'Geschäftspartner können als „interne Gesellschaft" markiert werden (z. B. Kunde „Probig DE“, Lieferant „Probig AT“) — neues Feld im Kundenformular.',
      'Wird eine Rechnung (Rechnung/Anzahlung/Schlussrechnung) einer Gesellschaft an einen internen Kunden festgeschrieben (Status → Versendet), erzeugt das System automatisch die passende Eingangs-Bestellung der empfangenden Gesellschaft (Lieferant = liefernde Gesellschaft), inkl. gespiegelter Positionen.',
      'Die Erzeugung ist idempotent (eine Bestellung je Rechnung) und blockiert das Festschreiben nicht, falls der interne Lieferant noch fehlt.',
    ],
  },
  {
    version: '3.22.0',
    date: '2026-06-22',
    title: 'Mehr-Gesellschaft Phase 5: Projekt-Owner (Gesellschaft)',
    tag: 'feature',
    changes: [
      'Projekte können einer Owner-Gesellschaft zugeordnet werden (Einstellung im Projekt-Formular). Das ist nur ein Label/Default — keine Zugriffsbeschränkung; AT darf weiterhin an einem DE-Projekt arbeiten.',
      'Wird ein Beleg mit Projekt angelegt, schlägt das System die Gesellschaft des Projekt-Owners vor (vor der Ableitung aus dem Kunden-Land); manueller Override bleibt möglich.',
    ],
  },
  {
    version: '3.21.0',
    date: '2026-06-22',
    title: 'Mehr-Gesellschaft Phase 3: automatische Steuerfindung',
    tag: 'feature',
    changes: [
      'Der USt-Satz wird beim Anlegen eines Belegs automatisch aus ausstellender Gesellschaft und Empfänger ermittelt: Inland (Satz der Gesellschaft, AT 20 % / DE 19 %), innergemeinschaftliche Lieferung mit gültiger UID → Reverse Charge 0 % (mit Hinweis auf dem Beleg), Drittland → steuerfreie Ausfuhr 0 %.',
      'Der Anlegen-Dialog zeigt den abgeleiteten Satz als Hinweis an; manuelles Übersteuern (Satz, steuerfrei, Grund) bleibt jederzeit möglich.',
    ],
  },
  {
    version: '3.20.0',
    date: '2026-06-22',
    title: 'Mehr-Gesellschaft Phase 2: Belege tragen die Gesellschaft (Nummern, Briefpapier)',
    tag: 'feature',
    changes: [
      'Offizielle Verkaufsbelege (Rechnung, Auftragsbestätigung, Lieferschein, An-/Schlussrechnung, Gutschrift, Pro-forma) werden jetzt einer Gesellschaft zugeordnet — automatisch aus dem Kunden abgeleitet (DE-Kunde → Probig DE, sonst Probig AT), im Anlegen-Dialog manuell übersteuerbar.',
      'Eigene Nummernkreise je Gesellschaft im Format z. B. RE2610001 (AT) bzw. RE2620001 (DE). Angebote und Kommissionierlisten bleiben geteilt im bisherigen Format.',
      'Das Beleg-PDF nutzt jetzt das Briefpapier und die Firmendaten der ausstellenden Gesellschaft.',
      'Umwandlungen und Anzahlungs-/Schlussrechnungen übernehmen die Gesellschaft des Ursprungsbelegs.',
    ],
  },
  {
    version: '3.19.0',
    date: '2026-06-22',
    title: 'Bestell-PDF im Layout der Verkaufsbelege',
    tag: 'improvement',
    changes: [
      'Die Bestell-PDFs sehen jetzt aus wie die Verkaufsbelege: gleicher Briefkopf mit Empfänger-Anschriftfeld, zweispaltiger Infoblock (Nr./Bestelldatum/Liefertermin), Belegtitel mit Projekt, dieselbe Positionstabelle (Art-Nr. | Beschreibung | Menge | Einzelpreis | Gesamt) und der typografische Summenblock.',
      'Hinterlegtes Briefpapier (Bild oder PDF) wird beim Bestell-PDF jetzt ebenfalls berücksichtigt — vorher nur beim Verkauf.',
      'Überschrifts-, Text- und Seitenumbruch-Positionen werden im Bestell-PDF korrekt dargestellt (wie im Verkauf), inklusive Langtext je Position.',
    ],
  },
  {
    version: '3.18.1',
    date: '2026-06-22',
    title: 'Bestellung anlegen: alle Geschäftspartner auswählbar',
    tag: 'fix',
    changes: [
      'Beim Anlegen einer Bestellung ließ sich der Geschäftspartner nur aus den ersten 200 Datensätzen wählen — Partner dahinter (z. B. „Katzengruber") waren nicht auffindbar. Die Auswahl sucht jetzt serverseitig über alle Kontakte (wie im Verkauf), sodass jeder Geschäftspartner gefunden und gewählt werden kann.',
    ],
  },
  {
    version: '3.18.0',
    date: '2026-06-22',
    title: 'Mehr-Gesellschaft: Fundament — Gesellschaften verwalten (Phase 1)',
    tag: 'feature',
    changes: [
      'Erster Schritt zur Zwei-Gesellschaften-Fähigkeit (Probig AT / Probig DE, ADR 0029): neue verwaltbare „Gesellschaften" unter Einstellungen → Tab „Gesellschaften" — je Gesellschaft Name, Land, UID, Bank, USt-Satz, Nummern-Ziffer sowie eigenes Logo und Briefpapier.',
      'Bestehende Firmenstammdaten wurden automatisch als Gesellschaft „AT" übernommen; eine Vorlage „DE" (19 %) ist angelegt.',
      'Noch ohne Wirkung auf Belege/Nummern/PDF — das folgt in Phase 2 (Belege bekommen die Gesellschaft, eigene Nummernkreise und das passende Briefpapier).',
    ],
  },
  {
    version: '3.17.0',
    date: '2026-06-22',
    title: 'Einkäufe wirken positionsgenau im Projekt-Controlling',
    tag: 'feature',
    changes: [
      'Das Positions-Projekt einer Bestellung fließt jetzt direkt ins Controlling: Beim Wareneingang wird der Material-Ist je Position auf das Projekt der jeweiligen Zeile gebucht — nicht mehr pauschal auf das Beleg-Kopf-Projekt. Eine Bestellung mit gemischten Projekten verteilt ihre Ist-Kosten dadurch korrekt auf die Plan-vs-Ist-Auswertung der einzelnen Projekte.',
      'Der Lagerzugang aus dem Wareneingang wird ebenfalls auf das Projekt der Position gebucht.',
      'Bestehende Bestellpositionen ohne eigenes Projekt erben rückwirkend das Projekt ihres Beleg-Kopfs (Datenmigration), sodass die bisherige Auswertung erhalten bleibt.',
    ],
  },
  {
    version: '3.16.1',
    date: '2026-06-22',
    title: 'Beschaffung: Klick auf eine Bestellung öffnet direkt den Editor',
    tag: 'improvement',
    changes: [
      'In der Bestellliste öffnet ein Klick auf einen Eintrag jetzt sofort das Belegblatt (Vollbild-Editor) — genau wie im Verkauf. Auch eine neu angelegte Bestellung springt direkt in den Editor.',
    ],
  },
  {
    version: '3.16.0',
    date: '2026-06-22',
    title: 'Einstellungen in Tabs gruppiert',
    tag: 'improvement',
    changes: [
      'Die Einstellungen sind jetzt in Tabs gegliedert: „Firma" (Stammdaten/Kontakt/Bank/Belege), „Branding" (Logo, Briefpapier, Login-Seite) und „Beschaffung" (Tag-Regeln) — statt einer langen Seite.',
    ],
  },
  {
    version: '3.15.0',
    date: '2026-06-22',
    title: 'Beschaffung: neuer Bestell-Editor wie im Verkauf (mit Projekt je Position)',
    tag: 'feature',
    changes: [
      'Bestellungen werden jetzt im selben Vollbild-Editor wie Verkaufsbelege bearbeitet — mit den Ansichten „Belegblatt" und „Werkbank", Inline-Artikelsuche, Excel-Einfügen, Ausfüll-Punkt, Drag-&-Drop-Reihenfolge sowie Abschnitts-, Text- und Seitenumbruch-Positionen.',
      'Neu in der Bestellung: eine Projekt-Spalte je Position. Jede Zeile kann auf ein eigenes Projekt gebucht werden; neue Zeilen erben automatisch das Projekt aus dem Beleg-Kopf. Der Verkaufs-Editor bleibt unverändert (ohne Projekt-Spalte).',
      'Das gemeinsame Editor-Gerüst (Positionsraster, Kopfleiste, Kopf-Felder) liegt jetzt im geteilten Modul „documents" und wird von Verkauf und Beschaffung genutzt.',
      'Die alte Positions-Dialog-Erfassung der Bestell-Detailseite entfällt; Positionen werden über „Bearbeiten" im Vollbild-Editor gepflegt.',
      'Die Bestellliste zeigt das Projekt jetzt als eigene Spalte (wie die Belegliste im Verkauf).',
    ],
  },
  {
    version: '3.14.0',
    date: '2026-06-22',
    title: 'Login-Seite: Farbe und Texte in den Einstellungen verwaltbar',
    tag: 'feature',
    changes: [
      'Das linke Panel der Anmeldeseite (Überschrift, Untertitel, Stichpunkte und Hintergrundfarbe) lässt sich jetzt unter Einstellungen → „Login-Seite" pflegen.',
      'Die Anmeldeseite lädt das Branding über einen öffentlichen Endpoint (kein Login nötig); leere Felder verwenden den bisherigen Standard.',
      'Der Footer der Login-Seite nutzt den hinterlegten Firmennamen.',
    ],
  },
  {
    version: '3.13.0',
    date: '2026-06-22',
    title: 'Beleg-Editor: Einfügen ergänzt Artikel-Felder automatisch',
    tag: 'improvement',
    changes: [
      'Beim Einfügen aus Excel reicht jetzt Artikelnummer (+ z. B. Menge, Rabatt): Beschreibung, Einheit und Einzelpreis werden automatisch aus dem Artikelstamm ergänzt, sobald die Nummer einem Artikel entspricht. Bereits eingegebene Werte (Menge, Rabatt, eigene Beschreibung/Preis) bleiben erhalten.',
    ],
  },
  {
    version: '3.12.4',
    date: '2026-06-22',
    title: 'Beleg-Editor: Einfügelinie beim Verschieben jetzt sichtbar',
    tag: 'fix',
    changes: [
      'Beim Verschieben einer Position per Drag & Drop erscheint jetzt eine deutliche Einfügelinie an der Stelle, wo die Zeile landet (oberhalb beim Hochziehen, unterhalb beim Runterziehen). Vorher war die Markierung technisch nicht sichtbar, weil sie als Schatten auf der Tabellenzeile gezeichnet wurde — jetzt als eigene Linie.',
    ],
  },
  {
    version: '3.12.3',
    date: '2026-06-22',
    title: 'Beleg-Editor: klare Drop-Markierung + spürbar schneller',
    tag: 'improvement',
    changes: [
      'Beim Verschieben einer Position per Drag & Drop zeigt jetzt eine deutliche Einfüge-Linie genau, wo die Zeile landet (über bzw. unter der Zielzeile, je nach Zugrichtung); die gezogene Zeile wird abgeblendet und es gibt keine störende Textauswahl mehr.',
      'Die Belegbearbeitung ist deutlich flotter: Beim Tippen wird nur noch die bearbeitete Zeile neu gezeichnet (statt aller Positionen), und der Artikelstamm wird nicht mehr bei jedem Tastendruck je Zelle durchgerechnet. In großen Belegen (viele Positionen, großer Artikelstamm) ist die Eingabe rund 5× reaktionsschneller.',
    ],
  },
  {
    version: '3.12.2',
    date: '2026-06-22',
    title: 'Hinweis-Pop-ups jetzt unten zentriert',
    tag: 'improvement',
    changes: [
      'Hinweis-Meldungen (z. B. „Beleg gespeichert") erscheinen jetzt unten mittig statt oben rechts.',
    ],
  },
  {
    version: '3.12.1',
    date: '2026-06-22',
    title: 'Belegeditor: Seitenumbruch lässt sich jetzt wirklich einfügen',
    tag: 'fix',
    changes: [
      'Der Seitenumbruch ist jetzt im tatsächlich genutzten Positions-Grid verfügbar: neuer Toolbar-Button „Seitenumbruch" (Scheren-Symbol) sowie der Eintrag „Als Seitenumbruch" im Zeilen-Menü („…").',
      'Die Umbruch-Zeile wird als gestrichelte Trennlinie mit „Seitenumbruch / neue Seite im PDF" dargestellt — ohne Eingabefelder, verschieb- und löschbar wie andere Positionen.',
    ],
  },
  {
    version: '3.12.0',
    date: '2026-06-22',
    title: 'Belegeditor: neue Positionsart „Seitenumbruch"',
    tag: 'feature',
    changes: [
      'Im Belegeditor gibt es eine neue Positionsart „Seitenumbruch". Damit lässt sich an einer beliebigen Stelle ein manueller Seitenumbruch einfügen — alle folgenden Positionen beginnen im PDF auf einer neuen Seite (mit Fortsetzungs-Kopfzeile).',
      'Die Seitenumbruch-Zeile ist eine reine Steuerposition ohne Art-Nr./Menge/Preis und wirkt nicht auf die Summen. Im Editor wird sie als „— Seitenumbruch (neue Seite) —" dargestellt; nur die Typ-Auswahl bleibt bedienbar.',
      'Neue Enum-Migration (SalesItemType: PAGE_BREAK) sowie Editor-, Schema- und PDF-Unterstützung.',
    ],
  },
  {
    version: '3.11.0',
    date: '2026-06-20',
    title: 'Beleg-Editor: Positions-Reihenfolge wird gespeichert',
    tag: 'fix',
    changes: [
      'Per Drag & Drop verschobene Positionen werden jetzt dauerhaft gespeichert: Die Reihenfolge (Position) jeder Zeile wird beim Speichern an den Beleg übergeben und bleibt nach dem Neuladen erhalten.',
      'Das reine Umsortieren markiert den Beleg jetzt als „nicht gespeichert" (Speichern wird aktiv), auch wenn keine Werte geändert wurden.',
    ],
  },
  {
    version: '3.10.0',
    date: '2026-06-20',
    title: 'Beleg-Editor: Artikel mit Sofort-Trefferliste',
    tag: 'improvement',
    changes: [
      'In der Artikel-Spalte erscheint jetzt schon beim Tippen eine Trefferliste (Nummer, Bezeichnung, VK-Preis). Mit ↑/↓ wählen, ↵ übernimmt den Artikel und springt direkt in die Menge — kein Umweg über das Such-Modal mehr (die Lupe öffnet es weiterhin für die volle Suche).',
      'Die Artikel-Spalte ist 20 % breiter, damit längere Artikelnummern besser lesbar sind.',
    ],
  },
  {
    version: '3.9.4',
    date: '2026-06-20',
    title: 'Beleg-PDF: größerer Abstand im Infoblock',
    tag: 'improvement',
    changes: [
      'Im Infoblock rechts ist der Abstand zwischen Label und Wert breiter — die Werte stehen weiter rechts und sind besser von der Bezeichnung getrennt.',
    ],
  },
  {
    version: '3.9.3',
    date: '2026-06-20',
    title: 'Beleg-PDF: Überschrift- und Textpositionen werden korrekt dargestellt',
    tag: 'fix',
    changes: [
      'Positionen vom Typ „Überschrift" (Import-Code K) werden im PDF jetzt als fetter Gruppentitel über die ganze Zeilenbreite mit Abstand nach oben gesetzt — ohne Art-Nr., Menge und Preis.',
      'Positionen vom Typ „Text" werden als Fließtext über die ganze Breite gerendert (inkl. Langtext), ebenfalls ohne Menge/Preis. Vorher erschienen beide fälschlich als Artikelzeile mit „—", Menge 0 und „0,00 €".',
      'Erfassung und Berechnung waren bereits korrekt (Editor-Typauswahl, keine Summen-Wirkung, Import-Mapping) — nur die PDF-Ausgabe wurde nachgezogen.',
    ],
  },
  {
    version: '3.9.2',
    date: '2026-06-20',
    title: 'Beleg-PDF: Gewicht automatisch, breiterer Infoblock, Bezug unter der Überschrift',
    tag: 'improvement',
    changes: [
      'Das Beleggewicht wird jetzt automatisch aus den Artikelgewichten berechnet (Summe aus Menge × Gewicht je Position). Das manuelle Gewichtsfeld im Belegeditor entfällt; das berechnete Gewicht wird als Info „Lieferangaben" angezeigt.',
      'Gewicht steht im PDF jetzt — zusammen mit Lieferzeit und Versandart — unten links unter den Positionen.',
      'Der Infoblock rechts (Nr., Datum, Kunden-Nr., USt-IdNr., Liefertermin) ist breiter, damit längere Werte besser passen.',
      'Der „Bezug" wandert aus dem Infoblock direkt unter die Belegüberschrift (z. B. unter „Angebot AN-2026-0001").',
    ],
  },
  {
    version: '3.9.1',
    date: '2026-06-20',
    title: 'Beleg-PDF: Infoblock zweispaltig, Lieferzeit/Versandart unten links',
    tag: 'improvement',
    changes: [
      'Der Infoblock rechts (Nr., Datum, Kunden-Nr., USt-IdNr., Liefertermin …) ist jetzt zweispaltig: Label und Wert stehen nebeneinander in einer Zeile (z. B. „Datum: 20.06.2026", „USt-IdNr.: ATU12345677") statt untereinander.',
      'Lieferzeit und Versandart sind aus dem Infoblock heraus nach unten links gewandert (unter die Positionen/Hinweise), wo mehr Platz für längere Texte ist.',
    ],
  },
  {
    version: '3.9.0',
    date: '2026-06-20',
    title: 'Beleg-PDF: zurück zum ursprünglichen Layout (mit Briefpapier & neuen Feldern)',
    tag: 'improvement',
    changes: [
      'Das Beleg-PDF nutzt wieder das bewährte ursprüngliche Layout (Empfänger links, Infoblock rechts, Tabelle Art-Nr. | Bezeichnung | Menge | Einzelpreis | Rabatt | Gesamt mit €-Beträgen, klassische Summen) — der Büroware-/Fintech-Umbau wurde zurückgenommen.',
      'Das hinterlegte Briefpapier wird weiter genutzt: Der Inhalt liegt mittig darauf, der eigene Text-Kopf/-Fuß entfällt dann.',
      'Neue Felder sind integriert: Kunden-Nr. und USt-IdNr. im Infoblock rechts, abweichende Rechnungs- und Lieferadresse als eigene Blöcke. Titel/Tabelle rücken automatisch unter die Kopfblöcke (kein Überlappen).',
      'Schrift wieder DejaVu Sans → das €-Zeichen wird korrekt dargestellt.',
    ],
  },
  {
    version: '3.8.0',
    date: '2026-06-20',
    title: 'Verkauf: keine Vorschau-Seite mehr + flüssigeres Positionen-Erfassen',
    tag: 'improvement',
    changes: [
      'Belege öffnen jetzt direkt im Editor (Belegblatt/Werkbank) — die separate Vorschau-/Detailseite entfällt. Alle Beleg-Aktionen (PDF, Beleg wandeln, Status ändern, Anzahlung, Schlussrechnung, Warenausgang, Belegfluss, Projekt verknüpfen, Löschen) sitzen jetzt im „…"-Menü der Editor-Kopfleiste. „Schließen" führt zurück zur Liste.',
      'Positionen schneller erfassen: Enter springt jetzt in die nächste Zeile direkt in die Artikel-Spalte (Loop: Art-Nr. ↵ Menge ↵ nächste Art-Nr. …). Pfeil-runter füllt weiterhin dieselbe Spalte nach unten.',
      'Alt-Links auf einen Beleg (z. B. aus Projekt, CRM, Buchhaltung, Suche, Dashboard) leiten automatisch in den Editor um.',
    ],
  },
  {
    version: '3.7.1',
    date: '2026-06-20',
    title: 'Artikel-Detailseite: Lagerbestand-Karte ohne Leerraum',
    tag: 'fix',
    changes: [
      'Die Lagerbestand-Karte (Ring-Diagramm) klebte oben und ließ darunter eine große leere Fläche, weil sie auf die Höhe der danebenliegenden Hauptzeichnung-Karte gestreckt wurde.',
      'Der Bestands-Ring sitzt jetzt vertikal zentriert, die Mindest-/Höchst-Werte sind als Fußzeile unten verankert — die Karte füllt ihre Höhe ausgewogen und richtet sich sauber an der Nachbarkarte aus.',
    ],
  },
  {
    version: '3.7.0',
    date: '2026-06-20',
    title: 'Nur noch ein „Verkauf" — neues Modul ersetzt das alte',
    tag: 'improvement',
    changes: [
      'Es gibt nur noch ein Verkaufsmodul: Die bisherige Erprobung „Verkauf 2" ist jetzt der Standard-„Verkauf". Die alte Belegliste und der alte Belegeditor wurden entfernt.',
      '„Verkauf" in der Seitenleiste öffnet die neue Liste (Tabs je Belegart + Filter); ein Beleg wird im neuen Vollbild-Editor (Belegblatt / Werkbank) bearbeitet — auch über „Bearbeiten" auf der Detailseite.',
    ],
  },
  {
    version: '3.6.0',
    date: '2026-06-20',
    title: 'Verkauf 2 wird zur Standard-Belegbearbeitung',
    tag: 'feature',
    changes: [
      'Belege werden jetzt im neuen Editor („Belegblatt"/„Werkbank") bearbeitet: „Bearbeiten" auf der Belegdetailseite und der Vollbild-Link öffnen den neuen Editor.',
      'Die Verkauf-2-Liste hat jetzt dieselbe Unterteilung wie der klassische Verkauf — Tabs je Belegart (Angebote, Aufträge, Rechnungen …) plus Filter für Suche, Geschäftsjahr und Status — und nutzt die volle Breite.',
      'Schnellere Tastatur-Erfassung: Tab springt sauber von Spalte zu Spalte (Aktions-Buttons stören nicht mehr); nach der Artikelwahl (oder Art-Nr. + ↵) geht der Fokus direkt in die Menge.',
      'Die Artikel-Spalte ist jetzt ein echtes Eingabefeld: Nummer tippen/einfügen, ↵ verknüpft den Artikel, die Lupe öffnet die Suche. Dadurch funktioniert auch das Einfügen einer Excel-Tabelle ab der Artikel-Spalte (passende Nummern werden verknüpft).',
      'Langtext je Position lässt sich über den Aufklapp-Pfeil rechts (oder das Zeilen-Menü) bearbeiten; er erscheint auf dem Beleg.',
      'Positionen lassen sich per Anfasser links (Ziehen) neu anordnen.',
    ],
  },
  {
    version: '3.5.0',
    date: '2026-06-20',
    title: 'Beleg-PDFs: frisches, modernes Design + mehr Belegdaten',
    tag: 'feature',
    changes: [
      'Komplett neues, modernes Beleg-Layout: klare Typografie, Akzentlinie unter dem Titel, dezente Info-Leiste (Datum, Kunden-Nr., USt-IdNr., Bearbeiter, Best.-Nr., Projekt), Abschnitts-Bänder mit Akzentbalken und ein hervorgehobener Gesamtbetrag in dunkler Box.',
      'Neue Belegfelder: abweichende Rechnungs- und Lieferadresse sowie Kundennummer und USt-IdNr. des Kunden (werden bei der Anlage automatisch aus dem Kundenstamm übernommen und sind im Editor änderbar).',
      'Diese Felder erscheinen jetzt auf dem PDF: Rechnung an / Rechnungsadresse / Lieferadresse, Kunden-Nr., USt-IdNr. und Bearbeiter.',
      'Beträge weiterhin im deutschen Format mit Währungscode (z. B. „50.580,00 EUR"); funktioniert mit und ohne hinterlegtem Briefpapier.',
    ],
  },
  {
    version: '3.4.1',
    date: '2026-06-20',
    title: 'Artikel-Detailseite: luftigere Abstände (shadcn-Look)',
    tag: 'improvement',
    changes: [
      'Alle Karten der Artikel-Detailseite nutzen jetzt den großzügigeren 24-px-Innenabstand (Padding/Abstände) nach shadcn-Vorbild statt der bisher kompakten 16-px-Kacheln — das wirkt aufgeräumter und ruhiger.',
      'Die Kennzahlen-Kacheln (Bestand, Bestandswert, VK, Marge) sind nicht mehr unten leer und oben gedrängt, sondern vertikal ausgewogen — Beschriftung und Wert sitzen mit gleichmäßigem Abstand.',
      'Die Abstände zwischen den Karten (Lagerbestand/Hauptzeichnung, Stammdaten/Zoll, Infotexte) wachsen auf größeren Bildschirmen auf 24 px — konsistent zur restlichen App.',
    ],
  },
  {
    version: '3.4.0',
    date: '2026-06-20',
    title: 'Artikel-Detailseite: Tags & aussagekräftigere Kennzahlen',
    tag: 'improvement',
    changes: [
      'Die Schlagworte (Tags) eines Artikels werden jetzt direkt im Kopf der Detailseite angezeigt — bisher waren sie nur intern gepflegt, aber nirgends sichtbar.',
      'Die Kennzahlen-Kacheln zeigen statt der doppelten Werte (Gewicht steht unter „Zoll & Logistik", Mindestbestand am Lager-Ring) nun Verkaufspreis (VK) und Marge — die für Einkauf/Verkauf relevanten Zahlen auf einen Blick.',
    ],
  },
  {
    version: '3.3.0',
    date: '2026-06-20',
    title: 'Beleg-PDFs vollständig im Büroware-Design',
    tag: 'improvement',
    changes: [
      'Die Verkaufsbelege (Angebot/Auftragsbestätigung/Rechnung …) übernehmen jetzt das Büroware-Layout 1:1: Kundenanschrift im DIN-Fenster, Belegtitel + Nr., Infoblock rechts (Datum/Seite), Best.-Nr.-Zeile.',
      'Positionstabelle exakt nach Vorlage: Artikel-Nr. | Bezeichnung | Menge (+Einheit) | Einzelpreis | Gesamt mit Linien über/unter dem Spaltenkopf; Überschriften fett, Textzeilen über die volle Breite, Langtext eingerückt.',
      'Summenband unten verankert wie in der Büroware: links Versandart/Zahlungsbedingung/Lieferzeit, rechts Nettobetrag/MwSt/Gesamtbetrag (Gesamt hervorgehoben), Steuerfrei-Hinweis und AGB-Fußnote.',
      'Zahlen und Datum im deutschen Format (z. B. „42.150,00", „29.04.2026") — auch auf englischen Belegen; Folgeseiten mit Kopfzeile „Seite X zu …" und „Fortsetzung auf Seite".',
    ],
  },
  {
    version: '3.2.0',
    date: '2026-06-20',
    title: 'Anmeldung: neues, professionelles Login-Design',
    tag: 'improvement',
    changes: [
      'Der Anmeldebildschirm ist neu gestaltet: zweispaltiges Layout mit dunklem Markenpanel (Produktname, Kurzbeschreibung, Highlights) links und aufgeräumtem Formular rechts — statt der bisherigen kleinen Box in viel Leerraum.',
      'Klarere Hierarchie mit Begrüßung und Hilfetexten, Platzhaltern in den Feldern und einheitlicher Fehlermeldung; voll responsiv (mobil gestapelt) und dark-mode-tauglich.',
      'Die Zwei-Faktor-Schritte (QR-Einrichtung und Code-Bestätigung) wurden ins neue Layout übernommen — Ablauf und Sicherheit unverändert.',
    ],
  },
  {
    version: '3.1.0',
    date: '2026-06-20',
    title: 'Verkauf 2: Belegerfassung neu gedacht (Prototyp)',
    tag: 'feature',
    changes: [
      'Neues Modul „Verkauf 2" (Sidebar unter Vertrieb & Projekte) erprobt eine hochwertigere, ruhigere Belegerfassung — gleiche Daten wie der klassische Verkauf, nur ein neues Erfassungserlebnis.',
      'Zwei umschaltbare Ansichten: „Belegblatt" (der Editor als ruhiges, zentriertes Dokument mit Briefkopf und randlosem Positionsraster) und „Werkbank" (Workspace mit horizontalem Kopf-Streifen, Raster über die volle Breite und fixer Summenleiste).',
      'Die bewährten Tabellen-Funktionen bleiben: Excel-Block per ⌘V einfügen, Ausfüll-Punkt der aktiven Zelle nach unten ziehen, Tastatur-Navigation, Inline-Artikelsuche, Abschnitts-/Textzeilen und Live-Summen.',
    ],
  },
  {
    version: '3.0.0',
    date: '2026-06-19',
    title: 'Beleg-PDFs im Büroware-Layout',
    tag: 'improvement',
    changes: [
      'Die Positionstabelle übernimmt das vertraute Büroware-Layout: Bezeichnung breit links, dann Menge, Einzelpreis, (Rabatt), Gesamt — und die Artikel-Nr. steht jetzt ganz rechts.',
      'Beträge erscheinen ohne €-Zeichen (reine Zahlen wie in der Büroware); die Währung steht als Code (z. B. EUR) bei den Summen.',
      'Überschrifts- und Textpositionen laufen über die volle Breite (ohne Mengen-/Preisspalten); Überschriften fett.',
    ],
  },
  {
    version: '2.9.0',
    date: '2026-06-19',
    title: 'Kunden-/Adress-Auswahl: blitzschnell (Server-Suche)',
    tag: 'improvement',
    changes: [
      'Die Auswahl-Dialoge (Kunde/Adresse — bei Beleg, Projekt, Ansprechpartner, Wartung) öffnen jetzt sofort, statt mehrere Sekunden zu laden: Sie holen nur noch die Treffer zur Eingabe vom Server (Server-Suche), statt den gesamten Kundenstamm zu laden und alle Zeilen zu rendern.',
      'Während der Suche erscheint ein kleiner Lade-Indikator; die Eingabe ist entprellt (eine Abfrage erst nach kurzer Tipppause).',
      'Gefunden wird weiterhin nach Nummer, Name und Ort — der bereits gewählte Eintrag bleibt sichtbar, auch wenn er nicht in den aktuellen Treffern ist.',
    ],
  },
  {
    version: '2.8.0',
    date: '2026-06-19',
    title: 'Briefpapier als PDF + Helvetica-Schrift',
    tag: 'feature',
    changes: [
      'Das Briefpapier lässt sich jetzt auch als PDF hinterlegen (empfohlen, vektor-scharf): Der Beleg-Inhalt wird passgenau auf jede Seite des PDF-Briefpapiers gestempelt. Bild (PNG/JPEG) bleibt ebenfalls möglich.',
      'Beleg-PDFs verwenden jetzt durchgängig die Schriftart Helvetica.',
    ],
  },
  {
    version: '2.7.0',
    date: '2026-06-19',
    title: 'Kunden-/Adress-Auswahl: Suche repariert',
    tag: 'fix',
    changes: [
      'Die Suche in den Auswahl-Dialogen (Kunde/Adresse — bei Beleg, Projekt, Ansprechpartner, Wartung) funktioniert wieder: Sie filtert jetzt korrekt nach Nummer, Name und Ort. Vorher lieferte z. B. eine Nummern-Suche willkürliche Treffer, weil intern gegen die technische ID gematcht wurde.',
      'Die Dialoge laden jetzt den vollständigen Kundenstamm (nicht nur die ersten 200) — gesuchte Kunden werden damit zuverlässig gefunden.',
      'Die Suche ist umlaut-/akzenttolerant und verknüpft mehrere Begriffe mit UND (z. B. „müller wien").',
    ],
  },
  {
    version: '2.6.0',
    date: '2026-06-19',
    title: 'Eigenes Briefpapier für Beleg-PDFs',
    tag: 'feature',
    changes: [
      'In den Einstellungen lässt sich jetzt ein eigenes Briefpapier hinterlegen (A4-Bild, PNG/JPEG, mit fertigem Kopf und Fuß). Es wird als vollflächiger Hintergrund auf jede Seite der Beleg-PDFs gelegt.',
      'Bei hinterlegtem Briefpapier entfällt der automatische Text-Briefkopf/-Fuß; der Beleg-Inhalt (Empfänger, Titel, Positionen, Summen) wird passend in die Mitte gesetzt, nur die Seitenzahl bleibt.',
      'Ohne Briefpapier funktioniert alles wie bisher (Briefkopf/Fuß aus den Firmenstammdaten).',
    ],
  },
  {
    version: '2.5.0',
    date: '2026-06-19',
    title: 'Belegerfassung: Tabellen-Eingabe wie in einer Tabellenkalkulation',
    tag: 'improvement',
    changes: [
      'Die Positionstabelle in Angeboten/Aufträgen/Rechnungen und in Bestellungen hat jetzt eine klare Raster-Optik (Zellenlinien, Typ-Symbole im Spaltenkopf) und fühlt sich beim Erfassen flüssiger an — die aktive Zelle ist deutlich umrandet.',
      'Neu: Ganze Tabellenblöcke lassen sich aus Excel kopieren und mit Strg+V / ⌘V direkt in die Positionen einfügen. Die Spalten werden ab der angeklickten Zelle der Reihe nach übernommen; fehlende Zeilen entstehen automatisch und passende Artikelnummern werden verknüpft.',
      'Neu: Der Ausfüll-Punkt am unteren rechten Rand der aktiven Zelle füllt einen Wert (z. B. Einheit, Menge, Rabatt) durch Ziehen nach unten in weitere Zeilen — wie in einer Tabellenkalkulation.',
    ],
  },
  {
    version: '2.4.0',
    date: '2026-06-19',
    title: 'Belegpositionen: Positionsart aus dem Export',
    tag: 'feature',
    changes: [
      'Der neue Positions-Export enthält eine Spalte „Positionsart" — diese wird jetzt direkt ausgewertet: K und T = Überschrift, Ziffern = Artikel, Z = Text, E/leer = Leerzeile. (Bisher musste der Typ geraten werden.)',
      'Reihenfolge strikt nach der laufenden Nummer (interne_lfdnr) — die niedrigste Nummer steht oben. Einträge wie „Übernahme von Auftrag/Angebot …" erscheinen als eigene Überschriften.',
      'Z-Textzeilen direkt nach einem Artikel wandern in dessen Langtext; lose oder nach einer Überschrift bleiben sie eigene Textzeilen. Gruppenenden/Leerzeilen bleiben erhalten; inhaltslose Zeilen ohne laufende Nummer werden verworfen.',
    ],
  },
  {
    version: '2.3.0',
    date: '2026-06-19',
    title: 'Import: Belege werden mit Projekten verknüpft',
    tag: 'feature',
    changes: [
      'Beim Beleg-Import wird aus der Projektnummer automatisch ein Projekt angelegt (Code = Projektnummer, Name = Projektname, Kunde übernommen) und der Beleg daran gehängt — die zentrale Projekt-Klammer ist damit durchgängig vernetzt.',
      'Idempotent: bestehende Projekte (gleiche Nummer) werden wiederverwendet, nicht doppelt angelegt.',
      'Damit ist die gesamte Importkette verbunden: Kunde ↔ Ansprechpartner ↔ Beleg ↔ Position ↔ Artikel ↔ Projekt.',
    ],
  },
  {
    version: '2.2.0',
    date: '2026-06-18',
    title: 'Verkauf: Geschäftsjahr-Filter',
    tag: 'improvement',
    changes: [
      'Die Belegliste (Verkauf) zeigt jetzt standardmäßig nur das laufende Geschäftsjahr — das macht die Übersicht deutlich aufgeräumter.',
      'Oben rechts gibt es einen Jahres-Filter: gewünschtes Geschäftsjahr wählen oder „Alle Jahre" für den Gesamtbestand.',
      'Sortierung weiterhin nach Belegdatum, neueste zuerst.',
    ],
  },
  {
    version: '2.1.0',
    date: '2026-06-18',
    title: 'Artikel-Detailseite: Zeichnungen (PDF) & Lager-Visual',
    tag: 'feature',
    changes: [
      'Neuer Tab „Zeichnungen" auf der Artikel-Detailseite: technische Zeichnungen als PDF hochladen (Button oder Drag & Drop), mit Titel und Revision. Die ausgewählte Zeichnung wird direkt eingebettet angezeigt (Inline-PDF-Vorschau).',
      'Eine Zeichnung lässt sich als „Hauptzeichnung" markieren — sie erscheint dann als eingebettete Vorschau direkt in der Artikel-Übersicht.',
      'Übersicht aufgewertet: neue Radial-Anzeige (shadcn-Chart) für den Lagerbestand im Verhältnis zu Höchst-/Mindestbestand, plus Make/Buy- und Warengruppen-Kennzeichnung im Kopf.',
    ],
  },
  {
    version: '2.0.0',
    date: '2026-06-18',
    title: 'Belegpositionen: Artikelnummer-Pflicht entschärft & importierte Nummern erkannt',
    tag: 'fix',
    changes: [
      'Eine Position lässt sich jetzt speichern, sobald sie eine Artikelnummer trägt (auch die beim Import übernommene) — die verknüpfte Artikel-ID ist nicht mehr zwingend. Text- und Überschriftszeilen brauchen ohnehin keine Artikelnummer.',
      'Importierte Artikelnummern werden beim Öffnen eines Belegs automatisch mit dem Artikelstamm abgeglichen und als echter Artikel erkannt (sofern die Nummer im Stamm existiert).',
    ],
  },
  {
    version: '1.9.0',
    date: '2026-06-18',
    title: 'Kunden-Dialog: Sektionen als Karten',
    tag: 'improvement',
    changes: [
      'Der Kunden-Dialog gliedert sich jetzt in klar abgegrenzte Karten (Firma, Kontaktdaten, Adresse, Konditionen, Notizen) — zweispaltig auf großen Bildschirmen, einspaltig auf kleinen. Übersichtlicher und ruhiger als die vorherige lose Anordnung.',
    ],
  },
  {
    version: '1.8.0',
    date: '2026-06-18',
    title: 'Tabellen: doppelter Scrollbalken entfernt',
    tag: 'fix',
    changes: [
      'In breiten Listen erschien neben dem Seiten-Scrollbalken ein zweiter, vertikaler Scrollbalken auf der Tabelle selbst. Ursache war, dass das horizontale Scrollen implizit auch vertikales Scrollen aktivierte. Jetzt scrollt die Tabelle nur horizontal, vertikal die Seite — ein Scrollbalken.',
    ],
  },
  {
    version: '1.7.0',
    date: '2026-06-18',
    title: 'Ansprechpartner als PDF',
    tag: 'feature',
    changes: [
      'In der Detailansicht eines Ansprechpartners (CRM › Ansprechpartner) gibt es jetzt einen „PDF“-Button. Er erzeugt ein Kontaktdatenblatt mit Firmen-Briefkopf und -Fußzeile (gleiche Vorlage wie die Belege).',
      'Das Datenblatt enthält Name, Position/Abteilung, Kontaktdaten (E-Mail, Telefon, Mobil, intern, Fax, Website), die zugehörige Organisation sowie persönliche Angaben und Notizen — leere Felder werden ausgelassen.',
    ],
  },
  {
    version: '1.6.0',
    date: '2026-06-18',
    title: 'Belegerfassung: Positionstyp (Artikel/Text/Überschrift)',
    tag: 'feature',
    changes: [
      'Die Positionstabelle in Angeboten/Aufträgen/Rechnungen hat jetzt eine Spalte „Typ": Positionen lassen sich als Artikel, Text oder Überschrift erfassen.',
      'Text- und Überschriftszeilen brauchen keinen Artikel/Menge/Preis (diese Felder sind ausgeblendet) und fließen nicht in die Summen ein — ideal zum Gliedern und Kommentieren von Belegen.',
    ],
  },
  {
    version: '1.5.0',
    date: '2026-06-18',
    title: 'Tabellen: Mehrfachauswahl nur auf Wunsch',
    tag: 'improvement',
    changes: [
      'Die Auswahl-Checkboxen (Bulk-Bearbeitung) werden in Listen nicht mehr automatisch angezeigt. Über den neuen Button „Auswählen" lassen sie sich einblenden; „Fertig" beendet den Auswahlmodus und hebt die Auswahl auf. Das hält die Tabellen aufgeräumt.',
    ],
  },
  {
    version: '1.4.0',
    date: '2026-06-18',
    title: 'Kunden-Dialog: breiter & zweispaltig',
    tag: 'improvement',
    changes: [
      'Der „Kunde anlegen/bearbeiten"-Dialog ist breiter und gliedert sich auf großen Bildschirmen in zwei Spalten (Firma/Kontakt links, Adresse/Konditionen/Notizen rechts) — deutlich weniger Scrollen, übersichtlicher.',
      'Die Typ-Auswahl (Kunde/Interessent/Lieferant) hat jetzt eine ruhigere, kompakte Breite.',
    ],
  },
  {
    version: '1.3.0',
    date: '2026-06-18',
    title: 'Belegpositionen mit dem Artikelstamm verknüpft',
    tag: 'fix',
    changes: [
      'Beim Positionsimport wurde die Artikelnummer bisher nur als Text gespeichert, aber nicht mit dem echten Artikel verknüpft. Jetzt wird der Artikelcode (z. B. „mont.wart") auf den Stammartikel aufgelöst — Voraussetzung: die Artikel sind vor den Positionen importiert.',
      'Gilt auch für Bestellpositionen (Einkauf).',
    ],
  },
  {
    version: '1.2.0',
    date: '2026-06-18',
    title: 'Artikel-Auswahl findet alle Artikel',
    tag: 'fix',
    changes: [
      'Bei der Positionserfassung (Angebote, Aufträge, Rechnungen, Bestellungen, Projekt-Material) wurden in der Artikel-Auswahl nur die ersten 200 Artikel durchsucht — gesuchte Artikel wie „mont.wart" wurden fälschlich als „Keine Treffer" angezeigt.',
      'Jetzt wird der vollständige Artikelkatalog (auch inaktive Artikel) geladen und durchsucht; die Auswahl wird zwischengespeichert.',
    ],
  },
  {
    version: '1.1.0',
    date: '2026-06-18',
    title: 'Belegpositionen: Textzeilen im Langtext & Artikelnummer sichtbar',
    tag: 'feature',
    changes: [
      'Beim Positionsimport werden reine Textzeilen (Z-Positionen ohne eigene Positionsnummer/Artikel) jetzt in den Langtext der zugehörigen Position eingefügt, statt als separate Zeilen zu erscheinen — die Belege bleiben übersichtlich.',
      'Die Artikelnummer wird in der Beleg-Positionsliste angezeigt (vor der Bezeichnung), in Desktop- und Mobilansicht.',
    ],
  },
  {
    version: '1.0.0',
    date: '2026-06-18',
    title: 'Verkaufspositionen: Reihenfolge, Einheit & Typ',
    tag: 'feature',
    changes: [
      'Der neue Verkaufspositionen-Export wird unterstützt: Die Reihenfolge der Positionen wird über die laufende Nummer (interne_lfdnr) korrekt übernommen — Textzeilen stehen direkt bei ihrem Artikel.',
      'Mengeneinheit und Positionsrabatt werden mitimportiert; Positionen werden weiterhin über Belegart + Belegnummer mit dem Beleg (und damit der Projektnummer) verknüpft.',
      'Positionstyp wird abgeleitet: Zeilen mit Artikelcode sind Artikel, Zeilen ohne (z. B. Langtext-Zeilen wie „TARIC CODE…") sind Textpositionen und werden ohne Schein-Menge/-Preis dargestellt.',
      'Auto-Erkennung der Zieltabelle erkennt jetzt auch die Dateinamen „belege_verkauf" und „verkaufspositionen".',
    ],
  },
  {
    version: '0.99.0',
    date: '2026-06-18',
    title: 'UI-Feinschliff: Status-Farben & sanftere Tabellen',
    tag: 'improvement',
    changes: [
      'Status-Badges und Hinweis-Banner (Aktiv, Interessent, Warnungen, Fehler) sind jetzt dark-mode-tauglich: dezente, themenkonforme Flächen mit gut lesbarer Schrift statt greller heller Pillen. Im hellen Modus unverändert.',
      'Tabellenzeilen blenden ihren Hover-Zustand jetzt weich ein (sanfte Farbübergänge).',
    ],
  },
  {
    version: '0.98.0',
    date: '2026-06-18',
    title: 'Import-Fix: Auto-Mapping auch ohne HTTPS',
    tag: 'fix',
    changes: [
      'Der CSV-Import (Drag & Drop) brach beim Hinzufügen einer Datei ab, wenn die App ohne HTTPS aufgerufen wird (z. B. intern über http://server) — dadurch erschien keine Datei-Karte und das Auto-Mapping schien nicht zu funktionieren. Die ID-Erzeugung nutzt jetzt einen Fallback und funktioniert in jedem Kontext.',
      'Schlägt das Einlesen einer Datei fehl, blockiert das nicht mehr die übrigen Dateien; stattdessen erscheint ein klarer Hinweis.',
    ],
  },
  {
    version: '0.97.0',
    date: '2026-06-18',
    title: 'Belegpositionen: Positionstypen (Artikel/Text/Überschrift)',
    tag: 'fix',
    changes: [
      'Belegpositionen unterscheiden jetzt die Büroware-Positionstypen: 0 = Artikel, Z = Text, K = Überschrift. Text- und Überschriftszeilen werden im Beleg ohne Schein-Menge/-Preis dargestellt, Überschriften hervorgehoben.',
      'Die Positionsnummer wird korrekt vom Typkennzeichen getrennt: „0000040" ist Position 4 (nicht 40), „000019K" ist Position 19 (Überschrift).',
      'Deutsche Mengen wie „1,00" werden beim Import korrekt als 1 übernommen (vorher 0).',
    ],
  },
  {
    version: '0.96.0',
    date: '2026-06-18',
    title: 'Tabellen: einzeilige Zeilen, lange Inhalte gekappt',
    tag: 'improvement',
    changes: [
      'Tabellenzeilen sind jetzt durchgehend einzeilig — kein Umbruch mehr auf zwei Zeilen.',
      'Zu lange Zell-Inhalte werden am Spaltenende sauber mit „…" abgeschnitten, statt die Zeile in die Höhe zu ziehen.',
    ],
  },
  {
    version: '0.95.0',
    date: '2026-06-18',
    title: 'CSV-Import: Drag & Drop, Bulk & Vorschau',
    tag: 'feature',
    changes: [
      'Mehrere CSV-Dateien per Drag & Drop auf einmal hochladen — je Datei wird die Zieltabelle automatisch erkannt (Dateiname + Spalten) und das Spalten-Mapping vorbelegt.',
      'Vorschau je Datei: erste Zeilen mit erkannter Feld-Zuordnung; Warnungen bei fehlenden Pflichtfeldern und nicht zugeordneten Spalten.',
      'Der passende Zeilenfilter (satzart=S/L, belegart) wird automatisch vorgeschlagen; Zuordnung & Filter bleiben pro Datei anpassbar.',
      '„Alle importieren" startet die Läufe in korrekter Reihenfolge (z. B. Kunden vor Belegen).',
    ],
  },
  {
    version: '0.94.0',
    date: '2026-06-18',
    title: 'Datenbank: „Tabelle leeren" funktioniert jetzt (mit Abhängigkeiten)',
    tag: 'fix',
    changes: [
      'Das Leeren einer Tabelle scheiterte bisher an Verknüpfungen (Fremdschlüssel). Jetzt werden abhängige Datensätze in der richtigen Reihenfolge mitgelöscht — in einer Transaktion und mandantengetrennt.',
      'Der Bestätigungsdialog zeigt vorab, welche abhängigen Tabellen ebenfalls geleert werden (mit Zeilenzahl). Lässt sich keine sichere Mandantentrennung herstellen, wird das Leeren blockiert.',
    ],
  },
  {
    version: '0.93.0',
    date: '2026-06-18',
    title: 'Belegpositionen importieren (mit Projektbezug)',
    tag: 'feature',
    changes: [
      'Belegpositionen lassen sich jetzt importieren: Jede Position wird über Belegart + Belegnummer automatisch mit ihrem Beleg verknüpft. Positionen ohne passenden Verkaufsbeleg (z. B. Einkaufsbelege) werden übersprungen.',
      'Die Projektnummer wird vom Beleg-Kopf auf jede Position übernommen — so lassen sich Positionen direkt nach Projekt auswerten, obwohl die Position selbst keine Projektspalte hat.',
      'Deutsche Mengen-/Zahlenangaben (z. B. „1,00") werden beim Import jetzt korrekt erkannt; Positionsnummern wie „000002T" werden auf die führende Zahl reduziert.',
      'Auto-Feldzuordnung auch für Belegpositionen (Belegart, Belegnummer, Position, Artikelcode, Bezeichnung, Menge, Einzelpreis).',
    ],
  },
  {
    version: '0.92.0',
    date: '2026-06-18',
    title: 'Import: automatische Feldzuordnung',
    tag: 'improvement',
    changes: [
      'Beim Hochladen einer CSV werden die Spalten jetzt automatisch den richtigen Zielfeldern zugeordnet — auch bei deutschen SoftENGINE-Spaltennamen (z. B. „artikelnummer", „bezeichnung_de", „ek_preis", „vk_preis2", „lagerbestand").',
      'Die Erkennung ist schreibweisen-tolerant (Groß-/Kleinschreibung, Umlaute/ß, Bindestriche/Unterstriche) und kennt für jedes Feld die gängigen deutschen und englischen Spaltenbezeichnungen.',
      'Damit entfällt das mühsame manuelle Zuordnen von ~30 Artikel-Feldern; eine Spalte wird höchstens einmal vergeben, Unpassendes bleibt frei und kann von Hand gesetzt werden.',
    ],
  },
  {
    version: '0.91.0',
    date: '2026-06-18',
    title: 'Datenbank: Tabelle leeren',
    tag: 'feature',
    changes: [
      'Im Modul Datenbank lassen sich jetzt alle Datensätze einer ausgewählten Tabelle gezielt löschen („Tabelle leeren"). Mehrfach abgesichert: Bestätigungsdialog mit Anzeige der Zeilenzahl und Pflicht-Eingabe des Tabellennamens, serverseitige Token-Prüfung, Beschränkung auf erlaubte Tabellen, Mandanten-Scope und Audit-Eintrag.',
    ],
  },
  {
    version: '0.90.0',
    date: '2026-06-18',
    title: 'Lieferadresse ↔ Stammkunde verlinkt',
    tag: 'fix',
    changes: [
      'Lieferadressen sind jetzt zuverlässig mit dem Stammkunden verknüpft: Der Bezug wird über die Kundennummer aufgelöst – auch bei bereits importierten Adressen, bei denen intern noch keine Kunden-ID hinterlegt war.',
      'Detail- und Listenansicht zeigen zusätzlich den Kundennamen; der Stammkunde ist von der Lieferadresse aus anklickbar.',
      'Der „Lieferadressen"-Tab beim Kunden findet auch Adressen, die nur über die Kundennummer (nicht über die interne ID) zugeordnet sind.',
    ],
  },
  {
    version: '0.89.0',
    date: '2026-06-18',
    title: 'Ansprechpartner: eigene Detailseite',
    tag: 'improvement',
    changes: [
      'Ein Klick auf einen Ansprechpartner öffnet jetzt eine vollständige Detailseite (statt eines Modals) mit allen Daten — Kontaktwege, Position/Abteilung, verknüpfte Adresse und Notizen — inklusive Brotkrumen-Navigation (CRM › Ansprechpartner › Name) sowie Bearbeiten/Löschen.',
    ],
  },
  {
    version: '0.88.0',
    date: '2026-06-18',
    title: 'Lieferadressen-Import: mehr Felder übernommen',
    tag: 'fix',
    changes: [
      'Der Name einer Lieferadresse setzt sich jetzt aus allen gepflegten Namenszeilen (1–3) zusammen – bisher gingen Zeile 2 und 3 verloren, wenn der Komplettname separat zugeordnet war.',
      'Das Länderkennzeichen wird bereinigt: führende Ziffern aus Werten wie „51IT" oder „04DE" werden entfernt, sodass „IT"/„DE" im Feld Land landet.',
      'Lieferadressen werden über die Kundennummer (bei SoftENGINE-L-Sätzen die Spalte „kurzname") mit dem Stammkunden verknüpft; der interne Kundenbezug wird dabei automatisch aufgelöst.',
      'Zusätzlich lässt sich nun das Notizfeld einer Lieferadresse importieren.',
    ],
  },
  {
    version: '0.87.0',
    date: '2026-06-18',
    title: 'Kunde: Tab „Lieferadressen"',
    tag: 'feature',
    changes: [
      'Die Kunden-Detailseite hat einen neuen Tab „Lieferadressen": zeigt alle dem Kunden zugeordneten Liefer-/Zusatzadressen mit Adresse und Kontakt — jede Zeile verlinkt auf die Lieferadressen-Detailseite.',
      'Das Lieferadressen-Verzeichnis lässt sich serverseitig nach Kunde filtern (neuer customerId-Parameter).',
    ],
  },
  {
    version: '0.86.0',
    date: '2026-06-18',
    title: 'Lieferadressen: Detailseite',
    tag: 'feature',
    changes: [
      'Ein Klick auf eine Lieferadresse öffnet jetzt eine eigene Detailseite mit allen Stammdaten (Anschrift, Kontakt, Matchcode, Notizen).',
      'Ist ein Stammkunde hinterlegt, führt ein Link von der Lieferadresse direkt zum Kunden.',
    ],
  },
  {
    version: '0.85.0',
    date: '2026-06-18',
    title: 'Beleg-Import (Verkauf)',
    tag: 'feature',
    changes: [
      'Verkaufsbelege lassen sich jetzt aus dem SoftENGINE-Beleg-Export importieren: Belegart-Codes (N/A/R/L/G) werden automatisch in Angebot, Auftrag, Rechnung, Lieferschein und Gutschrift übersetzt.',
      'Deutsche Datumsangaben (TT.MM.JJJJ) werden beim Import korrekt erkannt; unbekannte Währungen fallen auf EUR zurück; der Steuersatz wird aus Netto/Steuer abgeleitet, wenn er nicht in der Datei steht.',
      'Mehr Beleg-Kopffelder importierbar: Fälligkeit, Lieferdatum, Projektnummer, Zahlungsziel, Skonto, Versandart, Gewicht, Bezugstext und Anschrift.',
      'Der Zeilenfilter akzeptiert nun mehrere Werte (z. B. „N,A,R,L,G"), sodass alle Verkaufsbelegarten in einem Lauf importiert werden können.',
    ],
  },
  {
    version: '0.84.0',
    date: '2026-06-18',
    title: 'Tabellen: wirklich alle Felder als Spalten einblendbar',
    tag: 'fix',
    changes: [
      'Die Spalten-Auswahl der großen Listen (Kunden, Ansprechpartner, Artikel) zeigte bisher nur einen Teil der vorhandenen Felder. Grund: Die API gab mehrere Stammfelder gar nicht aus.',
      'Kunden geben jetzt zusätzlich Land (Klartext), Matchcode, Telefax, Steuergebiet, Adressart-Code und Belegsperre aus.',
      'Ansprechpartner ergänzen Anrede, Titel, Geschlecht, Geburtsdatum, Sprache, Telefax, interne Telefonnummer und Website.',
      'Artikel liefern Kategorie, Ø-EK, alle sechs VK-Preisstufen samt Staffelmengen, Höchstbestand, Matchcode, Alt-Artikelnummer, letzten Verkauf sowie die Kennzeichen „gesperrt" und „Stückliste".',
      'Die Spaltenliste sammelt ihre Felder nun robust über mehrere Zeilen statt nur der ersten.',
    ],
  },
  {
    version: '0.83.0',
    date: '2026-06-18',
    title: 'Datenimport: Läufe löschen',
    tag: 'improvement',
    changes: [
      'Abgeschlossene Import-Läufe lassen sich aus der Historie entfernen (Papierkorb je Lauf oder „Erledigte aufräumen"). Die bereits importierten Daten bleiben dabei erhalten — gelöscht wird nur der Protokolleintrag.',
      'Laufende Importe können nicht gelöscht werden (erst abbrechen).',
      'Import-Bericht zeigt jetzt auch die Anzahl importierter Lieferadressen.',
    ],
  },
  {
    version: '0.82.0',
    date: '2026-06-18',
    title: 'Kunden-Dialog modernisiert + Adresse automatisch aufteilen',
    tag: 'improvement',
    changes: [
      'Der „Neuer Kunde"-Dialog ist klarer und moderner: Typ (Kunde/Interessent/Lieferant) als segmentierte Auswahl oben, Abschnitte mit Icon-Köpfen, Sprache als Auswahlfeld.',
      'Neues Feld „Adresse einfügen": eine komplette Anschrift einfügen oder eintippen — sie wird automatisch auf Straße, PLZ, Ort und Land verteilt (DACH-Formate, ein-/mehrzeilig oder kommasepariert).',
    ],
  },
  {
    version: '0.81.0',
    date: '2026-06-18',
    title: 'CRM: Lieferadressen',
    tag: 'feature',
    changes: [
      'Neues CRM-Submenü „Lieferadressen" mit eigener Liste (Suche, Spalten ein-/ausblenden, Export).',
      'Neuer Import-Typ „Lieferadressen" (satzart=L) — die 486 Liefer-/Zusatzadressen aus dem Altsystem lassen sich nun importieren und in der Datenbank bearbeiten.',
    ],
  },
  {
    version: '0.80.0',
    date: '2026-06-18',
    title: 'Artikel: VK-Staffelmengen',
    tag: 'improvement',
    changes: [
      'Zu jeder der sechs VK-Preisstufen lässt sich nun auch die zugehörige Staffelmenge erfassen/importieren (Preis ab Menge X).',
    ],
  },
  {
    version: '0.79.0',
    date: '2026-06-18',
    title: 'Artikel-Import erweitert (alle Stammfelder + 6 VK-Preise)',
    tag: 'improvement',
    changes: [
      'Der Artikel-Import übernimmt jetzt das volle Feldset: Bezeichnung DE/EN, Matchcode, Alt-Code, Einheit (normalisiert), Warengruppe, Gewicht, Bestand, Mindest-/Höchstbestand, Standardlieferant, letzter Verkauf, gesperrt- und Stücklisten-Kennzeichen sowie aktiv/inaktiv.',
      'Alle sechs VK-Preisstufen (VK-Preis 1–6) und der Ø-EK-Preis werden mitgeführt; EUR-Beträge werden korrekt in interne Mikro-Einheiten umgerechnet.',
    ],
  },
  {
    version: '0.78.0',
    date: '2026-06-18',
    title: 'Listen auf dem Smartphone als Karten',
    tag: 'improvement',
    changes: [
      'Alle Tabellen-Listen (Verkauf, CRM, Ansprechpartner, Beschaffung, Projekte …) werden auf schmalen Bildschirmen jetzt als übersichtliche Karten dargestellt — statt horizontalem Scrollen mit abgeschnittenen Spalten. Pro Eintrag: Haupttext oben, restliche Felder als Beschriftung/Wert, antippbar.',
      'Damit ist die App durchgängig touch-freundlich: versteckte Seitenleiste mit Bottom-Navigation, volle Inhaltsbreite und keine horizontalen Scroll-Tabellen mehr.',
    ],
  },
  {
    version: '0.77.0',
    date: '2026-06-18',
    title: 'Import: CRM-Aktivitäten + Status/Belegsperre/Notizen',
    tag: 'feature',
    changes: [
      'Neuer Import-Typ „CRM-Aktivitäten": der Aktivitäten-Verlauf je Kunde (eine Zeile = ein Eintrag) wird in einzelne CRM-Aktivitäten zerlegt — mit Datum, Betreff/Inhalt und automatisch erkanntem Typ (Anruf/E-Mail/Termin/Notiz).',
      'Beim Kunden-Import werden jetzt auch Inaktiv-Kennzeichen (→ Status), Belegsperre (neues Feld) und Notizen übernommen.',
      'Mehrere Datumsformate im Verlauf werden verstanden (2026-01-31, 99/09/14, 31.01.2026); der Import ist wiederholbar ohne Dubletten.',
    ],
  },
  {
    version: '0.76.0',
    date: '2026-06-18',
    title: 'Ansprechpartner: Detailansicht & feineres Verzeichnis',
    tag: 'improvement',
    changes: [
      'Klick auf eine Zeile im Ansprechpartner-Verzeichnis öffnet jetzt eine Detailansicht (Modal) mit allen Infos: Kontaktwege (E-Mail/Telefon/Mobil direkt anwählbar), verknüpfte Adresse, Notizen und „zuletzt geändert“ — inklusive Bearbeiten/Löschen.',
      'Das Verzeichnis ist aufgeräumter: Initialen-Avatar je Person als visueller Anker in der Namensspalte.',
    ],
  },
  {
    version: '0.75.0',
    date: '2026-06-18',
    title: 'Tabellen: alle Felder als Spalten ein-/ausblendbar',
    tag: 'improvement',
    changes: [
      'Im „Spalten"-Menü lassen sich jetzt ALLE verfügbaren Felder der Tabelle ein-/ausblenden — nicht nur die vordefinierten (z. B. bei Kunden: USt-IdNr., Matchcode, Telefax, Land, Steuergebiet, Adressart-Code, Zahlungsziel …).',
      'Zusätzliche Spalten sind anfangs ausgeblendet; die Auswahl wird je Liste gespeichert und lässt sich mit „Zurücksetzen" auf den Standard bringen.',
    ],
  },
  {
    version: '0.74.0',
    date: '2026-06-18',
    title: 'Kunden-Import: Interessenten + Altsystem-Kennungen',
    tag: 'improvement',
    changes: [
      'Beim Import wird die Adressart jetzt dreigeteilt erkannt: „Lieferant" → Lieferant, „Kunde/Interessent" → Interessent, „Kunde" → Kunde (vorher alles außer Lieferant = Kunde).',
      'Zwei neue Felder aus dem Altsystem werden übernommen: Steuergebiet (landesart) und Adressart-Code (20/21/22) — im Import-Mapping auswählbar.',
    ],
  },
  {
    version: '0.73.0',
    date: '2026-06-18',
    title: 'Parallelbetrieb mehrerer Sessions ohne Konflikte',
    tag: 'improvement',
    changes: [
      'Entwickler-Workflow: Protokoll für gleichzeitiges Arbeiten mehrerer Sessions (Modul-Aufteilung, Rebase vor jedem Merge) in CLAUDE.md §18.',
      'Versionierter pre-push-Git-Hook verhindert, dass ein Push „main" überschreibt, wenn parallel jemand anderes gemergt hat (Rebase wird erzwungen).',
      'CODEOWNERS für klare Modul-Zuständigkeiten und ein Worktree-Helfer für lokalen Parallelbetrieb.',
    ],
  },
  {
    version: '0.72.0',
    date: '2026-06-18',
    title: 'Sicherheits-Härtung (Header, Rate-Limit, CORS)',
    tag: 'improvement',
    changes: [
      'Sicherheits-Header (Content-Security-Policy, X-Frame-Options, HSTS u. a.) schützen die App gegen Clickjacking, MIME-Sniffing und eingeschleuste Skripte.',
      'Rate-Limit gegen Brute-Force/Überlastung: striktes Limit für Login/2FA, großzügiges Gesamtlimit pro IP (Health-Checks ausgenommen).',
      'CORS ist standardmäßig geschlossen (nur same-origin); fremde Frontend-Hosts müssen explizit freigegeben werden. Request-Timeout wieder begrenzt (Schutz vor Slow-DoS).',
    ],
  },
  {
    version: '0.71.0',
    date: '2026-06-17',
    title: 'Eigene Analyse-Seite je Modul',
    tag: 'feature',
    changes: [
      'Neue „Analyse"-Seiten in Verkauf, CRM, Artikel & Lager, Beschaffung und Projekten — erreichbar über den Button „Analyse" in der jeweiligen Liste.',
      'Verkauf: Umsatzverlauf, Top-Kunden, Angebots-Trichter, Belegarten und Status. CRM: Adressen nach Art/Status und umsatzstärkste Kunden.',
      'Artikel & Lager: Lagerwert je Warengruppe, größte Unterdeckung, Beschaffungs- und Sortimentsmix. Beschaffung: Bestellvolumen, Status, Top-Lieferanten.',
      'Projekte: Ampel, Phasenverteilung, Auftragswert je Phase und Projektarten.',
      'Auswertungen über große Datenbestände (Kunden, Artikel) laden jetzt alle Sätze, damit Verteilungen stimmen.',
    ],
  },
  {
    version: '0.70.0',
    date: '2026-06-17',
    title: 'Datenleichen-Finder im Daten-Modul',
    tag: 'feature',
    changes: [
      'Neuer Tab „Datenleichen" im Daten-Modul: findet verwaiste Referenzen (z.B. Ansprechpartner, deren Kunde gelöscht wurde) und leere/unvollständige Datensätze (z.B. Kunden ohne Namen, Artikel ohne Nummer).',
      'Pro Prüfung lassen sich die betroffenen Zeilen ansehen, auswählen und gezielt endgültig löschen — verknüpfte oder inzwischen bereinigte Zeilen werden dabei übersprungen.',
      'Prüfungen ohne Befund werden zusammengeklappt, damit der Blick auf den tatsächlichen Aufräumbedarf frei bleibt.',
    ],
  },
  {
    version: '0.69.0',
    date: '2026-06-17',
    title: 'Barrierefreiheit, flüssige Listen & „Rückgängig" beim Löschen',
    tag: 'improvement',
    changes: [
      'Löschen lässt sich jetzt rückgängig machen: Die Zeile verschwindet sofort, ein Toast mit „Rückgängig" hält die Aktion 5 Sekunden offen, erst danach wird endgültig gelöscht (Kunden, Ansprechpartner, Artikel).',
      'Barrierefreiheit: deutlich sichtbarer Tastatur-Fokus, kontraststärkere Sekundärtexte (WCAG-AA), größere Touch-Ziele auf Touch-Geräten und Rücksicht auf „Bewegung reduzieren".',
      'Große Listen (über 80 Zeilen) rendern flüssiger — nicht sichtbare Zeilen werden vom Browser übersprungen.',
    ],
  },
  {
    version: '0.68.0',
    date: '2026-06-17',
    title: 'Spalten ein-/ausblenden + gespeicherte Ansicht je Liste',
    tag: 'improvement',
    changes: [
      'Neue Schaltfläche „Spalten" in allen großen Listen (Kunden, Ansprechpartner, Artikel, Belege, Bestellungen): einzelne Spalten lassen sich aus-/einblenden.',
      'Die Spaltenauswahl und die Zeilenhöhe (kompakt/normal) werden je Liste im Browser gespeichert und beim nächsten Besuch wiederhergestellt.',
      'Schlüsselspalten (Name, Aktionen) bleiben immer sichtbar.',
      'CSV-Export über alle gefilterten Treffer jetzt auch bei Ansprechpartnern, Belegen und Bestellungen (vorher nur die aktuelle Seite).',
    ],
  },
  {
    version: '0.67.0',
    date: '2026-06-17',
    title: 'Export aller Treffer + lesbarer Daten-Editor',
    tag: 'improvement',
    changes: [
      'CSV-Export bei Kunden und Artikeln umfasst jetzt alle gefilterten Treffer (nicht nur die aktuelle Seite).',
      'Im Daten-Editor werden Geldspalten als EUR und Datumsspalten lokal formatiert statt als Rohwerte.',
    ],
  },
  {
    version: '0.66.0',
    date: '2026-06-17',
    title: 'Dark Mode',
    tag: 'feature',
    changes: [
      'Neuer Hell/Dunkel-Umschalter in der Kopfleiste (Sonne/Mond). Die Wahl wird gespeichert; beim ersten Besuch wird die Systemeinstellung übernommen.',
    ],
  },
  {
    version: '0.65.0',
    date: '2026-06-17',
    title: 'Belege & Bestellungen: Sortieren, Filtern, Export',
    tag: 'improvement',
    changes: [
      'Verkaufsbelege (je Belegart) und Bestellungen nutzen jetzt die einheitliche Datentabelle: Sortierung per Spaltenkopf (Datum/Nummer/Betrag/Status), Status-Filter, kompakte Darstellung.',
      'CSV-Export der gefilterten Liste, wählbare Seitengröße und Pagination — wie bei Kunden/Artikeln.',
    ],
  },
  {
    version: '0.64.1',
    date: '2026-06-17',
    title: 'Dashboard: Umsatz-Trend ggü. Vorjahr',
    tag: 'improvement',
    changes: [
      'Die Umsatz-Kennzahl zeigt jetzt die Veränderung zum Vorjahr (Pfeil + Prozent). KPI-Kacheln verlinken weiterhin ins jeweilige Modul, die Umsatzkurve zeigt Hover-Tooltips.',
    ],
  },
  {
    version: '0.64.0',
    date: '2026-06-17',
    title: 'Listen-Fundament: Sortieren, Auswahl, Export (UI/UX-Pass 1)',
    tag: 'improvement',
    changes: [
      'Neue einheitliche Datentabelle für Kunden- und Ansprechpartner-Liste: Sortierung per Spaltenkopf, kompaktere Darstellung, Zeilen-Hover.',
      'Mehrfachauswahl mit Bulk-Aktionen (mehrere deaktivieren/löschen) und CSV-Export der gefilterten Liste.',
      'Aktive Filter als entfernbare Chips, wählbare Seitengröße (100/250/500) und Pagination.',
    ],
  },
  {
    version: '0.63.0',
    date: '2026-06-17',
    title: 'CRM-Listen: Pagination (500/Seite) & Filter',
    tag: 'improvement',
    changes: [
      'Kunden- und Ansprechpartner-Liste zeigen jetzt 500 Einträge pro Seite mit Vor/Zurück-Pagination statt einer gekappten Liste.',
      'Kunden filterbar nach Typ (Kunde/Lieferant/Interessent) und Status (aktiv/inaktiv); Ansprechpartner nach Adressart (Kunde/Lieferant) und „nur Hauptkontakte".',
      'Server-Pagination bis 500 Einträge erhöht; Filter laufen server-seitig über die ganze Datenmenge.',
    ],
  },
  {
    version: '0.62.1',
    date: '2026-06-17',
    title: 'Import: kompletten Namen direkt übernehmen',
    tag: 'improvement',
    changes: [
      'Beim Kunden-Import wird ein bereits zusammengesetztes Namensfeld (z. B. „name_komplett", inkl. 3. Namenszeile) bevorzugt übernommen; ansonsten weiterhin Fallback auf name1 + name2 + name3.',
    ],
  },
  {
    version: '0.62.0',
    date: '2026-06-17',
    title: 'Import: Zeilenfilter, Namen-Kombination & Kunden-Zusatzfelder',
    tag: 'feature',
    changes: [
      'Neuer optionaler Zeilenfilter beim CSV-Import (z. B. nur satzart = S) — so landen aus der Kundendatei nur die echten Stammadressen, keine Lieferadressen/Sonstige.',
      'Kundenname wird aus zwei Spalten (name1 + name2) zusammengesetzt; Typ Kunde/Lieferant wird aus „adressart" abgeleitet.',
      'Kunden haben neue Felder: Matchcode (Kurzname), Telefax und Land-Klartext (zusätzlich zum ISO-Code).',
    ],
  },
  {
    version: '0.61.0',
    date: '2026-06-17',
    title: 'Import: Verknüpfung per Nummer + Kontakt-Zusatzfelder',
    tag: 'feature',
    changes: [
      'CSV-Import löst Fremdschlüssel über die Nummer auf: Kontakte/Belege werden per Kundennummer (Bestellungen per Lieferantennummer) dem richtigen Datensatz zugeordnet — Voraussetzung: Kunden/Lieferanten sind vorher importiert.',
      'Kontakte haben neue Felder: Anrede, Titel, Geschlecht, Geburtsdatum, Sprache, Telefax, Telefon (intern) und Website — passend zum Ansprechpartner-Import.',
    ],
  },
  {
    version: '0.60.0',
    date: '2026-06-17',
    title: 'Import: CSV pro Tabelle (statt JSON-Bündel)',
    tag: 'feature',
    changes: [
      'Der Import läuft jetzt tabellenweise mit CSV: Ziel-Tabelle wählen, eine CSV hochladen, fertig — kein Manifest/JSON-Bündel mehr nötig.',
      'Trennzeichen (Komma/Semikolon/Tab) wird automatisch erkannt, Kopfzeile = Spaltennamen; Geldbeträge als EUR (z. B. „49,95") werden automatisch in Mikro-Einheiten umgerechnet.',
      'Feld-Zuordnung liest die CSV-Spalten und schlägt passende Felder vor; fehlt eine id-Spalte, wird eine ID erzeugt. Import bleibt additiv & idempotent (mit id-Spalte).',
    ],
  },
  {
    version: '0.59.0',
    date: '2026-06-17',
    title: 'Neues Modul „Datenbank“: Datensätze direkt bearbeiten',
    tag: 'feature',
    changes: [
      'Eigener Menüpunkt „Datenbank“ (System): Geschäftstabellen browsen, durchsuchen, paginiert anzeigen und Zeilen/Zellen bearbeiten, anlegen oder löschen.',
      'Sicher per Allowlist: nur Geschäftsdaten, keine Benutzer/Einstellungen; Schema-Spalten (id, tenant_id, Zeitstempel) sind schreibgeschützt. KEINE Schema-Änderungen — nur Daten. RBAC (data.read/data.write) + Audit.',
    ],
  },
  {
    version: '0.58.0',
    date: '2026-06-17',
    title: 'Import: Feld-Zuordnung (Mapping) vor dem Lauf',
    tag: 'feature',
    changes: [
      'Vor dem Import lassen sich je Entität die Quellfelder den ProERP-Zielfeldern zuordnen — vorbefüllt aus den Dateien, anpassbar, wenn Quellfelder anders heißen.',
      'Die Zuordnung wird am Job gespeichert und beim Lauf angewandt; „leer" lässt ein Feld weg. So lassen sich auch abweichend benannte NDJSON-Exporte importieren.',
    ],
  },
  {
    version: '0.57.0',
    date: '2026-06-17',
    title: 'Import: „Alle Daten löschen"-Button (Gefahrenzone)',
    tag: 'feature',
    changes: [
      'Neuer Button im Import-Modul, um vor einem Import sauber zu starten: löscht alle Geschäftsdaten (Kunden, Artikel, Belege, Projekte, Lager, HR …).',
      'Behalten bleiben Benutzer & Rechte, Einstellungen und die Dateiablage (DMS). Endgültig — daher mit Tippbestätigung („LÖSCHEN") und Audit-Eintrag.',
    ],
  },
  {
    version: '0.56.0',
    date: '2026-06-17',
    title: 'Import: NDJSON-Dateien statt SQLite-Upload',
    tag: 'feature',
    changes: [
      'Der Altdaten-Import nimmt jetzt die vorverarbeiteten NDJSON-Dateien je Entität plus manifest.json entgegen — kein 2,3-GB-SQLite-Upload mehr.',
      'Mehrere Dateien auf einmal auswählen (inkl. der geteilten …partNN.ndjson); sie werden gechunkt hochgeladen und in FK-Reihenfolge (Kunden → Artikel → Kontakte → Belege → Positionen → Bestellungen) idempotent importiert.',
      'Geld wird als Mikro-Einheiten übernommen, fehlende Pflichtfelder bekommen sichere Defaults. Gegen echte Daten verifiziert (4.252 Kunden / 1.890 Artikel / 5.397 Kontakte).',
    ],
  },
  {
    version: '0.55.1',
    date: '2026-06-17',
    title: 'Eigenes Import-Modul + stabiler Upload großer Dateien',
    tag: 'improvement',
    changes: [
      'Der Altdaten-Import ist jetzt ein eigener Menüpunkt „Import“ (System) — raus aus den Einstellungen.',
      'Die Alt-Datenbank wird in 16-MB-Stücken hochgeladen statt in einem einzigen riesigen Request — mit Fortschrittsanzeige in Prozent und ohne Timeout-Abbruch (zuvor HTTP 408 bei 2,3 GB).',
      'Server-Request-Timeout für große Uploads entfernt; Upload-Route korrigiert (zuvor „Not Found").',
    ],
  },
  {
    version: '0.55.0',
    date: '2026-06-17',
    title: 'Datenmigration: Altdaten-Import direkt in der App',
    tag: 'feature',
    changes: [
      'Neue Karte unter Einstellungen → „Datenmigration“: die Alt-Datenbank (SoftENGINE erp_clean.db, SQLite) hochladen — der Import läuft im Hintergrund.',
      'Geschrieben wird in eine separat konfigurierte Ziel-Datenbank; die laufenden Daten bleiben unberührt. Wiederholte Läufe sind idempotent (keine Dubletten).',
      'Importiert Kunden, Kontakte, Artikel, Belege/Bestellungen und Positionen inkl. korrekter Geld-Umrechnung, Belegart-Erkennung und Dedupe. Status + Validierungsbericht (Zeilenzahlen je Tabelle) live in der Übersicht.',
    ],
  },
  {
    version: '0.54.0',
    date: '2026-06-16',
    title: 'Belegerfassung neu: eine Maske, zwei Spalten',
    tag: 'improvement',
    changes: [
      'Der Beleg-Editor zeigt jetzt alle Kopfdaten und die Positionen auf einer Maske — keine Reiter mehr zwischen „Belegerfassung“ und „Positionen“.',
      'Links eine schlanke Kopf-Spalte: Adresswarntext, Kunden-/Lieferanten-Stammdaten (read-only) und alle Kopf-Felder als EINE Form mit klaren Abschnitten statt vieler Karten.',
      'Rechts bekommen die Positionen die volle Breite. Artikel-Infos, Bestand und Langtext erscheinen on demand: Zeile per Pfeil aufklappen — keine fixe dritte Spalte mehr.',
    ],
  },
  {
    version: '0.53.0',
    date: '2026-06-16',
    title: 'Beleg-PDF mit Lieferangaben + DMS-Treffer mit Ordnerpfad',
    tag: 'improvement',
    changes: [
      'Die neuen Kopf-Felder erscheinen jetzt auch auf dem Beleg-PDF: Liefertermin, Lieferzeit, Versandart, Gewicht und Bezug (DE/EN).',
      'Such-Ergebnisse für Dateien zeigen jetzt den Ordnerpfad als Untertitel (Kontext, „woher“). Die DMS-Volltextsuche (inkl. extrahiertem Dateiinhalt) war bereits aktiv.',
    ],
  },
  {
    version: '0.52.0',
    date: '2026-06-16',
    title: 'Konfigurierbare Beschaffungs-Regeln (Artikel-Tag → Quelle)',
    tag: 'feature',
    changes: [
      'Neue Regel-Verwaltung in den Einstellungen: Artikel-Tags lassen sich einem Verhalten zuordnen — „Immer bestellen“, „Immer aus Lager“ oder „Lager bevorzugen (sonst bestellen)“.',
      'Die Quelle-Vorschläge im Tab „Bedarf / Material“ berücksichtigen diese Regeln: eine passende Tag-Regel schlägt die reine Lager-Heuristik (Vorrang: Immer bestellen > Immer aus Lager > Lager bevorzugen); ohne Regel gilt weiter „Lager wenn verfügbar, sonst bestellen“.',
    ],
  },
  {
    version: '0.51.0',
    date: '2026-06-16',
    title: 'Bedarf/Material: Quelle je Position (Bestellen/Lager) + Listen-Status',
    tag: 'feature',
    changes: [
      'Jede Materialposition hat jetzt eine Quelle: „Bestellen“ oder „Lager“. Das System schlägt sie vor (Lager, wenn der verfügbare Bestand den offenen Bedarf deckt — sonst bestellen); der Vorschlag ist je Zeile überschreibbar.',
      'Neuer Listen-Status oben: Engineering → Bereit zum Bestellen → Bestellt. Manuell umschaltbar; „Bestellt“ wird automatisch gesetzt, sobald aus der Liste eine Bestellung erzeugt wurde.',
      '„Bestellung erzeugen“ berücksichtigt nur Positionen mit Quelle „Bestellen“; Lager-Positionen sind dafür gesperrt.',
    ],
  },
  {
    version: '0.50.0',
    date: '2026-06-16',
    title: 'Artikel-Tags am Artikelstamm',
    tag: 'feature',
    changes: [
      'Artikel können jetzt freie Schlagworte (Tags) tragen — z. B. „Standardartikel“, „Projektbezogen“. Pflege im Artikelformular als Chips (Enter/Komma fügt hinzu). Grundlage für die kommenden Beschaffungs-Vorschläge.',
    ],
  },
  {
    version: '0.49.0',
    date: '2026-06-16',
    title: 'Excel-Import (.xlsx) für die Bedarfsliste',
    tag: 'improvement',
    changes: [
      'Die Bedarfsliste lässt sich jetzt direkt aus Excel (.xlsx/.xls) importieren — nicht mehr nur CSV/TSV. Erstes Tabellenblatt, gleiche Spalten (Artikelnummer, Menge, Bezeichnung/Notiz) und derselbe strikte Artikel-Abgleich (ADR 0024).',
    ],
  },
  {
    version: '0.48.0',
    date: '2026-06-16',
    title: 'Material-Import: nur bekannte Artikel, Fehlerliste mit Zeilennummer',
    tag: 'improvement',
    changes: [
      'Beim CSV-Import der Bedarfsliste werden nur Zeilen mit einem im Artikelstamm angelegten Artikel importiert. Unbekannte Artikelnummern werden mit Zeilennummer als Fehlerliste angezeigt und nicht importiert.',
      'Bezeichnung und Einheit werden aus dem Artikel gezogen; die CSV-Spalte „Bezeichnung/Notiz" landet als Notiz an der Position.',
    ],
  },
  {
    version: '0.47.0',
    date: '2026-06-16',
    title: 'Projekt-Material: Tab „Bedarf / Material“ + Artikelpflicht bei manueller Position',
    tag: 'improvement',
    changes: [
      'Der Projekt-Tab heißt jetzt „Bedarf / Material“.',
      'Beim manuellen „+ Position“ öffnet sich sofort die durchsuchbare Artikel-Auswahltabelle (Tastaturnavigation). Ein Artikel ist Pflicht; Bezeichnung und Einheit kommen aus dem Artikelstamm — erfasst werden nur Bedarfsmenge und eine optionale Notiz.',
    ],
  },
  {
    version: '0.46.0',
    date: '2026-06-16',
    title: 'Klare, stabile Brotkrumen',
    tag: 'improvement',
    changes: [
      'Brotkrumen sind jetzt stabil und modulbasiert: Eine Seite zeigt immer ihren festen Pfad (z. B. „Verkauf › RE-…“, „CRM › Kunde“) — unabhängig davon, woher man kam. Kein mitwachsender Verlauf mehr bei Quernavigation.',
      'Querverweise (Beleg öffnen, Kunden-/Projektlinks) navigieren weiterhin in der App; der Zurück-Button führt zur Herkunft, Cmd-/Mittelklick öffnet wie gewohnt in einem neuen Tab.',
    ],
  },
  {
    version: '0.45.0',
    date: '2026-06-16',
    title: 'Suche: Fundstellen-Vorschau (Textauszug) bei Volltext-Treffern',
    tag: 'feature',
    changes: [
      'Die Such-Ergebnisseite zeigt jetzt unter dem Treffer einen Auszug aus dem gefundenen Text — man sieht sofort, woher der Treffer kommt (z. B. die Stelle im PDF-Inhalt). Der Suchbegriff ist im Auszug hervorgehoben.',
      'Technisch: Meilisearch liefert per Crop/Highlight einen gekürzten Ausschnitt des durchsuchten Textes; der Treffer trägt ein neues, optionales Feld „snippet".',
    ],
  },
  {
    version: '0.44.0',
    date: '2026-06-16',
    title: 'Belegerfassung: Lieferangaben + Adresswarntext (neue Felder)',
    tag: 'feature',
    changes: [
      'Neuer Abschnitt „Lieferangaben" im Beleg: Liefertermin, Lieferzeit (Freitext), Versandart, Gewicht (kg) und Bezugstext — am Beleg gespeichert und im Editor bearbeitbar.',
      'Neuer „Adresswarntext" am Kundenstamm (CRM): ein roter Hinweis, der oben in der Belegerfassung zum Kunden angezeigt wird (z. B. „Rechnungen an …“). Pflegbar im Kunden-Formular.',
      'Schema-Migration (additiv, nullable): sales_documents um delivery_date/-time_text, shipping_method/-weight_kg, reference_text; customers um warning_text.',
    ],
  },
  {
    version: '0.43.0',
    date: '2026-06-16',
    title: 'Kunden-Belege filtern & Notizen erfassen',
    tag: 'feature',
    changes: [
      'Der Belege-Tab eines Kunden lässt sich jetzt nach Belegart und Datumsbereich (von/bis) filtern und per Freitext über Nummer/Belegart durchsuchen — mit Trefferzähler und „Filter zurücksetzen“.',
      'Der Notizen-Tab hat einen Button „Notiz hinzufügen“ (bzw. „Bearbeiten“): Notizen werden in einem Dialog erfasst und direkt gespeichert.',
    ],
  },
  {
    version: '0.42.0',
    date: '2026-06-16',
    title: 'Kunden-Detailseite als Cockpit',
    tag: 'feature',
    changes: [
      'Neuer Kopf mit Kundennummer, Adresse und Angelegt-/Geändert-Verlauf; Übersicht zeigt eine KPI-Leiste (Umsatz laufendes Jahr vs. Vorjahr mit Trend, Beleganzahl, letzter Beleg).',
      'Neuer Tab „Umsatz“: Vergleichs-Balkendiagramm aktuelles Jahr vs. Vorjahr je Monat plus Tabelle aller Geschäftsjahre im Vergleich.',
      'Tab „Belege“ als Master-Detail: Belegliste mit Netto/Brutto und darunter die Positionen des gewählten Belegs — Beleg direkt öffnen möglich.',
    ],
  },
  {
    version: '0.41.0',
    date: '2026-06-16',
    title: 'Beleg-Editor auch im normalen Fenster (kein Vollbild-Zwang)',
    tag: 'improvement',
    changes: [
      '„Bearbeiten" öffnet den Beleg-Editor jetzt eingebettet in der App (mit Sidebar/Topbar) statt zwingend als separaten Vollbild-Tab — gleiche Maske (Belegerfassung + Positionen), nur ohne „Maximal-Modus".',
      'Wer den Vollbild-Tab weiter möchte: neue Schaltfläche „Im Vollbild öffnen" auf der Belegseite. Gilt für Verkauf und Einkauf.',
    ],
  },
  {
    version: '0.40.0',
    date: '2026-06-16',
    title: 'Such-Ergebnisseite: Meilisearch-Treffer als vollständige Liste',
    tag: 'feature',
    changes: [
      'Neue Seite „Suche" (/suche): zeigt die Meilisearch-Treffer als vollständige, nach Typ gruppierte Ergebnisliste (Kunden, Projekte, Belege, Artikel …) mit Trefferzahl — Gegenstück zur ⌘K-Schnellsuche. Jede Zeile springt zum Datensatz.',
      'Der Suchbegriff lebt in der URL (?q=) — Ergebnisse sind verlink- und teilbar; das Suchfeld auf der Seite verfeinert live.',
      'Die ⌘K-Palette hat unten einen neuen Eintrag „Alle Ergebnisse anzeigen", der auf die Ergebnisseite führt.',
    ],
  },
  {
    version: '0.39.0',
    date: '2026-06-16',
    title: 'Belegerfassung als gruppierte Maske (klassischer ERP-Aufbau)',
    tag: 'improvement',
    changes: [
      'Der Reiter „Belegerfassung" im Vollbild-Editor ist jetzt in thematische Abschnitte gegliedert (Adressinfo, Kommunikation, Beleginfo, Konditionen & Steuer, Referenzen, Projekt, Belegnotiz) statt eines flachen Feld-Rasters.',
      'Adressinfo und Kommunikation werden read-only aus dem Kundenstamm gezogen (Kundennummer, Firmenname, Straße/PLZ/Ort/Land, E-Mail, Telefon, USt-IdNr.). Gilt für Verkauf und Einkauf.',
    ],
  },
  {
    version: '0.38.2',
    date: '2026-06-16',
    title: 'Redis ist Teil des Deployment-Stacks (DMS-Abgleich out of the box)',
    tag: 'fix',
    changes: [
      'Redis läuft jetzt als Service im selben Docker-Compose-Stack wie API und PostgreSQL — die API erreicht ihn intern als „redis" (REDIS_URL Default redis://redis:6379). Damit funktionieren Volltext-Extraktion und WebDAV-Abgleich ohne separate Redis-Resource und ohne Netzwerk-Konfiguration.',
      'Behebt „getaddrinfo ENOTFOUND" beim Verbinden zu einem extern angelegten Redis in einem anderen Docker-Netz.',
    ],
  },
  {
    version: '0.38.1',
    date: '2026-06-16',
    title: 'Robustheit: fehlerhafte Redis-Verbindung legt die API nicht mehr lahm',
    tag: 'fix',
    changes: [
      'Eine falsche oder (noch) nicht erreichbare REDIS_URL führte beim Start zu einem Absturz der API (unbehandeltes Redis-Verbindungs-Event) — bis hin zu „No available server". Jetzt werden Verbindungsfehler sauber abgefangen und nur geloggt.',
      'BullMQ-Verbindung mit belastbarem Retry (gibt nie auf, Backoff bis 10 s) — Redis darf später hochkommen, ohne API-Neustart. Der periodische Datei-Abgleich wird nicht-blockierend geplant; ein Redis-Ausfall hält den Start weder auf noch lässt er ihn scheitern.',
      'Die Volltext-Extraktion fällt bei Redis-Problemen still auf „nicht eingereiht" zurück — der Datei-Upload/-Commit schlägt dadurch nie fehl.',
    ],
  },
  {
    version: '0.38.0',
    date: '2026-06-16',
    title: 'Neues Analyse-Modul: modulübergreifendes Business-Cockpit',
    tag: 'feature',
    changes: [
      'Neuer Menüpunkt „Analyse" (Übersicht) mit modulübergreifenden Kennzahlen: Umsatz, Auftragsbestand, offene Posten, aktiver Auftragswert, Lagerwert und offenes Bestellvolumen als KPI-Kacheln.',
      'Vier Auswertungsbereiche mit Diagrammen (recharts): Vertrieb (Umsatzverlauf, Umsatz nach Kunde, Angebots-Trichter, Belegarten), Finanzen (OP-Altersstruktur, OP nach Kunde), Projekte (Projektampel, Projekte nach Status), Lager & Einkauf (Lagerwert je Warengruppe, größte Unterdeckung).',
      'Datenbasis wie das Dashboard: Aggregation aus den vorhandenen Modul-Endpunkten (kein zusätzliches Backend). Geldbeträge rechnen intern in Mikro-Einheiten (kein Float).',
    ],
  },
  {
    version: '0.37.0',
    date: '2026-06-15',
    title: 'Vollbild-Editor als klassische Belegmaske (3-Spalten-Layout)',
    tag: 'improvement',
    changes: [
      'Der Vollbild-Editor (Verkauf & Einkauf) ist jetzt im gewohnten ERP-Belegmasken-Layout aufgebaut: Reiter „Belegerfassung / Positionen", links eine Kontextspalte zur markierten Position (Artikel, Bestand/Verfügbar/Einheit, editierbarer Langtext), in der Mitte die Positionstabelle mit Kopf-Streifen, rechts eine Aktionsleiste mit Tastaturkürzeln.',
      'Die markierte Zeile steuert die linke Kontextspalte (ersetzt das zeilenweise Aufklappen). „Verfügbar" = Bestand minus aktive Reservierungen.',
      'Neue Funktionstasten passend zur Aktionsleiste: F3 neue Position, F8 duplizieren, F4 löschen (zusätzlich zu ⌘/Strg+D, ⌘/Strg+⌫). Summenleiste zeigt Warenwert, MwSt und Gesamtbetrag.',
    ],
  },
  {
    version: '0.36.0',
    date: '2026-06-15',
    title: 'Belegvorschau gestapelt & Editor mit Artikel-Modal und Positionsdetails',
    tag: 'improvement',
    changes: [
      'Beleg-Vorschau: Daten und PDF stehen jetzt untereinander statt nebeneinander — in zwei eigenständigen Containern, die sich einzeln ein- und ausklappen lassen. Die PDF wird erst beim Aufklappen geladen.',
      'Vollbild-Editor: Die Artikelauswahl öffnet jetzt — wie bei der Kundenauswahl — ein Modal mit durchsuchbarer Artikeltabelle (Nummer, Bezeichnung, VK-Preis) statt eines Inline-Dropdowns.',
      'Vollbild-Editor: Ein Klick auf die Positionsnummer klappt die Positionsdetails auf — mit editierbarem Langtext und allen Stammdaten des verknüpften Artikels (Hersteller, EAN, Zolltarifnr., Gewicht, Ursprungsland, Einheit, Preise, Bestand …).',
    ],
  },
  {
    version: '0.35.0',
    date: '2026-06-15',
    title: 'Belege: Vorschau (Daten + PDF) und Bearbeiten nur im Vollbild-Editor',
    tag: 'improvement',
    changes: [
      'Die Beleg-Detailseite ist jetzt eine reine Vorschau: links die strukturierten Daten (Positionen, Konditionen, Belegfluss), rechts die echte Beleg-PDF. Auf schmalen Bildschirmen per Umschalter „Daten / Beleg (PDF)“.',
      'Bearbeitet wird ausschließlich im Vollbild-Editor — erreichbar über die neue, prominente Schaltfläche „Bearbeiten“. Inline-Bearbeitung von Kopf und Positionen auf der Detailseite entfällt; Workflow-Aktionen (Status, Wandeln, Anzahlung, Schlussrechnung, Warenausgang, Löschen, PDF) bleiben erhalten.',
      'Der Vollbild-Editor zeigt jetzt wirklich alle Kopf-Felder — ergänzt um „Steuerfreie Rechnung (USt 0 %)“ samt Begründung (nur sichtbar, wenn Steuerbefreiung aktiv ist).',
      'Tastatur-first: ⌘/Strg+D dupliziert die fokussierte Position, ⌘/Strg+⌫ löscht sie, Esc schließt den Editor und „?“ blendet eine Übersicht aller Tastaturkürzel ein.',
    ],
  },
  {
    version: '0.34.0',
    date: '2026-06-15',
    title: 'Moderne Datumsauswahl in der ganzen App',
    tag: 'improvement',
    changes: [
      'Alle Datumsfelder nutzen jetzt einen einheitlichen, modernen Kalender-Picker (Popover statt OS-Dialog) — deutsche Anzeige (TT.MM.JJJJ), Wochenstart Montag.',
      'Komfort: Monats-/Jahres-Navigation, Hervorhebung von heute und Auswahl, Schnellaktion „Heute“, Tastaturbedienung (Pfeile, Bild ↑/↓, ↵/Esc) und optionales Löschen bei freiwilligen Datumsfeldern.',
      'Umgestellt u. a. in: Verkaufsbelege (Anlegen, Kopf, Vollbild-Editor), Projekte/Phasen/Meilensteine/Abnahme, Einkauf, Buchhaltung, CRM-Aktivitäten, HR-Zeiterfassung, Lagerbewegungen sowie Montage/Wartung.',
    ],
  },
  {
    version: '0.33.0',
    date: '2026-06-15',
    title: 'Beleg-Kopf: Referenzen, Skonto & steuerfreie Rechnungen',
    tag: 'feature',
    changes: [
      'Neue Kopf-Felder beim Anlegen und Bearbeiten von Belegen: „Ihre Referenz/Bestellnr.“ und „Unser Zeichen/Sachbearbeiter“.',
      'Zahlungsziel (Tage) wird aus dem Kundenstamm vorgeschlagen und steuert die Fälligkeit; zusätzlich Skonto in % mit Skonto-Frist.',
      'Steuerfreie Rechnung (USt 0 %) als Schalter inkl. Begründung (z. B. Reverse Charge) — erscheint als Hinweis auf dem PDF.',
      'Referenzen, Skonto-Hinweis und Steuerbefreiung werden im Beleg-PDF (DE/EN) und in der Belegdetail-Ansicht ausgegeben; bei Umwandlung/Folgerechnungen werden die Konditionen übernommen.',
    ],
  },
  {
    version: '0.32.0',
    date: '2026-06-15',
    title: 'Beleg-Erstellung erweitert',
    tag: 'improvement',
    changes: [
      'Belegart jetzt direkt im Anlegen-Dialog wählbar (nicht mehr nur über den Tab).',
      'Neue Felder: Währung, „Gültig bis“ (bei Angeboten/Pro-forma) und „Fällig am“ (bei Rechnungen) — die Fälligkeit wird aus dem Zahlungsziel des Kunden automatisch vorgeschlagen.',
      'Belegdatum ist mit dem heutigen Tag vorbelegt; nach Kundenwahl zeigt eine Infozeile Nummer, Sprache, Zahlungsziel und USt-IdNr.',
    ],
  },
  {
    version: '0.31.0',
    date: '2026-06-15',
    title: 'Projekt-Auswahl als großes Such-Modal',
    tag: 'improvement',
    changes: [
      'Auch die Projekt-Auswahl öffnet jetzt das große Such-Modal mit Tabelle (Projekt, Code, Kunde) und Tastaturbedienung — wie bei Kunden/Lieferanten.',
      'Umgesetzt in: Angebot/Auftrag, Bestellung, Wartung und beim Projektwechsel am Beleg.',
    ],
  },
  {
    version: '0.30.0',
    date: '2026-06-15',
    title: 'Dateien: Zwei-Wege-Abgleich mit dem Netzlaufwerk',
    tag: 'feature',
    changes: [
      'Dateien, die direkt im eingebundenen Netzlaufwerk (Nextcloud/WebDAV in Finder oder Explorer) angelegt, geändert, umbenannt, verschoben oder gelöscht werden, übernimmt ProERP jetzt automatisch — sie erscheinen mit Projekt-/Kundenbezug in der Ablage und werden für die Volltextsuche indexiert. Der Abgleich läuft regelmäßig im Hintergrund und lässt sich manuell anstoßen.',
      'Umbenennen/Verschieben im Netzlaufwerk bleibt als dieselbe Datei erhalten (über die stabile Nextcloud-Datei-ID), statt als Löschen+Neuanlegen. Ein Sicherheitsschutz verhindert versehentliche Massen-Löschungen bei einem unvollständigen Abgleich.',
    ],
  },
  {
    version: '0.29.0',
    date: '2026-06-15',
    title: 'Dateien: Nextcloud-Speicher mit echter Ordnerstruktur',
    tag: 'feature',
    changes: [
      'Vorbereitung für einen in Finder/Explorer einbindbaren Dateispeicher: Dateien können jetzt in einem Nextcloud-/WebDAV-Speicher abgelegt werden (per Umgebungsvariablen aktivierbar, sonst weiterhin S3). Damit lässt sich derselbe Ablageort später als Netzlaufwerk/Mount im Betriebssystem öffnen.',
      'Dateien werden unter sprechenden Pfaden statt kryptischer IDs gespeichert (z. B. „Projekte/<Projekt>/<Ordner>/<Datei>"); gleichnamige Dateien erhalten wie im Explorer automatisch „ (1)", „ (2)". Umbenennen/Verschieben in ProERP zieht die Datei im Speicher mit; Up-/Download und Vorschau laufen über die App.',
    ],
  },
  {
    version: '0.28.0',
    date: '2026-06-15',
    title: 'Kunden-/Lieferant-Auswahl als großes Modal',
    tag: 'improvement',
    changes: [
      'Die Auswahl von Kunden, Lieferanten und Adressen öffnet jetzt ein großes, zentriertes Modal (statt eines kleinen Aufklapp-Popups) mit Titel, Suchleiste und breiter Tabelle — deutlich übersichtlicher bei vielen Einträgen.',
      'Tastatur weiterhin: Tippen filtert, ↑/↓ navigiert, ↵ wählt, Esc schließt.',
    ],
  },
  {
    version: '0.27.0',
    date: '2026-06-15',
    title: 'Kunden-/Lieferant-Auswahl als Such-Popup mit Tabelle',
    tag: 'improvement',
    changes: [
      'Die Auswahl von Kunden, Lieferanten und Adressen öffnet jetzt ein Popup mit durchsuchbarer Tabelle (Name, Nummer/Typ, Ort) statt einer langen Aufklappliste.',
      'Bedienung per Tastatur: Tippen filtert sofort, ↑/↓ navigiert, ↵ wählt, Esc schließt.',
      'Umgesetzt überall, wo Kunden/Lieferanten gewählt werden: Ansprechpartner, Artikel-Standardlieferant, Angebot/Auftrag (Kunde), Bestellung (Lieferant), Projekt (Kunde) und Wartung.',
    ],
  },
  {
    version: '0.26.0',
    date: '2026-06-15',
    title: 'CRM: Ansprechpartner einer anderen Adresse zuordnen',
    tag: 'improvement',
    changes: [
      'Im Ansprechpartner-Dialog lässt sich die zugeordnete Adresse (Kunde/Lieferant) jetzt auch nachträglich ändern — der Kontakt wird damit verschoben. Bisher war die Zuordnung nach dem Anlegen fix.',
    ],
  },
  {
    version: '0.25.0',
    date: '2026-06-15',
    title: 'CRM: automatische, fortlaufende Kundennummer',
    tag: 'improvement',
    changes: [
      'Beim Anlegen eines Kunden wird die Kundennummer automatisch fortlaufend vergeben (K-1001, K-1002 …), wenn das Feld leer bleibt — eine eigene Nummer kann weiterhin manuell eingetragen werden.',
      'Lieferanten erhalten einen eigenen Nummernkreis (L-1001 …).',
      'Nummern werden nie wiederverwendet (auch nicht nach dem Löschen) und sind gegen gleichzeitiges Anlegen aus zwei Sitzungen abgesichert.',
    ],
  },
  {
    version: '0.24.0',
    date: '2026-06-14',
    title: 'Mobil-Optimierung (iPhone)',
    tag: 'improvement',
    changes: [
      'Listen werden am Smartphone als kompakte Karten statt breiter Tabellen dargestellt — kein horizontales Scrollen mehr. Umgesetzt für Ansprechpartner, HR (Zeiterfassung/Mitarbeiter/Positionen), Buchhaltung (offene Posten), Montage (Einsätze/Wartungen) und Benutzer & Rechte. Desktop bleibt unverändert die Tabellenansicht.',
      'Auch die Detailansichten sind mobil aufgeräumt: Projekt-Tabs (Stunden, Material, Phasen, Controlling, Vorkalkulation, Beschaffung, Belege), Beleg- und Bestell-Positionen, Kunden-Tabs sowie Artikel-/Lagerbewegungen zeigen am iPhone Karten statt Quertabellen.',
      'Dialoge/Formulare sind auf kleinen Bildschirmen jetzt scrollbar und laufen nicht mehr oben/unten aus dem Bild; Seiten-Kopfzeilen stapeln Titel und Aktionen sauber, statt sich zu überlagern.',
    ],
  },
  {
    version: '0.23.0',
    date: '2026-06-12',
    title: 'Volltextsuche im Dateiinhalt',
    tag: 'feature',
    changes: [
      'Die universelle Suche durchsucht jetzt den Inhalt von Dateien, nicht nur den Dateinamen: PDFs, Word-Dokumente (.docx) und Textdateien werden nach dem Hochladen automatisch im Hintergrund ausgelesen und indexiert. So findet man ein Dokument auch über ein Wort, das nur im Text steht.',
      'Die Inhaltsextraktion läuft entkoppelt über eine Warteschlange (Redis/BullMQ) und blockiert den Upload nicht; bei Fehlern bleibt die Datei über den Namen auffindbar. Gescannte Bilder (OCR) und Excel/PowerPoint folgen, sobald die nötigen Komponenten freigeschaltet sind.',
    ],
  },
  {
    version: '0.22.0',
    date: '2026-06-12',
    title: 'Dateien: Vorschau & Such-Treffer öffnen Details',
    tag: 'feature',
    changes: [
      'Dateien öffnen statt herunterladen: Ein Klick auf eine Datei öffnet sie jetzt im neuen Tab und zeigt PDF/Bilder direkt an (statt sie sofort herunterzuladen). Zusätzlich im Datei-Menü: „Vorschau" (eingebettete Anzeige in der App) und „Herunterladen".',
      'Such-Treffer öffnen jetzt die passende Detailansicht: Datei → ihr Ordner, Ansprechpartner/Montage-Einsatz/Wartung/Mitarbeiter → direkt der jeweilige Dialog im richtigen Tab.',
      'Projekt-Dateien zentral erreichbar: In „Dateien" lässt sich über den Ablage-Umschalter zwischen der allgemeinen Ablage und einzelnen Projekten wechseln — ohne ins Projekt navigieren zu müssen. Such-Treffer auf eine Projekt-Datei wählen das passende Projekt automatisch vor.',
      'Datei-Browser mit Explorer-Bedienung: Dateien per Klick auswählen (Strg/Umschalt für Mehrfachauswahl), Doppelklick öffnet, Rechtsklick zeigt das Kontextmenü (Öffnen, Vorschau, Verschieben/Kopieren nach, Duplizieren, Umbenennen, Löschen). Tastatur: Entf, F2, Enter, Strg+A. Dateien lassen sich per Drag & Drop auf Ordner oder in den Baum verschieben; auch Ordner können verschoben werden.',
      'Datei-Browser navigiert jetzt wie ein echter Finder: Unterordner erscheinen als Zeilen in der Inhaltsansicht (Klick öffnet sie), es gibt „eine Ebene nach oben", und Ordner lassen sich direkt umbenennen und löschen. Auf dem Smartphone funktioniert die komplette Navigation ohne Ordnerbaum.',
      'Datei-Browser flexibler & aufgeräumter: Ordnerspalte per Drag-Griff in der Breite verstellbar (wird gemerkt) und komplett ein-/ausklappbar; Dateien haben typ-spezifische Icons und lesbare Typ-Bezeichnungen (PDF, Word, Bild …) statt technischer MIME-Typen.',
      'Mehrere Dateien auf einmal hochladen: per Mehrfachauswahl oder Drag & Drop direkt auf die Dateiliste — mit Fortschrittsanzeige (z. B. „Lädt 3/8 …").',
      'Formular-Dialoge sind auf dem Smartphone nutzbar: die Eingabefelder stapeln sich auf schmalen Bildschirmen, statt sich zu quetschen.',
    ],
  },
  {
    version: '0.21.0',
    date: '2026-06-11',
    title: 'Universelle Suche: mehr Treffer & smarter',
    tag: 'improvement',
    changes: [
      'Die ⌘K-Suche findet jetzt auch Ansprechpartner, Mitarbeiter, Fertigungs-, Montage- und Wartungsaufträge, Zahlungen, Warengruppen und Stellen — zusätzlich zu Projekten, Kunden, Artikeln, Belegen und Dateien.',
      'Tippfehler-tolerant: kleine Vertipper liefern trotzdem Treffer (mit aktivem Meilisearch).',
      'Treffer trotz Trennzeichen: „P1001" findet „P-1001", „müllerag" findet „Müller.AG“ — Bindestriche, Punkte etc. stören nicht mehr.',
      'Ohne Suchdienst greift weiterhin die Datenbank-Suche mit derselben Trefferabdeckung.',
    ],
  },
  {
    version: '0.20.0',
    date: '2026-06-11',
    title: 'CRM: Ansprechpartner-Verzeichnis',
    tag: 'feature',
    changes: [
      'Neues CRM-Submenü „Ansprechpartner“: alle Kontaktpersonen über Kunden und Lieferanten hinweg auf einen Blick, mit Suche.',
      'Jeder Ansprechpartner ist mit einer Adresse (Kunde/Lieferant) verknüpft — als Querverweis sichtbar.',
      'Anlegen/Bearbeiten mit Adress-Auswahl und mehr Feldern: Position, Abteilung, E-Mail, Telefon, Mobil, Notizen, Hauptansprechpartner.',
      'Behoben: Bearbeiten-Dialoge zeigten teils zwei Bildlaufleisten — jetzt nur noch eine.',
    ],
  },
  {
    version: '0.19.0',
    date: '2026-06-11',
    title: 'Artikel: Standardlieferant, letzter Bezugspreis & größeres Modal',
    tag: 'feature',
    changes: [
      'Ist kein EK gepflegt, wird automatisch der letzte Bezugspreis aus Einkaufsbelegen verwendet — gekennzeichnet mit „Letzter Bezugspreis“.',
      'Statt „Beschaffung (Eigenfertigung/Zukauf)“ jetzt ein Standardlieferant-Dropdown (Lieferanten aus dem CRM); im Artikel als Querverweis sichtbar.',
      'Bearbeiten-Dialog deutlich breiter und in klare Abschnitte gegliedert — bessere Übersicht.',
      'Feld „Kategorie“ entfernt — die Warengruppe ist die Kategorie.',
      'EK-Staffelpreise bleiben im Tab „Preise“ pflegbar.',
    ],
  },
  {
    version: '0.18.0',
    date: '2026-06-11',
    title: 'Dokumentenmanagement: Dateiablage im Finder-Stil',
    tag: 'feature',
    changes: [
      'Neuer Bereich „Dateien“: Ordnerbaum links, Dateiliste rechts — Ordner anlegen, Dateien hochladen, öffnen, umbenennen und löschen.',
      'Globale Ablage (Vorlagen, QM, Verträge) und ein eigener Tab „Dateien“ je Projekt — Uploads landen automatisch im richtigen Bereich.',
      'Dateien liegen im Objektspeicher (S3/MinIO); Upload läuft direkt zum Speicher (presigned), die App bleibt schlank.',
      'Hochgeladene Dateien sind über die universelle Suche (⌘K) auffindbar.',
    ],
  },
  {
    version: '0.17.0',
    date: '2026-06-11',
    title: 'Artikel: EK/VK-Preis-Historie & Margen-Auswertung',
    tag: 'feature',
    changes: [
      'Jede Preisänderung (EK/VK) wird jetzt historisiert — Bestandspreise wurden als Startpunkt übernommen.',
      'Neues Stufen-Diagramm im Auswertung-Tab: EK- und VK-Entwicklung über die Zeit, fortgeschrieben bis heute.',
      'Margen-Kennzahlen je Artikel: VK − EK absolut und in Prozent vom VK.',
      'Tabelle der letzten Preisänderungen mit Veränderung in Prozent (Erhöhungen rot, Senkungen grün).',
    ],
  },
  {
    version: '0.16.0',
    date: '2026-06-11',
    title: 'Artikelbewegungen: Ledger mit Filtern & Export',
    tag: 'feature',
    changes: [
      'Bewegungen je Artikel mit laufendem Bestand („Bestand danach“) — rückgerechnet vom aktuellen Stand, Korrekturen brechen die Kette sichtbar ab.',
      'Neue Spalten: EK-Snapshot je Bewegung und Bewegungswert (Menge × EK); Projektbezug als klickbarer Querverweis.',
      'Filter nach Art (Zugänge/Abgänge/Korrekturen), Zeitraum und Volltext in Notiz/Projekt — mit Summenzeile über die gefilterte Menge.',
      'CSV-Export der gefilterten Bewegungen (Semikolon, deutsches Zahlenformat).',
      'Stabilität nach Updates: Lädt ein noch geöffneter Browser nach einem Deploy einen veralteten Baustein, lädt die App sich jetzt einmal selbst neu statt mit Fehlerseite stehen zu bleiben.',
    ],
  },
  {
    version: '0.15.0',
    date: '2026-06-11',
    title: 'Minimal-Feinschliff „Hairline“ (A1)',
    tag: 'improvement',
    changes: [
      'Karten app-weit mit feiner Kontur statt Schatten und Verlauf — ruhigeres, klareres Bild (Variante A1 aus der Vorschlagsrunde).',
      'Grauer Fußstreifen in Karten entfernt — KPI-Kacheln wirken jetzt aus einem Guss.',
      'Seitenleiste weiß, nur durch eine feine Linie abgesetzt.',
      'Tabellen-Spaltenköpfe ohne Grauhinterlegung.',
    ],
  },
  {
    version: '0.14.0',
    date: '2026-06-11',
    title: 'Artikel-Ausbau & Benutzerverwaltung',
    tag: 'feature',
    changes: [
      'Artikel zweisprachig: englischer Name und Infotexte DE/EN für Belege und Doku.',
      'Neue Artikel-Stammdaten: TARIC-/Zolltarifnummer, Gewicht je Einheit, EAN/GTIN, Ursprungsland, Hersteller und interne Notizen (nie auf Belegen).',
      'Artikel-Detailseite neu: Kennzahlen-Kacheln (Bestand, Bestandswert, Mindestbestand, Gewicht), Karten für Stammdaten, Zoll & Logistik und Infotexte.',
      'Neuer Tab „Auswertung" je Artikel: Zu-/Abgänge je Monat als Diagramm plus Verbrauch der letzten 30/90 Tage.',
      'Benutzerverwaltung ausgebaut: Name und E-Mail änderbar, Aktionen je Benutzer als Menü, Rollen als Badges — Navigationspunkt heißt jetzt „Benutzer & Rechte".',
    ],
  },
  {
    version: '0.13.0',
    date: '2026-06-11',
    title: 'Feinschliff: mehr Leben im Design',
    tag: 'improvement',
    changes: [
      'Umsatz-Diagramm jetzt in Blau mit Farbverlauf — Diagramme dürfen Farbe tragen, die Oberfläche bleibt ruhig.',
      'KPI-Kacheln mit Modul-Symbol rechts oben; kritische Kacheln behalten den roten Hinweis.',
      'Deutlich kräftigere Seitentitel für bessere Orientierung.',
      'Tabellen-Spaltenköpfe in normaler Schreibweise statt Großbuchstaben — ruhigeres Schriftbild.',
      'Login mit ProERP-Logo-Kachel.',
      'Unter der Haube: Formularfelder und Overlays reichen Refs korrekt durch (react-hook-form/Radix unter React 18) — behebt latente Probleme bei Formularen und Schließ-Animationen.',
    ],
  },
  {
    version: '0.12.0',
    date: '2026-06-11',
    title: 'Design-Sprache app-weit vereinheitlicht',
    tag: 'improvement',
    changes: [
      'Alle Module komplett auf die nova-Designsprache umgestellt: rund 520 Stellen mit alten Grau-/Akzent-Klassen auf semantische Design-Tokens migriert.',
      'Häkchen-Felder app-weit als echte Checkbox-Komponente (einheitliche Optik, Tastatur und Fokus-Ring) — 16 Stellen umgestellt.',
      'Suchfelder in allen Listen (Projekte, Kunden, Artikel, Belege, Bestellungen) als einheitliche Eingabegruppe mit Lupen-Symbol.',
      'Querverweise (Kunde, Projekt, Beleg …) im neuen Link-Stil: dezent unterstrichen, voll eingefärbt beim Überfahren.',
      'Autocomplete- und Spalten-Menüs im Beleg-Editor in Popover-Optik angeglichen.',
    ],
  },
  {
    version: '0.11.0',
    date: '2026-06-11',
    title: 'Seitenleiste im sidebar-07-Stil',
    tag: 'improvement',
    changes: [
      'Neuer Workspace-Kopf in der Seitenleiste: Logo-Kachel, Name und Version auf einen Blick.',
      'Einträge mit Unterpunkten klappen auf — „Artikel & Lager" führt jetzt direkt zu Artikeln und Artikelbewegungen.',
      'Klassisches Seitenleisten-Layout (shadcn-Block sidebar-07) statt eingerückter Inhaltskarte.',
    ],
  },
  {
    version: '0.10.0',
    date: '2026-06-11',
    title: 'shadcn-Preset „nova" & Tailwind v4',
    tag: 'improvement',
    changes: [
      'Offizielles shadcn-Preset b0 angewendet: alle UI-Bausteine im neuen nova-Stil (weichere Overlays mit Blur, feinere Fokus-Ringe, ruhigere Flächen) — ADR 0020.',
      'Unterbau auf Tailwind CSS v4 migriert: Design-Tokens leben jetzt im CSS (@theme), kein JS-Preset mehr.',
      'Schrift Inter (lokal gebündelt, keine Google-Fonts-Abhängigkeit für die UI-Schrift).',
      'Kompaktere Bedienelemente nach nova-Maß (Buttons/Eingaben 32 px Standard).',
      'Destruktive Aktionen im neuen nova-Look: rote Schrift auf dezentem roten Grund.',
    ],
  },
  {
    version: '0.9.0',
    date: '2026-06-11',
    title: 'Komplett-Redesign nach shadcn „dashboard-01"',
    tag: 'improvement',
    changes: [
      'Neues Shell-Layout: Inhalt als abgerundete Karte (Inset-Sidebar), Benutzermenü unten in der Seitenleiste mit Avatar, Name und E-Mail (ADR 0019).',
      'Dashboard neu: KPI-Kacheln im shadcn-Stil (große Zahl, Status-Badge, Hinweiszeile) — jede Kachel führt ins Modul.',
      'Neues Umsatz-Diagramm: fakturierter Netto-Umsatz je Monat als Flächen-Chart, umschaltbar 3/6/12 Monate.',
      'Badges moderner: dezent abgerundet statt Großbuchstaben-Pille; Status-Farben bleiben.',
      'Luftigere Maße wie ui.shadcn.com: Schrift 14 px, Eingabefelder und Buttons 36 px Standardhöhe.',
      'Cards mit feinem Schatten und stärkerer Rundung — einheitlich in allen Modulen.',
    ],
  },
  {
    version: '0.8.0',
    date: '2026-06-11',
    title: 'Neues Erscheinungsbild: shadcn-Default-Theme',
    tag: 'improvement',
    changes: [
      'Monochromer Look wie ui.shadcn.com: schwarze Primär-Buttons und Active-States statt Blau, dezente Grauflächen (ADR 0018).',
      'Hellere Rahmenlinien und etwas rundere Ecken — ruhigeres, moderneres Gesamtbild.',
      'Neue Schrift „Geist" (inkl. Geist Mono für Beleg- und Artikelnummern).',
      'Status-Farben (Erfolg/Warnung/Fehler) bleiben farbig — Information statt Dekoration.',
      'Checkbox-Häkchen folgen jetzt ebenfalls dem Theme (vorher Browser-Blau).',
    ],
  },
  {
    version: '0.7.0',
    date: '2026-06-11',
    title: 'UI-Redesign: neue Navigation, Menüs & Dialoge (shadcn/ui)',
    tag: 'improvement',
    changes: [
      'Neue Seitenleiste mit gruppierten Modulen (Vertrieb, Material, Verwaltung, System) — kollabierbar auf Icon-Breite mit Tooltips, Zustand bleibt gespeichert, Strg/⌘+B klappt um.',
      'Benutzermenü oben rechts als Dropdown (Avatar, Passwort ändern, Abmelden) statt loser Icon-Buttons.',
      'Universelle Suche (⌘K) als echte Command-Palette mit Tastaturführung und animiertem Overlay.',
      'Benachrichtigungen (Toasts) neu: gestapelt, schließbar, Erfolg blendet nach 4 s aus, Fehler bleiben stehen.',
      'Tabs auf Detailseiten mit Pfeiltasten-Navigation; Auswahlfelder app-weit einheitlich gestylt.',
      'Login: 6-stelliger 2FA-Code als komfortables Einzelziffern-Feld (OTP-Eingabe).',
      'Dialoge mit sanften Ein-/Ausblend-Animationen und einheitlichem Aufbau (ADR 0017).',
    ],
  },
  {
    version: '0.6.0',
    date: '2026-06-11',
    title: 'Kosten-Controlling: Plan vs. Ist (Vor-/Nachkalkulation)',
    tag: 'feature',
    changes: [
      'Arbeitspakete vollständig abgelöst — Stunden und Material hängen jetzt direkt am Projekt (ADR 0016).',
      'Controlling zeigt Plan gegen Ist: Material je Hauptwarengruppe, Stunden je Position, je mit Abweichung und Gesamtsumme.',
      'Material-Ist = projektbezogene Bestellungen (Wareneingang) + Lagerentnahmen, bewertet zum EK-Snapshot.',
      'Artikel haben eine 4-stellige Warengruppe; die Hauptwarengruppe (Tausenderstelle) gliedert das Material-Controlling.',
      'Stundensätze als Positions-Stammdaten (z. B. Techniker, Monteur); der Mitarbeiter-Satz wird aus der Position vorbefüllt.',
      'Neuer Tab „Vorkalkulation" je Projekt: Plan-Material je Hauptwarengruppe und Plan-Stunden je Position.',
    ],
  },
  {
    version: '0.5.0',
    date: '2026-06-11',
    title: 'Beleg-Editor: Artikel-Autocomplete & Artikelpflicht',
    tag: 'feature',
    changes: [
      'Beim Tippen in einer Position erscheinen Vorschläge aus dem Artikelstamm — gefiltert nach Nummer oder Name.',
      'Auswahl per Tastatur (↑/↓, ↵) oder Klick füllt Beschreibung, Einheit und Preis der Zeile.',
      'Jede Verkaufsbeleg-Position muss eine Artikelnummer haben; Zeilen ohne Artikel werden markiert und blockieren das Speichern.',
    ],
  },
  {
    version: '0.4.0',
    date: '2026-06-11',
    title: 'Navigation: Breadcrumbs & einheitliche Querverweise',
    tag: 'improvement',
    changes: [
      'Kontextuelle Brotkrumen auf allen Detailseiten — sie zeigen den Weg, über den man gekommen ist.',
      'Querverweise zwischen Modulen (Kunde, Projekt, Beleg …) als echte Links: Cmd-/Mittelklick öffnet in neuem Tab.',
    ],
  },
  {
    version: '0.3.0',
    date: '2026-06-11',
    title: 'Dashboard: Kennzahlen auf einen Blick',
    tag: 'feature',
    changes: [
      'Neue Kennzahlen: fakturierter Umsatz (laufendes Jahr) und offene Angebote mit Volumen.',
      'Karte „Projektgesundheit": Ampel-Verteilung über aktive Projekte plus Liste der Projekte mit Handlungsbedarf.',
    ],
  },
  {
    version: '0.2.0',
    date: '2026-06-10',
    title: 'Universelle Suche & Vollbild-Beleg-Editor',
    tag: 'feature',
    changes: [
      'Universelle Suche über alle Module mit ⌘K / Strg+K (Meilisearch mit DB-Fallback, ADR 0015).',
      'Such-Index bleibt per Change-Data-Capture live mit den Daten synchron.',
      'Vollbild-Editor für Belege in eigenem Tab: tastaturgetriebene Positions-Tabelle.',
    ],
  },
  {
    version: '0.1.0',
    date: '2026-06-10',
    title: 'Beleg-PDF nach Firmenvorlage',
    tag: 'feature',
    changes: [
      'Mehrseitiges Beleg-Layout nach Firmenvorlage mit Unicode-Schrift (ADR 0014).',
      'Logo, Anschriftenblock und Spaltenbreiten an die Vorlage angepasst.',
    ],
  },
];

/** Aktuelle App-Version (oberster Changelog-Eintrag). */
export const APP_VERSION = CHANGELOG[0].version;
